import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { 
  Users, Wallet, FileText, Package, PackagePlus, UserCheck, 
  RefreshCw, Plus, Search, Eye, EyeOff, Share2, AlertTriangle, 
  Trash2, ShieldCheck, Sun, Moon, LogOut, Check, Pencil, X, FileCheck, 
  DollarSign, Settings, Building, MapPin, Phone, Mail, Award, Printer, 
  Lock, Database, Activity, Shield, ArrowLeft, Unlock, Download, HardDrive, Clock, CheckCircle2, XCircle, Menu, Globe, Calculator, Camera, Barcode, Palette, Paperclip,
  MessageSquare, Bell, Receipt, ChartColumn, Info, KeyRound, ChevronUp, ChevronDown, Smartphone, Image as ImageIcon,
} from 'lucide-react';
import { I18nProvider, useI18n } from './i18n.jsx';
import {
  BACKEND_URL, getLogoUrl, getPhotoUrl, getSessionToken, setSessionToken,
  authHeaders, setAdminToken, adminHeaders, api,
} from './lib/api.js';
import { loadHtml2pdf } from './lib/pdf.js';
import DynIcon from './components/DynIcon.jsx';
import AttachmentPreviewModal from './components/AttachmentPreviewModal.jsx';
import {
  filterByDate, dateFilterLabel, matchPartySearch, filterCashEntries,
  computeRunningBalances, STANDARD_BANK_OPTIONS,
} from './lib/filters.js';
import DateFilterBar from './components/DateFilterBar.jsx';
import BarcodePreview from './components/BarcodePreview.jsx';
import {
  round2, numOr, packSizeFor, packBreakdown, moneyText, pcsText, priceText,
  packMode, stockQtyType, packInputValue,
  changeUnits, totalPieces, totalPiecesText,
  productToEditForm, stockStatusText, normalizeLineItem,
  describeLineQty, initialPiecesFor,
} from './lib/stockMath.js';
import { SaveHint } from './components/SaveHint.jsx';
import ManageUsersTab from './components/ManageUsersTab.jsx';
import ActivityLogTab from './components/ActivityLogTab.jsx';
import CalculatorOverlay from './components/CalculatorOverlay.jsx';
import { FbrBadge, FbrPrintBlock } from './components/Fbr.jsx';
import { WACard } from './components/WACard.jsx';


function App() {
  const { lang, setLang, t, isRTL } = useI18n();
  // Application Lifecycle States
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [theme, setTheme] = useState('light');
  const [online, setOnline] = useState(true);
  
  // Active Navigation & Business States
  const [activeTab, setActiveTab] = useState('dashboard');
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  
  // Data State Arrays
  const [parties, setParties] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [cashEntries, setCashEntries] = useState([]);
  const [incomeExpenses, setIncomeExpenses] = useState([]);
  const [products, setProducts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);
  
  // Modal / Detail views state
  const [selectedParty, setSelectedParty] = useState(null);
  // One search box per tab: Customers, Suppliers and Stock each keep their own
  // query, so text typed on one tab can no longer silently filter another.
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [staffSearch, setStaffSearch] = useState('');    // Search filter for Staff Directory
  const [salarySearch, setSalarySearch] = useState('');  // Search filter for Payroll History
  const [statusMessage, setStatusMessage] = useState(null);

  // Form State: Add Party (Customer/Supplier)
  const [newParty, setNewParty] = useState({
    name: '', phone: '', party_type: 'CUSTOMER', cnic: '', address: '', area: '', items_supplied: ''
  });
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [showAddAreaInline, setShowAddAreaInline] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [showAddProductTypeInline, setShowAddProductTypeInline] = useState(false);
  const [newProductTypeName, setNewProductTypeName] = useState('');

  // Form State: Ledger Entry (You Gave / You Got)
  const [ledgerForm, setLedgerForm] = useState({ amount: '', entry_type: 'GIVE', description: '', date: new Date().toISOString().split('T')[0], time: '', payment_method: 'CASH', bank_account_id: '' });
  
  // Form State: Cash Entry
  const [cashForm, setCashForm] = useState({ amount: '', entry_type: 'IN', description: '', category: 'Sales', date: new Date().toISOString().split('T')[0], time: '', attachment: null });
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomCategoryInput, setShowCustomCategoryInput] = useState(false);

  // Form State: Income Expense
  const [ieForm, setIeForm] = useState({ type: 'EXPENSE', amount: '', category: 'Rent', description: '', date: new Date().toISOString().split('T')[0], time: '', attachment: null });
  const [customIeCategory, setCustomIeCategory] = useState('');
  const [showCustomIeCategory, setShowCustomIeCategory] = useState(false);
  const [showAddIeModal, setShowAddIeModal] = useState(false);
const [ieDateFilter, setIeDateFilter] = useState({ mode: 'all', from: '', to: '' });
const [ieSearchQuery, setIeSearchQuery] = useState('');

// Ledger & Cash Book filters
const [ledgerDateFilter, setLedgerDateFilter] = useState({ mode: 'all', from: '', to: '' });
const [ledgerSearchQuery, setLedgerSearchQuery] = useState('');
const [customerAreaFilter, setCustomerAreaFilter] = useState('all');
const [supplierAreaFilter, setSupplierAreaFilter] = useState('all');
const [cashDateFilter, setCashDateFilter] = useState({ mode: 'all', from: '', to: '' });
const [cashCategoryFilter, setCashCategoryFilter] = useState('all');
const [cashSearchQuery, setCashSearchQuery] = useState('');
const [bankDateFilter, setBankDateFilter] = useState({ mode: 'all', from: '', to: '' });
const [bankAccountFilter, setBankAccountFilter] = useState('all');
const [bankTransactionTypeFilter, setBankTransactionTypeFilter] = useState('all');
const [previewAttachment, setPreviewAttachment] = useState(null);
const [currentPin, setCurrentPin] = useState('');
const [newPin, setNewPin] = useState('');
const [confirmPin, setConfirmPin] = useState('');
const [pinMessage, setPinMessage] = useState('');
const [pinMessageType, setPinMessageType] = useState(''); // 'success' or 'error'
const [pinVisible, setPinVisible] = useState(false);
const [showSubscriptionExpired, setShowSubscriptionExpired] = useState(false);
const [expiredOrgId, setExpiredOrgId] = useState(null);
const [expiredOrgName, setExpiredOrgName] = useState('');
const [renewalAlreadyRequested, setRenewalAlreadyRequested] = useState(false);
const [renewalMessage, setRenewalMessage] = useState('');
const [showRenewalPlanModal, setShowRenewalPlanModal] = useState(false);
const [selectedRenewalPlan, setSelectedRenewalPlan] = useState('1_month');

  // Form State: Product
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', purchase_price: '', sale_price: '', stock_quantity: '', min_stock_alert: '', unit: 'pcs', product_type: '', expiration_date: '', units_per_bulk: 1, hsn_code: '', fbr_tax_rate: '', fbr_uom: '' });
  const [productPhotoFile, setProductPhotoFile] = useState(null);
  const [editProductPhotoFile, setEditProductPhotoFile] = useState(null);
  const [galleryCategoryFilter, setGalleryCategoryFilter] = useState('all');
  const [gallerySelectMode, setGallerySelectMode] = useState(false);
  const [gallerySelectTarget, setGallerySelectTarget] = useState(null); // { type: 'sale'|'saleEntry'|'purchase'|'purchaseEntry', index?: number }
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [restockModal, setRestockModal] = useState({ show: false, product: null, quantity: '' });
  const [barcodePrintModal, setBarcodePrintModal] = useState({ show: false, product: null, quantity: 1 });

  // Form State: Business Card Designer
  const [designerTemplate, setDesignerTemplate] = useState('minimalist');
  const [designerColor, setDesignerColor] = useState('#2563eb');
  const [designerData, setDesignerData] = useState({ name: '', tagline: '', phone: '', email: '', address: '', website: '', companyName: '', companyTagline: '' });
  const [printLayout, setPrintLayout] = useState('single');
  const [showCardPrintOverlay, setShowCardPrintOverlay] = useState(false);
  // Form State: Ledger & Cash Entry Modals
  const [showAddLedgerModal, setShowAddLedgerModal] = useState(false);
  const [showAddCashModal, setShowAddCashModal] = useState(false);

  // Form State: Attendance
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);

  // Form State: Staff Member Add
  const [staffForm, setStaffForm] = useState({ name: '', phone: '', base_salary: '', salary_type: 'MONTHLY', cnic: '' });
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);

  // Form State: Payroll
  const [payrollForm, setPayrollForm] = useState({ staff_member_id: '', pay_period_start: new Date().toISOString().slice(0, 7) + '-01', pay_period_end: new Date().toISOString().split('T')[0], allowances: '0', deductions: '0', status: 'UNPAID' });
  const [showAddPayrollModal, setShowAddPayrollModal] = useState(false);

  // Form State: Business Settings
  const [businessForm, setBusinessForm] = useState({ name: '', address: '', email: '', phone: '', currency: 'PKR', language: 'en', logo: '' });
  const [bankForm, setBankForm] = useState({ name: '', account_title: '', account_number: '', iban: '', branch: '', opening_balance: '0', is_active: true, notes: '' });
  const [businessLogoFile, setBusinessLogoFile] = useState(null);
  const [showEditBusinessModal, setShowEditBusinessModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [bankAccountNameOther, setBankAccountNameOther] = useState(false);

  // Print Invoice View
  const [printInvoiceData, setPrintInvoiceData] = useState(null);

  // Print Individual Entry View
  const [printEntryData, setPrintEntryData] = useState(null);

  // Onboarding & Multi-Tenant States
  const [orgAuth, setOrgAuth] = useState(localStorage.getItem('org_id') || '');
  const [orgName, setOrgName] = useState(localStorage.getItem('org_name') || '');
  const [onboardingTab, setOnboardingTab] = useState('login'); // login, register
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminRoute, setIsAdminRoute] = useState(() => window.location.pathname === '/admin-panel');
  const [backupInfo, setBackupInfo] = useState({ last_backup_at: null, last_backup_size: null });
  const [isBackingUp, setIsBackingUp] = useState(false);

  // ── Member / User Session ────────────────────────────────────────────────
  // After org PIN login, user selects which member they are and enters their PIN.
  // Admin (org owner) logs in directly via org PIN without a 2nd PIN.
  const [memberSession, setMemberSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem('member_session') || 'null'); } catch { return null; }
  });
  // memberSession shape: { member_id, member_name, role, permissions: {...} }
  // null means no member has been selected yet (show member-select screen)

  const [showMemberSelect, setShowMemberSelect] = useState(false); // step-2 screen
  const [memberList, setMemberList] = useState([]);                 // active members from API
  const [selectedMemberId, setSelectedMemberId] = useState(null);  // chosen member before PIN entry
  const [memberPinInput, setMemberPinInput] = useState('');
  const [memberPinError, setMemberPinError] = useState(false);
  const [memberPinLoading, setMemberPinLoading] = useState(false);

  const fallbackAdminSession = React.useMemo(() => {
    if (!orgAuth) return null;
    const adminMember = memberList.find(m => m.role === 'ADMIN');
    return {
      member_id: adminMember?.id || 0,
      member_name: adminMember?.name || 'Admin',
      role: 'ADMIN',
      permissions: {
        ledger: true, cash: true, invoices: true, stock: true,
        income_expense: true, staff: true, reports: true,
        settings: true, manage_users: true, can_delete: true, can_edit: true,
      },
    };
  }, [memberList, orgAuth]);

  const currentMemberSession = memberSession || fallbackAdminSession;

  // Helper: resolved permissions from current session
  const perms = currentMemberSession?.permissions || {};
  // If no member session at all (fresh org login), grant full access until member-select completes
  const hasAccess = (key) => !currentMemberSession ? true : (perms[key] !== false && perms[key] !== undefined ? !!perms[key] : false);
  const canEdit   = currentMemberSession ? !!perms.can_edit   : true;
  const canDelete = currentMemberSession ? !!perms.can_delete : true;
  const isAdminMember = !currentMemberSession || currentMemberSession.role === 'ADMIN';
  // ──────────────────────────────────────────────────────────────────────────

  // Registration Form State
  const [regForm, setRegForm] = useState({ name: '', owner_name: '', email: '', phone: '', address: '', subscription_plan: '1_month' });
  const [regSuccess, setRegSuccess] = useState(false);
  const [regError, setRegError] = useState('');

  // Org Login Form State
  const [orgLoginForm, setOrgLoginForm] = useState({ phone: '', pin_code: '' });
  const [orgLoginError, setOrgLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Forgot PIN State
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [forgotPinForm, setForgotPinForm] = useState({ email: '', phone: '', name: '' });
  // Set once a reset code has been requested: { message }. The PIN itself is
  // never shown — the backend emails a single-use code instead.
  const [forgotPinResult, setForgotPinResult] = useState(null);
  const [forgotPinResetForm, setForgotPinResetForm] = useState({ code: '', new_pin: '', confirm_pin: '' });
  const [forgotPinResetError, setForgotPinResetError] = useState('');
  const [forgotPinDone, setForgotPinDone] = useState(false); // PIN successfully reset
  const [forgotPinError, setForgotPinError] = useState('');
  const [forgotPinLoading, setForgotPinLoading] = useState(false);

  // Email OTP sign-in — an alternative way in for the owner. PIN login stays
  // the default and the fallback, so this can never lock anyone out.
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otpSentTo, setOtpSentTo] = useState(''); // address the code actually went to
  const [otpCode, setOtpCode] = useState('');
  const [otpStage, setOtpStage] = useState('email'); // 'email' -> 'code'
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Client-side mirror of the server's resend cooldown. Keyed on the address
  // it was started for, so typing a different email is never blocked by a
  // countdown belonging to the previous one.
  const otpCooldownActive = otpCooldown > 0 && otpSentTo === otpEmail.trim();
  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const id = setTimeout(() => setOtpCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [otpCooldown]);


  // Admin Login State
  const [adminForm, setAdminForm] = useState({ username: '', password: '' });
  const [adminLoginError, setAdminLoginError] = useState('');

  // Admin Dashboard State
  const [adminStats, setAdminStats] = useState(null);
  const [adminOrgs, setAdminOrgs] = useState([]);
  const [selectedAdminOrg, setSelectedAdminOrg] = useState(null); // for backups drawer
  const [orgBackups, setOrgBackups] = useState([]);
  const [recycleBinItems, setRecycleBinItems] = useState([]);
  const [approveModal, setApproveModal] = useState(null); // { id, name, pin: '' }
  const [deleteOrgModal, setDeleteOrgModal] = useState(null); // { id, name }
  const [adminTab, setAdminTab] = useState('requests'); // requests, tenants, stats
  const [adminOrgSearch, setAdminOrgSearch] = useState('');


  // Print Salary Slip View
  const [printSalarySlipData, setPrintSalarySlipData] = useState(null);

  // Reports filters


  // Sale numbers use the first unused compact sequence value; legacy large numbers must not inflate it.
  const generateDailyInvoiceNumber = (invoiceList = invoices) => {
    const prefix = 'INV-';
    const usedNumbers = new Set(invoiceList
      .filter(inv => !inv.is_deleted && (!selectedBusiness || String(inv.business) === String(selectedBusiness.id)))
      .map(inv => String(inv.invoice_number || ''))
      .filter(number => number.startsWith(prefix))
      .map(number => Number.parseInt(number.slice(prefix.length), 10))
      .filter(Number.isFinite));
    let nextNumber = 1;
    while (usedNumbers.has(nextNumber)) nextNumber += 1;
    return `${prefix}${String(nextNumber).padStart(3, '0')}`;
  };

  const generateDailyPurchaseInvoiceNumber = (invoiceList = invoices) => {
    const prefix = 'PI-';
    const highestNumber = invoiceList
      .filter(inv => !inv.is_deleted && (!selectedBusiness || String(inv.business) === String(selectedBusiness.id)))
      .map(inv => String(inv.invoice_number || ''))
      .filter(number => number.startsWith(prefix))
      .map(number => Number.parseInt(number.slice(prefix.length), 10))
      .filter(Number.isFinite)
      .reduce((highest, number) => Math.max(highest, number), 0);
    return `${prefix}${String(highestNumber + 1).padStart(3, '0')}`;
  };

  const createInvoiceWithAvailableNumber = async (invoiceObj) => {
    const savedInvoice = await api('/invoices/', 'POST', invoiceObj);
    if (!savedInvoice?._duplicate) return savedInvoice;

    const freshInvoices = await api('/invoices/');
    const nextNumber = String(invoiceObj.invoice_number || '').startsWith('PI-')
      ? generateDailyPurchaseInvoiceNumber(freshInvoices || [])
      : generateDailyInvoiceNumber(freshInvoices || []);
    const retriedInvoice = await api('/invoices/', 'POST', { ...invoiceObj, invoice_number: nextNumber });
    return retriedInvoice?._duplicate ? null : retriedInvoice;
  };

  // Form State: Purchase Invoice Builder
  const [showPurchaseInvoiceModal, setShowPurchaseInvoiceModal] = useState(false);
  const [purchaseInvoiceBuilder, setPurchaseInvoiceBuilder] = useState({
    party_id: '',
    invoice_number: '',
    issue_date: '',
    issue_time: new Date().toTimeString().slice(0, 5),
    status: 'UNPAID',
    payment_method: 'CASH',
    bank_account_id: '',
    payment_reference: '',
    paid_amount: '',
    discount_amount: '',
    items: [{ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5 }]
  });
  const [piProdDropdownRow, setPiProdDropdownRow] = useState(null);
  const [piProdSearchQuery, setPiProdSearchQuery] = useState('');
  const [piProdHighlightedIdx, setPiProdHighlightedIdx] = useState(0);

  // Form State: Invoice Builder
  const [invoiceBuilder, setInvoiceBuilder] = useState({
    party_id: '',
    invoice_number: generateDailyInvoiceNumber(),
    issue_date: new Date().toISOString().split('T')[0],
    issue_time: new Date().toTimeString().slice(0, 5),
    status: 'UNPAID',
    payment_method: 'CASH',
    bank_account_id: '',
    payment_reference: '',
    paid_amount: '',
    discount_amount: '',
    items: [{ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '' }]
  });
  const invoiceSubmitInProgress = useRef(false);
  // The invoice from the most recent save — used by saveAndWhatsApp to add
  // the FBR invoice number to the card/text (the card itself is composed
  // before the save, when the FBR number does not exist yet).
  const lastSavedInvoiceRef = useRef(null);

  // Edit States
  const [editingParty, setEditingParty] = useState(null);
  const bankNameOptions = [...new Set([
    ...STANDARD_BANK_OPTIONS,
    ...bankAccounts.map(account => account.name).filter(Boolean),
    ...parties.map(party => party.bank_name).filter(Boolean),
  ])].filter(bank => bank !== 'Other');
  const [editingCashEntry, setEditingCashEntry] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingLedgerEntry, setEditingLedgerEntry] = useState(null);
  const [editingStockMovement, setEditingStockMovement] = useState(null);
  const [editingIeEntry, setEditingIeEntry] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { label, onConfirm }

  // Live clock state for header display
  const [liveTime, setLiveTime] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setLiveTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const formatLiveTime = (d) => {
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m}:${s} ${ampm}`;
  };
  const formatLiveDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

  // Global Calculator State
  const [calculatorState, setCalculatorState] = useState({
    isOpen: false,
    value: '',
    label: '',
    onUpdate: null
  });
  const [calculatorTarget, setCalculatorTarget] = useState(null);
  const calcFocusedInput = useRef(null);

  const today = new Date().toISOString().split('T')[0];
  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentTime = () => new Date().toTimeString().slice(0, 5);
  const formatDateTime = (date, time) => {
    if (!date) return '';
    if (!time) return date;
    const parts = time.split(':');
    let h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${date} ${h}:${m} ${ampm}`;
  };
  const sortByDateTimeDesc = (a, b) => {
    const da = new Date(`${a.date || '1970-01-01'}T${a.time || '00:00'}`);
    const db = new Date(`${b.date || '1970-01-01'}T${b.time || '00:00'}`);
    return db - da;
  };
  const sortByDateTimeAsc = (a, b) => sortByDateTimeDesc(b, a);
  const [invoicePeriod, setInvoicePeriod] = useState({ mode: 'all', from: '', to: '' });
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState('');
  const [custSearchQuery, setCustSearchQuery] = useState('');
  const [prodSearchQuery, setProdSearchQuery] = useState('');
  const [custDropdownOpen, setCustDropdownOpen] = useState(false);
  const [prodDropdownRow, setProdDropdownRow] = useState(null);
  const [prodHighlightedIdx, setProdHighlightedIdx] = useState(0);
  const [billBookView, setBillBookView] = useState('builder');
  const [isMobileView, setIsMobileView] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [saleEntryItem, setSaleEntryItem] = useState({ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', qty_type: 'piece' });
  const [saleEntryDropdownOpen, setSaleEntryDropdownOpen] = useState(false);
  const [purchaseEntryItem, setPurchaseEntryItem] = useState({ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5, expiration_date: '', qty_type: 'piece' });
  const [piEntryDropdownOpen, setPiEntryDropdownOpen] = useState(false);
  const saleProductInputRef = useRef(null);
  const purchaseProductInputRef = useRef(null);
  // Once a cashier picks "Packs", every new line starts in Packs too.
  const lastQtyTypeRef = useRef('piece');

  // Live conversion line under the qty box, so nobody does 2 × 12 in their
  // head:  1 pack = 12 pieces · 2 Packs = 24 pieces · Rs. 288 · Stock: 240
  const qtyModeHint = (item, product) => {
    if (!product) return null;
    const upb = packSizeFor(item, product);
    if (upb <= 1) return null;
    const asPacks = item.qty_type === 'pack';
    const pieces = round2(numOr(item.quantity, 0));
    const perPiece = numOr(item.unit_price, 0);
    const stock = numOr(product.stock_quantity, 0);
    const packCount = round2(pieces / upb);
    const packWord = packCount === 1 ? t('pack') : t('packs');
    const pieceWord = pieces === 1 ? t('piece') : t('qtyTypePiece');
    const approx = pieces >= upb ? ` (${packBreakdown(pieces, upb, t)})` : '';
    const qtyText = asPacks
      ? `${packCount} ${packWord} · ${pieces} ${pieceWord}`
      : `${pieces} ${pieceWord}${approx}`;
    const parts = [
      t('onePackEquals', { n: upb }),
      qtyText,
      `Rs. ${round2(pieces * perPiece).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
      `${t('stockShort')}: ${stockStatusText(stock, upb, t)}`,
    ];
    return <div style={{ gridColumn: '1 / -1', fontSize: '0.72rem', color: 'var(--text-muted)' }}>{parts.join('  ·  ')}</div>;
  };

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keep ALL time fields synced with device clock (every 10s)
  const [invoiceTimeManual, setInvoiceTimeManual] = useState(false);
  const [piTimeManual, setPiTimeManual] = useState(false);
  useEffect(() => {
    const syncTime = () => {
      const now = new Date().toTimeString().slice(0, 5);
      if (!invoiceTimeManual) setInvoiceBuilder(prev => ({ ...prev, issue_time: now }));
      if (!piTimeManual) setPurchaseInvoiceBuilder(prev => ({ ...prev, issue_time: now }));
    };
    const timer = setInterval(syncTime, 10000);
    return () => clearInterval(timer);
  }, [invoiceTimeManual, piTimeManual]);

  const filteredSaleProducts = React.useMemo(() => products.filter(p => !p.is_deleted).filter(p => {
    if (!prodSearchQuery) return true;
    return (p.name || '').toLowerCase().includes(prodSearchQuery.toLowerCase()) || (p.sku || '').toLowerCase().includes(prodSearchQuery.toLowerCase());
  }), [products, prodSearchQuery]);

  const filteredPiProducts = React.useMemo(() => products.filter(p => !p.is_deleted).filter(p => {
    if (!piProdSearchQuery) return true;
    return (p.name || '').toLowerCase().includes(piProdSearchQuery.toLowerCase()) || (p.sku || '').toLowerCase().includes(piProdSearchQuery.toLowerCase());
  }), [products, piProdSearchQuery]);

  const [stockView, setStockView] = useState('stock');
  const [stockHistorySearch, setStockHistorySearch] = useState('');
  const [stockHistoryPeriod, setStockHistoryPeriod] = useState({ mode: 'all', from: '', to: '' });
  const [stockProductTypeFilter, setStockProductTypeFilter] = useState('all');
  const [stockHistoryProductTypeFilter, setStockHistoryProductTypeFilter] = useState('all');

  const filteredStockHistory = stockMovements.filter(m => {
    if (!filterByDate(m.date, stockHistoryPeriod)) return false;
    if (stockHistoryProductTypeFilter !== 'all') {
      const prod = products.find(p => p && p.id === m.product);
      if (!prod || prod.product_type !== stockHistoryProductTypeFilter) return false;
    }
    if (stockHistorySearch) {
      const q = stockHistorySearch.toLowerCase();
      const prodName = (products.find(p => p && p.id === m.product) || {}).name || '';
      if (!prodName.toLowerCase().includes(q) && !(m.description || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const filteredInvoices = invoices.filter(i => {
    if (i.is_deleted) return false;
    if (!filterByDate(i.issue_date, invoicePeriod)) return false;
    if (invoiceSearchQuery) {
      const q = invoiceSearchQuery.toLowerCase();
      const cust = (parties.find(p => p && p.id === (i.party || i.party_id)) || null);
      if (cust) {
        const matchName = (cust.name || '').toLowerCase().includes(q);
        const matchPhone = (cust.phone || '').includes(invoiceSearchQuery);
        if (!matchName && !matchPhone) return false;
      } else {
        return false;
      }
    }
    return true;
  });
  const filteredBankTransactions = bankTransactions
    .filter(txn => {
      if (txn.is_deleted || (selectedBusiness && String(txn.business) !== String(selectedBusiness.id))) return false;
      if (!filterByDate(txn.date, bankDateFilter)) return false;
      if (bankAccountFilter !== 'all' && String(txn.bank_account) !== String(bankAccountFilter)) return false;
      if (bankTransactionTypeFilter !== 'all' && txn.transaction_type !== bankTransactionTypeFilter) return false;
      return true;
    })
    .sort((a, b) => sortByDateTimeDesc(a, b) || new Date(b.created_at) - new Date(a.created_at));
  const duplicateGroups = useMemo(() => {
    const normalize = value => value === null || value === undefined ? '' : String(value).trim().toLowerCase();
    const groups = [];
    const collect = (type, label, records, fields) => {
      const grouped = new Map();
      records.filter(record => !record.is_deleted).forEach(record => {
        const recordDate = record.date || record.issue_date;
        const recordTime = record.time || record.issue_time;
        if (!recordDate || !recordTime) return;
        const key = fields.map(field => normalize(record[field])).join('|');
        if (!key.replace(/\|/g, '')) return;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(record);
      });
      grouped.forEach(matches => {
        if (matches.length > 1) {
          const ordered = [...matches].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
          groups.push({ type, label, records: ordered, key: `${type}:${fields.map(field => normalize(ordered[0][field])).join('|')}` });
        }
      });
    };
    collect('invoice', t('invoice') || 'Invoice', invoices.filter(record => !selectedBusiness || String(record.business) === String(selectedBusiness.id)), ['business', 'invoice_number', 'issue_date', 'issue_time', 'total_amount', 'paid_amount', 'party']);
    collect('bank', t('bankTransaction') || 'Bank transaction', bankTransactions.filter(record => !selectedBusiness || String(record.business) === String(selectedBusiness.id)), ['business', 'bank_account', 'transaction_type', 'amount', 'date', 'time', 'reference', 'description']);
    collect('ledger', t('transaction') || 'Ledger transaction', ledgerEntries.filter(record => !selectedBusiness || String(record.business) === String(selectedBusiness.id)), ['business', 'party', 'entry_type', 'amount', 'date', 'time', 'description']);
    collect('cash', t('cashEntry') || 'Cash entry', cashEntries.filter(record => !selectedBusiness || String(record.business) === String(selectedBusiness.id)), ['business', 'entry_type', 'amount', 'date', 'time', 'category', 'description']);
    collect('income', t('incomeExpense') || 'Income/expense record', incomeExpenses.filter(record => !selectedBusiness || String(record.business) === String(selectedBusiness.id)), ['business', 'type', 'amount', 'category', 'date', 'time', 'description']);
    collect('stock', t('stockMovement') || 'Stock movement', stockMovements.filter(record => !selectedBusiness || String(record.business) === String(selectedBusiness.id)), ['business', 'product', 'movement_type', 'quantity', 'date', 'time', 'description']);
    return groups;
  }, [invoices, bankTransactions, ledgerEntries, cashEntries, incomeExpenses, stockMovements, selectedBusiness, t]);
  const [duplicateReview, setDuplicateReview] = useState(null);
  const reviewedDuplicateGroups = useRef(new Set());
  useEffect(() => {
    const nextGroup = duplicateGroups.find(group => {
      return !reviewedDuplicateGroups.current.has(group.key);
    });
    if (nextGroup && !duplicateReview) setDuplicateReview(nextGroup);
  }, [duplicateGroups, duplicateReview]);
  const [activePrintReport, setActivePrintReport] = useState(null); // { type: 'cash'|'ie'|'ledger'|'invoice', data: {...} }

  // WhatsApp Message Templates (stored in localStorage per-org)
  const defaultWATemplates = {
    balance_reminder: `Dear {party_name},\n\nYour outstanding balance in {business_name} is Rs. {balance}.\n\nPlease clear it soon.\n\nThank you!`,
    transaction_receipt: `Dear {party_name},\n\nThis is a transaction receipt from {business_name}.\n\nDate: {date}\nDescription: {description}\nAmount: Rs. {amount}\nType: {type}\n\nYour current outstanding balance is Rs. {balance}.\n\nThank you!`,
    invoice_message: `{starting_note}\n\nDear {party_name},\n\nInvoice# {invoice_number}\n\n{items}\n---------------------\nTotal Amount: Rs. {total}\nDiscount: Rs. {discount}\nSub Total: Rs. {subtotal}\nPaid Amount: Rs. {paid_amount}\nRemaining: Rs. {remaining}\n---------------------\n{ending_note}\n\nSent On: {date}\n\n{business_name}`,
    salary_slip: `*Salary Slip*\n---------------------\nEmployee: {employee_name}\nPeriod: {period_start} to {period_end}\nBasic: Rs. {basic_salary}\nAllowances: Rs. {allowances}\nDeductions: Rs. {deductions}\nNet Paid: Rs. {net_paid}\nStatus: {status}\n---------------------\n{business_name}`
  };

  const [waTemplates, setWATemplates] = useState(() => {
    try {
      const saved = localStorage.getItem(`wa_templates_${localStorage.getItem('org_id')}`);
      return saved ? JSON.parse(saved) : defaultWATemplates;
    } catch { return defaultWATemplates; }
  });

  const [editingTemplate, setEditingTemplate] = useState(null);

  const saveWATemplate = (key, text) => {
    const updated = { ...waTemplates, [key]: text };
    setWATemplates(updated);
    localStorage.setItem(`wa_templates_${localStorage.getItem('org_id')}`, JSON.stringify(updated));
  };

  const resetWATemplate = (key) => {
    const updated = { ...waTemplates, [key]: defaultWATemplates[key] };
    setWATemplates(updated);
    localStorage.setItem(`wa_templates_${localStorage.getItem('org_id')}`, JSON.stringify(updated));
  };

  const formatWAMessage = (templateKey, vars) => {
    let msg = waTemplates[templateKey] || defaultWATemplates[templateKey];
    Object.entries(vars).forEach(([key, val]) => {
      msg = msg.replace(new RegExp(`\\{${key}\\}`, 'g'), val ?? '');
    });
    return msg;
  };

  // ─── WhatsApp share mode: editable text template vs rendered image card ─────
  // Persisted per organisation alongside the templates above. Defaults to 'text'
  // so existing behaviour is untouched until the user opts in to image cards.
  const [waShareMode, setWAShareMode] = useState(() => {
    try {
      return localStorage.getItem(`wa_share_mode_${localStorage.getItem('org_id')}`) === 'image' ? 'image' : 'text';
    } catch {
      return 'text';
    }
  });

  const updateWAShareMode = (mode) => {
    setWAShareMode(mode);
    try {
      localStorage.setItem(`wa_share_mode_${localStorage.getItem('org_id')}`, mode);
    } catch { /* storage unavailable — the in-memory value still applies */ }
  };

  const waShareBusyRef = useRef(false);

  // Business fields every card carries (the card itself has no access to state).
  const waCardBase = () => ({
    businessName: selectedBusiness?.name || t('brand'),
    businessPhone: selectedBusiness?.phone || '',
    businessLogo: selectedBusiness?.logo ? getLogoUrl(selectedBusiness.logo) : '',
    brandName: t('brand'),
  });

  // Card payloads for the five WhatsApp buttons. All copy is resolved here so the
  // card stays hook-free and can be rendered outside the I18n provider.
  const waCardForParty = (party) => {
    const raw = parseFloat(party?.cached_balance || 0);
    return {
      ...waCardBase(),
      heading: t('waBalanceReminder'),
      partyName: party?.name || '',
      dateTime: new Date().toLocaleDateString(),
      amount: `Rs. ${Math.abs(raw).toLocaleString()}`,
      amountLabel: t('outstanding'),
      tone: raw < 0 ? 'debit' : 'credit',
      sections: [
        { label: t('phone'), value: party?.phone || '' },
        { label: t('address'), value: party?.address || '' },
        { label: t('myBusiness'), value: selectedBusiness?.name || '' },
      ],
      footerLabel: t('balance'),
      footerValue: `Rs. ${raw.toLocaleString()}`,
      footerTone: raw < 0 ? 'debit' : 'credit',
    };
  };

  const waCardForLedgerEntry = (e, party) => {
    const give = e.entry_type === 'GIVE';
    const running = parseFloat(e.runningBalance ?? party?.cached_balance ?? 0) || 0;
    const method = e.payment_method === 'ONLINE'
      ? (bankAccounts.find(acc => String(acc.id) === String(e.bank_account))?.name || t('bank'))
      : t('cash');
    return {
      ...waCardBase(),
      heading: t('waTransactionReceipt'),
      partyName: party?.name || '',
      dateTime: formatDateTime(e.date, e.time),
      amount: `Rs. ${(parseFloat(e.amount) || 0).toLocaleString()}`,
      amountLabel: give ? t('waYouGave') : t('waYouGot'),
      tone: give ? 'debit' : 'credit',
      sections: [
        { label: t('paymentMethod'), value: method },
        { label: t('detailsLabel'), value: e.description || t('genericEntry') },
        { label: t('attachment'), image: e.attachment ? getPhotoUrl(e.attachment) : '' },
      ],
      footerLabel: t('waRunningBalance'),
      footerValue: `Rs. ${Math.abs(running).toLocaleString()}`,
      footerTone: running > 0 ? 'credit' : running < 0 ? 'debit' : 'neutral',
    };
  };

  const waCardForPayroll = (emp, pay) => {
    const period = `${pay?.pay_period_start || ''} – ${pay?.pay_period_end || ''}`;
    return {
      ...waCardBase(),
      heading: t('waSalarySlip'),
      partyName: emp?.name || '',
      dateTime: period,
      amount: `Rs. ${(parseFloat(pay?.net_paid) || 0).toLocaleString()}`,
      amountLabel: t('netPaid'),
      tone: 'credit',
      sections: [
        { label: t('period'), value: period },
        { label: t('basic'), value: `Rs. ${(parseFloat(pay?.basic_salary) || 0).toLocaleString()}` },
        { label: t('allowances'), value: `Rs. ${(parseFloat(pay?.allowances) || 0).toLocaleString()}` },
        { label: t('deductions'), value: `Rs. ${(parseFloat(pay?.deductions) || 0).toLocaleString()}` },
      ],
      footerLabel: t('status'),
      footerValue: String(pay?.status || '—'),
      footerTone: pay?.status === 'PAID' ? 'credit' : 'neutral',
    };
  };

  // One invoice line, priced exactly the way calcSaleTotal / calcPurchaseTotal
  // price it: bulk rows are costed from their bulk + small components rather
  // than qty × unit_price, so the shown amounts always add up to the subtotal.
  const waInvoiceLine = (row) => {
    const product = products.find(p => p.id === row.product_id);
    const name = product ? product.name : (row.custom_item_name || '');
    // quantity is stored in PIECES and subtotal is the canonical line amount,
    // so the WhatsApp card always adds up to exactly what the invoice says.
    const qty = parseFloat(row.quantity) || 0;
    const amount = (row.subtotal !== undefined && row.subtotal !== null && row.subtotal !== '')
      ? (parseFloat(row.subtotal) || 0)
      : qty * (parseFloat(row.unit_price) || 0);
    const unit = qty > 0 ? amount / qty : (parseFloat(row.unit_price) || 0);
    const round = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
    // The row's own deduction, blank when this invoice counts discount as one box
    // — a value left behind by an earlier session must not appear on the card.
    const disc = itemDiscMode ? discountValueOf(row.discount, amount) : 0;

    const bQtyVal = row.bulk_quantity !== null && row.bulk_quantity !== undefined ? parseFloat(row.bulk_quantity) : 0;
    const hasBulkQty = bQtyVal > 0;
    const bPriceVal = row.bulk_unit_price !== null && row.bulk_unit_price !== undefined ? parseFloat(row.bulk_unit_price) : 0;
    const sQtyVal = row.small_quantity !== null && row.small_quantity !== undefined ? parseFloat(row.small_quantity) : 0;
    const hasSmallQty = sQtyVal > 0;
    const sPriceVal = row.small_unit_price !== null && row.small_unit_price !== undefined ? parseFloat(row.small_unit_price) : (row.unit_price ? parseFloat(row.unit_price) : 0);

    return {
      name,
      qty: String(round(qty)),
      price: `Rs. ${round(unit).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
      amount: `Rs. ${round(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
      discount: disc > 0 ? `- ${round(disc).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '',
      bulkQty: hasBulkQty ? String(bQtyVal) : '-',
      hasBulkQty,
      bulkPrice: (hasBulkQty && bPriceVal > 0) ? `Rs. ${round(bPriceVal).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '-',
      smallQty: hasSmallQty ? String(round(sQtyVal)) : '-',
      hasSmallQty,
      smallPrice: (hasSmallQty && sPriceVal > 0) ? `Rs. ${round(sPriceVal).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '-',
      bulkUnitName: row.bulk_unit_name || product?.bulk_unit || 'PACK',
    };
  };

  const waCardForInvoice = ({ partyName, invoiceNumber, subtotal, discount, total, paid, remaining, date, items, isPurchase }) => {
    const rs = (n) => `Rs. ${(parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    const disc = parseFloat(discount) || 0;
    const lines = (items || []).filter(it => it && it.name);

    const hasBulkItems = lines.some(it => it.hasBulkQty);
    const hasSmallItems = lines.some(it => it.hasSmallQty);

    const colNames = printSettings.columnNames || {};
    const bulkUnitItem = lines.find(r => r.bulkUnitName);
    const bulkUnitLabel = colNames.bulkQuantity?.trim() || bulkUnitItem?.bulkUnitName?.toUpperCase() || 'PACK';
    const smallUnitLabel = colNames.smallQuantity?.trim() || 'PC';
    const bulkPriceLabel = colNames.bulkPrice?.trim() || (isPurchase ? `${bulkUnitLabel} Cost` : `${bulkUnitLabel} Price`);
    const smallPriceLabel = colNames.smallPrice?.trim() || (isPurchase ? 'PC Cost' : 'Pc Price');

    let cols = {
      description: colNames.itemDescription?.trim() || t('description'),
      qty: colNames.quantity?.trim() || t('qty'),
      price: colNames.unitPrice?.trim() || t('unitPrice'),
      amount: colNames.subtotal?.trim() || t('amount'),
      ...(lines.some(it => it.discount) ? { discount: t('discount') } : {})
    };

    if (hasBulkItems) {
      if (hasSmallItems) {
        cols = {
          description: colNames.itemDescription?.trim() || t('description'),
          bulkQty: bulkUnitLabel,
          smallQty: smallUnitLabel,
          bulkPrice: bulkPriceLabel,
          smallPrice: smallPriceLabel,
          amount: colNames.subtotal?.trim() || t('amount'),
          ...(lines.some(it => it.discount) ? { discount: t('discount') } : {})
        };
      } else {
        cols = {
          description: colNames.itemDescription?.trim() || t('description'),
          bulkQty: bulkUnitLabel,
          bulkPrice: bulkPriceLabel,
          amount: colNames.subtotal?.trim() || t('amount'),
          ...(lines.some(it => it.discount) ? { discount: t('discount') } : {})
        };
      }
    }

    return {
      ...waCardBase(),
      heading: isPurchase ? t('purchaseInvoice') : t('invoice'),
      partyName: partyName || '',
      dateTime: date || '',
      amount: rs(total),
      amountLabel: t('total'),
      tone: 'credit',
      sections: [
        { label: t('invoiceNumber'), value: invoiceNumber || '' },
        lines.length ? {
          label: t('itemsLabel'),
          items: lines,
          cols,
        } : null,
        // The summary rows keep their exact look — uppercase header with
        // the amount right under it — but the whole block sits on the RIGHT.
        { label: t('subtotalLabel'), value: rs(subtotal), rightAlign: true },
        disc > 0 ? { label: t('discount'), value: `- ${rs(disc)}`, rightAlign: true } : null,
        { label: t('grandTotal') || 'Grand Total', value: rs(total), rightAlign: true },
        paid > 0 ? { label: t('paid'), value: rs(paid), rightAlign: true } : null,
        (paid > 0 || remaining > 0) ? { label: t('remainingBalance'), value: rs(remaining), rightAlign: true } : null,
      ].filter(Boolean),
      footerLabel: t('remainingBalance'),
      footerValue: rs(remaining),
      footerTone: 'debit',
    };
  };

  const waSaleInvoiceCard = () => {
    const subtotal = calcSaleTotal();
    const discount = saleDiscountTotal();
    const total = Math.max(0, subtotal - discount);
    const paid = parseFloat(invoiceBuilder.paid_amount) || 0;
    return waCardForInvoice({
      partyName: parties.find(p => p.id === invoiceBuilder.party_id)?.name || t('valuedCustomer'),
      invoiceNumber: invoiceBuilder.invoice_number,
      subtotal,
      discount,
      total,
      paid,
      remaining: Math.max(0, total - paid),
      date: invoiceBuilder.issue_date,
      items: resolveSaleItemsForSubmit().map(waInvoiceLine),
    });
  };

  const waPurchaseInvoiceCard = () => {
    const subtotal = calcPurchaseTotal();
    const discount = purchaseDiscountTotal();
    const total = Math.max(0, subtotal - discount);
    const paid = parseFloat(purchaseInvoiceBuilder.paid_amount) || 0;
    return waCardForInvoice({
      partyName: parties.find(p => p.id === purchaseInvoiceBuilder.party_id)?.name || t('supplierLabel'),
      invoiceNumber: purchaseInvoiceBuilder.invoice_number,
      subtotal,
      discount,
      total,
      paid,
      remaining: Math.max(0, total - paid),
      date: purchaseInvoiceBuilder.issue_date,
      items: resolvePurchaseItemsForSubmit().map(waInvoiceLine),
      isPurchase: true,
    });
  };

  // Template variables for the *text* invoice message. Kept in step with the
  // on-screen totals and with the image card: both subtract the discount, which
  // this template previously hard-coded to zero (so it always printed
  // "Discount: Rs. 0" and a pre-discount total/remaining).
  const waSaleInvoiceVars = () => {
    const subtotal = calcSaleTotal();
    const discount = saleDiscountTotal();
    const total = Math.max(0, subtotal - discount);
    const paid = parseFloat(invoiceBuilder.paid_amount) || 0;
    return {
      starting_note: '',
      party_name: parties.find(p => p.id === invoiceBuilder.party_id)?.name || t('valuedCustomer'),
      invoice_number: invoiceBuilder.invoice_number,
      items: resolveSaleItemsForSubmit().map(row => {
        const product = products.find(p => p.id === row.product_id);
        const name = product ? product.name : (row.custom_item_name || '');
        const amount = round2(numOr(row.subtotal, 0));
        const disc = itemDiscMode ? discountValueOf(row.discount, amount) : 0;
        return name ? `${name} @ Rs.${row.unit_price} x ${describeLineQty(row)} = Rs.${amount.toLocaleString()}${disc > 0 ? ` (-Rs.${round2(disc).toLocaleString()})` : ''}` : '';
      }).filter(Boolean).join('\n'),
      total: total.toLocaleString(),
      discount: discount.toLocaleString(),
      subtotal: subtotal.toLocaleString(),
      paid_amount: invoiceBuilder.paid_amount || '0',
      remaining: Math.max(0, total - paid).toLocaleString(),
      ending_note: '',
      date: invoiceBuilder.issue_date,
      business_name: selectedBusiness?.name || 'My Shop',
    };
  };

  const waPurchaseInvoiceVars = () => {
    const subtotal = calcPurchaseTotal();
    const discount = purchaseDiscountTotal();
    const total = Math.max(0, subtotal - discount);
    const paid = parseFloat(purchaseInvoiceBuilder.paid_amount) || 0;
    return {
      starting_note: '',
      party_name: parties.find(p => p.id === purchaseInvoiceBuilder.party_id)?.name || t('supplierLabel'),
      invoice_number: purchaseInvoiceBuilder.invoice_number,
      items: resolvePurchaseItemsForSubmit().map(row => {
        const product = products.find(p => p.id === row.product_id);
        const name = product ? product.name : (row.custom_item_name || '');
        const amount = round2(numOr(row.subtotal, 0));
        const disc = itemDiscMode ? discountValueOf(row.discount, amount) : 0;
        return name ? `${name} @ Rs.${row.unit_price} x ${describeLineQty(row)} = Rs.${amount.toLocaleString()}${disc > 0 ? ` (-Rs.${round2(disc).toLocaleString()})` : ''}` : '';
      }).filter(Boolean).join('\n'),
      total: total.toLocaleString(),
      discount: discount.toLocaleString(),
      subtotal: subtotal.toLocaleString(),
      paid_amount: purchaseInvoiceBuilder.paid_amount || '0',
      remaining: Math.max(0, total - paid).toLocaleString(),
      ending_note: '',
      date: purchaseInvoiceBuilder.issue_date,
      business_name: selectedBusiness?.name || 'My Shop',
    };
  };

  // Sample shown in the settings panel so the user can see the card before opting in.
  const waSampleCard = () => ({
    ...waCardBase(),
    heading: t('waTransactionReceipt'),
    partyName: 'Ahmed Ali',
    dateTime: new Date().toLocaleString(),
    amount: 'Rs. 2,500',
    amountLabel: t('waYouGave'),
    tone: 'debit',
    sections: [
      { label: t('detailsLabel'), value: 'Grocery order' },
      { label: t('paymentMethod'), value: t('cash') },
    ],
    footerLabel: t('waRunningBalance'),
    footerValue: 'Rs. 5,000',
    footerTone: 'credit',
  });

  // ─── Desktop fallback ────────────────────────────────────────────────────────
  // A desktop browser may well expose navigator.share({files}) and still offer
  // no WhatsApp target in its share sheet — and a wa.me link can bounce to a
  // desktop app that is not installed. So only a handheld device takes the
  // native sheet; everything else addresses WhatsApp Web directly, which always
  // works in the browser.
  const isHandheld = () => /Android|iPhone|iPad|iPod|Windows Phone|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent || '');

  const supportsFileShare = () => {
    try {
      if (!isHandheld() || !navigator.canShare) return false;
      const probe = new File([new Blob(['x'], { type: 'text/plain' })], 'probe.txt', { type: 'text/plain' });
      return navigator.canShare({ files: [probe] });
    } catch { return false; }
  };

  const waWebUrl = (phone, text) => (
    phone
      ? `https://web.whatsapp.com/send?phone=${encodeURIComponent(phone)}${text ? `&text=${encodeURIComponent(text)}` : ''}`
      : 'https://web.whatsapp.com/'
  );

  // Returns the window handle so the tab can be reused later. `noopener` is set
  // by hand because passing it to window.open() would make it return null.
  const openWhatsAppWeb = (phone, text) => {
    try {
      const win = window.open(waWebUrl(phone, text), '_blank');
      if (win) { try { win.opener = null; } catch { /* cross-origin noop */ } }
      return win;
    } catch { return null; }
  };

  // Called after the capture: makes sure a WhatsApp Web tab really is there.
  const ensureWhatsAppWebOpen = (card) => {
    let win = card.waWindow || null;
    if (!win || win.closed) win = openWhatsAppWeb(card.phone, card.text || '');
    card.waWindow = win;
    return win;
  };

  // Mounts the card off-screen, rasterises it to a PNG and hands it to the
  // platform share sheet. Never throws: if the image cannot be produced the
  // plain-text template already carried by the anchor is used instead.
  const shareWACard = async (card) => {
    if (waShareBusyRef.current) return;
    waShareBusyRef.current = true;

    const openTextFallback = () => {
      if (!card.text) return;
      // On desktop the WhatsApp Web tab was opened synchronously on click and
      // already carries the text, so there is nothing left to do.
      if (card.waWindow && !card.waWindow.closed) return;
      const url = `https://wa.me/${card.phone || ''}?text=${encodeURIComponent(card.text)}`;
      try { window.open(url, '_blank', 'noopener,noreferrer'); } catch { /* popup blocked */ }
    };

    // 1. Render + capture (cleaned up unconditionally).
    let canvas = null;
    const holder = document.createElement('div');
    let root = null;
    try {
      holder.style.cssText = 'position:absolute;top:0;left:-100000px;width:400px;pointer-events:none;';
      document.body.appendChild(holder);
      root = createRoot(holder);
      root.render(<WACard card={card} />);
      // Wait for React to commit the card, then for the browser to lay it out.
      for (let i = 0; i < 50 && !holder.firstElementChild; i++) {
        await new Promise(res => setTimeout(res, 20));
      }
      // Two frames so layout has settled — raced against a timer because
      // requestAnimationFrame stops firing entirely while this tab is in the
      // background, which would otherwise hang the capture for ever.
      await Promise.race([
        new Promise(res => requestAnimationFrame(() => requestAnimationFrame(res))),
        new Promise(res => setTimeout(res, 300)),
      ]);
      // Wait for the logo / attachment so it is actually painted into the canvas.
      const imgs = Array.from(holder.querySelectorAll('img'));
      await Promise.all(imgs.map(img => {
        if (img.complete && img.naturalWidth > 0) return null;
        return new Promise(res => {
          const done = () => res();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          setTimeout(done, 6000);
        });
      }));
      const node = holder.firstElementChild;
      if (!node) throw new Error('card-not-rendered');
      // html2canvas (~200 kB) is only needed when the user shares an image
      // card, so it is fetched on demand instead of on first paint.
      const { default: html2canvasFn } = await import('html2canvas');
      canvas = await html2canvasFn(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: false,
        imageTimeout: 8000,
      });
    } catch {
      canvas = null;
    } finally {
      try { if (root) root.unmount(); } catch { /* noop */ }
      holder.remove();
    }

    if (!canvas) {
      showNotification(t('waCardFallback'), 'info');
      openTextFallback();
      waShareBusyRef.current = false;
      return;
    }

    // 2. Share it (or download + open WhatsApp when the platform cannot share files).
    try {
      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('empty-png');
      const slug = String(card.heading || 'card').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'card';
      const filename = `hisabflow-${slug}.png`;
      const file = new File([blob], filename, { type: 'image/png', lastModified: Date.now() });
      if (!card.waDesktop && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        // Desktop path: no file sharing here. Put the PNG on the clipboard so it
        // can simply be pasted into the chat, fall back to saving the file when
        // the clipboard refuses (no transient user activation left), and make
        // sure a WhatsApp Web tab is open.
        let copied = false;
        try {
          if (navigator.clipboard && typeof ClipboardItem !== 'undefined' && window.isSecureContext) {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            copied = true;
          }
        } catch { copied = false; }

        if (!copied) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 8000);
        }

        ensureWhatsAppWebOpen(card);
        showNotification(copied ? t('waCardCopied') : t('waCardDownloaded'), 'info');
      }
    } catch (err) {
      // A dismissed share sheet is not an error — leave everything alone.
      if (!err || err.name !== 'AbortError') {
        showNotification(t('waCardFallback'), 'info');
        openTextFallback();
      }
    } finally {
      waShareBusyRef.current = false;
    }
  };

  // Shared onClick for the five WhatsApp anchors. In 'text' mode it returns
  // straight away, so the anchors keep navigating exactly as they did before.
  const onWAClick = (ev, card) => {
    if (waShareMode !== 'image') return;
    ev.preventDefault();
    const href = ev.currentTarget.getAttribute('href') || '';
    const prefix = 'https://wa.me/';
    const sep = href.indexOf('?text=');
    let text = '';
    try {
      text = sep >= 0 ? decodeURIComponent(href.slice(sep + 6)) : '';
    } catch { text = ''; }
    const phone = href.startsWith(prefix) ? href.slice(prefix.length).split('?')[0] : '';
    // Browsers without a usable file share need a WhatsApp Web tab, and it has
    // to be opened right here, inside the click, while user activation still
    // allows a popup — waiting until the image is captured would get it blocked.
    const waDesktop = !supportsFileShare();
    const waWindow = waDesktop ? openWhatsAppWeb(phone, text) : null;
    // …then switch straight back to this tab: the capture below needs a visible
    // document (rAF stops in a background tab) and the clipboard write is only
    // allowed while this document still has focus.
    if (waWindow) { try { window.focus(); } catch { /* browser refused */ } }
    shareWACard({ ...card, phone, text, waDesktop, waWindow });
  };

  // ─── Print Customization Defaults & State ─────────────────────────────────────
  const defaultPrintSettings = {
    // How a discount is entered on an invoice. 'whole' = one box for the
    // whole invoice — the original behaviour, and the default. 'item' = a box
    // on every row, added up and stored as the invoice's discount_amount.
    // 'both' = the two together: discount_amount = box + the sum of the rows.
    discountMode: 'whole',

    // How the number typed into ANY discount box is read. 'amount' = a flat
    // figure in rupees (the original behaviour, and the default). 'percentage'
    // = a percent of what the box applies to: the line for a row box, the
    // subtotal for the whole-invoice box. Every total converts to rupees
    // through discountValueOf, so nothing downstream — payloads, backend,
    // printer, WhatsApp card — needs to know which one is chosen.
    discountType: 'amount',

    // Visibility toggles
    showSerialNumber: true,
    showSubtotal: true,
    showDiscount: true,
    showPaidAmount: true,
    showRemainingBalance: true,
    showPartyPhone: true,
    showPartyCnic: true,
    showPartyAddress: true,
    showBusinessPhoneEmail: true,
    showBusinessAddress: true,
    showInvoiceTime: true,
    showSignatureBlock: false,

    // Layout & Header
    logoPosition: 'left', // 'left' | 'center' | 'right' | 'none'
    headerLayout: 'standard', // 'standard' | 'center'
    itemsPosition: 'standard', // 'standard' | 'items_first'

    // Invoice Footer Note
    showInvoiceFooterNote: true,
    invoiceNoteTitle: 'Note',
    invoiceNoteText: '',

    // Custom Table Column Headers
    columnNames: {
      serialNo: '',
      itemDescription: '',
      quantity: '',
      unitPrice: '',
      subtotal: '',
      bulkQuantity: '',
      smallQuantity: '',
      bulkPrice: '',
      smallPrice: ''
    },

    // Reports Notes
    showReportFooterNote: true,
    reportNoteTitle: 'Note',
    reportNoteText: '',
    tabNotes: {},
    tabNoteTitles: {}
  };

  const [printSettings, setPrintSettings] = useState(() => {
    try {
      const orgKey = localStorage.getItem('org_id');
      const saved = localStorage.getItem(`print_settings_${orgKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultPrintSettings,
          ...parsed,
          columnNames: {
            ...defaultPrintSettings.columnNames,
            ...(parsed.columnNames || {})
          }
        };
      }
      return defaultPrintSettings;
    } catch {
      return defaultPrintSettings;
    }
  });

  // The discount mode is two independent switches, so the third mode ('both')
  // can have both on while 'whole' and 'item' keep behaving exactly as before:
  //   itemDiscMode  — a discount box on every row    ('item' or 'both')
  //   wholeDiscMode — one box for the whole invoice  ('whole' or 'both')
  const itemDiscMode = printSettings.discountMode !== 'whole';
  const wholeDiscMode = printSettings.discountMode !== 'item';
  // Whether a typed discount number is a percentage instead of a flat amount.
  const isPercentDiscount = printSettings.discountType === 'percentage';
  // The ONE place a typed discount becomes rupees: a percentage is taken of
  // the base it applies to (the line for item boxes, the subtotal for the
  // whole-invoice box) and the result is capped at that base, exactly the way
  // an amount always was. Every total, payload, card and print funnels through
  // this, so an absolute figure is all the rest of the app ever sees — which
  // is why switching the type cannot disturb any existing behaviour.
  const discountValueOf = (raw, base) => {
    const b = Math.max(numOr(base, 0), 0);
    const n = Math.max(numOr(raw, 0), 0);
    return Math.min(isPercentDiscount ? (b * n) / 100 : n, b);
  };
  const [printSettingsSubTab, setPrintSettingsSubTab] = useState('invoices'); // 'invoices' | 'reports'
  const [settingsSubTab, setSettingsSubTab] = useState('general'); // 'general' | 'gallery' | 'designs' | 'fbr'
  // FBR Digital Invoicing settings for this business (null = never loaded /
  // never configured). The whole feature hangs off this one object: when
  // fbrConfig.enabled is false nothing FBR-related renders anywhere and no
  // FBR call is made — the app behaves exactly as before the feature existed.
  const [fbrConfig, setFbrConfig] = useState(null);
  const fbrEnabled = !!(fbrConfig && fbrConfig.enabled);
  // Does the configured FBR scope cover this invoice? (PI- prefix = purchase
  // — the same marker the backend and the history split use.) Only the
  // auto-submit and the row's "send" chip obey this; a badge for an already
  // submitted invoice always shows, because it is a fact.
  const fbrScopeCovers = (invoice) => {
    if (!fbrEnabled) return false;
    const scope = fbrConfig?.invoice_scope || 'SALE';
    if (scope === 'BOTH') return true;
    const isPurchase = String(invoice?.invoice_number || '').toUpperCase().startsWith('PI-');
    return scope === (isPurchase ? 'PURCHASE' : 'SALE');
  };
  // Settings → FBR screen: the form edits a COPY of the loaded config, so
  // nothing reaches the server until Save (or Save & Test) is pressed. The
  // token box is write-only by design — it always starts empty, and a blank
  // box means "keep the token already saved on the server".
  const [fbrForm, setFbrForm] = useState(null);
  const [fbrTestResult, setFbrTestResult] = useState(null); // {ok, message, provinces}
  const [fbrBusy, setFbrBusy] = useState(false);
  const [fbrProvinces, setFbrProvinces] = useState([
    'PUNJAB', 'SINDH', 'KHYBER PAKHTUNKHWA', 'BALOCHISTAN',
    'ISLAMABAD CAPITAL TERRITORY', 'AZAD JAMMU & KASHMIR',
    'GILGIT BALTISTAN', 'FOREIGN COUNTRY',
  ]); // replaced with FBR's live list after a successful test

  useEffect(() => {
    // Initialize the form once per tab-open. If the config call failed
    // (offline, older backend) an EMPTY tab would be worse than a defaults
    // form — the user can still fill everything in and Save, which writes
    // the config through the normal PUT.
    if (settingsSubTab === 'fbr' && !fbrForm) {
      setFbrForm({
        enabled: false, environment: 'SANDBOX', invoice_scope: 'SALE',
        seller_ntn: '', seller_business_name: '', seller_province: '',
        seller_address: '', pos_id: '', scenario_id: 'SN001',
        default_tax_rate: '0%', default_sale_type: '',
        security_token_set: false, ...(fbrConfig || {}),
        security_token: '',
      });
    }
  }, [settingsSubTab, fbrConfig, fbrForm]);

  const saveFbrSettings = async ({ withTest = false } = {}) => {
    if (!fbrForm) return false;
    setFbrBusy(true);
    setFbrTestResult(null);
    try {
      // Name the business explicitly: an organization may hold several and
      // each keeps its own FBR config.
      const payload = { ...fbrForm, business: selectedBusiness?.id || fbrForm.business || null };
      const saved = await api('/fbr/config/', 'PUT', payload);
      setFbrConfig(saved);
      setFbrForm({ ...saved, security_token: '' });
      if (!withTest) showNotification('FBR settings saved', 'success');
      if (withTest) {
        const res = await api('/fbr/test/', 'POST', { business: selectedBusiness?.id || null });
        setFbrTestResult(res || null);
        if (res?.ok && Array.isArray(res.provinces) && res.provinces.length) {
          setFbrProvinces(res.provinces);
        }
      }
      return true;
    } catch (error) {
      setFbrTestResult(withTest ? { ok: false, message: error.message || 'Could not save FBR settings.' } : null);
      if (!withTest) showNotification(error.message || 'Could not save FBR settings', 'error');
      return false;
    } finally {
      setFbrBusy(false);
    }
  };
  const [selectedReportTabToEdit, setSelectedReportTabToEdit] = useState('ledger');
  const [showInvoicePrintPreview, setShowInvoicePrintPreview] = useState(false);

  const savePrintSettings = (newSettings) => {
    setPrintSettings(newSettings);
    const orgKey = localStorage.getItem('org_id');
    localStorage.setItem(`print_settings_${orgKey}`, JSON.stringify(newSettings));
  };

  const updatePrintSetting = (key, value) => {
    const updated = { ...printSettings, [key]: value };
    savePrintSettings(updated);
  };

  const updateColumnName = (colKey, value) => {
    const currentCols = printSettings.columnNames || {};
    const updated = {
      ...printSettings,
      columnNames: {
        ...currentCols,
        [colKey]: value
      }
    };
    savePrintSettings(updated);
  };

  const updateTabNote = (tabKey, text) => {
    const currentNotes = printSettings.tabNotes || {};
    const updated = {
      ...printSettings,
      tabNotes: { ...currentNotes, [tabKey]: text }
    };
    savePrintSettings(updated);
  };

  const updateTabNoteTitle = (tabKey, title) => {
    const currentTitles = printSettings.tabNoteTitles || {};
    const updated = {
      ...printSettings,
      tabNoteTitles: { ...currentTitles, [tabKey]: title }
    };
    savePrintSettings(updated);
  };

  const resetPrintSettings = () => {
    savePrintSettings(defaultPrintSettings);
    showNotification(t('resetPrintSettings') || 'Settings reset to defaults', 'info');
  };

  // Load Initial Configurations
  useEffect(() => {
    async function init() {
      const savedTheme = localStorage.getItem('theme') || 'light';
      setTheme(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);

      if (localStorage.getItem('org_id')) {
        // An org id alone is no longer a credential, so an existing browser
        // that was signed in before the token rollout (org_id but no token)
        // cannot resume by itself. Rather than firing requests that would all
        // 403, hold the lock screen: the owner's PIN still works and mints a
        // fresh token on the next line of the normal flow. Nothing is lost —
        // no re-registration, no data change, just one PIN entry.
        const hasToken = !!getSessionToken();
        const rememberMe = localStorage.getItem('hisabflow_remember') === 'true';
        if (rememberMe && hasToken) {
          setIsLocked(false);
          await refreshData();
        }
      }
      
      setTimeout(() => {
        setShowSplash(false);
        setLoading(false);
      }, 1000);
    }
    init();
  }, []);

  // Listen to afterprint event to clear print states and return user back to application immediately
  useEffect(() => {
    const handleAfterPrint = () => {
      // Note: Do NOT clear React state here — Chrome for Android fires afterprint
      // when the dialog OPENS (not closes), causing early state cleanup that makes
      // the PDF show the app UI instead of the print content.
      // State is cleared by "Back"/"Close" buttons after the user dismisses the view.
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  // A session token is short-lived, so a "remember me" tab left open past its
  // lifetime would otherwise sit unlocked while every request 403'd. api()
  // raises this event; we return the user to the lock screen, which is the
  // normal, already-understood path back in (and the PIN mints a fresh token).
  useEffect(() => {
    const handleSessionExpired = () => {
      setIsLocked((wasLocked) => {
        if (!wasLocked) {
          showNotification(t('sessionExpired') || 'Session expired. Please enter your PIN again.', 'info');
        }
        return true;
      });
      setPin('');
      setPinError(false);
    };
    window.addEventListener('hisabflow:session-expired', handleSessionExpired);
    return () => window.removeEventListener('hisabflow:session-expired', handleSessionExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-trigger the browser print dialog immediately when any print preview is activated
  useEffect(() => {
    if (printInvoiceData || printEntryData || printSalarySlipData) {
      const timer = setTimeout(() => {
        window.print();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [printInvoiceData, printEntryData, printSalarySlipData]);

  // Calculator auto-focus: track which amount input is focused
  useEffect(() => {
    let blurTimer = null;
    const handleFocusIn = (e) => {
      const input = e.target;
      if (input.tagName === 'INPUT' && input.dataset.calc !== undefined) {
        clearTimeout(blurTimer);
        calcFocusedInput.current = input;
        setCalculatorTarget(() => (val) => {
          const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
          nativeSetter.call(input, String(val));
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }
    };
    const handleFocusOut = () => {
      blurTimer = setTimeout(() => {
        calcFocusedInput.current = null;
        setCalculatorTarget(null);
      }, 150);
    };
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      clearTimeout(blurTimer);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Listen for browser back/forward to toggle admin route
  useEffect(() => {
    const onPopState = () => {
      setIsAdminRoute(window.location.pathname === '/admin-panel');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Fetch recycle bin when tab is opened
  useEffect(() => {
    if (activeTab === 'recyclebin' && orgAuth) fetchRecycleBin();
  }, [activeTab, orgAuth]);

  // Fetch / Refresh UI data from API
  const refreshData = async () => {
    if (!localStorage.getItem('org_id')) return;
    try {
      // One wave instead of four serial round-trips: businesses, every data
      // list and the organization-scoped tenant-info all resolve in parallel.
      // tenant-info is non-critical by design (the subscription card falls
      // back to its localStorage cache), so its failure must not reject the
      // whole batch — hence the catch.
      const [bizList, partyList, ledgerList, cashList, ieList, productList, invoiceList, staffList, attList, payrollList, movementList, areaList, productTypeList, bankAccountList, bankTransactionList, tenantInfo] = await Promise.all([
        api('/businesses/'),
        api('/parties/'), api('/ledger-entries/'), api('/cash-entries/'), api('/income-expenses/'),
        api('/products/'), api('/invoices/'), api('/staff/'), api('/attendances/'), api('/payrolls/'),
        api('/stock-movements/'), api('/areas/'), api('/product-types/'), api('/bank-accounts/'), api('/bank-transactions/'),
        api('/tenant-info/').catch(() => null)
      ]);
      setBusinesses(bizList || []);
      let activeBizId = localStorage.getItem('selected_business_id');
      let activeBiz = null;
      if (activeBizId && bizList) {
        activeBiz = bizList.find(b => String(b.id) === String(activeBizId));
      }
      if (!activeBiz && bizList && bizList.length > 0) {
        activeBiz = bizList[0];
        activeBizId = activeBiz.id;
        localStorage.setItem('selected_business_id', activeBizId);
      }
      if (activeBiz) {
        setSelectedBusiness(activeBiz);
        setBusinessForm({ name: activeBiz.name || '', address: activeBiz.address || '', email: activeBiz.email || '', phone: activeBiz.phone || '', currency: activeBiz.currency || 'PKR', language: activeBiz.language || 'en', logo: activeBiz.logo || '' });
      }
      setParties((partyList || []).filter(Boolean)); setLedgerEntries((ledgerList || []).filter(Boolean)); setCashEntries((cashList || []).filter(Boolean));
      setIncomeExpenses((ieList || []).filter(Boolean)); setProducts((productList || []).filter(Boolean)); setInvoices((invoiceList || []).filter(Boolean));
      setStaff((staffList || []).filter(Boolean)); setAttendances((attList || []).filter(Boolean)); setPayrolls((payrollList || []).filter(Boolean));
      setStockMovements((movementList || []).filter(Boolean)); setAreas((areaList || []).filter(Boolean)); setProductTypes((productTypeList || []).filter(Boolean));
      setBankAccounts((bankAccountList || []).filter(Boolean)); setBankTransactions((bankTransactionList || []).filter(Boolean));

      // FBR Digital Invoicing settings — non-critical: an FBR-off business
      // renders exactly as before whether or not this call succeeds. The
      // selected business is named explicitly: an organization may hold
      // several, and each keeps its own FBR config.
      try {
        const fbrCfg = await api(`/fbr/config/${activeBiz ? `?business=${activeBiz.id}` : ''}`);
        setFbrConfig(fbrCfg || null);
      } catch (_) { /* FBR not available / not configured — treat as off */ }

      // Subscription info from the parallel wave above — non-critical: the
      // subscription card falls back to its localStorage cache when null.
      if (tenantInfo) {
        if (tenantInfo.subscription_end_date) localStorage.setItem('subscription_end_date', tenantInfo.subscription_end_date);
        if (tenantInfo.subscription_plan) localStorage.setItem('subscription_plan', tenantInfo.subscription_plan);
        setBackupInfo({ last_backup_at: tenantInfo.last_backup_at || null, last_backup_size: tenantInfo.last_backup_size || null });
      }
      return invoiceList || [];
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  const handleTenantManualBackup = async () => {
    setIsBackingUp(true);
    try {
      const res = await api('/tenant-info/', 'POST');
      if (res?.success) {
        setBackupInfo({
          last_backup_at: res.last_backup_at,
          last_backup_size: res.last_backup_size,
        });
        showNotification(t('backupCompleted') || 'Backup completed successfully!', 'success');
      } else {
        showNotification(res?.error || t('backupFailed') || 'Backup failed.', 'warning');
      }
    } catch (err) {
      showNotification(err?.message || t('backupFailed') || 'Backup failed.', 'warning');
    } finally {
      setIsBackingUp(false);
    }
  };

  // Manual FBR resend — the button on a failed/pending invoice. The backend
  // never re-sends an invoice that already holds an FBR number, so double
  // taps are safe.
  const resendToFbr = async (invoice) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    try {
      const res = await api('/fbr/submit/', 'POST', { invoice: invoice.id });
      if (res?.invoice) {
        setInvoices(prev => prev.map(i => (i.id === res.invoice.id ? res.invoice : i)));
      }
      const r = res?.result || {};
      if (r.already_submitted) {
        showNotification(`Already sent to FBR — Invoice No. ${r.fbr_invoice_number}`, 'success');
      } else if (r.status === 'SUBMITTED') {
        showNotification(`Sent to FBR — Invoice No. ${r.fbr_invoice_number}`, 'success');
      } else if (r.status === 'PENDING') {
        showNotification(r.error || 'FBR did not answer — check the portal before resending.', 'warning');
      } else {
        showNotification(r.error || 'FBR rejected the invoice.', 'error');
      }
    } catch (error) {
      showNotification(error.message || t('networkError'), 'error');
    }
  };

  // A one-line FBR outcome for the save toast — '' when FBR is off or this
  // invoice wasn't in scope, so nothing about the old flow changes.
  const fbrToastSuffix = (invoice) => {
    if (!fbrEnabled || !invoice) return '';
    const st = invoice.fbr_status || '';
    if (st === 'SUBMITTED') return ` — FBR No. ${invoice.fbr_invoice_number}`;
    if (st === 'PENDING') return ' — FBR gave no answer: check the FBR portal';
    if (st === 'FAILED') return ' — FBR rejected it: open the invoice for the reason';
    return '';
  };
  const fbrToastTone = (invoice) => {
    const st = invoice?.fbr_status || '';
    return (fbrEnabled && (st === 'FAILED' || st === 'PENDING')) ? 'warning' : 'success';
  };

  const refreshInvoiceData = async () => {
    try {
      const [invoiceList, partyList, productList, movementList, ledgerList, bankTransactionList] = await Promise.all([
        api('/invoices/'), api('/parties/'), api('/products/'),
        api('/stock-movements/'), api('/ledger-entries/'), api('/bank-transactions/')
      ]);
      setInvoices((invoiceList || []).filter(Boolean));
      setParties((partyList || []).filter(Boolean));
      setProducts((productList || []).filter(Boolean));
      setStockMovements((movementList || []).filter(Boolean));
      setLedgerEntries((ledgerList || []).filter(Boolean));
      setBankTransactions((bankTransactionList || []).filter(Boolean));
      return invoiceList || [];
    } catch (err) {
      console.error("Failed to refresh invoice data:", err);
      return [];
    }
  };

  // Theme Switcher
  const toggleTheme = () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    document.documentElement.setAttribute('data-theme', nextTheme);
  };

  const showNotification = (message, type = 'info') => {
    setStatusMessage({ message, type });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const saveBankAccount = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    e.preventDefault();
    if (!selectedBusiness) return;
    const payload = {
      business: selectedBusiness.id,
      name: bankAccountNameOther ? bankForm.name.trim() : bankForm.name,
      account_title: bankForm.account_title,
      account_number: bankForm.account_number,
      iban: bankForm.iban,
      branch: bankForm.branch,
      opening_balance: parseFloat(bankForm.opening_balance || 0),
      is_active: !!bankForm.is_active,
      notes: bankForm.notes,
    };

    try {
      if (editingBankAccount) {
        await api(`/bank-accounts/${editingBankAccount.id}/`, 'PATCH', payload);
        showNotification(t('bankAccountUpdated') || 'Bank account updated.', 'success');
      } else {
        await api('/bank-accounts/', 'POST', payload);
        showNotification(t('bankAccountAdded') || 'Bank account added.', 'success');
      }
      setShowBankModal(false);
      setEditingBankAccount(null);
      setBankAccountNameOther(false);
      setBankForm({ name: '', account_title: '', account_number: '', iban: '', branch: '', opening_balance: '0', is_active: true, notes: '' });
      await refreshData();
    } catch (err) {
      showNotification(err.message || t('networkError'), 'error');
    }
  };

  const deleteBankAccount = async (account) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    try {
      await api(`/bank-accounts/${account.id}/`, 'DELETE');
      showNotification(t('bankAccountDeleted') || 'Bank account deleted.', 'success');
      await refreshData();
    } catch (err) {
      showNotification(err.message || t('networkError'), 'error');
    }
  };

  // Unlock PIN Check -- verifies against the backend (not just localStorage)
  const [pinVerifying, setPinVerifying] = useState(false);
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinVerifying(true);
    try {
      const orgId = localStorage.getItem('org_id');
      if (!orgId) {
        setPinError(true);
        setPin('');
        return;
      }
      const currentToken = getSessionToken();
      const res = await fetch(`${BACKEND_URL}/verify-pin/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Org-Id': orgId,
          // Present the (possibly expired) token so the server can carry any
          // member binding across the refresh — unlocking must not turn a    restricted member session into an owner session.
          ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}),
        },
        body: JSON.stringify({ org_id: orgId, pin_code: pin })
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        // Refresh the session token: the PIN was just proven, so this is a
        // legitimate moment to renew it (tokens are short-lived).
        if (data.session_token) setSessionToken(data.session_token);
        if (data.subscription_end_date) localStorage.setItem('subscription_end_date', data.subscription_end_date);
        if (data.subscription_plan) localStorage.setItem('subscription_plan', data.subscription_plan);
        setIsLocked(false);
        setPinError(false);
        // Load (or reload) the data here rather than assuming it arrived at
        // bootstrap: a session migrating from the old header-only scheme has no
        // token until this PIN succeeds, so nothing could have been fetched yet.
        await refreshData();
      } else if (data.subscription_expired) {
        // The expired reply carries a token too, otherwise the renewal button
        // on this screen could not authenticate its own request.
        if (data.session_token) setSessionToken(data.session_token);
        setShowSubscriptionExpired(true);
        setExpiredOrgName(data.org_name || localStorage.getItem('org_name') || '');
        setExpiredOrgId(data.org_id || localStorage.getItem('org_id'));
        if (data.subscription_end_date) localStorage.setItem('subscription_end_date', data.subscription_end_date);
        setRenewalAlreadyRequested(data.renewal_requested || false);
        setPin('');
        setPinError(false);
      } else {
        setPinError(true);
        setPin('');
      }
    } catch {
      // Backend unreachable - deny access instead of falling back to hardcoded PINs
      setPinError(true);
      setPin('');
      showNotification(t('networkError'), 'error');
    } finally {
      setPinVerifying(false);
    }
  };

  // ------ Multi-Tenant Handler Functions ----------------------------------------------------------------------------------

  // Everything that happens once the server has accepted a login. PIN login
  // and email-OTP login reply with the same body, so both land here — one
  // shared code path means the two methods can never drift apart.
  const finishOrgLogin = async (data) => {
    localStorage.setItem('org_id', data.org_id);
    localStorage.setItem('org_name', data.org_name);
    localStorage.setItem('selected_business_id', data.business_id);
    // The signed token is what actually authorises later requests — the
    // org id above is only an identifier now, not a credential.
    setSessionToken(data.session_token);
    if (data.subscription_end_date) localStorage.setItem('subscription_end_date', data.subscription_end_date);
    if (data.subscription_plan) localStorage.setItem('subscription_plan', data.subscription_plan);
    setOrgAuth(data.org_id);
    setOrgName(data.org_name);

    // ── Seed admin member then fetch member list for step-2 ──
    try {
      await fetch(`${BACKEND_URL}/members/seed-admin/`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', 'X-Org-Id': data.org_id }),
        body: JSON.stringify({ org_id: data.org_id, org_name: data.org_name })
      });
      const listRes = await fetch(`${BACKEND_URL}/members/list/`, {
        headers: authHeaders({ 'X-Org-Id': data.org_id })
      });
      if (!listRes.ok) {
        throw new Error('Member list endpoint failed');
      }
      const listData = await listRes.json();
      const members = listData.members || [];
      setMemberList(members);

      // If <= 1 member, auto-login directly as admin without interrupting the user
      if (members.length <= 1) {
        const adminMember = members.length === 1 ? members[0] : null;
        const adminSession = {
          member_id: adminMember ? adminMember.id : null,
          member_name: adminMember ? adminMember.name : (data.org_name || 'Admin'),
          role: 'ADMIN',
          permissions: {
            ledger: true, cash: true, invoices: true, stock: true,
            income_expense: true, staff: true, reports: true,
            settings: true, manage_users: true, can_delete: true, can_edit: true,
          },
        };
        localStorage.setItem('member_session', JSON.stringify(adminSession));
        setMemberSession(adminSession);
        setShowMemberSelect(false);
        await refreshData(false);
      } else {
        // Multiple members — show selection screen
        setShowMemberSelect(true);
        setSelectedMemberId(null);
        setMemberPinInput('');
        setMemberPinError(false);
      }
    } catch {
      // If member API fails, fall back to admin session and open the app
      const fallbackSession = {
        member_id: null,
        member_name: data.org_name || 'Admin',
        role: 'ADMIN',
        permissions: {
          ledger: true, cash: true, invoices: true, stock: true,
          income_expense: true, staff: true, reports: true,
          settings: true, manage_users: true, can_delete: true, can_edit: true,
        },
      };
      localStorage.setItem('member_session', JSON.stringify(fallbackSession));
      setMemberSession(fallbackSession);
      setShowMemberSelect(false);
      await refreshData(false);
    }
  };

  const handleOrgLogin = async (e) => {
    e.preventDefault();
    setOrgLoginError('');
    try {
      const res = await fetch(`${BACKEND_URL}/org-login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orgLoginForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // PIN-specific side effects. The email path below deliberately has
        // neither: there is no PIN to remember on this device.
        localStorage.setItem('org_pin', orgLoginForm.pin_code);
        if (rememberMe) {
          localStorage.setItem('hisabflow_remember', 'true');
        } else {
          localStorage.removeItem('hisabflow_remember');
        }
        await finishOrgLogin(data);
      } else if (data.subscription_expired) {
        localStorage.setItem('org_id', data.org_id);
        localStorage.setItem('org_name', data.org_name);
        // Needed by the renewal request fired from the expired screen.
        if (data.session_token) setSessionToken(data.session_token);
        if (data.subscription_end_date) localStorage.setItem('subscription_end_date', data.subscription_end_date);
        if (data.subscription_plan) localStorage.setItem('subscription_plan', data.subscription_plan);
        setShowSubscriptionExpired(true);
        setExpiredOrgId(data.org_id);
        setExpiredOrgName(data.org_name);
        setRenewalAlreadyRequested(data.renewal_requested || false);
      } else {
        setOrgLoginError(data.error || t('loginFailed'));
      }
    } catch {
      setOrgLoginError(t('networkError'));
    }
  };

  // ── Email OTP sign-in ─────────────────────────────────────────────────────────
  const resetOtpFlow = () => {
    setOtpStage('email');
    setOtpCode('');
    setOtpError('');
    setOtpNotice('');
    setOtpSentTo('');
    setOtpCooldown(0);
  };

  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    const email = otpEmail.trim();
    if (!email || otpLoading) return;
    setOtpError('');
    setOtpNotice('');
    setOtpLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/auth/request-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 429 from the throttle, or any other refusal, carries its own text.
        setOtpError(data.error || t('networkError'));
        return;
      }
      if (data.otp_enabled === false) {
        // SMTP is not configured on this server. Say so plainly instead of
        // letting the owner sit and wait for a code that will never arrive.
        setOtpError(t('emailSignInUnavailable'));
        return;
      }
      setOtpSentTo(email);
      setOtpStage('code');
      setOtpCode('');
      setOtpNotice(t('otpSent'));
      // Matches OTP_COOLDOWN_SECONDS on the server (60s).
      setOtpCooldown(60);
    } catch {
      setOtpError(t('networkError'));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otpCode.trim();
    if (otpLoading) return;
    if (code.length !== 6) {
      // The field happily accepts fewer than six digits, so say what is
      // missing rather than swallowing the submit with no feedback.
      setOtpError(t('otpCodeIncomplete'));
      return;
    }
    setOtpError('');
    setOtpLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/auth/verify-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: otpSentTo || otpEmail.trim(), code })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        // The code *is* the credential, so there is no PIN to re-confirm on
        // a lock screen afterwards — go straight in and stay signed in.
        // The PIN path is untouched and still behaves exactly as before.
        localStorage.setItem('hisabflow_remember', 'true');
        await finishOrgLogin(data);
        setIsLocked(false);
        resetOtpFlow();
        setOtpEmail('');
        return;
      }
      if (data.subscription_expired) {
        // Mirrors the PIN login: the renewal button on this screen still has
        // to authenticate its own request, so it needs the token too.
        localStorage.setItem('org_id', data.org_id);
        localStorage.setItem('org_name', data.org_name);
        if (data.session_token) setSessionToken(data.session_token);
        if (data.subscription_end_date) localStorage.setItem('subscription_end_date', data.subscription_end_date);
        if (data.subscription_plan) localStorage.setItem('subscription_plan', data.subscription_plan);
        setShowSubscriptionExpired(true);
        setExpiredOrgId(data.org_id);
        setExpiredOrgName(data.org_name);
        setRenewalAlreadyRequested(!!data.renewal_requested);
        return;
      }
      // Wrong, expired or exhausted code: clear the box and let them try
      // again. The server's message is deliberately generic.
      setOtpError(data.error || t('loginFailed'));
      setOtpCode('');
    } catch {
      setOtpError(t('networkError'));
    } finally {
      setOtpLoading(false);
    }
  };

  const handleMemberLogin = async (memberId) => {
    if (!memberPinInput || memberPinInput.length !== 4) return;
    setMemberPinLoading(true);
    setMemberPinError(false);
    try {
      const orgId = localStorage.getItem('org_id');
      const res = await fetch(`${BACKEND_URL}/members/login/`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', 'X-Org-Id': orgId }),
        body: JSON.stringify({ member_id: memberId, pin_code: memberPinInput, org_id: orgId })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Replace the org-level token with the member-bound one the server
        // just minted: from here on every request carries this member's    signed claim, so the server enforces the role below itself instead of    trusting the browser.
        if (data.session_token) setSessionToken(data.session_token);
        const session = {
          member_id: data.member_id,
          member_name: data.member_name,
          role: data.role,
          permissions: data.permissions,
        };
        localStorage.setItem('member_session', JSON.stringify(session));
        setMemberSession(session);
        setShowMemberSelect(false);
        setMemberPinInput('');
        setSelectedMemberId(null);
        await refreshData(false);
      } else {
        setMemberPinError(true);
        setMemberPinInput('');
      }
    } catch {
      setMemberPinError(true);
      setMemberPinInput('');
    } finally {
      setMemberPinLoading(false);
    }
  };

  const handleContinueAsAdmin = () => {
    const adminMember = memberList.find(m => m.role === 'ADMIN');
    if (!adminMember) return;
    setSelectedMemberId(adminMember.id);
    setMemberPinInput('');
    setMemberPinError(false);
  };

  const clearMemberSessionState = () => {
    localStorage.removeItem('member_session');
    setMemberSession(null);
    setShowMemberSelect(false);
    setSelectedMemberId(null);
    setMemberPinInput('');
    setMemberPinError(false);
  };

  const handleMemberLogout = () => {
    // Clear only member session, keep org session so they can pick another member
    clearMemberSessionState();
    setShowMemberSelect(true);
  };

  const handleForgotPin = async (e) => {
    e.preventDefault();
    setForgotPinError('');
    setForgotPinResult(null);
    setForgotPinResetError('');
    setForgotPinLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/forgot-pin/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: (forgotPinForm.email || '').trim(),
          phone: (forgotPinForm.phone || '').trim(),
          name: (forgotPinForm.name || '').trim()
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForgotPinResult(data);
        setForgotPinResetForm(prev => ({ ...prev, code: '', new_pin: '', confirm_pin: '' }));
      } else {
        setForgotPinError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setForgotPinError(t('networkError'));
    } finally {
      setForgotPinLoading(false);
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    setForgotPinResetError('');
    if (forgotPinResetForm.new_pin !== forgotPinResetForm.confirm_pin) {
      setForgotPinResetError('Both PIN entries must match.');
      return;
    }
    setForgotPinLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/reset-pin/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: (forgotPinForm.email || '').trim(),
          name: (forgotPinForm.name || '').trim(),
          phone: (forgotPinForm.phone || '').trim(),
          code: forgotPinResetForm.code.trim(),
          new_pin: forgotPinResetForm.new_pin.trim()
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForgotPinDone(true);
      } else {
        setForgotPinResetError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setForgotPinResetError(t('networkError'));
    } finally {
      setForgotPinLoading(false);
    }
  };

  const handleRegisterOrg = async (e) => {
    e.preventDefault();
    setRegError('');
    setRegSuccess(false);
    try {
      const res = await fetch(`${BACKEND_URL}/register-org/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regForm)
      });
      const data = await res.json();
      if (res.status === 201) {
        setRegSuccess(true);
        setRegForm({ name: '', owner_name: '', email: '', phone: '', address: '' });
      } else {
        setRegError(data.error || t('regFailed'));
      }
    } catch {
      setRegError(t('regNetworkError'));
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoginError('');
    try {
      const res = await fetch(`${BACKEND_URL}/admin/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adminForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Without this the panel would authenticate on every call with... nothing.
        setAdminToken(data.token);
        window.history.pushState({}, '', '/admin-panel');
        setIsAdmin(true);
        fetchAdminData();
      } else {
        setAdminLoginError(data.error || t('invalidCredentials'));
      }
    } catch {
      setAdminLoginError(t('networkError'));
    }
  };

  const fetchAdminData = async () => {
    try {
      const [statsRes, orgsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/admin/stats/`, { headers: adminHeaders() }),
        fetch(`${BACKEND_URL}/admin/orgs/`, { headers: adminHeaders() })
      ]);
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setAdminStats(stats);
        if (stats.latest_backup_created_at) {
          const lastSeen = localStorage.getItem('lastSeenBackup');
          if (stats.latest_backup_created_at !== lastSeen) {
            localStorage.setItem('lastSeenBackup', stats.latest_backup_created_at);
            showNotification(t('autoBackupCompleted'), 'info');
          }
        }
      }
      if (orgsRes.ok) setAdminOrgs(await orgsRes.json());
    } catch { /* offline */ }
  };

  const handleApproveOrg = async () => {
    if (!approveModal?.pin || approveModal.pin.length !== 4) return;
    try {
      const res = await fetch(`${BACKEND_URL}/admin/orgs/${approveModal.id}/approve/`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ pin_code: approveModal.pin })
      });
      if (res.ok) {
        setApproveModal(null);
        fetchAdminData();
      }
    } catch { /* ignore */ }
  };

  const handleAdminAction = async (orgId, action) => {
    try {
      await fetch(`${BACKEND_URL}/admin/orgs/${orgId}/${action}/`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' })
      });
      fetchAdminData();
    } catch { /* ignore */ }
  };

  const handleDeleteOrg = async () => {
    if (!deleteOrgModal) return;
    try {
      const res = await fetch(`${BACKEND_URL}/admin/orgs/${deleteOrgModal.id}/delete/`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showNotification(data.message || 'Organization deleted successfully.', 'success');
        setDeleteOrgModal(null);
        fetchAdminData();
      } else {
        showNotification(data.error || 'Failed to delete organization.', 'warning');
      }
    } catch {
      showNotification('Failed to delete organization.', 'warning');
    }
  };

  const handleFetchBackups = async (org) => {
    setSelectedAdminOrg(org);
    try {
      const res = await fetch(`${BACKEND_URL}/admin/orgs/${org.id}/backups/`, { headers: adminHeaders() });
      if (res.ok) setOrgBackups(await res.json());
    } catch { setOrgBackups([]); }
  };

  const handleRunBackup = async () => {
    if (!selectedAdminOrg) return;
    try {
      const res = await fetch(`${BACKEND_URL}/admin/orgs/${selectedAdminOrg.id}/backups/`, {
        method: 'POST',
        headers: adminHeaders()
      });
      if (res.ok) {
        handleFetchBackups(selectedAdminOrg);
        showNotification(t('backupCompleted'), 'success');
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || t('backupFailed'), 'warning');
      }
    } catch { showNotification(t('backupFailed'), 'warning'); }
  };

  // Downloads must be fetched rather than linked: attaching the admin token
  // is impossible on a plain navigation, and the endpoint now correctly
  // rejects requests that carry no credential at all.
  const handleDownloadBackup = async (backupId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/admin/backups/download/${backupId}/`, {
        headers: adminHeaders()
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showNotification(err.error || t('downloadFailed'), 'warning');
        return;
      }
      const blob = await res.blob();
      // Prefer the filename the server sent, fall back to the backup id.
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `backup_${backupId}`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showNotification(t('downloadFailed'), 'warning');
    }
  };

  const handleRestoreBackup = async (backupId) => {
    if (!window.confirm(t('restoreConfirm'))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/admin/backups/${backupId}/restore/`, { method: 'POST', headers: adminHeaders() });
      const data = await res.json();
      if (res.ok) {
        showNotification(t('restoreCompleted'), 'success');
      } else {
        showNotification(data.error || t('restoreFailed'), 'warning');
      }
    } catch { showNotification(t('restoreFailed'), 'warning'); }
  };

  const fetchRecycleBin = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/recycle-bin/`, { headers: authHeaders({ 'X-Org-Id': orgAuth }) });
      if (res.ok) setRecycleBinItems(await res.json());
    } catch { setRecycleBinItems([]); }
  };

  const handleRestoreItem = async (itemId) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/recycle-bin/${itemId}/restore/`, {
        method: 'POST',
        headers: authHeaders({ 'X-Org-Id': orgAuth })
      });
      const data = await res.json();
      if (res.ok) {
        showNotification(data.message || t('itemRestored'), 'success');
        fetchRecycleBin();
        refreshData();
      } else {
        showNotification(data.error || t('restoreFailed'), 'warning');
      }
    } catch { showNotification(t('restoreFailed'), 'warning'); }
  };

  const handlePermanentDelete = async (itemId) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    if (!window.confirm(t('permanentDeleteConfirm'))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/recycle-bin/${itemId}/permanent-delete/`, {
        method: 'POST',
        headers: authHeaders({ 'X-Org-Id': orgAuth })
      });
      const data = await res.json();
      if (res.ok) {
        showNotification(data.message || t('itemPermanentlyDeleted'), 'success');
        fetchRecycleBin();
      } else {
        showNotification(data.error || t('deleteFailed'), 'warning');
      }
    } catch { showNotification(t('deleteFailed'), 'warning'); }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    setPinMessage('');
    const effectiveCurrentPin = (currentPin || localStorage.getItem('org_pin') || '').trim();
    if (!effectiveCurrentPin) {
      setPinMessage(t('enterCurrentPIN') || 'Please enter current PIN');
      setPinMessageType('error');
      return;
    }
    if (newPin !== confirmPin) {
      setPinMessage(t('pinMismatch'));
      setPinMessageType('error');
      return;
    }
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setPinMessage(t('pinLength'));
      setPinMessageType('error');
      return;
    }
    const orgId = localStorage.getItem('org_id') || orgAuth || selectedBusiness?.id;
    if (!orgId) {
      setPinMessage(t('networkError') || 'Missing organization ID');
      setPinMessageType('error');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/change-pin/`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', 'X-Org-Id': orgId }),
        body: JSON.stringify({ org_id: orgId, current_pin: effectiveCurrentPin, new_pin: newPin })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('org_pin', newPin);
        setPinMessage(t('pinChanged'));
        setPinMessageType('success');
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
      } else {
        setPinMessage(data.error || t('pinFailed'));
        setPinMessageType('error');
      }
    } catch {
      setPinMessage(t('networkError'));
      setPinMessageType('error');
    }
  };

  const handleRenewalRequest = async (plan) => {
    setRenewalMessage('');
    const orgId = expiredOrgId || localStorage.getItem('org_id');
    if (!orgId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/renewal-request/`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json', 'X-Org-Id': orgId }),
        body: JSON.stringify({ org_id: orgId, renewal_plan: plan || '1_month' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRenewalMessage(t('renewalSent'));
        setRenewalAlreadyRequested(true);
        setShowRenewalPlanModal(false);
      } else {
        setRenewalMessage(data.error || t('renewalFailed'));
      }
    } catch {
      setRenewalMessage(t('networkError'));
    }
  };

  const handleLogoutBusiness = async () => {
    if (!window.confirm(t('logoutConfirm'))) return;

    localStorage.removeItem('member_session');
    localStorage.removeItem('org_id');
    localStorage.removeItem('org_name');
    localStorage.removeItem('org_pin');
    localStorage.removeItem('selected_business_id');
    localStorage.removeItem('last_sync_timestamp');
    localStorage.removeItem('hisabflow_remember');
    // Drop the credential too — leaving it behind would keep the old org
    // authenticated after the user believed they had signed out.
    setSessionToken(null);
    setMemberSession(null);
    setShowMemberSelect(false);
    setSelectedMemberId(null);
    setMemberPinInput('');
    setMemberPinError(false);
    setOrgAuth('');
    setOrgName('');
    setShowSidebar(false);
    window.location.reload();
  };

  // CRUD: Add Party
  const addNewArea = async () => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    if (!newAreaName.trim() || !selectedBusiness) return;
    try {
      const result = await api('/areas/', 'POST', { business: selectedBusiness.id, name: newAreaName.trim() });
      if (result) {
        setAreas(prev => [...prev, result].sort((a, b) => a.name.localeCompare(b.name)));
        setNewParty(prev => ({ ...prev, area: result.name }));
      }
      setNewAreaName('');
      setShowAddAreaInline(false);
    } catch (err) {
      showNotification(err.message || 'Failed to add area', 'error');
    }
  };

  const addNewProductType = async () => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    if (!newProductTypeName.trim() || !selectedBusiness) return;
    try {
      const result = await api('/product-types/', 'POST', { business: selectedBusiness.id, name: newProductTypeName.trim() });
      if (result) {
        setProductTypes(prev => [...prev, result].sort((a, b) => a.name.localeCompare(b.name)));
        setNewProduct(prev => ({ ...newProduct, product_type: result.name }));
        if (editingProduct) setEditingProduct(prev => ({ ...prev, product_type: result.name }));
      }
      setNewProductTypeName('');
      setShowAddProductTypeInline(false);
    } catch (err) {
      showNotification(err.message || 'Failed to add product type', 'error');
    }
  };

  const generateSku = () => {
    const ts = Date.now().toString(36).toUpperCase();
    const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `PRD-${ts.slice(-4)}${rand}`;
  };

  const handleAddParty = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!selectedBusiness) return;
    
    const partyObj = {
      business: selectedBusiness.id,
      name: newParty.name,
      phone: newParty.phone,
      party_type: newParty.party_type,
      cnic: newParty.cnic,
      // FBR: a registered customer's tax number, when the business uses FBR.
      ntn_strn: newParty.ntn_strn || '',
      address: newParty.address,
      area: newParty.area || null,
      items_supplied: newParty.party_type === 'SUPPLIER' || newParty.party_type === 'BOTH' ? newParty.items_supplied : '',
      cached_balance: 0.00
    };
    await api('/parties/', 'POST', partyObj);
    
    setNewParty({
      name: '', phone: '', party_type: 'CUSTOMER', cnic: '', address: '', area: '', items_supplied: ''
    });
    setShowAddPartyModal(false);
    setShowAddAreaInline(false);
    setNewAreaName('');
    showNotification(t('partyAdded', { type: newParty.party_type === 'CUSTOMER' ? t('customers') : newParty.party_type === 'SUPPLIER' ? t('suppliers') : t('both') || 'Both' }), 'success');
    await refreshData();
  };

  const resetNewParty = (partyType = 'CUSTOMER') => {
    setNewParty({ name: '', phone: '', party_type: partyType, cnic: '', address: '', area: '', items_supplied: '' });
  };

  const [importedContacts, setImportedContacts] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);

  const parseVCard = (text) => {
    const contacts = [];
    const cards = text.split('BEGIN:VCARD');
    for (const card of cards) {
      if (!card.trim()) continue;
      let name = '';
      let phone = '';
      const lines = card.split(/\r?\n/);
      for (const line of lines) {
        if (line.startsWith('FN:') || line.startsWith('fn:')) {
          name = line.substring(3).trim();
        } else if (line.startsWith('N:') || line.startsWith('n:')) {
          if (!name) {
            const parts = line.substring(2).split(';');
            name = ((parts[1] || '').trim() + ' ' + (parts[0] || '').trim()).trim();
          }
        } else if (line.match(/^TEL[;:]/i)) {
          const val = line.split(':').slice(1).join(':').trim();
          if (!phone) phone = val;
        }
      }
      if (name || phone) contacts.push({ name, phone });
    }
    return contacts;
  };

  const handleImportContact = async () => {
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        const props = ['name', 'tel'];
        const opts = { multiple: false };
        const contacts = await navigator.contacts.select(props, opts);
        if (contacts.length > 0) {
          const c = contacts[0];
          const name = (c.name && c.name[0]) || '';
          const phone = (c.tel && c.tel[0]) || '';
          setNewParty({ ...newParty, name, phone });
          return;
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.vcf,.vcard';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const contacts = parseVCard(text);
        if (contacts.length === 0) {
          showNotification(t('noContactsFound') || 'No contacts found in file', 'warning');
          return;
        }
        if (contacts.length === 1) {
          setNewParty({ ...newParty, name: contacts[0].name, phone: contacts[0].phone });
        } else {
          setImportedContacts(contacts);
          setShowContactPicker(true);
        }
      } catch {
        showNotification(t('contactImportFailed') || 'Failed to read contacts file', 'error');
      }
    };
    input.click();
  };

  // CRUD: Add Ledger Entry (Udhaar)
  const handleAddLedgerEntry = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!selectedParty || !selectedBusiness) return;
    
    if (ledgerForm.payment_method === 'ONLINE' && !ledgerForm.bank_account_id) {
      showNotification(t('selectBankAccount') || 'Select a bank account for this online payment.', 'warning');
      return;
    }

    const amountNum = parseFloat(ledgerForm.amount);
    if (isNaN(amountNum) || amountNum <= 0) return;

    const entryObj = {
      business: selectedBusiness.id,
      party: selectedParty.id,
      amount: amountNum,
      entry_type: ledgerForm.entry_type,
      date: ledgerForm.date,
      time: ledgerForm.time || null,
      description: ledgerForm.description,
      payment_method: ledgerForm.payment_method,
      bank_account: ledgerForm.payment_method === 'ONLINE' ? ledgerForm.bank_account_id : null
    };
    let payload = entryObj;
    if (ledgerForm.attachment) {
      payload = new FormData();
      Object.keys(entryObj).forEach(key => {
        if (entryObj[key] !== null) payload.append(key, entryObj[key]);
      });
      payload.append('attachment', ledgerForm.attachment);
    }
    const factor = ledgerForm.entry_type === 'GIVE' ? 1 : -1;
    const balanceChange = amountNum * factor;
    const partyEntries = ledgerEntries.filter(e => (e.party === selectedParty.id || e.party_id === selectedParty.id) && !e.is_deleted);
    const currentBal = partyEntries.reduce((s, e) => s + (e.entry_type === 'GIVE' ? parseFloat(e.amount || 0) : -parseFloat(e.amount || 0)), 0);
    const newBalance = currentBal + balanceChange;
    // Show the new row immediately from the POST response; the refresh below
    // replaces the list with the server's authoritative copy (same id, so no
    // duplicate can survive that reconcile).
    const createdEntry = await api('/ledger-entries/', 'POST', payload);
    if (createdEntry && createdEntry.id) {
      setLedgerEntries(prev => [createdEntry, ...prev.filter(x => x.id !== createdEntry.id)]);
    }
    // The PATCH response IS the updated party — no follow-up GET is needed.
    const updatedParty = await api(`/parties/${selectedParty.id}/`, 'PATCH', { cached_balance: newBalance });
    if (updatedParty) setSelectedParty(updatedParty);

    // Refresh Local States
    setLedgerForm({ ...ledgerForm, amount: '', description: '', attachment: null, payment_method: 'CASH', bank_account_id: '' });
    showNotification(t('udhaarLogged'), 'success');
    await refreshData();
  };

  // CRUD: Add Cash Book Entry
  const handleAddCashEntry = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!selectedBusiness) return;

    const amountNum = parseFloat(cashForm.amount);
    if (isNaN(amountNum) || amountNum <= 0) return;

    const categoryText = showCustomCategoryInput ? customCategory : cashForm.category;

    const cashObj = {
      business: selectedBusiness.id,
      amount: amountNum,
      entry_type: cashForm.entry_type,
      date: cashForm.date,
      time: cashForm.time || null,
      category: categoryText || 'General',
      description: cashForm.description
    };
    let payload = cashObj;
    if (cashForm.attachment) {
      payload = new FormData();
      Object.keys(cashObj).forEach(key => {
        if (cashObj[key] !== null) payload.append(key, cashObj[key]);
      });
      payload.append('attachment', cashForm.attachment);
    }
    const createdEntry = await api('/cash-entries/', 'POST', payload);
    // Instant row — see the ledger handler for why the reconcile is safe.
    if (createdEntry && createdEntry.id) {
      setCashEntries(prev => [createdEntry, ...prev.filter(x => x.id !== createdEntry.id)]);
    }

    setCashForm({ ...cashForm, amount: '', description: '', attachment: null });
    setCustomCategory('');
    setShowCustomCategoryInput(false);
    showNotification(t('cashSaved'), 'success');
    await refreshData();
  };

  // CRUD: Add Income/Expense Record
  const handleAddIeEntry = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    e.preventDefault();
    if (!selectedBusiness) return;

    const amountNum = parseFloat(ieForm.amount);
    if (isNaN(amountNum) || amountNum <= 0) return;

    const categoryText = showCustomIeCategory ? customIeCategory : ieForm.category;

    const ieObj = {
      business: selectedBusiness.id,
      type: ieForm.type,
      amount: amountNum,
      category: categoryText || 'Other',
      date: ieForm.date,
      time: ieForm.time || null,
      description: ieForm.description
    };
    let payload = ieObj;
    if (ieForm.attachment) {
      payload = new FormData();
      Object.keys(ieObj).forEach(key => {
        if (ieObj[key] !== null) payload.append(key, ieObj[key]);
      });
      payload.append('attachment', ieForm.attachment);
    }
    const createdEntry = await api('/income-expenses/', 'POST', payload);
    // Instant row — see the ledger handler for why the reconcile is safe.
    if (createdEntry && createdEntry.id) {
      setIncomeExpenses(prev => [createdEntry, ...prev.filter(x => x.id !== createdEntry.id)]);
    }

    setIeForm({ ...ieForm, amount: '', description: '', attachment: null });
    setCustomIeCategory('');
    setShowCustomIeCategory(false);
    setShowAddIeModal(false);
    showNotification(t('ieLogged'), 'success');
    await refreshData();
  };

  // CRUD: Add Product (Inventory)
  const handleAddProduct = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!selectedBusiness) return;

    // The form collects raw inputs and sends them as-is; pack prices, pack
    // counts and stock values are derived by the backend and come back on the
    // response. Units Quantity decides the unit of the two number boxes — 1
    // means pieces, more than 1 means packs — so the flag is derived here:
    // there is nothing to select, and the server stores pieces either way.
    const prodObj = {
      business: selectedBusiness.id,
      name: newProduct.name,
      sku: newProduct.sku,
      purchase_price: newProduct.purchase_price,
      sale_price: newProduct.sale_price,
      stock_quantity: newProduct.stock_quantity,
      stock_qty_type: stockQtyType(newProduct.units_per_bulk),
      min_stock_alert: newProduct.min_stock_alert,
      unit: newProduct.unit,
      product_type: newProduct.product_type || null,
      expiration_date: newProduct.expiration_date || null,
      units_per_bulk: newProduct.units_per_bulk,
      // FBR Digital Invoicing — sent regardless; the backend stores them, and
      // they are only READ when this business has FBR switched on.
      hsn_code: newProduct.hsn_code || '',
      fbr_tax_rate: newProduct.fbr_tax_rate || '',
      fbr_uom: newProduct.fbr_uom || ''
    };

    try {
      let result = await api('/products/', 'POST', prodObj);

      // Advisory only (e.g. sale price below cost) — show it, don't keep it.
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      if (warnings.length) delete result.warnings;

      if (productPhotoFile && result && result.id) {
        const formData = new FormData();
        formData.append('photo', productPhotoFile);
        const photoRes = await api(`/products/${result.id}/upload-photo/`, 'POST', formData);
        if (photoRes && photoRes.product) {
          result = photoRes.product;
        }
      }

      setNewProduct({ name: '', sku: '', purchase_price: '', sale_price: '', stock_quantity: '', min_stock_alert: '', unit: 'pcs', product_type: '', expiration_date: '', units_per_bulk: 1, hsn_code: '', fbr_tax_rate: '', fbr_uom: '' });
      setProductPhotoFile(null);
      setShowAddProductModal(false);
      setShowAddProductTypeInline(false);
      setNewProductTypeName('');
      // The saved toast also carries any advisory, since only one toast shows at a time.
      showNotification(
        warnings.length ? `${t('productAdded')} — ${warnings.join(' ')}` : t('productAdded'),
        warnings.length ? 'warning' : 'success'
      );

      if (result && result.id) {
        setProducts(prev => [result, ...prev.filter(p => p.id !== result.id)]);
      } else {
        const fresh = await api('/products/');
        if (fresh) setProducts(fresh);
      }
    } catch (error) {
      // Keep the modal open so the rejected value can be corrected.
      showNotification(error.message || t('networkError'), 'error');
    }
  };

  // CRUD: Add Staff Member
  const handleAddStaff = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!selectedBusiness) return;

    const staffObj = {
      business: selectedBusiness.id,
      name: staffForm.name,
      phone: staffForm.phone,
      base_salary: parseFloat(staffForm.base_salary) || 0,
      salary_type: staffForm.salary_type,
      cnic: staffForm.cnic,
      status: 'ACTIVE'
    };
    const createdStaff = await api('/staff/', 'POST', staffObj);
    // Instant row — see the ledger handler for why the reconcile is safe.
    if (createdStaff && createdStaff.id) {
      setStaff(prev => [createdStaff, ...prev.filter(x => x.id !== createdStaff.id)]);
    }

    setStaffForm({ name: '', phone: '', base_salary: '', salary_type: 'MONTHLY', cnic: '' });
    setShowAddStaffModal(false);
    showNotification(t('staffAdded'), 'success');
    await refreshData();
  };

  // CRUD: Log Payroll record
  const handleAddPayroll = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    const employee = staff.find(s => s.id === payrollForm.staff_member_id);
    if (!employee) return;

    const basicSal = employee.base_salary;
    const net = basicSal + (parseFloat(payrollForm.allowances) || 0) - (parseFloat(payrollForm.deductions) || 0);

    const payrollObj = {
      business: selectedBusiness.id,
      staff_member: employee.id,
      pay_period_start: payrollForm.pay_period_start,
      pay_period_end: payrollForm.pay_period_end,
      basic_salary: basicSal,
      allowances: parseFloat(payrollForm.allowances) || 0,
      deductions: parseFloat(payrollForm.deductions) || 0,
      net_paid: net,
      status: payrollForm.status,
      date_paid: payrollForm.status === 'PAID' ? new Date().toISOString().split('T')[0] : null
    };
    await api('/payrolls/', 'POST', payrollObj);

    if (payrollForm.status === 'PAID') {
      await api('/cash-entries/', 'POST', {
        business: selectedBusiness.id,
        amount: net,
        entry_type: 'OUT',
        date: new Date().toISOString().split('T')[0],
        time: currentTime(),
        category: 'Salary',
        description: `Salary paid to ${employee.name}`
      });
      await api('/income-expenses/', 'POST', {
        business: selectedBusiness.id,
        type: 'EXPENSE',
        amount: net,
        category: 'Salary',
        date: new Date().toISOString().split('T')[0],
        time: currentTime(),
        description: `Salary paid to ${employee.name}`
      });
    }

    setPayrollForm({ staff_member_id: '', pay_period_start: new Date().toISOString().slice(0, 7) + '-01', pay_period_end: new Date().toISOString().split('T')[0], allowances: '0', deductions: '0', status: 'UNPAID' });
    setShowAddPayrollModal(false);
    showNotification(t('salaryLogged'), 'success');
    await refreshData();
  };

  // Update Business Settings
  const [businessLogoPreview, setBusinessLogoPreview] = useState(null);

  const handleUpdateBusiness = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    e.preventDefault();
    if (!selectedBusiness) return;

    try {
      // 1. Update text configurations via standard JSON PATCH request
      const updatedData = {
        name: businessForm.name,
        address: businessForm.address,
        email: businessForm.email,
        phone: businessForm.phone,
        currency: businessForm.currency,
        language: businessForm.language
      };
      await api(`/businesses/${selectedBusiness.id}/`, 'PATCH', updatedData);

      // 2. Upload logo file if selected via a separate POST endpoint
      let logoError = null;
      if (businessLogoFile) {
        const formData = new FormData();
        formData.append('logo', businessLogoFile);
        try {
          const result = await api(`/businesses/${selectedBusiness.id}/upload-logo/`, 'POST', formData);
          console.log("Logo upload response:", result);
        } catch (logoErr) {
          console.error("Logo upload failed:", logoErr);
          logoError = logoErr;
        }
      }

      setShowEditBusinessModal(false);
      setBusinessLogoFile(null);
      setBusinessLogoPreview(null);
      if (logoError) {
        showNotification(logoError.message || 'Profile saved but logo upload failed — check server permissions.', 'error');
      } else {
        showNotification(t('profileUpdated'), 'success');
      }
      await refreshData();
    } catch (err) {
      console.error("Failed to update company profile:", err);
      showNotification(t('networkError') || 'Failed to save changes.', 'error');
    }
  };

  // CRUD: Log Attendance
  const handleAttendanceChange = async (staffId, status) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    const existing = attendances.find(a => a.staff_member === staffId && a.date === attendanceDate);
    if (existing) {
      await api(`/attendances/${existing.id}/`, 'PATCH', { status });
    } else {
      const attObj = {
        business: selectedBusiness.id,
        staff_member: staffId,
        date: attendanceDate,
        time: currentTime(),
        status,
        overtime_hours: 0.0
      };
      await api('/attendances/', 'POST', attObj);
    }
    await refreshData();
  };

  // CRUD: Invoice Generation
  const handleInvoiceItemChange = (index, field, value) => {
    const updatedItems = [...invoiceBuilder.items];
    updatedItems[index][field] = value;

    // Toggle the unit the cashier is typing in. Existing quantities keep their
    // exact meaning in pieces — only the untouched default of "1" becomes "1 pack".
    if (field === 'qty_type') {
      lastQtyTypeRef.current = value;
      const rowProd = updatedItems[index].product_id ? products.find(p => p.id === updatedItems[index].product_id) : null;
      const rowUpb = packSizeFor(updatedItems[index], rowProd);
      updatedItems[index].quantity = initialPiecesFor(value, rowUpb, updatedItems[index].quantity);
    }

    // Auto populate price and units qty if product is selected
    if (field === 'product_id' && value) {
      const prod = products.find(p => p.id === value);
      if (prod) {
        const upb = packSizeFor({}, prod);
        const perPiece = numOr(prod.sale_price, 0);
        updatedItems[index].unit_price = perPiece;   // always per piece
        updatedItems[index].custom_item_name = prod.name;
        updatedItems[index].units_per_bulk = upb;
        updatedItems[index].qty_type = updatedItems[index].qty_type || lastQtyTypeRef.current;
        if (upb <= 1) updatedItems[index].qty_type = 'piece';
        updatedItems[index].quantity = initialPiecesFor(updatedItems[index].qty_type, upb, updatedItems[index].quantity);
      }
    } else if (field === 'product_id') {
      // Product removed: it no longer carries a pack size, so the row must not
      // keep showing pack columns for a product it is no longer linked to.
      delete updatedItems[index].units_per_bulk;
    }

    setInvoiceBuilder({ ...invoiceBuilder, items: updatedItems });
  };

  const addInvoiceItemRow = () => {
    setInvoiceBuilder({
      ...invoiceBuilder,
      items: [...invoiceBuilder.items, { product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', qty_type: lastQtyTypeRef.current }]
    });
  };

  const selectSaleProduct = (index, prod) => {
    if (!prod) return;
    setInvoiceBuilder(prev => {
      const updatedItems = [...prev.items];
      const current = updatedItems[index] || {};
      const upb = packSizeFor(current, prod);
      const perPiece = numOr(prod.sale_price, 0);
      let qtyType = current.qty_type || lastQtyTypeRef.current;
      if (upb <= 1) qtyType = 'piece';   // no pack concept → always pieces
      // Always stored PER PIECE (never the pack total): the rate box re-multiplies
      // by units_per_bulk for its pack display, normalizeLineItem prices pieces
      // with it, and the other two product-select paths carry an explicit
      // "always per piece" comment. Multiplying here made every line picked while
      // the row already sat in Pack mode (2nd+ line of a session, where
      // lastQtyTypeRef still reads 'pack') read and charge upb x too much.
      const unitPrice = perPiece;
      updatedItems[index] = {
        ...current,
        product_id: prod.id,
        unit_price: unitPrice,
        custom_item_name: prod.name,
        quantity: initialPiecesFor(qtyType, upb, current.quantity || 1),
        units_per_bulk: upb,
        qty_type: qtyType,
      };
      return { ...prev, items: updatedItems };
    });
    setProdSearchQuery('');
    setProdDropdownRow(null);
    setProdHighlightedIdx(0);
  };

  const selectSaleCustomItem = (index, name) => {
    const updatedItems = [...invoiceBuilder.items];
    updatedItems[index] = { ...updatedItems[index], product_id: '', custom_item_name: name, unit_price: updatedItems[index].unit_price || 0, quantity: updatedItems[index].quantity || 1, units_per_bulk: undefined };
    setInvoiceBuilder({ ...invoiceBuilder, items: updatedItems });
    setProdSearchQuery('');
    setProdDropdownRow(null);
    setProdHighlightedIdx(0);
  };

  // Desktop quick-add: single working row commits on Enter, then clears for the next item
  const commitSaleEntryItem = (itemOverride) => {
    const item = itemOverride || saleEntryItem;
    if (!item.product_id && !item.custom_item_name) return;
    const totalQty = parseFloat(item.quantity) || 1;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const itemSubtotal = totalQty * unitPrice;

    setInvoiceBuilder(prev => ({
      ...prev,
      items: [...prev.items.filter(it => it.product_id || it.custom_item_name), {
        ...item,
        quantity: totalQty,
        unit_price: unitPrice,
        bulk_quantity: null,
        small_quantity: null,
        bulk_unit_name: null,
        bulk_unit_price: null,
        small_unit_price: null,
        subtotal: itemSubtotal
      }]
    }));
    setSaleEntryItem({ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', qty_type: lastQtyTypeRef.current });
    setSaleEntryDropdownOpen(false);
    setProdSearchQuery('');
    setProdHighlightedIdx(0);
    setTimeout(() => saleProductInputRef.current && saleProductInputRef.current.focus(), 0);
  };

  const selectSaleEntryProduct = (prod) => {
    if (!prod) return;
    setSaleEntryItem(prev => {
      const upb = packSizeFor(prev, prod);
      let qtyType = prev.qty_type || lastQtyTypeRef.current;
      if (upb <= 1) qtyType = 'piece';
      const basePrice = numOr(prod.sale_price, 0);
      // Per PIECE — see selectSaleProduct: the rate box and normalizeLineItem both
      // treat unit_price as the price of one piece, so it must never be pre-multiplied.
      const unitPrice = basePrice;
      return {
        ...prev,
        product_id: prod.id,
        unit_price: unitPrice,
        custom_item_name: prod.name,
        quantity: initialPiecesFor(qtyType, upb, prev.quantity || 1),
        units_per_bulk: upb,
        qty_type: qtyType,
      };
    });
    setSaleEntryDropdownOpen(false);
    setProdSearchQuery('');
    setProdHighlightedIdx(0);
    setTimeout(() => saleProductInputRef.current && saleProductInputRef.current.focus(), 0);
  };

  const selectSaleEntryCustomItem = (name) => {
    setSaleEntryItem({ ...saleEntryItem, product_id: '', custom_item_name: name, quantity: saleEntryItem.quantity || 1, units_per_bulk: undefined });
    setSaleEntryDropdownOpen(false);
    setProdSearchQuery('');
    setProdHighlightedIdx(0);
    setTimeout(() => saleProductInputRef.current && saleProductInputRef.current.focus(), 0);
  };

  const removeCommittedSaleItem = (index) => {
    const committed = invoiceBuilder.items.filter(it => it.product_id || it.custom_item_name);
    const target = committed[index];
    setInvoiceBuilder({ ...invoiceBuilder, items: invoiceBuilder.items.filter(it => it !== target) });
  };

  // Committed rows are shown filtered out, so a row's index on screen is not its
  // index in the builder. Match by identity — the same trick removal uses.
  const setCommittedSaleDiscount = (row, value) => {
    setInvoiceBuilder({ ...invoiceBuilder, items: invoiceBuilder.items.map(it => it === row ? { ...it, discount: value } : it) });
  };

  // Single place where typed packs become pieces. Every total, WhatsApp card,
  // live preview and submit payload runs through this, so they can never
  // disagree with each other or with the stock that will be moved.
  const resolveSaleItemsForSubmit = () => {
    const committed = invoiceBuilder.items.filter(it => it.product_id || it.custom_item_name);
    const lines = (!isMobileView && (saleEntryItem.product_id || saleEntryItem.custom_item_name))
      ? [...committed, saleEntryItem]
      : committed;
    return lines.map(it => normalizeLineItem(it, products));
  };

  const calcSaleTotal = () => {
    return resolveSaleItemsForSubmit().reduce((sum, item) => sum + (numOr(item.subtotal, 0)), 0);
  };

  // The discount on this invoice as a single number, however it was entered.
  // Whole mode reads the typed box; item mode adds the row boxes together, each
  // capped at what its own line is worth; 'both' adds the two halves together.
  // Either half can be zero, so one figure still reaches the server, the card
  // and the printer — the number on screen, the number sent and the number
  // printed can never disagree.
  const saleItemDiscountSum = () => resolveSaleItemsForSubmit().reduce(
    (sum, it) => sum + discountValueOf(it.discount, it.subtotal),
    0);
  const saleDiscountTotal = () =>
    (wholeDiscMode ? discountValueOf(invoiceBuilder.discount_amount, calcSaleTotal()) : 0) +
    (itemDiscMode ? saleItemDiscountSum() : 0);

  // CRUD: Purchase Invoice Generation
  const handlePurchaseInvoiceItemChange = (index, field, value) => {
    const updatedItems = [...purchaseInvoiceBuilder.items];
    updatedItems[index][field] = value;

    // Toggle the unit the cashier is typing in. Existing quantities keep their
    // exact meaning in pieces — only the untouched default of "1" becomes "1 pack".
    if (field === 'qty_type') {
      lastQtyTypeRef.current = value;
      const rowProd = updatedItems[index].product_id ? products.find(p => p.id === updatedItems[index].product_id) : null;
      const rowUpb = packSizeFor(updatedItems[index], rowProd);
      updatedItems[index].quantity = initialPiecesFor(value, rowUpb, updatedItems[index].quantity);
    }

    // Auto populate purchase price and units qty if product is selected
    if (field === 'product_id' && value) {
      const prod = products.find(p => p.id === value);
      if (prod) {
        const upb = packSizeFor({}, prod);
        const perPiece = numOr(prod.purchase_price, 0);
        updatedItems[index].unit_price = perPiece;   // always per piece
        updatedItems[index].custom_item_name = prod.name;
        updatedItems[index].sku = prod.sku || '';
        updatedItems[index].unit = prod.unit || 'pcs';
        updatedItems[index].sale_price = prod.sale_price || 0;
        updatedItems[index].min_stock_alert = prod.min_stock_alert || 5;
        updatedItems[index].units_per_bulk = upb;
        updatedItems[index].qty_type = updatedItems[index].qty_type || lastQtyTypeRef.current;
        if (upb <= 1) updatedItems[index].qty_type = 'piece';
        updatedItems[index].quantity = initialPiecesFor(updatedItems[index].qty_type, upb, updatedItems[index].quantity);
      }
    } else if (field === 'product_id') {
      // Product removed: it no longer carries a pack size, so the row must not
      // keep showing pack columns for a product it is no longer linked to.
      delete updatedItems[index].units_per_bulk;
    }

    setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, items: updatedItems });
  };

  const addPurchaseInvoiceItemRow = () => {
    setPurchaseInvoiceBuilder({
      ...purchaseInvoiceBuilder,
      items: [...purchaseInvoiceBuilder.items, { product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5, expiration_date: '', qty_type: lastQtyTypeRef.current }]
    });
  };

  const selectPurchaseProduct = (index, prod) => {
    if (!prod) return;
    setPurchaseInvoiceBuilder(prev => {
      const updatedItems = [...prev.items];
      const current = updatedItems[index] || {};
      const upb = packSizeFor(current, prod);
      const perPiece = numOr(prod.purchase_price, 0);
      let qtyType = current.qty_type || lastQtyTypeRef.current;
      if (upb <= 1) qtyType = 'piece';
      updatedItems[index] = {
        ...current,
        product_id: prod.id,
        unit_price: perPiece,            // always per piece
        custom_item_name: prod.name,
        sku: prod.sku || '',
        unit: prod.unit || 'pcs',
        sale_price: prod.sale_price || 0,
        min_stock_alert: prod.min_stock_alert || 5,
        quantity: initialPiecesFor(qtyType, upb, current.quantity || 1),
        units_per_bulk: upb,
        qty_type: qtyType,
      };
      return { ...prev, items: updatedItems };
    });
    setPiProdSearchQuery('');
    setPiProdDropdownRow(null);
    setPiProdHighlightedIdx(0);
  };

  const selectPurchaseCustomItem = (index, name) => {
    const updatedItems = [...purchaseInvoiceBuilder.items];
    updatedItems[index] = { ...updatedItems[index], product_id: '', custom_item_name: name, quantity: updatedItems[index].quantity || 1, units_per_bulk: undefined };
    setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, items: updatedItems });
    setPiProdSearchQuery('');
    setPiProdDropdownRow(null);
    setPiProdHighlightedIdx(0);
  };

  // Desktop quick-add: single working row commits on Enter, then clears for the next item
  const commitPurchaseEntryItem = (itemOverride) => {
    const item = itemOverride || purchaseEntryItem;
    if (!item.product_id && !item.custom_item_name) return;
    const totalQty = parseFloat(item.quantity) || 1;
    const unitPrice = parseFloat(item.unit_price) || 0;
    const itemSubtotal = totalQty * unitPrice;

    setPurchaseInvoiceBuilder(prev => ({
      ...prev,
      items: [...prev.items.filter(it => it.product_id || it.custom_item_name), {
        ...item,
        quantity: totalQty,
        unit_price: unitPrice,
        bulk_quantity: null,
        small_quantity: null,
        bulk_unit_name: null,
        bulk_unit_price: null,
        small_unit_price: null,
        subtotal: itemSubtotal
      }]
    }));
    setPurchaseEntryItem({ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5, expiration_date: '', qty_type: lastQtyTypeRef.current });
    setPiEntryDropdownOpen(false);
    setPiProdSearchQuery('');
    setPiProdHighlightedIdx(0);
    setTimeout(() => purchaseProductInputRef.current && purchaseProductInputRef.current.focus(), 0);
  };

  const selectPurchaseEntryProduct = (prod) => {
    if (!prod) return;
    setPurchaseEntryItem(prev => {
      const upb = packSizeFor(prev, prod);
      let qtyType = prev.qty_type || lastQtyTypeRef.current;
      if (upb <= 1) qtyType = 'piece';
      const basePrice = numOr(prod.purchase_price, 0);
      // Per PIECE — see selectSaleProduct: the rate box and normalizeLineItem both
      // treat unit_price as the price of one piece, so it must never be pre-multiplied.
      const unitPrice = basePrice;
      return {
        ...prev,
        product_id: prod.id,
        unit_price: unitPrice,
        custom_item_name: prod.name,
        sku: prod.sku || '',
        unit: prod.unit || 'pcs',
        sale_price: prod.sale_price || 0,
        min_stock_alert: prod.min_stock_alert || 5,
        expiration_date: prod.expiration_date || '',
        quantity: initialPiecesFor(qtyType, upb, prev.quantity || 1),
        units_per_bulk: upb,
        qty_type: qtyType,
      };
    });
    setPiEntryDropdownOpen(false);
    setPiProdSearchQuery('');
    setPiProdHighlightedIdx(0);
    setTimeout(() => purchaseProductInputRef.current && purchaseProductInputRef.current.focus(), 0);
  };

  const selectPurchaseEntryCustomItem = (name) => {
    setPurchaseEntryItem({ ...purchaseEntryItem, product_id: '', custom_item_name: name, quantity: purchaseEntryItem.quantity || 1, units_per_bulk: undefined });
    setPiEntryDropdownOpen(false);
    setPiProdSearchQuery('');
    setPiProdHighlightedIdx(0);
    setTimeout(() => purchaseProductInputRef.current && purchaseProductInputRef.current.focus(), 0);
  };

  const removeCommittedPurchaseItem = (index) => {
    const committed = purchaseInvoiceBuilder.items.filter(it => it.product_id || it.custom_item_name);
    const target = committed[index];
    setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, items: purchaseInvoiceBuilder.items.filter(it => it !== target) });
  };

  // Same identity match as the sale side.
  const setCommittedPurchaseDiscount = (row, value) => {
    setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, items: purchaseInvoiceBuilder.items.map(it => it === row ? { ...it, discount: value } : it) });
  };

  // Same single conversion point as the sale side: packs in, pieces out.
  const resolvePurchaseItemsForSubmit = () => {
    const committed = purchaseInvoiceBuilder.items.filter(it => it.product_id || it.custom_item_name);
    const lines = (!isMobileView && (purchaseEntryItem.product_id || purchaseEntryItem.custom_item_name))
      ? [...committed, purchaseEntryItem]
      : committed;
    return lines.map(it => normalizeLineItem(it, products));
  };

  const calcPurchaseTotal = () => {
    return resolvePurchaseItemsForSubmit().reduce((sum, item) => sum + (numOr(item.subtotal, 0)), 0);
  };

  // Same single source of truth as the sale side: the invoice-level box and the
  // row boxes are added, never substituted for one another, so 'both' reaches
  // every total through this one function.
  const purchaseItemDiscountSum = () => resolvePurchaseItemsForSubmit().reduce(
    (sum, it) => sum + discountValueOf(it.discount, it.subtotal),
    0);
  const purchaseDiscountTotal = () =>
    (wholeDiscMode ? discountValueOf(purchaseInvoiceBuilder.discount_amount, calcPurchaseTotal()) : 0) +
    (itemDiscMode ? purchaseItemDiscountSum() : 0);

  const triggerPurchaseInvoice = (supplier) => {
    setPurchaseInvoiceBuilder({
      party_id: supplier.id,
      invoice_number: generateDailyPurchaseInvoiceNumber(),
      issue_date: new Date().toISOString().split('T')[0],
      issue_time: currentTime(),
      status: 'UNPAID',
      payment_method: 'CASH',
      paid_amount: '',
      discount_amount: '',
      items: [{ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5, qty_type: lastQtyTypeRef.current }]
    });
    setPurchaseEntryItem({ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5, qty_type: lastQtyTypeRef.current });
    setShowPurchaseInvoiceModal(true);
  };

  const submitPurchaseInvoice = async (e, shouldPrint = true) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    if (!selectedBusiness) {
      showNotification(t('selectCustomerFirst') || 'Select business first', 'warning');
      return;
    }

    const itemsForSubmit = resolvePurchaseItemsForSubmit();
    let totalAmt = calcPurchaseTotal();

    if (itemsForSubmit.length === 0) {
      showNotification(t('addItemAtLeast') || 'Add at least one item', 'warning');
      return;
    }
    if (invoiceSubmitInProgress.current) return;
    invoiceSubmitInProgress.current = true;

    try {
      const discountAmt = purchaseDiscountTotal();
      totalAmt = Math.max(0, totalAmt - discountAmt);
      const paidAmt = parseFloat(purchaseInvoiceBuilder.paid_amount) || 0;
      const remaining = Math.max(0, totalAmt - paidAmt);
      const isOnlinePaid = purchaseInvoiceBuilder.status === 'PAID' && (purchaseInvoiceBuilder.payment_method || 'CASH') === 'ONLINE' && paidAmt > 0;
      if (isOnlinePaid && !purchaseInvoiceBuilder.bank_account_id) {
        showNotification(t('selectBankAccount') || 'Select a bank account for this online payment.', 'warning');
        return;
      }

      const invoiceObj = {
        business: selectedBusiness.id,
        party: purchaseInvoiceBuilder.party_id || null,
        invoice_number: purchaseInvoiceBuilder.invoice_number,
        issue_date: purchaseInvoiceBuilder.issue_date,
        issue_time: purchaseInvoiceBuilder.issue_time || null,
        status: remaining === 0 ? 'PAID' : 'UNPAID',
        payment_method: paidAmt > 0 ? (purchaseInvoiceBuilder.payment_method || 'CASH') : null,
        bank_account: isOnlinePaid ? purchaseInvoiceBuilder.bank_account_id : null,
        payment_reference: isOnlinePaid ? (purchaseInvoiceBuilder.payment_reference || purchaseInvoiceBuilder.invoice_number) : null,
        total_amount: totalAmt,
        discount_amount: discountAmt,
        paid_amount: paidAmt
      };

      const party = parties.find(p => p.id === purchaseInvoiceBuilder.party_id);
      let ledgerEntryPayload = null;
      if (party && remaining > 0) {
        ledgerEntryPayload = {
          amount: remaining,
          entry_type: 'GET',
          date: purchaseInvoiceBuilder.issue_date,
          time: purchaseInvoiceBuilder.issue_time || null,
          discount_amount: discountAmt,
          description: `Purchase Invoice: ${purchaseInvoiceBuilder.invoice_number}${paidAmt > 0 ? ` (Paid: Rs.${paidAmt})` : ''}${discountAmt > 0 ? ` (Discount: Rs.${discountAmt})` : ''}`
        };
      }

      // itemsForSubmit is already normalized: quantity is in PIECES and
      // unit_price is per piece, so stock moves by pieces and the pack columns
      // below are display-only (they never change what gets added/deducted).
      const itemsPayload = itemsForSubmit
        .filter(it => it.product_id || it.custom_item_name)
        .map(it => {
          const totalQty = numOr(it.quantity, 0);
          const unitPrice = numOr(it.unit_price, 0);
          const itemSubtotal = round2(numOr(it.subtotal, totalQty * unitPrice));

          return {
            product_id: it.product_id || null,
            custom_item_name: it.custom_item_name || '',
            quantity: totalQty,
            unit_price: unitPrice,
            subtotal: itemSubtotal,
            sale_price: parseFloat(it.sale_price) || 0,
            discount: itemDiscMode
              ? round2(discountValueOf(it.discount, itemSubtotal))
              : 0,
            sku: it.sku || '',
            unit: it.unit || 'pcs',
            min_stock_alert: parseFloat(it.min_stock_alert) || 5,
            expiration_date: it.expiration_date || null,
            bulk_quantity: it.bulk_quantity,
            small_quantity: it.small_quantity,
            bulk_unit_name: it.bulk_unit_name,
            bulk_unit_price: it.bulk_unit_price,
            small_unit_price: it.small_unit_price,
            units_per_bulk: numOr(it.units_per_bulk, 1) > 1 ? numOr(it.units_per_bulk, 1) : null,
          };
        });

      let payload = {
        invoice: invoiceObj,
        invoice_type: 'PURCHASE',
        items: itemsPayload,
        ledger_entry: ledgerEntryPayload
      };

      let res = await api('/invoices/create-full/', 'POST', payload);

      if (res?._duplicate) {
        const nextNum = generateDailyPurchaseInvoiceNumber(invoices);
        invoiceObj.invoice_number = nextNum;
        if (ledgerEntryPayload) {
          ledgerEntryPayload.description = `Purchase Invoice: ${nextNum}${paidAmt > 0 ? ` (Paid: Rs.${paidAmt})` : ''}${discountAmt > 0 ? ` (Discount: Rs.${discountAmt})` : ''}`;
        }
        payload.invoice = invoiceObj;
        payload.ledger_entry = ledgerEntryPayload;
        res = await api('/invoices/create-full/', 'POST', payload);
      }

      if (!res || !res.invoice) {
        showNotification(t('invoiceFailed') || 'Failed to create invoice', 'error');
        return;
      }

      const savedInvoice = res.invoice;
      const savedItems = res.items || [];

      lastSavedInvoiceRef.current = savedInvoice;
      setInvoices(prev => [savedInvoice, ...prev.filter(i => i.id !== savedInvoice.id)]);

      if (res.updated_products && res.updated_products.length > 0) {
        setProducts(prev => {
          const map = new Map(prev.map(p => [p.id, p]));
          res.updated_products.forEach(p => map.set(p.id, p));
          return Array.from(map.values());
        });
      }

      if (res.stock_movements && res.stock_movements.length > 0) {
        setStockMovements(prev => [...res.stock_movements, ...prev]);
      }

      if (res.ledger_entry) {
        setLedgerEntries(prev => [res.ledger_entry, ...prev]);
      }

      if (res.bank_transaction) {
        setBankTransactions(prev => [res.bank_transaction, ...prev]);
      }

      if (party && res.new_party_balance !== null && res.new_party_balance !== undefined) {
        setParties(prev => prev.map(p => p.id === party.id ? { ...p, cached_balance: res.new_party_balance } : p));
      }

      const printData = {
        invoice: savedInvoice,
        items: savedItems,
        party: party || { name: 'Supplier', phone: '', address: '' },
        isPurchase: true
      };

      setShowPurchaseInvoiceModal(false);

      setPiTimeManual(false);
      setPurchaseInvoiceBuilder({
        party_id: '',
        invoice_number: generateDailyPurchaseInvoiceNumber([savedInvoice, ...invoices]),
        issue_date: new Date().toISOString().split('T')[0],
        issue_time: currentTime(),
        status: 'UNPAID',
        payment_method: 'CASH',
        bank_account_id: '',
        payment_reference: '',
        paid_amount: '',
        discount_amount: '',
        items: [{ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', qty_type: lastQtyTypeRef.current }]
      });

      if (shouldPrint) {
        setPrintInvoiceData(printData);
      }
      showNotification((t('purchaseInvoiceCreatedMsg') || 'Purchase Invoice created! Ready to print.') + fbrToastSuffix(savedInvoice), fbrToastTone(savedInvoice));
      return true;

    } catch (err) {
      console.error('Purchase Invoice submit error:', err);
      showNotification(t('invoiceErrorMsg', { error: err.message || t('networkError') }), 'error');
    } finally {
      invoiceSubmitInProgress.current = false;
    }
  };

  const submitInvoice = async (e, shouldPrint = true) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    if (!selectedBusiness) {
      showNotification(t('selectCustomerFirst'), 'warning');
      return;
    }

    const itemsForSubmit = resolveSaleItemsForSubmit();
    let totalAmt = calcSaleTotal();

    if (itemsForSubmit.length === 0) {
      showNotification(t('addItemAtLeast'), 'warning');
      return;
    }

    // Selling more pieces than the shelf holds is nearly always a pack/piece
    // mix-up (e.g. typing 240 instead of 2 packs), so ask instead of quietly
    // driving stock negative.
    const shortStock = itemsForSubmit
      .filter(it => it.product_id)
      .map(it => {
        const prod = products.find(p => p.id === it.product_id);
        if (!prod) return null;
        const need = numOr(it.quantity, 0);
        const have = numOr(prod.stock_quantity, 0);
        if (need <= have) return null;
        const upb = packSizeFor(it, prod);
        const stockInPacks = packBreakdown(have, upb, t);
        const packNote = stockInPacks ? ` (${stockInPacks})` : '';
        return `• ${prod.name}: ${t('needsQty', { n: need })} — ${t('onlyQty', { n: have })}${packNote}`;
      })
      .filter(Boolean);

    if (shortStock.length > 0) {
      const proceed = window.confirm(`${t('insufficientStock')}\n\n${shortStock.join('\n')}\n\n${t('continueAnyway')}`);
      if (!proceed) return;
    }

    if (invoiceSubmitInProgress.current) return;
    invoiceSubmitInProgress.current = true;

    try {
      const discountAmt = saleDiscountTotal();
      totalAmt = Math.max(0, totalAmt - discountAmt);
      const paidAmt = parseFloat(invoiceBuilder.paid_amount) || 0;
      const remaining = Math.max(0, totalAmt - paidAmt);
      const isOnlinePaid = invoiceBuilder.status === 'PAID' && (invoiceBuilder.payment_method || 'CASH') === 'ONLINE' && paidAmt > 0;
      if (isOnlinePaid && !invoiceBuilder.bank_account_id) {
        showNotification(t('selectBankAccount') || 'Select a bank account for this online payment.', 'warning');
        return;
      }

      const invoiceObj = {
        business: selectedBusiness.id,
        party: invoiceBuilder.party_id || null,
        invoice_number: invoiceBuilder.invoice_number,
        issue_date: invoiceBuilder.issue_date,
        issue_time: invoiceBuilder.issue_time || null,
        status: remaining === 0 ? 'PAID' : 'UNPAID',
        payment_method: paidAmt > 0 ? (invoiceBuilder.payment_method || 'CASH') : null,
        bank_account: isOnlinePaid ? invoiceBuilder.bank_account_id : null,
        payment_reference: isOnlinePaid ? (invoiceBuilder.payment_reference || invoiceBuilder.invoice_number) : null,
        total_amount: totalAmt,
        discount_amount: discountAmt,
        paid_amount: paidAmt
      };

      const party = parties.find(p => p.id === invoiceBuilder.party_id);
      let ledgerEntryPayload = null;
      if (party && remaining > 0) {
        ledgerEntryPayload = {
          amount: remaining,
          entry_type: 'GIVE',
          date: invoiceBuilder.issue_date,
          time: invoiceBuilder.issue_time || null,
          discount_amount: discountAmt,
          description: `Billed Invoice: ${invoiceBuilder.invoice_number}${paidAmt > 0 ? ` (Paid: Rs.${paidAmt})` : ''}${discountAmt > 0 ? ` (Discount: Rs.${discountAmt})` : ''}`
        };
      }

      // Normalized: quantity is in PIECES, unit_price is per piece. Stock is
      // therefore always deducted in pieces no matter how the line was typed.
      const itemsPayload = itemsForSubmit
        .filter(it => it.product_id || it.custom_item_name)
        .map(it => {
          const totalQty = numOr(it.quantity, 0);
          const unitPrice = numOr(it.unit_price, 0);
          const itemSubtotal = round2(numOr(it.subtotal, totalQty * unitPrice));

          return {
            product_id: it.product_id || null,
            custom_item_name: it.custom_item_name || '',
            quantity: totalQty,
            unit_price: unitPrice,
            subtotal: itemSubtotal,
            bulk_quantity: it.bulk_quantity,
            discount: itemDiscMode
              ? round2(discountValueOf(it.discount, itemSubtotal))
              : 0,
            small_quantity: it.small_quantity,
            bulk_unit_name: it.bulk_unit_name,
            bulk_unit_price: it.bulk_unit_price,
            small_unit_price: it.small_unit_price,
            units_per_bulk: numOr(it.units_per_bulk, 1) > 1 ? numOr(it.units_per_bulk, 1) : null,
          };
        });

      let payload = {
        invoice: invoiceObj,
        invoice_type: 'SALE',
        items: itemsPayload,
        ledger_entry: ledgerEntryPayload
      };

      let res = await api('/invoices/create-full/', 'POST', payload);

      if (res?._duplicate) {
        const nextNum = generateDailyInvoiceNumber(invoices);
        invoiceObj.invoice_number = nextNum;
        if (ledgerEntryPayload) {
          ledgerEntryPayload.description = `Billed Invoice: ${nextNum}${paidAmt > 0 ? ` (Paid: Rs.${paidAmt})` : ''}${discountAmt > 0 ? ` (Discount: Rs.${discountAmt})` : ''}`;
        }
        payload.invoice = invoiceObj;
        payload.ledger_entry = ledgerEntryPayload;
        res = await api('/invoices/create-full/', 'POST', payload);
      }

      if (!res || !res.invoice) {
        showNotification(t('invoiceFailed'), 'error');
        return;
      }

      const savedInvoice = res.invoice;
      const savedItems = res.items || [];

      lastSavedInvoiceRef.current = savedInvoice;
      setInvoices(prev => [savedInvoice, ...prev.filter(i => i.id !== savedInvoice.id)]);

      if (res.updated_products && res.updated_products.length > 0) {
        setProducts(prev => {
          const map = new Map(prev.map(p => [p.id, p]));
          res.updated_products.forEach(p => map.set(p.id, p));
          return Array.from(map.values());
        });
      }

      if (res.stock_movements && res.stock_movements.length > 0) {
        setStockMovements(prev => [...res.stock_movements, ...prev]);
      }

      if (res.ledger_entry) {
        setLedgerEntries(prev => [res.ledger_entry, ...prev]);
      }

      if (res.bank_transaction) {
        setBankTransactions(prev => [res.bank_transaction, ...prev]);
      }

      if (party && res.new_party_balance !== null && res.new_party_balance !== undefined) {
        setParties(prev => prev.map(p => p.id === party.id ? { ...p, cached_balance: res.new_party_balance } : p));
      }

      const printData = {
        invoice: savedInvoice,
        items: savedItems,
        party: party || { name: 'Cash Customer', phone: '', address: '' }
      };

      setInvoiceTimeManual(false);
      setInvoiceBuilder({
        party_id: '',
        invoice_number: generateDailyInvoiceNumber([savedInvoice, ...invoices]),
        issue_date: new Date().toISOString().split('T')[0],
        issue_time: currentTime(),
        status: 'UNPAID',
        payment_method: 'CASH',
        bank_account_id: '',
        payment_reference: '',
        paid_amount: '',
        discount_amount: '',
        items: [{ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', qty_type: lastQtyTypeRef.current }]
      });
      setSaleEntryItem({ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', qty_type: lastQtyTypeRef.current });

      if (shouldPrint) {
        setPrintInvoiceData(printData);
      }
      showNotification(t('invoiceCreatedMsg') + fbrToastSuffix(savedInvoice), fbrToastTone(savedInvoice));
      return true;

    } catch (err) {
      console.error('Invoice submit error:', err);
      showNotification(t('invoiceErrorMsg', { error: err.message || t('networkError') }), 'error');
    } finally {
      invoiceSubmitInProgress.current = false;
    }
  };


  // One click for "save it, then send it on WhatsApp". The message, the card
  // and the recipient are read while the form still holds them — the save
  // clears the builder — and the WhatsApp tab is opened inside the click
  // itself: a window requested after the save's await would be caught by the
  // popup blocker. The tab stays blank until the save has landed, so nothing
  // can ever be sent for an invoice that was never created, and a failed save
  // simply closes it again. Image mode on a handheld hands the card to the
  // native share sheet instead, which needs no window of its own.
  const saveAndWhatsApp = async ({ phone, text, card, save }) => {
    const waPhone = phone || '';
    const waText = text || '';
    const imageMode = waShareMode === 'image';
    const waDesktop = imageMode && !supportsFileShare();
    const needsWindow = !imageMode || waDesktop;

    let win = null;
    if (needsWindow) {
      try {
        win = imageMode ? openWhatsAppWeb(waPhone, waText) : window.open('', '_blank');
        if (win) { try { win.opener = null; } catch { /* cross-origin noop */ } }
      } catch { win = null; }
    }

    let saved = false;
    lastSavedInvoiceRef.current = null; // ignore any stale invoice from a prior save
    try { saved = !!(await save()); } catch { saved = false; }

    if (!saved) {
      if (win) { try { win.close(); } catch { /* noop */ } }
      return;
    }

    // FBR: the card and text above were composed before the save (no FBR
    // number existed yet). If this business runs FBR and the invoice just
    // came back accepted, add the FBR invoice number now — the only piece
    // of the receipt that only exists after saving.
    let waTextFinal = waText;
    let cardFinal = card;
    const fbrInvoice = lastSavedInvoiceRef.current;
    if (fbrEnabled && fbrInvoice?.fbr_status === 'SUBMITTED' && fbrInvoice.fbr_invoice_number) {
      const fbrLine = `FBR Invoice No: ${fbrInvoice.fbr_invoice_number}`;
      waTextFinal = waText ? `${waText}\n${fbrLine}` : fbrLine;
      cardFinal = {
        ...card,
        sections: [...(card.sections || []), { label: 'FBR', value: fbrLine }],
      };
    }

    if (imageMode) {
      // The capture below needs this tab in front: rAF stops in a background
      // tab and the clipboard write is only allowed while we still have focus.
      try { window.focus(); } catch { /* browser refused */ }
      shareWACard({ ...cardFinal, phone: waPhone, text: waTextFinal, waDesktop, waWindow: win });
      return;
    }

    const url = `https://wa.me/${waPhone}?text=${encodeURIComponent(waTextFinal)}`;
    if (win && !win.closed) {
      win.location = url;
      try { win.focus(); } catch { /* noop */ }
      return;
    }
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch { /* popup blocked */ }
  };

  // Helper Stats Calcs
  const getDuesSummary = () => {
    let totalGot = 0; // we owe suppliers
    let totalGive = 0; // customers owe us

    parties.forEach(p => {
      if (p.is_deleted) return;
      const bal = parseFloat(p.cached_balance || 0);
      if (bal > 0) {
        totalGive += bal;
      } else {
        totalGot += Math.abs(bal);
      }
    });

    return { totalGive, totalGot };
  };

  const getCashBalance = () => {
    let totalIn = 0;
    let totalOut = 0;
    
    cashEntries.forEach(c => {
      if (c.is_deleted) return;
      if (c.entry_type === 'IN') totalIn += parseFloat(c.amount) || 0;
      else totalOut += parseFloat(c.amount) || 0;
    });

    return totalIn - totalOut;
  };

  const { totalGive, totalGot } = getDuesSummary();
  const cashBalance = getCashBalance();
  const lowStockCount = products.filter(p => !p.is_deleted && Number(p.stock_quantity) <= Number(p.min_stock_alert)).length;
  const globalWalkInSalesTotal = invoices
    .filter(inv => !inv.is_deleted && !inv.party && !inv.party_id)
    .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);

  // Filter lists based on type
  const customers = parties.filter(p => !p.is_deleted && (p.party_type === 'CUSTOMER' || p.party_type === 'BOTH'));
  const suppliers = parties.filter(p => !p.is_deleted && (p.party_type === 'SUPPLIER' || p.party_type === 'BOTH'));
  const customerAreas = [...new Set(customers.map(p => p.area).filter(Boolean))].sort();
  const supplierAreas = [...new Set(suppliers.map(p => p.area).filter(Boolean))].sort();

  // --------------------------------------------------------------------------------------
  // UPDATE HANDLERS
  // --------------------------------------------------------------------------------------

  const handleUpdateParty = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }


    e.preventDefault();
    if (!editingParty) return;
    const updated = { ...editingParty };
    await api(`/parties/${editingParty.id}/`, 'PATCH', updated);
    setEditingParty(null);
    setShowAddAreaInline(false);
    setNewAreaName('');
    showNotification(t('partyUpdated'), 'success');
    await refreshData();
    if (selectedParty?.id === editingParty.id) {
      const refreshed = await api(`/parties/${editingParty.id}/`);
      setSelectedParty(refreshed);
    }
  };

  const handleUpdateCashEntry = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    e.preventDefault();
    if (!editingCashEntry) return;
    const updated = { ...editingCashEntry };
    // Strip attachment if it's already a URL string (not a new File) to avoid Django 400
    if (updated.attachment && typeof updated.attachment === 'string') {
      delete updated.attachment;
    }
    await api(`/cash-entries/${editingCashEntry.id}/`, 'PATCH', updated);
    setEditingCashEntry(null);
    showNotification(t('cashUpdated'), 'success');
    await refreshData();
  };

  const handleUpdateProduct = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!editingProduct) return;

    // Only what the form actually collected travels. Pack prices, pack counts
    // and stock values are recomputed by the backend on every write, and the
    // photo has its own upload endpoint (a stored path is not a valid upload).
    const DERIVED_ON_SAVE = ['bulk_unit', 'bulk_purchase_price', 'bulk_sale_price',
                             'photo', 'stockValueCost', 'stockValueRetail',
                             'expectedProfit', 'packCost', 'packRetail',
                             'fullPacks', 'loosePcs'];
    const updated = { ...editingProduct };
    DERIVED_ON_SAVE.forEach(key => delete updated[key]);
    // The packs + loose split belongs to the editor alone: the server keeps one
    // figure per box and does the multiplying itself, so the halves go back
    // together before the flag below travels. An empty optional box is left
    // exactly as it was.
    const upb = numOr(updated.units_per_bulk, 1);
    if (packMode(upb)) {
      const packs = numOr(updated.stock_quantity, NaN);
      if (Number.isFinite(packs)) {
        updated.stock_quantity = packInputValue(
          totalPieces(updated.stock_quantity, updated.stock_loose, upb), upb);
      }
      const alertPacks = numOr(updated.min_stock_alert, NaN);
      if (Number.isFinite(alertPacks)) {
        updated.min_stock_alert = packInputValue(
          totalPieces(updated.min_stock_alert, updated.alert_loose, upb), upb);
      }
    }
    delete updated.stock_loose;
    delete updated.alert_loose;
    // Same rule as the Add form: the boxes were filled in the unit of the
    // Units Quantity field, so the flag travels with them and the server
    // stores pieces.
    updated.stock_qty_type = stockQtyType(updated.units_per_bulk);

    try {
      const saved = await api(`/products/${editingProduct.id}/`, 'PATCH', updated);
      const warnings = Array.isArray(saved?.warnings) ? saved.warnings : [];

      if (editProductPhotoFile) {
        const formData = new FormData();
        formData.append('photo', editProductPhotoFile);
        await api(`/products/${editingProduct.id}/upload-photo/`, 'POST', formData);
      }

      setEditingProduct(null);
      setEditProductPhotoFile(null);
      setShowAddProductTypeInline(false);
      setNewProductTypeName('');
      // One toast at a time, so an advisory rides along with the confirmation.
      showNotification(
        warnings.length ? `${t('productUpdated')} — ${warnings.join(' ')}` : t('productUpdated'),
        warnings.length ? 'warning' : 'success'
      );
      await refreshData();
    } catch (error) {
      // Keep the modal open so the rejected value can be corrected.
      showNotification(error.message || t('networkError'), 'error');
    }
  };

  const handleRestock = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }
    e.preventDefault();
    const product = restockModal.product;
    const typed = parseFloat(restockModal.quantity) || 0;
    if (!product || typed <= 0) return;
    // The box counts in the unit of the Units Quantity field — packs when it
    // is above 1 — while the shelf only ever holds pieces, so the input is
    // converted first. No flag travels with the PATCH: it is already pieces.
    const addPieces = packMode(product.units_per_bulk)
      ? Math.round(typed * numOr(product.units_per_bulk, 1))
      : typed;
    const currentStock = parseFloat(product.stock_quantity) || 0;
    const newQty = currentStock + addPieces;
    await api(`/products/${product.id}/`, 'PATCH', { stock_quantity: newQty });

    // Log manual restock movement (pieces, like every other movement)
    await api('/stock-movements/', 'POST', {
      product: product.id,
      quantity: addPieces,
      movement_type: 'IN',
      date: new Date().toISOString().split('T')[0],
      time: currentTime(),
      description: 'Manual Restock'
    });

    setRestockModal({ show: false, product: null, quantity: '' });
    showNotification(t('productRestocked', {
      name: product.name,
      qty: `${pcsText(addPieces)} ${product.unit || 'pcs'}`
    }), 'success');
    await refreshData();
  };

  const handleUpdateStockMovement = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!editingStockMovement) return;
    const oldEntry = stockMovements.find(l => l.id === editingStockMovement.id);
    const oldFactor = oldEntry.movement_type === 'OUT' ? -1 : 1;
    const newFactor = editingStockMovement.movement_type === 'OUT' ? -1 : 1;
    const balanceDelta = (parseFloat(editingStockMovement.quantity) * newFactor) - (parseFloat(oldEntry.quantity) * oldFactor);
    
    const updated = { ...editingStockMovement, quantity: parseFloat(editingStockMovement.quantity) };
    await api(`/stock-movements/${editingStockMovement.id}/`, 'PATCH', updated);
    
    const product = products.find(p => p.id === editingStockMovement.product);
    if (product) {
      const newStock = parseFloat(product.stock_quantity || 0) + balanceDelta;
      await api(`/products/${product.id}/`, 'PATCH', { stock_quantity: newStock });
    }
    
    setEditingStockMovement(null);
    showNotification(t('recordUpdated') || 'Record updated successfully', 'success');
    await refreshData();
  };

  const handleUpdateLedgerEntry = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!editingLedgerEntry || !selectedParty) return;
    const oldEntry = ledgerEntries.find(l => l.id === editingLedgerEntry.id);
    const oldFactor = oldEntry.entry_type === 'GIVE' ? 1 : -1;
    const newFactor = editingLedgerEntry.entry_type === 'GIVE' ? 1 : -1;
    const balanceDelta = (parseFloat(editingLedgerEntry.amount) * newFactor) - (parseFloat(oldEntry.amount) * oldFactor);
    const updated = { 
      ...editingLedgerEntry, 
      amount: parseFloat(editingLedgerEntry.amount),
      payment_method: editingLedgerEntry.payment_method || 'CASH',
      bank_account: editingLedgerEntry.payment_method === 'ONLINE' ? editingLedgerEntry.bank_account : null
    };
    await api(`/ledger-entries/${editingLedgerEntry.id}/`, 'PATCH', updated);
    const partyEntries = ledgerEntries.filter(e => (e.party === selectedParty.id || e.party_id === selectedParty.id) && !e.is_deleted);
    const currentBal = partyEntries.reduce((s, e) => s + (e.entry_type === 'GIVE' ? parseFloat(e.amount || 0) : -parseFloat(e.amount || 0)), 0);
    const newBalance = currentBal + balanceDelta;
    await api(`/parties/${selectedParty.id}/`, 'PATCH', { cached_balance: newBalance });
    setEditingLedgerEntry(null);
    showNotification(t('entryUpdated'), 'success');
    await refreshData();
    const refreshed = await api(`/parties/${selectedParty.id}/`);
    setSelectedParty(refreshed);
  };

  const handleUpdateIeEntry = async (e) => {
    e.preventDefault();
    if (!editingIeEntry) return;
    const updated = { ...editingIeEntry, amount: parseFloat(editingIeEntry.amount) };
    await api(`/income-expenses/${editingIeEntry.id}/`, 'PATCH', updated);
    setEditingIeEntry(null);
    showNotification(t('recordUpdated'), 'success');
    await refreshData();
  };

  const handleUpdateInvoice = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!editingInvoice) return;
    const updated = { status: editingInvoice.status, total_amount: parseFloat(editingInvoice.total_amount) };
    await api(`/invoices/${editingInvoice.id}/`, 'PATCH', updated);
    if (editingInvoice._items) {
      for (const item of editingInvoice._items) {
        await api(`/invoice-items/${item.id}/`, 'PATCH', {
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          subtotal: parseFloat(item.quantity) * parseFloat(item.unit_price),
          product: item.product || null,
          description: item.description || ''
        });
      }
    }
    const newTotal = editingInvoice._items
      ? editingInvoice._items.reduce((s, r) => s + parseFloat(r.quantity) * parseFloat(r.unit_price), 0)
      : parseFloat(editingInvoice.total_amount);
    await api(`/invoices/${editingInvoice.id}/`, 'PATCH', { total_amount: newTotal });
    setEditingInvoice(null);
    showNotification(t('invoiceUpdated'), 'success');
    await refreshData();
  };

  const handlePrintSingleInvoice = async (inv) => {
    try {
      const items = await api(`/invoice-items/?invoice=${inv.id}`);
      const cust = parties.find(p => p.id === (inv.party || inv.party_id));
      const isPurch = inv.invoice_type === 'PURCHASE' || inv.invoice_number?.startsWith('PI-') || (cust && cust.party_type === 'SUPPLIER');
      setPrintInvoiceData({
        invoice: inv,
        items: items || [],
        party: cust || { name: isPurch ? 'Supplier' : 'Customer', phone: '', address: '' },
        isPurchase: isPurch
      });
    } catch {
      showNotification(t('error'), 'error');
    }
  };

  const handleUpdateStaff = async (e) => {
    if (!canEdit) { alert('You do not have permission to edit.'); return; }

    e.preventDefault();
    if (!editingStaff) return;
    const updated = { ...editingStaff, base_salary: parseFloat(editingStaff.base_salary) };
    await api(`/staff/${editingStaff.id}/`, 'PATCH', updated);
    setEditingStaff(null);
    showNotification(t('staffUpdated'), 'success');
    await refreshData();
  };

  // --------------------------------------------------------------------------------------
  // DELETE HANDLERS
  // --------------------------------------------------------------------------------------

  const deleteParty = async (party) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    await api(`/parties/${party.id}/`, 'PATCH', { is_deleted: true });
    if (selectedParty?.id === party.id) setSelectedParty(null);
    showNotification(t('deleted', { name: party.name }), 'success');
    await refreshData();
  };

  const deleteCashEntry = async (entry) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    await api(`/cash-entries/${entry.id}/`, 'PATCH', { is_deleted: true });
    showNotification(t('cashDeleted'), 'success');
    await refreshData();
  };

  const deleteBankTransaction = async (entry) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    await api(`/bank-transactions/${entry.id}/`, 'PATCH', { is_deleted: true });
    showNotification(t('recordDeleted'), 'success');
    await refreshData();
  };

  const deleteProduct = async (product) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    await api(`/products/${product.id}/`, 'PATCH', { is_deleted: true });
    showNotification(t('productRemoved', { name: product.name }), 'success');
    await refreshData();
  };

  const deleteIeEntry = async (entry) => {
    await api(`/income-expenses/${entry.id}/`, 'PATCH', { is_deleted: true });
    showNotification(t('recordDeleted'), 'success');
    await refreshData();
  };

  const deleteInvoice = async (inv) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    await api(`/invoices/${inv.id}/`, 'PATCH', { is_deleted: true });
    showNotification(t('invoiceDeleted'), 'success');
    await refreshData();
  };

  const deleteStaff = async (employee) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    await api(`/staff/${employee.id}/`, 'PATCH', { is_deleted: true });
    showNotification(t('staffRemoved'), 'success');
    await refreshData();
  };

  const deleteLedgerEntry = async (entry) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    const factor = entry.entry_type === 'GIVE' ? 1 : -1;
    const balanceDelta = -(parseFloat(entry.amount) * factor);
    await api(`/ledger-entries/${entry.id}/`, 'PATCH', { is_deleted: true });
    if (selectedParty) {
      const partyEntries = ledgerEntries.filter(e => (e.party === selectedParty.id || e.party_id === selectedParty.id) && !e.is_deleted);
      const currentBal = partyEntries.reduce((s, e) => s + (e.entry_type === 'GIVE' ? parseFloat(e.amount || 0) : -parseFloat(e.amount || 0)), 0);
      const newBalance = currentBal + balanceDelta;
      await api(`/parties/${selectedParty.id}/`, 'PATCH', { cached_balance: newBalance });
      const refreshed = await api(`/parties/${selectedParty.id}/`);
      setSelectedParty(refreshed);
    }
    showNotification(t('transactionDeleted'), 'success');
    await refreshData();
  };

  const deleteStockMovement = async (entry) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }

    const factor = entry.movement_type === 'OUT' ? -1 : 1;
    const balanceDelta = -(parseFloat(entry.quantity) * factor);
    await api(`/stock-movements/${entry.id}/`, 'PATCH', { is_deleted: true });
    
    const product = products.find(p => p.id === entry.product);
    if (product) {
      const newStock = parseFloat(product.stock_quantity || 0) + balanceDelta;
      await api(`/products/${product.id}/`, 'PATCH', { stock_quantity: newStock });
    }
    showNotification(t('recordDeleted') || 'Record deleted successfully', 'success');
    await refreshData();
  };

  const deleteDuplicateRecord = (group, record) => {
    if (group.type === 'invoice') return deleteInvoice(record);
    if (group.type === 'bank') return deleteBankTransaction(record);
    if (group.type === 'ledger') return deleteLedgerEntry(record);
    if (group.type === 'cash') return deleteCashEntry(record);
    if (group.type === 'income') return deleteIeEntry(record);
    return deleteStockMovement(record);
  };

  const triggerDelete = (label, onConfirm) => {
    setDeleteConfirm({ label, onConfirm });
  };

  // Print trigger — opens beautiful print overlay, then triggers window.print()
  const triggerPrintReport = (type, data) => {
    setActivePrintReport({ type, data });
    setTimeout(() => window.print(), 400);
  };

  const handlePrint = () => {
    window.print();
  };

  const triggerPrint = () => {
    window.print();
  };

  // ─── PrintReportOverlay Component ───────────────────────────────────────────
  const PrintReportOverlay = ({ report, onClose }) => {
    const biz = selectedBusiness;
    const now = new Date().toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' });
    const [waSharing, setWaSharing] = useState(false);

    // ── WhatsApp share logic ────────────────────────────────────────────────
    const generateReportWhatsAppText = () => {
      const bizName = biz?.name || 'Business';
      const header = `*${bizName}*\n${t('reportDate') || 'Report Date'}: ${now}\n`;
      const line = '─'.repeat(28);

      if (report.type === 'cash') {
        const entries = report.data.entries || [];
        const totalIn = entries.filter(e => e.entry_type === 'IN').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const totalOut = entries.filter(e => e.entry_type === 'OUT').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const balance = totalIn - totalOut;
        const period = report.data.period || t('allTime') || 'All Time';
        let text = `${header}*${t('cashBook') || 'Cash Book'}* | ${period}\n${line}\n`;
        text += `✅ ${t('totalCashIn') || 'Cash In'}: Rs. ${totalIn.toLocaleString()}\n`;
        text += `❌ ${t('totalCashOut') || 'Cash Out'}: Rs. ${totalOut.toLocaleString()}\n`;
        text += `${line}\n💰 ${t('closingBalance') || 'Balance'}: Rs. ${Math.abs(balance).toLocaleString()} (${balance >= 0 ? (t('surplus') || 'Surplus') : (t('deficit') || 'Deficit')})\n`;
        text += `\n${t('totalEntries') || 'Total Entries'}: ${entries.length}`;
        return text;
      }

      if (report.type === 'ie_category') {
        const entries = report.data.entries || [];
        const catMap = {};
        entries.forEach(e => {
          if (!catMap[e.category]) catMap[e.category] = { income: 0, expense: 0 };
          if (e.type === 'INCOME') catMap[e.category].income += parseFloat(e.amount || 0);
          else catMap[e.category].expense += parseFloat(e.amount || 0);
        });
        const totalIncome = Object.values(catMap).reduce((s, c) => s + c.income, 0);
        const totalExpense = Object.values(catMap).reduce((s, c) => s + c.expense, 0);
        const net = totalIncome - totalExpense;
        const period = report.data.period || t('allTime') || 'All Time';
        let text = `${header}*${t('incomeByCat') || 'Income/Expense by Category'}* | ${period}\n${line}\n`;
        Object.keys(catMap).sort().forEach(cat => {
          if (catMap[cat].income > 0) text += `📈 ${cat} (Income): Rs. ${catMap[cat].income.toLocaleString()}\n`;
          if (catMap[cat].expense > 0) text += `📉 ${cat} (Expense): Rs. ${catMap[cat].expense.toLocaleString()}\n`;
        });
        text += `${line}\n✅ ${t('totalIncome') || 'Total Income'}: Rs. ${totalIncome.toLocaleString()}\n`;
        text += `❌ ${t('totalExpenses') || 'Total Expense'}: Rs. ${totalExpense.toLocaleString()}\n`;
        text += `💰 ${t('netProfit') || 'Net'}: Rs. ${Math.abs(net).toLocaleString()} (${net >= 0 ? (t('profit') || 'Profit') : (t('loss') || 'Loss')})`;
        return text;
      }

      if (report.type === 'ie') {
        const entries = report.data.entries || [];
        const totalIncome = entries.filter(e => e.type === 'INCOME').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const totalExpense = entries.filter(e => e.type === 'EXPENSE').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const net = totalIncome - totalExpense;
        const period = report.data.period || t('allTime') || 'All Time';
        let text = `${header}*${t('incomeExpense') || 'Income & Expense'}* | ${period}\n${line}\n`;
        text += `✅ ${t('totalIncome') || 'Total Income'}: Rs. ${totalIncome.toLocaleString()}\n`;
        text += `❌ ${t('totalExpenses') || 'Total Expense'}: Rs. ${totalExpense.toLocaleString()}\n`;
        text += `${line}\n💰 ${t('netProfitLoss') || 'Net P/L'}: Rs. ${Math.abs(net).toLocaleString()} (${net >= 0 ? (t('profit') || 'Profit') : (t('loss') || 'Loss')})\n`;
        text += `\n${t('totalEntries') || 'Entries'}: ${entries.length}`;
        return text;
      }

      if (report.type === 'bank') {
        const entries = report.data.entries || [];
        const totalDeposits = entries.filter(e => e.transaction_type === 'DEPOSIT').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const totalWithdrawals = entries.filter(e => e.transaction_type === 'WITHDRAWAL').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
        const period = report.data.period || t('allTime') || 'All Time';
        let text = `${header}*${t('bankTransactions') || 'Bank Transactions'}* | ${period}\n${line}\n`;
        text += `🏦 ${t('deposits') || 'Deposits'}: Rs. ${totalDeposits.toLocaleString()}\n`;
        text += `🏧 ${t('withdrawals') || 'Withdrawals'}: Rs. ${totalWithdrawals.toLocaleString()}\n`;
        text += `${line}\n💰 ${t('netBalance') || 'Net Balance'}: Rs. ${Math.abs(totalDeposits - totalWithdrawals).toLocaleString()}\n`;
        text += `\n${t('totalEntries') || 'Entries'}: ${entries.length}`;
        return text;
      }

      if (report.type === 'ledger') {
        const { party, entries: ledEntries, dateFilter, searchQuery } = report.data;
        const activeEntries = (ledEntries || []).filter(e => !e.is_deleted);
        // Balance stays all-time (matches the party card in the UI); only the
        // entry count follows the active date/search filters.
        const visibleEntries = activeEntries.filter(e => {
          if (!filterByDate(e.date, dateFilter)) return false;
          if (searchQuery && !(e.description || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
          return true;
        });
        const balance = activeEntries.length > 0
          ? activeEntries.reduce((s, e) => s + (e.entry_type === 'GIVE' ? parseFloat(e.amount || 0) : -parseFloat(e.amount || 0)), 0)
          : parseFloat(party?.cached_balance || 0);
        let text = `${header}*${t('ledgerStatement') || 'Ledger Statement'}* | ${dateFilterLabel(dateFilter, t)}\n${line}\n`;
        text += `👤 ${t('party') || 'Party'}: ${party?.name || '—'}\n`;
        if (party?.phone) text += `📞 ${t('phone') || 'Phone'}: ${party.phone}\n`;
        text += `${line}\n`;
        text += `💰 ${t('closingBal') || 'Closing Balance'}: Rs. ${Math.abs(balance).toLocaleString()} (${balance >= 0 ? (t('receivable') || 'Receivable') : (t('payable') || 'Payable')})\n`;
        text += `\n${t('totalEntries') || 'Entries'}: ${visibleEntries.length}`;
        return text;
      }

      if (report.type === 'customers') {
        const list = customers.filter(p => !p.is_deleted && (!report.data.area || p.area === report.data.area) && matchPartySearch(p, report.data.searchQuery));
        const totalBalance = list.reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0);
        const receivable = list.filter(p => (p.cached_balance || 0) > 0).reduce((s, p) => s + parseFloat(p.cached_balance), 0);
        const payable = list.filter(p => (p.cached_balance || 0) < 0).reduce((s, p) => s + Math.abs(parseFloat(p.cached_balance)), 0);
        let text = `${header}*${t('customerDirectory') || 'Customer Directory'}*${report.data.area ? ` — ${report.data.area}` : ''}\n${line}\n`;
        text += `👥 ${t('totalCustomers') || 'Total Customers'}: ${list.length}\n`;
        text += `✅ ${t('totalReceivable') || 'Receivable'}: Rs. ${receivable.toLocaleString()}\n`;
        text += `❌ ${t('totalPayable') || 'Payable'}: Rs. ${payable.toLocaleString()}\n`;
        text += `${line}\n💰 ${t('netOutstanding') || 'Net Outstanding'}: Rs. ${Math.abs(totalBalance).toLocaleString()}`;
        return text;
      }

      if (report.type === 'suppliers') {
        const list = suppliers.filter(p => !p.is_deleted && (!report.data.area || p.area === report.data.area) && matchPartySearch(p, report.data.searchQuery));
        const totalBalance = list.reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0);
        const receivable = list.filter(p => (p.cached_balance || 0) > 0).reduce((s, p) => s + parseFloat(p.cached_balance), 0);
        const payable = list.filter(p => (p.cached_balance || 0) < 0).reduce((s, p) => s + Math.abs(parseFloat(p.cached_balance)), 0);
        let text = `${header}*${t('supplierDirectory') || 'Supplier Directory'}*${report.data.area ? ` — ${report.data.area}` : ''}\n${line}\n`;
        text += `👥 ${t('totalSuppliers') || 'Total Suppliers'}: ${list.length}\n`;
        text += `✅ ${t('totalReceivable') || 'Receivable'}: Rs. ${receivable.toLocaleString()}\n`;
        text += `❌ ${t('totalPayable') || 'Payable'}: Rs. ${payable.toLocaleString()}\n`;
        text += `${line}\n💰 ${t('netOutstanding') || 'Net Outstanding'}: Rs. ${Math.abs(totalBalance).toLocaleString()}`;
        return text;
      }

      if (report.type === 'products') {
        const prodSearch = (report.data.searchQuery || '').toLowerCase();
        const prodType = report.data.productType || 'all';
        const list = products.filter(p => !p.is_deleted
          && (!prodSearch || (p.name || '').toLowerCase().includes(prodSearch))
          && (prodType === 'all' || p.product_type === prodType));
        const totalValue = list.reduce((s, p) => s + ((parseFloat(p.stock_quantity) || 0) * (parseFloat(p.purchase_price) || 0)), 0);
        const totalItems = list.reduce((s, p) => s + (parseFloat(p.stock_quantity) || 0), 0);
        let text = `${header}*${t('stockInventory') || 'Stock Inventory'}*\n${line}\n`;
        text += `📦 ${t('products') || 'Products'}: ${list.length}\n`;
        text += `🔢 ${t('totalUnits') || 'Total Units'}: ${totalItems.toLocaleString()}\n`;
        text += `${line}\n💰 ${t('totalStockValue') || 'Total Stock Value'}: Rs. ${totalValue.toLocaleString()}`;
        return text;
      }

      if (report.type === 'stock_history') {
        const list = report.data.entries || [];
        const periodLabel = report.data.periodLabel || t('allTime') || 'All Time';
        const inCount = list.filter(m => m.movement_type === 'IN').length;
        const outCount = list.filter(m => m.movement_type === 'OUT').length;
        let text = `${header}*${t('stockHistory') || 'Stock History'}* | ${periodLabel}\n${line}\n`;
        text += `📥 ${t('stockIn') || 'Stock In'}: ${inCount} ${t('entries') || 'entries'}\n`;
        text += `📤 ${t('stockOut') || 'Stock Out'}: ${outCount} ${t('entries') || 'entries'}\n`;
        text += `${line}\n${t('totalEntries') || 'Total'}: ${list.length}`;
        return text;
      }

      if (report.type === 'staff') {
        const staffSearch = (report.data.searchQuery || '').toLowerCase();
        const empList = staff.filter(s => !s.is_deleted && (!staffSearch
          || (s.name || '').toLowerCase().includes(staffSearch)
          || (s.phone || '').includes(report.data.searchQuery)
          || (s.cnic || '').includes(report.data.searchQuery)));
        const totalSalary = empList.reduce((s, e) => s + parseFloat(e.base_salary || 0), 0);
        const totalPaid = payrolls.filter(p => p.status === 'PAID').reduce((s, p) => s + parseFloat(p.net_paid || 0), 0);
        let text = `${header}*${t('staffSalaryReport') || 'Staff & Salary Report'}*\n${line}\n`;
        text += `👔 ${t('totalEmployees') || 'Employees'}: ${empList.length}\n`;
        text += `💸 ${t('totalMonthlyPayroll') || 'Monthly Payroll'}: Rs. ${totalSalary.toLocaleString()}\n`;
        text += `✅ ${t('totalPaid') || 'Total Paid'}: Rs. ${totalPaid.toLocaleString()}`;
        return text;
      }

      if (report.type === 'invoices') {
        const invList = filteredInvoices;
        const totalAmount = invList.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);
        const paidCount = invList.filter(i => i.status === 'PAID').length;
        const unpaidCount = invList.filter(i => i.status !== 'PAID').length;
        const periodLabel = report.data.periodLabel || t('allTime') || 'All Time';
        let text = `${header}*${t('invoiceSummaryReport') || 'Invoice Summary'}* | ${periodLabel}\n${line}\n`;
        text += `🧾 ${t('totalInvoices') || 'Total'}: ${invList.length}\n`;
        text += `✅ ${t('paid') || 'Paid'}: ${paidCount}\n`;
        text += `❌ ${t('unpaid') || 'Unpaid'}: ${unpaidCount}\n`;
        text += `${line}\n💰 ${t('totalRevenue') || 'Total Revenue'}: Rs. ${totalAmount.toLocaleString()}`;
        return text;
      }

      if (report.type === 'dashboard') {
        const totalCust = customers.filter(p => !p.is_deleted).length;
        const totalSupp = suppliers.filter(p => !p.is_deleted).length;
        const cashIn = cashEntries.filter(c => !c.is_deleted && c.entry_type === 'IN').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
        const cashOut = cashEntries.filter(c => !c.is_deleted && c.entry_type === 'OUT').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
        const income = incomeExpenses.filter(i => !i.is_deleted && i.type === 'INCOME').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
        const expense = incomeExpenses.filter(i => !i.is_deleted && i.type === 'EXPENSE').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
        const stockValue = products.filter(p => !p.is_deleted).reduce((s, p) => s + ((parseFloat(p.stock_quantity) || 0) * (parseFloat(p.purchase_price) || 0)), 0);
        let text = `${header}*BUSINESS OVERVIEW*\n${line}\n`;
        text += `👥 ${t('customers') || 'Customers'}: ${totalCust}\n`;
        text += `🏪 ${t('suppliers') || 'Suppliers'}: ${totalSupp}\n`;
        text += `📦 ${t('products') || 'Products'}: ${products.filter(p => !p.is_deleted).length}\n`;
        text += `👔 ${t('employeesLabel') || 'Staff'}: ${staff.filter(s => !s.is_deleted).length}\n`;
        text += `${line}\n`;
        text += `✅ ${t('totalIncome') || 'Income'}: Rs. ${income.toLocaleString()}\n`;
        text += `❌ ${t('totalExpenses') || 'Expense'}: Rs. ${expense.toLocaleString()}\n`;
        text += `💵 ${t('cashIn') || 'Cash In'}: Rs. ${cashIn.toLocaleString()}\n`;
        text += `💸 ${t('cashOut') || 'Cash Out'}: Rs. ${cashOut.toLocaleString()}\n`;
        text += `${line}\n📊 ${t('stockValue') || 'Stock Value'}: Rs. ${stockValue.toLocaleString()}`;
        return text;
      }

      return `${header}Report`;
    };

    const shareOnWhatsApp = async () => {
      if (waSharing) return;
      const text = generateReportWhatsAppText();
      const rawTitle = report.title || report.type || 'report';
      const cleanTitle = String(rawTitle).toLowerCase().replace(/[^a-z0-9_-]/g, '_');
      const dateStr = new Date().toISOString().slice(0, 10);
      const filename = `${cleanTitle}_${dateStr}.pdf`;
      const reportElement = document.querySelector('.print-report-page');

      if (reportElement) {
        try {
          setWaSharing(true);
          // Injected on demand now — it is ~924 KiB and this is the only
          // place it is used. If the load fails, the catch below falls back
          // to sharing the WhatsApp text, same as before.
          const html2pdf = await loadHtml2pdf();
          const opt = {
            margin: [8, 8, 8, 8],
            filename: filename,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              logging: false,
              windowWidth: 800
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
          };

          const pdfBlob = await html2pdf().from(reportElement).set(opt).output('blob');
          const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });

          // If device supports Web Share API with files (Android, iOS mobile, etc.)
          if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
            await navigator.share({
              files: [pdfFile],
              title: `${biz?.name || 'HisabFlow'} - ${report.title || report.type}`,
              text: text
            });
            setWaSharing(false);
            return;
          }

          // Desktop fallback: download the PDF directly and open WhatsApp Web
          const fileUrl = URL.createObjectURL(pdfBlob);
          const downloadLink = document.createElement('a');
          downloadLink.href = fileUrl;
          downloadLink.download = filename;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);

          const encoded = encodeURIComponent(text);
          window.open(`https://wa.me/?text=${encoded}`, '_blank');
          showNotification(
            t('pdfDownloadedShareWA') || 'Report PDF downloaded! Attach it in the opened WhatsApp chat.',
            'info'
          );
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('PDF WhatsApp sharing error:', err);
            const encoded = encodeURIComponent(text);
            window.open(`https://wa.me/?text=${encoded}`, '_blank');
          }
        } finally {
          setWaSharing(false);
        }
      } else {
        const encoded = encodeURIComponent(text);
        window.open(`https://wa.me/?text=${encoded}`, '_blank');
      }
    };

    const renderReportActionBar = () => (
      <div className="rpt-action-bar">
        <button onClick={onClose} className="btn btn-secondary" style={{ padding: '10px 24px' }}>{t('closeBtn')}</button>
        <button
          onClick={shareOnWhatsApp}
          disabled={waSharing}
          className="btn btn-primary"
          style={{
            padding: '10px 24px',
            background: '#25D366',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: waSharing ? 0.75 : 1,
            cursor: waSharing ? 'wait' : 'pointer'
          }}
        >
          {waSharing ? (
            <span style={{ width: 15, height: 15, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          )}
          <span>{waSharing ? (t('generatingPdf') || 'Preparing PDF...') : (t('whatsapp') || 'WhatsApp')}</span>
        </button>
        <button onClick={triggerPrint} className="btn btn-primary" style={{ padding: '10px 24px', background: '#1e40af' }}>
          <Printer size={16} /> <span style={{ marginLeft: 6 }}>{t('printSavePDF')}</span>
        </button>
      </div>
    );
    // ───────────────────────────────────────────────────────────────────────────

    const renderReportFooterNote = (repType) => {
      if (!printSettings.showReportFooterNote) return null;
      const keyMap = {
        cash: 'cash',
        ie_category: 'ie',
        ie: 'ie',
        bank: 'bank',
        ledger: 'ledger',
        customers: 'customers',
        suppliers: 'suppliers',
        products: 'products',
        stock_history: 'products',
        staff: 'staff',
        invoices: 'invoices',
        dashboard: 'dashboard'
      };
      const tabKey = keyMap[repType] || repType;
      const customNote = printSettings.tabNotes?.[tabKey]?.trim();
      const noteText = customNote ? customNote : printSettings.reportNoteText?.trim();
      if (!noteText) return null;
      const customTitle = printSettings.tabNoteTitles?.[tabKey]?.trim();
      const title = customTitle || printSettings.reportNoteTitle || (t('note') || 'Note');
      return (
        <div style={{ marginTop: '16px', marginBottom: '16px', padding: '10px 14px', border: '1px dashed #cbd5e1', borderRadius: '6px', background: '#f8fafc', fontSize: '0.85rem' }}>
          <strong style={{ display: 'block', marginBottom: '4px', color: '#334155' }}>{title}:</strong>
          <div style={{ whiteSpace: 'pre-wrap', color: '#475569', lineHeight: 1.4 }}>{noteText}</div>
        </div>
      );
    };

    if (report.type === 'cash') {
      const entries = report.data.entries || [];
      const totalIn = entries.filter(e => e.entry_type === 'IN').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
      const totalOut = entries.filter(e => e.entry_type === 'OUT').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
      const balance = totalIn - totalOut;
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('cashBook')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('period')}: {report.data.period || t('allTime')}</p>
              </div>
            </div>

            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalCashIn')}</div>
                <div className="value">Rs. {totalIn.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalCashOut')}</div>
                <div className="value">Rs. {totalOut.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${balance >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('netBalance')}</div>
                <div className="value">Rs. {Math.abs(balance).toLocaleString()}</div>
              </div>
            </div>

            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('date')}</th>
                  <th>{t('category')}</th>
                  <th>{t('description')}</th>
                  <th style={{ textAlign: 'center' }}>{t('type')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cashIn')}</th>
                  <th style={{ textAlign: 'right' }}>{t('cashOut')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                    <td>{formatDateTime(e.date, e.time)}</td>
                    <td style={{ fontWeight: 600 }}>{e.category}</td>
                    <td style={{ color: '#555' }}>{e.description || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`rpt-badge rpt-badge-${e.entry_type === 'IN' ? 'in' : 'out'}`}>{e.entry_type}</span>
                    </td>
                    <td style={{ textAlign: 'right', color: '#15803d', fontWeight: 600 }}>
                      {e.entry_type === 'IN' ? `Rs. ${parseFloat(e.amount).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                      {e.entry_type === 'OUT' ? `Rs. ${parseFloat(e.amount).toLocaleString()}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 700 }}>{t('totals')}</td>
                  <td style={{ textAlign: 'right', color: '#15803d' }}>Rs. {totalIn.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', color: '#dc2626' }}>Rs. {totalOut.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>

            <div className="rpt-totals">
              <div className="rpt-totals-row grand">
                <span>{t('closingBalance')}:</span>
                <span style={{ color: balance >= 0 ? '#15803d' : '#dc2626' }}>
                  Rs. {Math.abs(balance).toLocaleString()} {balance >= 0 ? t('surplus') : t('deficit')}
                </span>
              </div>
            </div>

            {renderReportFooterNote('cash')}

            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'ie_category') {
      const entries = report.data.entries || [];
      const catMap = {};
      entries.forEach(e => {
        if (!catMap[e.category]) catMap[e.category] = { income: 0, expense: 0, count: 0 };
        if (e.type === 'INCOME') catMap[e.category].income += parseFloat(e.amount || 0);
        else catMap[e.category].expense += parseFloat(e.amount || 0);
        catMap[e.category].count++;
      });
      const categories = Object.keys(catMap).sort();
      const totalIncome = categories.reduce((s, c) => s + catMap[c].income, 0);
      const totalExpense = categories.reduce((s, c) => s + catMap[c].expense, 0);
      const net = totalIncome - totalExpense;
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('categoryReport') || 'Category Report'}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('period')}: {report.data.period || t('allTime')}</p>
              </div>
            </div>

            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalIncome')}</div>
                <div className="value">Rs. {totalIncome.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalExpenses')}</div>
                <div className="value">Rs. {totalExpense.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${net >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{net >= 0 ? t('netProfit') : (t('netLoss') || 'Net Loss')}</div>
                <div className="value">Rs. {Math.abs(net).toLocaleString()}</div>
              </div>
            </div>

            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('category')}</th>
                  <th style={{ textAlign: 'right' }}>{t('income')}</th>
                  <th style={{ textAlign: 'right' }}>{t('expenses') || 'Expenses'}</th>
                  <th style={{ textAlign: 'right' }}>{net >= 0 ? t('netProfit') : (t('netLoss') || 'Net Loss')}</th>
                  <th style={{ textAlign: 'center' }}>{t('transactions') || 'Transactions'}</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, i) => {
                  const c = catMap[cat];
                  const catNet = c.income - c.expense;
                  return (
                    <tr key={i}>
                      <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{cat}</td>
                      <td style={{ textAlign: 'right', color: '#15803d' }}>Rs. {c.income.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>Rs. {c.expense.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: catNet >= 0 ? '#15803d' : '#dc2626' }}>Rs. {Math.abs(catNet).toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}>{c.count}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 700 }}>#</td>
                  <td style={{ fontWeight: 700 }}>{t('totals')}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#15803d' }}>Rs. {totalIncome.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>Rs. {totalExpense.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: net >= 0 ? '#15803d' : '#dc2626' }}>Rs. {Math.abs(net).toLocaleString()}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{entries.length}</td>
                </tr>
              </tfoot>
            </table>

            {renderReportFooterNote('ie_category')}

            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'ie') {
      const entries = report.data.entries || [];
      const totalIncome = entries.filter(e => e.type === 'INCOME').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
      const totalExpense = entries.filter(e => e.type === 'EXPENSE').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
      const net = totalIncome - totalExpense;
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('incomeExpense')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('period')}: {report.data.period || t('allTime')}</p>
              </div>
            </div>

            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalIncome')}</div>
                <div className="value">Rs. {totalIncome.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalExpenses')}</div>
                <div className="value">Rs. {totalExpense.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${net >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('netProfit')}</div>
                <div className="value">Rs. {Math.abs(net).toLocaleString()}</div>
              </div>
            </div>

            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('date')}</th>
                  <th style={{ textAlign: 'center' }}>{t('type')}</th>
                  <th>{t('category')}</th>
                  <th>{t('description')}</th>
                  <th style={{ textAlign: 'right' }}>{t('amount')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                    <td>{formatDateTime(e.date, e.time)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`rpt-badge rpt-badge-${e.type === 'INCOME' ? 'income' : 'expense'}`}>{e.type}</span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{e.category}</td>
                    <td style={{ color: '#555' }}>{e.description || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: e.type === 'INCOME' ? '#15803d' : '#dc2626' }}>
                      Rs. {parseFloat(e.amount).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 700 }}>{t('netProfitLoss')}</td>
                  <td style={{ textAlign: 'right', color: net >= 0 ? '#15803d' : '#dc2626' }}>
                    Rs. {Math.abs(net).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>

            {renderReportFooterNote('ie')}

            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'bank') {
      const entries = report.data.entries || [];
      const totalDeposits = entries.filter(e => e.transaction_type === 'DEPOSIT').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      const totalWithdrawals = entries.filter(e => e.transaction_type === 'WITHDRAWAL').reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      const accountName = (accountId) => bankAccounts.find(account => String(account.id) === String(accountId))?.name || accountId || '-';
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('bankTransactions')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('period')}: {report.data.period || t('allTime')}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income"><div className="label">{t('deposits') || 'Deposits'}</div><div className="value">Rs. {totalDeposits.toLocaleString()}</div></div>
              <div className="rpt-summary-card expense"><div className="label">{t('withdrawals') || 'Withdrawals'}</div><div className="value">Rs. {totalWithdrawals.toLocaleString()}</div></div>
              <div className="rpt-summary-card balance"><div className="label">{t('netBalance')}</div><div className="value">Rs. {Math.abs(totalDeposits - totalWithdrawals).toLocaleString()}</div></div>
            </div>
            <table className="rpt-table">
              <thead><tr><th>#</th><th>{t('date')}</th><th>{t('bankAccount')}</th><th>{t('bankTransactionType')}</th><th>{t('description')}</th><th style={{ textAlign: 'right' }}>{t('amount')}</th></tr></thead>
              <tbody>{entries.map((entry, index) => <tr key={entry.id || index}>
                <td style={{ color: '#64748b', fontWeight: 600 }}>{index + 1}</td>
                <td>{formatDateTime(entry.date, entry.time)}</td>
                <td style={{ fontWeight: 600 }}>{accountName(entry.bank_account)}</td>
                <td><span className={`rpt-badge rpt-badge-${entry.transaction_type === 'DEPOSIT' ? 'in' : 'out'}`}>{entry.transaction_type || '-'}</span></td>
                <td style={{ color: '#555' }}>{entry.description || '-'}</td>
                <td style={{ textAlign: 'right', fontWeight: 600, color: entry.transaction_type === 'WITHDRAWAL' ? '#dc2626' : '#15803d' }}>{entry.transaction_type === 'WITHDRAWAL' ? '- ' : '+ '}Rs. {parseFloat(entry.amount || 0).toLocaleString()}</td>
              </tr>)}</tbody>
              <tfoot><tr><td colSpan={5} style={{ fontWeight: 700 }}>{t('totals')}</td><td style={{ textAlign: 'right', fontWeight: 700 }}>Rs. {(totalDeposits - totalWithdrawals).toLocaleString()}</td></tr></tfoot>
            </table>
            {renderReportFooterNote('bank')}
            <div className="rpt-footer"><span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span><span>{biz?.name}</span></div>
          </div>
        </div>
      );
    }

    if (report.type === 'ledger') {
      const { party, entries: ledEntries, dateFilter, searchQuery } = report.data;
      const activeEntries = (ledEntries || []).filter(e => !e.is_deleted);
      const withRunning = computeRunningBalances(activeEntries);
      // Mirror the UI exactly: running balance is computed over ALL entries first,
      // then the active date/search filters decide which rows are displayed.
      const sortedLedgerEntries = withRunning
        .filter(e => {
          if (!filterByDate(e.date, dateFilter)) return false;
          if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!(e.description || '').toLowerCase().includes(q)) return false;
          }
          return true;
        })
        .sort((a, b) => sortByDateTimeDesc(a, b) || new Date(b.created_at) - new Date(a.created_at));
      const balance = activeEntries.length > 0
        ? activeEntries.reduce((s, e) => s + (e.entry_type === 'GIVE' ? parseFloat(e.amount || 0) : -parseFloat(e.amount || 0)), 0)
        : parseFloat(party?.cached_balance || 0);
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('ledgerStatement')}</div>
                <p>{t('statementDate')}: {now}</p>
                <p>{t('period')}: {dateFilterLabel(dateFilter, t)}{searchQuery ? ` · "${searchQuery}"` : ''}</p>
              </div>
            </div>

            <div className="rpt-info-row">
              <div className="rpt-info-box">
                <h4>{t('partyDetails')}</h4>
                <p><strong>{party?.name}</strong></p>
                <p>Phone: {party?.phone || 'N/A'}</p>
                {party?.cnic && <p>CNIC: {party.cnic}</p>}
                {party?.address && <p>Address: {party.address}</p>}
              </div>
              <div className="rpt-info-box" style={{ textAlign: 'right' }}>
                <h4>{t('balanceSummary')}</h4>
                <p style={{ fontSize: '20pt', fontWeight: 800, color: balance > 0 ? '#15803d' : balance < 0 ? '#dc2626' : '#1a1a2e' }}>
                  Rs. {Math.abs(balance).toLocaleString()}
                </p>
                <p style={{ fontWeight: 600, color: balance > 0 ? '#15803d' : '#dc2626' }}>
                  {balance > 0 ? `● ${t('receivableToGet')}` : balance < 0 ? `● ${t('payableToGive')}` : `● ${t('clear')}`}
                </p>
              </div>
            </div>

            <table className="rpt-table">
              <thead>
                <tr>
                  <th>{t('entryNumber')}</th>
                  <th>{t('date')}</th>
                  <th>{t('description')}</th>
                  <th style={{ textAlign: 'center' }}>{t('type')}</th>
                  <th style={{ textAlign: 'right' }}>{t('debit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('credit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('runningBalance')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedLedgerEntries.map((e, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b' }}>{i + 1}</td>
                    <td>{formatDateTime(e.date, e.time)}</td>
                    <td>{e.description || t('genericEntry')}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`rpt-badge rpt-badge-${e.entry_type === 'GIVE' ? 'give' : 'get'}`}>{e.entry_type === 'GIVE' ? t('debit') : t('credit')}</span>
                    </td>
                    <td style={{ textAlign: 'right', color: '#1e40af', fontWeight: 600 }}>
                      {e.entry_type === 'GIVE' ? `Rs. ${parseFloat(e.amount).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: '#854d0e', fontWeight: 600 }}>
                      {e.entry_type === 'GET' ? `Rs. ${parseFloat(e.amount).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: e.runningBalance > 0 ? '#15803d' : e.runningBalance < 0 ? '#dc2626' : '#1a1a2e' }}>
                      Rs. {Math.abs(e.runningBalance).toLocaleString()} {e.runningBalance > 0 ? `(${t('receivable')})` : e.runningBalance < 0 ? `(${t('payable')})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontWeight: 700 }}>{t('closingBal')}</td>
                  <td colSpan={3} style={{ textAlign: 'right', color: balance >= 0 ? '#15803d' : '#dc2626' }}>
                    Rs. {Math.abs(balance).toLocaleString()} {balance >= 0 ? t('receivable') : t('payable')}
                  </td>
                </tr>
              </tfoot>
            </table>

            {renderReportFooterNote('ledger')}

            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'customers') {
      const list = customers.filter(p => !p.is_deleted && (!report.data.area || p.area === report.data.area) && matchPartySearch(p, report.data.searchQuery));
      const totalBalance = list.reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0);
      const receivable = list.filter(p => (p.cached_balance || 0) > 0).reduce((s, p) => s + parseFloat(p.cached_balance), 0);
      const payable = list.filter(p => (p.cached_balance || 0) < 0).reduce((s, p) => s + Math.abs(parseFloat(p.cached_balance)), 0);
      const allList = customers.filter(p => !p.is_deleted && matchPartySearch(p, report.data.searchQuery));
      const areaSummary = [...new Set(allList.map(p => p.area).filter(Boolean))].sort().map(a => ({
        area: a,
        count: allList.filter(p => p.area === a).length,
        balance: allList.filter(p => p.area === a).reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0)
      }));
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{report.data.area ? `${t('customerDirectory')} — ${report.data.area}` : t('customerDirectory')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('totalCustomers')}: {list.length}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalCustomers')}</div>
                <div className="value">{list.length}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalReceivable')}</div>
                <div className="value">Rs. {receivable.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('totalPayable')}</div>
                <div className="value">Rs. {payable.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${totalBalance >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('netOutstanding')}</div>
                <div className="value">Rs. {Math.abs(totalBalance).toLocaleString()}</div>
              </div>
            </div>
            {!report.data.area && areaSummary.length > 0 && (
              <>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '16px 0 8px' }}>{t('areaWiseSummary') || 'Area-wise Summary'}</h3>
                <table className="rpt-table" style={{ marginBottom: '16px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>{t('area') || 'Area'}</th>
                      <th style={{ textAlign: 'center' }}>{t('totalCustomers')}</th>
                      <th style={{ textAlign: 'right' }}>{t('outstanding') || 'Outstanding'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaSummary.map((a, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{a.area}</td>
                        <td style={{ textAlign: 'center' }}>{a.count}</td>
                        <td style={{ textAlign: 'right', color: a.balance >= 0 ? '#15803d' : '#dc2626' }}>
                          Rs. {Math.abs(a.balance).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('name')}</th>
                  <th>{t('phone')}</th>
                  <th>{t('address')}</th>
                  <th>{t('area')}</th>
                  <th style={{ textAlign: 'right' }}>{t('balanceDue')}</th>
                  <th style={{ textAlign: 'right' }}>{t('paying') || 'Paying'}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{p.phone || 'N/A'}</td>
                    <td>{p.address || 'N/A'}</td>
                    <td>{p.area || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: (p.cached_balance || 0) > 0 ? '#15803d' : (p.cached_balance || 0) < 0 ? '#dc2626' : '#1a1a2e' }}>
                      {(p.cached_balance || 0) > 0 ? `Rs. ${parseFloat(p.cached_balance).toLocaleString()}` : (p.cached_balance || 0) < 0 ? `(Rs. ${Math.abs(parseFloat(p.cached_balance)).toLocaleString()})` : t('settled')}
                    </td>
                    <td style={{ textAlign: 'right', borderBottom: '1px solid #ddd' }}></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ fontWeight: 700 }}>{t('totalOutstanding')}</td>
                  <td style={{ textAlign: 'right', color: totalBalance >= 0 ? '#15803d' : '#dc2626' }}>
                    Rs. {Math.abs(totalBalance).toLocaleString()}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            {renderReportFooterNote('customers')}
            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'suppliers') {
      const list = suppliers.filter(p => !p.is_deleted && (!report.data.area || p.area === report.data.area) && matchPartySearch(p, report.data.searchQuery));
      const totalBalance = list.reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0);
      const receivable = list.filter(p => (p.cached_balance || 0) > 0).reduce((s, p) => s + parseFloat(p.cached_balance), 0);
      const payable = list.filter(p => (p.cached_balance || 0) < 0).reduce((s, p) => s + Math.abs(parseFloat(p.cached_balance)), 0);
      const allList = suppliers.filter(p => !p.is_deleted && matchPartySearch(p, report.data.searchQuery));
      const areaSummary = [...new Set(allList.map(p => p.area).filter(Boolean))].sort().map(a => ({
        area: a,
        count: allList.filter(p => p.area === a).length,
        balance: allList.filter(p => p.area === a).reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0)
      }));
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{report.data.area ? `${t('supplierDirectory')} — ${report.data.area}` : t('supplierDirectory')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('totalSuppliers')}: {list.length}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalSuppliers')}</div>
                <div className="value">{list.length}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalReceivable')}</div>
                <div className="value">Rs. {receivable.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('totalPayable')}</div>
                <div className="value">Rs. {payable.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${totalBalance >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('netOutstanding')}</div>
                <div className="value">Rs. {Math.abs(totalBalance).toLocaleString()}</div>
              </div>
            </div>
            {!report.data.area && areaSummary.length > 0 && (
              <>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '16px 0 8px' }}>{t('areaWiseSummary') || 'Area-wise Summary'}</h3>
                <table className="rpt-table" style={{ marginBottom: '16px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>{t('area') || 'Area'}</th>
                      <th style={{ textAlign: 'center' }}>{t('totalSuppliers')}</th>
                      <th style={{ textAlign: 'right' }}>{t('outstanding') || 'Outstanding'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaSummary.map((a, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{a.area}</td>
                        <td style={{ textAlign: 'center' }}>{a.count}</td>
                        <td style={{ textAlign: 'right', color: a.balance >= 0 ? '#15803d' : '#dc2626' }}>
                          Rs. {Math.abs(a.balance).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('name')}</th>
                  <th>{t('phone')}</th>
                  <th>{t('address')}</th>
                  <th>{t('area')}</th>
                  <th>{t('itemsSupplied')}</th>
                  <th style={{ textAlign: 'right' }}>{t('balanceDue')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{p.phone || 'N/A'}</td>
                    <td>{p.address || 'N/A'}</td>
                    <td>{p.area || '—'}</td>
                    <td>{p.items_supplied || '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: (p.cached_balance || 0) > 0 ? '#15803d' : (p.cached_balance || 0) < 0 ? '#dc2626' : '#1a1a2e' }}>
                      {(p.cached_balance || 0) > 0 ? `Rs. ${parseFloat(p.cached_balance).toLocaleString()}` : (p.cached_balance || 0) < 0 ? `(Rs. ${Math.abs(parseFloat(p.cached_balance)).toLocaleString()})` : t('settled')}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ fontWeight: 700 }}>{t('totalOutstanding')}</td>
                  <td style={{ textAlign: 'right', color: totalBalance >= 0 ? '#15803d' : '#dc2626' }}>
                    Rs. {Math.abs(totalBalance).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
            {renderReportFooterNote('suppliers')}
            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'products') {
      const prodSearch = (report.data.searchQuery || '').toLowerCase();
      const prodType = report.data.productType || 'all';
      const list = products.filter(p => !p.is_deleted
        && (!prodSearch || (p.name || '').toLowerCase().includes(prodSearch))
        && (prodType === 'all' || p.product_type === prodType));
      const totalValue = list.reduce((s, p) => s + ((parseFloat(p.stock_quantity) || 0) * (parseFloat(p.purchase_price) || 0)), 0);
      const totalItems = list.reduce((s, p) => s + (parseFloat(p.stock_quantity) || 0), 0);
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('stockInventory')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('productsCount')}: {list.length}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('products')}</div>
                <div className="value">{list.length}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalUnits')}</div>
                <div className="value">{totalItems.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('totalStockValue')}</div>
                <div className="value">Rs. {totalValue.toLocaleString()}</div>
              </div>
            </div>
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('productName')}</th>
                  <th style={{ textAlign: 'right' }}>{t('qty')}</th>
                  <th style={{ textAlign: 'right' }}>{t('purchasePrice')}</th>
                  <th style={{ textAlign: 'right' }}>{t('salePrice')}</th>
                  <th style={{ textAlign: 'right' }}>{t('totalValue')}</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>
                      {p.name}
                      {p.expiration_date && <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 'normal' }}>{t('expirationDate')}: {p.expiration_date}</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>{(parseFloat(p.stock_quantity) || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>Rs. {parseFloat(p.purchase_price || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>Rs. {parseFloat(p.sale_price || 0).toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>Rs. {((parseFloat(p.stock_quantity) || 0) * (parseFloat(p.purchase_price) || 0)).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontWeight: 700 }}>{t('totalInventoryValue')}</td>
                  <td style={{ textAlign: 'right', color: '#15803d' }}>Rs. {totalValue.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
            {renderReportFooterNote('products')}
            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'stock_history') {
      const list = report.data.entries || [];
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('stockHistory')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('period')}: {report.data.periodLabel || t('allTime')}</p>
              </div>
            </div>
            
            <table className="rpt-table" style={{ marginTop: '20px' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('product')}</th>
                  <th>{t('date')}</th>
                  <th>{t('type')}</th>
                  <th style={{ textAlign: 'right' }}>{t('qty')}</th>
                  <th>{t('supplierOrDesc')}</th>
                </tr>
              </thead>
              <tbody>
                {list.slice().sort((a, b) => sortByDateTimeDesc(a, b) || new Date(b.created_at) - new Date(a.created_at)).map((m, i) => {
                  const prod = products.find(p => p.id === m.product);
                  const typeLabel = m.movement_type === 'IN' ? (t('stockIn') || 'Stock In') : m.movement_type === 'OUT' ? (t('stockOut') || 'Stock Out') : (t('adjustment') || 'Adjustment');
                  return (
                    <tr key={m.id}>
                      <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{prod ? prod.name : (t('unknownProduct') || 'Unknown Product')}</td>
                      <td>{formatDateTime(m.date, m.time)}</td>
                      <td>{typeLabel}</td>
                      <td style={{ textAlign: 'right' }}>{m.quantity} {prod ? prod.unit : ''}</td>
                      <td>{m.description || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            
            {renderReportFooterNote('stock_history')}

            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'staff') {
      const staffSearch = (report.data.searchQuery || '').toLowerCase();
      const matchesStaff = s => !staffSearch
        || (s.name || '').toLowerCase().includes(staffSearch)
        || (s.phone || '').includes(report.data.searchQuery)
        || (s.cnic || '').includes(report.data.searchQuery);
      const empList = staff.filter(s => !s.is_deleted && matchesStaff(s));
      const totalSalary = empList.reduce((s, e) => s + parseFloat(e.base_salary || 0), 0);
      const totalPaid = payrolls.filter(p => p.status === 'PAID' && matchesStaff(staff.find(s => s.id === (p.staff_member || p.staff_member_id)) || {})).reduce((s, p) => s + parseFloat(p.net_paid || 0), 0);
      const monthlyCount = empList.filter(e => e.salary_type === 'MONTHLY').length;
      const dailyCount = empList.filter(e => e.salary_type === 'DAILY').length;
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('staffSalaryReport')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('employeesLabel')}: {empList.length}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalEmployees')}</div>
                <div className="value">{empList.length}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('monthly')}</div>
                <div className="value">{monthlyCount}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('dailyWage')}</div>
                <div className="value">{dailyCount}</div>
              </div>
              <div className={`rpt-summary-card balance ${totalPaid > 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('totalPaid')}</div>
                <div className="value">Rs. {totalPaid.toLocaleString()}</div>
              </div>
            </div>
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('employee')}</th>
                  <th>{t('phone')}</th>
                  <th>{t('type')}</th>
                  <th style={{ textAlign: 'right' }}>{t('baseSalary')}</th>
                </tr>
              </thead>
              <tbody>
                {empList.map((e, i) => (
                  <tr key={i}>
                    <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{e.name}</td>
                    <td>{e.phone || 'N/A'}</td>
                    <td><span className={`rpt-badge rpt-badge-${e.salary_type === 'MONTHLY' ? 'income' : 'expense'}`}>{e.salary_type}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>Rs. {parseFloat(e.base_salary || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontWeight: 700 }}>{t('totalMonthlyPayroll')}</td>
                  <td style={{ textAlign: 'right', color: '#1e40af' }}>Rs. {totalSalary.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
            {renderReportFooterNote('staff')}
            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'invoices') {
      const invList = filteredInvoices;
      const totalAmount = invList.reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);
      const paidCount = invList.filter(i => i.status === 'PAID').length;
      const unpaidCount = invList.filter(i => i.status !== 'PAID').length;
      const periodLabel = report.data.periodLabel || t('allTime');
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">{t('invoiceSummaryReport')}</div>
                <p>{t('reportDate')}: {now}</p>
                <p>{t('period')}: {periodLabel}</p>
                <p>{t('totalInvoices')}: {invList.length}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalInvoices')}</div>
                <div className="value">{invList.length}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('paid')}</div>
                <div className="value">{paidCount}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('unpaid')}</div>
                <div className="value">{unpaidCount}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('totalRevenue')}</div>
                <div className="value">Rs. {totalAmount.toLocaleString()}</div>
              </div>
            </div>
            <table className="rpt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('invoiceNumber')}</th>
                  <th>{t('name')}</th>
                  <th>{t('date')}</th>
                  <th style={{ textAlign: 'right' }}>{t('amount')}</th>
                  <th style={{ textAlign: 'center' }}>{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {invList.map((inv, i) => {
                  const cust = parties.find(p => p.id === (inv.party || inv.party_id));
                  return (
                    <tr key={i}>
                      <td style={{ color: '#64748b', fontWeight: 600 }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>
                        {inv.invoice_number}
                        {fbrEnabled && (inv.fbr_status || fbrScopeCovers(inv)) && (
                          <div style={{ marginTop: '4px' }}><FbrBadge invoice={inv} onResend={resendToFbr} /></div>
                        )}
                      </td>
                      <td>{cust?.name || '—'}</td>
                      <td>{formatDateTime(inv.issue_date, inv.issue_time)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>Rs. {parseFloat(inv.total_amount || 0).toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}><span className={`rpt-badge rpt-badge-${inv.status === 'PAID' ? 'income' : 'expense'}`}>{inv.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontWeight: 700 }}>{t('totalInvoiceRevenue')}</td>
                  <td style={{ textAlign: 'right', color: '#15803d' }}>Rs. {totalAmount.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            {renderReportFooterNote('invoices')}
            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    if (report.type === 'dashboard') {
      const totalCust = customers.filter(p => !p.is_deleted).length;
      const totalSupp = suppliers.filter(p => !p.is_deleted).length;
      const custBalance = customers.filter(p => !p.is_deleted).reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0);
      const suppBalance = suppliers.filter(p => !p.is_deleted).reduce((s, p) => s + parseFloat(p.cached_balance || 0), 0);
      const cashIn = cashEntries.filter(c => !c.is_deleted && c.entry_type === 'IN').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
      const cashOut = cashEntries.filter(c => !c.is_deleted && c.entry_type === 'OUT').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
      const income = incomeExpenses.filter(i => !i.is_deleted && i.type === 'INCOME').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
      const expense = incomeExpenses.filter(i => !i.is_deleted && i.type === 'EXPENSE').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
      const stockValue = products.filter(p => !p.is_deleted).reduce((s, p) => s + ((parseFloat(p.stock_quantity) || 0) * (parseFloat(p.purchase_price) || 0)), 0);
      const totalStaff = staff.filter(s => !s.is_deleted).length;
      const totalInv = invoices.filter(i => !i.is_deleted).length;
      const walkInSalesTotal = invoices
        .filter(inv => !inv.is_deleted && (!inv.party && !inv.party_id))
        .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
      return (
        <div className="print-report-overlay">
          {renderReportActionBar()}
          <div className="rpt-zoom-hint no-print">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
            Pinch to zoom &amp; scroll to pan
          </div>
          <div className="print-report-page">
            <div className="rpt-header">
              <div className="rpt-header-left" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                {biz?.logo && <img src={getLogoUrl(biz.logo)} alt="Logo" className="print-logo" />}
                <div>
                  <h1>{biz?.name || t('yourCompany')}</h1>
                  <p>{biz?.address || ''}</p>
                  <p>{t('email')}: {biz?.email || t('none')} &nbsp;|&nbsp; {t('phone')}: {biz?.phone || t('none')}</p>
                </div>
              </div>
              <div className="rpt-header-right">
                <div className="rpt-title-badge">BUSINESS OVERVIEW</div>
                <p>{t('reportDate')}: {now}</p>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('customers')}</div>
                <div className="value">{totalCust}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('suppliers')}</div>
                <div className="value">{totalSupp}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('products')}</div>
                <div className="value">{products.filter(p => !p.is_deleted).length}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('employeesLabel')}</div>
                <div className="value">{totalStaff}</div>
              </div>
            </div>
            <h3 style={{ margin: '24px 0 12px', fontSize: '14pt', color: '#1a1a2e', borderBottom: '2px solid #eee', paddingBottom: '8px' }}>Financial Summary</h3>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('totalIncome')}</div>
                <div className="value">Rs. {income.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('totalExpenses')}</div>
                <div className="value">Rs. {expense.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${(income - expense) >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('netProfit')}</div>
                <div className="value">Rs. {Math.abs(income - expense).toLocaleString()}</div>
              </div>
            </div>
            <div className="rpt-summary-row">
              <div className="rpt-summary-card income">
                <div className="label">{t('cashIn')}</div>
                <div className="value">Rs. {cashIn.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card expense">
                <div className="label">{t('cashOut')}</div>
                <div className="value">Rs. {cashOut.toLocaleString()}</div>
              </div>
              <div className={`rpt-summary-card balance ${(cashIn - cashOut) >= 0 ? 'positive' : 'negative'}`}>
                <div className="label">{t('cashBalanceLabel')}</div>
                <div className="value">Rs. {Math.abs(cashIn - cashOut).toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card income" style={{ background: 'rgba(139,92,246,0.05)', borderColor: 'rgba(139,92,246,0.2)' }}>
                <div className="label" style={{ color: '#8b5cf6' }}>{t('walkInSales')}</div>
                <div className="value" style={{ color: '#8b5cf6' }}>Rs. {walkInSalesTotal.toLocaleString()}</div>
              </div>
            </div>
            <div className="rpt-summary-row" style={{ marginBottom: '24px' }}>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('customerOutstanding')}</div>
                <div className="value">Rs. {Math.abs(custBalance).toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('supplierOutstanding')}</div>
                <div className="value">Rs. {Math.abs(suppBalance).toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('stockValue')}</div>
                <div className="value">Rs. {stockValue.toLocaleString()}</div>
              </div>
              <div className="rpt-summary-card balance positive">
                <div className="label">{t('invoicesLabel')}</div>
                <div className="value">{totalInv}</div>
              </div>
            </div>
            {renderReportFooterNote('dashboard')}
            <div className="rpt-footer">
              <span>{t('generatedBy')} &nbsp;|&nbsp; {now}</span>
              <span>{biz?.name}</span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Reports Computations
  // Print Individual Entry View Check
  if (printEntryData) {
    const entryPartyEntries = ledgerEntries
      .filter(e => (e.party === printEntryData.party.id || e.party_id === printEntryData.party.id) && !e.is_deleted)
      .sort((a, b) => sortByDateTimeAsc(a, b));
    const entryNumber = entryPartyEntries.findIndex(e => e.id === printEntryData.entry.id) + 1;
    return (
      <div className="receipt-container animate-fade-in">
        <div className="receipt-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {selectedBusiness?.logo && <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" className="print-logo" />}
            <div>
              <h1>{selectedBusiness?.name || t('yourCompany')}</h1>
              <p>{selectedBusiness?.address || t('companyAddress')}</p>
              <p>{t('email')}: {selectedBusiness?.email || t('none')} | {t('phone')}: {selectedBusiness?.phone || t('none')}</p>
            </div>
          </div>
          <div>
            <h2>{t('paymentReceipt')}</h2>
            <p>{t('entryNumber')}: {entryNumber} &nbsp;|&nbsp; {t('date')}: {formatDateTime(printEntryData.entry.date, printEntryData.entry.time)}</p>
          </div>
        </div>

        <div className="receipt-details">
          <div className="receipt-details-section">
            <h3>{t('partyDetails')}</h3>
            <h4>{printEntryData.party.name}</h4>
            <p>{t('phone')}: {printEntryData.party.phone || t('none')}</p>
          </div>
        </div>

        <table className="receipt-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('description')}</th>
              <th style={{ textAlign: 'right' }}>{t('amount')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{printEntryData.entry.description || t('genericEntry')}</td>
              <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                {printEntryData.entry.entry_type === 'GIVE' ? `${t('gaveUdhaar')} Rs. ${printEntryData.entry.amount.toLocaleString()}` : `${t('gotPayment')} Rs. ${printEntryData.entry.amount.toLocaleString()}`}
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: '16px', marginTop: '40px' }} className="no-print">
          <button onClick={triggerPrint} className="btn btn-primary" style={{ padding: '12px 24px', background: '#0284c7' }}>{t('printReceipt')}</button>
          <button onClick={() => setPrintEntryData(null)} className="btn btn-secondary" style={{ padding: '12px 24px' }}>{t('backToLedger')}</button>
        </div>
      </div>
    );
  }

  // Print Invoice View Check
  if (printInvoiceData) {
    const isCenteredHeader = printSettings.headerLayout === 'center';
    const showLogo = printSettings.logoPosition !== 'none' && selectedBusiness?.logo;
    const itemsFirst = printSettings.itemsPosition === 'items_first';

    const customerDetailsNode = (
      <div className="receipt-details" style={{ marginBottom: '24px' }}>
        <div className="receipt-details-section">
          <h3>{printInvoiceData.isPurchase ? (t('supplierDetails') || 'Supplier Details') : t('customerDetails')}</h3>
          <h4>{printInvoiceData.party.name}</h4>
          {printSettings.showPartyPhone && printInvoiceData.party.phone && <p>{t('phone')}: {printInvoiceData.party.phone}</p>}
          {printSettings.showPartyCnic && printInvoiceData.party.cnic && <p>{t('cnic')}: {printInvoiceData.party.cnic}</p>}
          {printSettings.showPartyAddress && printInvoiceData.party.address && <p>{t('address')}: {printInvoiceData.party.address}</p>}
        </div>
      </div>
    );

    const itemsTableNode = (() => {
      // Pack columns follow the Pack/Pcs choice made on the line, not the size of
      // the count. normalizeLineItem writes a NUMBER (0 included) whenever the
      // line was entered in pack mode and null whenever it was entered in pieces,
      // so "present" is what selects the pack table. The old "> 0" test made a
      // partial pack (fewer pieces than one full pack → bulk_quantity 0) fail and
      // silently dropped every pack/loose column — even though Pack was selected
      // on the line and the names for those columns were configured in Settings.
      const hasBulkItems = printInvoiceData.items && printInvoiceData.items.some(
        r => r.bulk_quantity !== null && r.bulk_quantity !== undefined && parseFloat(r.bulk_quantity) > 0
      );
      // Only show loose pieces columns (PC / Pc Price) if at least one item on this invoice has loose pieces.
      // When only whole packs are bought, omit the loose columns so only pack details are printed.
      const hasSmallItems = printInvoiceData.items && printInvoiceData.items.some(
        r => r.small_quantity !== null && r.small_quantity !== undefined && parseFloat(r.small_quantity) > 0
      );
      const bulkUnitItem = printInvoiceData.items && (
        printInvoiceData.items.find(r => parseFloat(r.bulk_quantity) > 0 && r.bulk_unit_name) ||
        printInvoiceData.items.find(r => r.bulk_unit_name)
      );
      const bulkUnitLabel = bulkUnitItem?.bulk_unit_name || 'PACK';

      const colNames = printSettings.columnNames || {};
      const snHeader = colNames.serialNo?.trim() || 'S.N';
      const descHeader = colNames.itemDescription?.trim() || (t('itemDescription') || 'Product');
      const qtyHeader = colNames.quantity?.trim() || (t('qty') || 'Qty');
      const priceHeader = colNames.unitPrice?.trim() || (t('unitPrice') || 'Price');
      const amountHeader = colNames.subtotal?.trim() || (t('subtotal') || 'Amount');
      const showItemDiscount = (printInvoiceData.items || []).some(r => parseFloat(r.discount) > 0);

      if (hasBulkItems) {
        const bulkColHeader = colNames.bulkQuantity?.trim() || bulkUnitLabel.toUpperCase();
        const smallColHeader = colNames.smallQuantity?.trim() || 'PC';
        const bulkPriceColHeader = colNames.bulkPrice?.trim() || (printInvoiceData.isPurchase ? `${bulkUnitLabel.toUpperCase()} Cost` : `${bulkUnitLabel.toUpperCase()} Price`);
        const smallPriceColHeader = colNames.smallPrice?.trim() || (printInvoiceData.isPurchase ? 'PC Cost' : 'Pc Price');

        return (
          <table className="receipt-table">
            <thead>
              <tr>
                {printSettings.showSerialNumber && <th style={{ textAlign: 'center', width: '40px' }}>{snHeader}</th>}
                <th style={{ textAlign: 'left' }}>{descHeader}</th>
                <th style={{ textAlign: 'center' }}>{bulkColHeader}</th>
                {hasSmallItems && <th style={{ textAlign: 'center' }}>{smallColHeader}</th>}
                <th style={{ textAlign: 'right' }}>{bulkPriceColHeader}</th>
                {hasSmallItems && <th style={{ textAlign: 'right' }}>{smallPriceColHeader}</th>}
                <th style={{ textAlign: 'right' }}>{amountHeader}</th>
                {showItemDiscount && <th style={{ textAlign: 'right' }}>{t('discount')}</th>}
              </tr>
            </thead>
            <tbody>
              {printInvoiceData.items.map((row, index) => {
                const bQtyVal = row.bulk_quantity !== null && row.bulk_quantity !== undefined ? parseFloat(row.bulk_quantity) : 0;
                const hasBulkQty = bQtyVal > 0;
                const bPriceVal = row.bulk_unit_price !== null && row.bulk_unit_price !== undefined ? parseFloat(row.bulk_unit_price) : 0;
                const hasBulkPrice = bPriceVal > 0;

                const rawSQty = row.small_quantity !== null && row.small_quantity !== undefined ? parseFloat(row.small_quantity) : null;
                const sQtyVal = rawSQty !== null ? rawSQty : (hasBulkQty ? 0 : (parseFloat(row.quantity) || 0));

                const rawSPrice = row.small_unit_price !== null && row.small_unit_price !== undefined
                  ? parseFloat(row.small_unit_price)
                  : (row.unit_price ? parseFloat(row.unit_price) : 0);

                return (
                  <tr key={index}>
                    {printSettings.showSerialNumber && <td style={{ textAlign: 'center' }}>{index + 1}</td>}
                    <td>{row.description}</td>
                    <td style={{ textAlign: 'center', fontWeight: hasBulkQty ? 600 : 400 }}>
                      {hasBulkQty ? bQtyVal : '-'}
                    </td>
                    {hasSmallItems && (
                      <td style={{ textAlign: 'center' }}>
                        {sQtyVal > 0 ? sQtyVal : '-'}
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      {hasBulkQty && hasBulkPrice
                        ? `Rs. ${bPriceVal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                        : '-'}
                    </td>
                    {hasSmallItems && (
                      <td style={{ textAlign: 'right' }}>
                        {sQtyVal > 0 && rawSPrice > 0
                          ? `Rs. ${rawSPrice.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
                          : '-'}
                      </td>
                    )}
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      Rs. {parseFloat(row.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                    </td>
                    {showItemDiscount && (<td style={{ textAlign: 'right', color: '#0f766e' }}>- Rs. {parseFloat(row.discount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }

      return (
        <table className="receipt-table">
          <thead>
            <tr>
              {printSettings.showSerialNumber && <th style={{ textAlign: 'center', width: '40px' }}>{snHeader}</th>}
              <th style={{ textAlign: 'left' }}>{descHeader}</th>
              <th>{qtyHeader}</th>
              <th>{priceHeader}</th>
              <th style={{ textAlign: 'right' }}>{amountHeader}</th>
              {showItemDiscount && <th style={{ textAlign: 'right' }}>{t('discount')}</th>}
            </tr>
          </thead>
          <tbody>
            {printInvoiceData.items.map((row, index) => (
              <tr key={index}>
                {printSettings.showSerialNumber && <td style={{ textAlign: 'center' }}>{index + 1}</td>}
                <td>{row.description}</td>
                <td>{row.quantity}</td>
                <td>Rs. {parseFloat(row.unit_price || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                <td style={{ textAlign: 'right' }}>Rs. {parseFloat(row.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                {showItemDiscount && (<td style={{ textAlign: 'right', color: '#0f766e' }}>- Rs. {parseFloat(row.discount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      );
    })();

    return (
      <div className="receipt-container animate-fade-in">
        {isCenteredHeader ? (
          <div className="receipt-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '8px', borderBottom: '2px solid #333', paddingBottom: '16px', marginBottom: '20px' }}>
            {showLogo && <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" className="print-logo" style={{ maxHeight: '65px', objectFit: 'contain' }} />}
            <div>
              <h1 style={{ margin: '0 0 4px' }}>{selectedBusiness?.name || t('yourCompany')}</h1>
              {printSettings.showBusinessAddress && <p style={{ margin: '2px 0' }}>{selectedBusiness?.address || t('companyAddress')}</p>}
              {printSettings.showBusinessPhoneEmail && <p style={{ margin: '2px 0' }}>{t('email')}: {selectedBusiness?.email || t('none')} | {t('phone')}: {selectedBusiness?.phone || t('none')}</p>}
            </div>
            <div style={{ marginTop: '6px', borderTop: '1px dashed #ccc', paddingTop: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, background: printInvoiceData.isPurchase ? '#7c3aed' : '#0284c7', color: '#fff', padding: '6px 14px', borderRadius: '6px', display: 'inline-block', fontSize: '0.95rem', letterSpacing: '0.05em' }}>
                {printInvoiceData.isPurchase ? (t('purchaseInvoice') || 'PURCHASE INVOICE') : t('invoiceReceipt')}
              </h2>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0 }}>{t('invoiceNumber')}: {printInvoiceData.invoice.invoice_number}</p>
                <p style={{ margin: 0 }}>{t('date')}: {printSettings.showInvoiceTime ? formatDateTime(printInvoiceData.invoice.issue_date, printInvoiceData.invoice.issue_time) : printInvoiceData.invoice.issue_date}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="receipt-header" style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #333', paddingBottom: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexDirection: printSettings.logoPosition === 'center' ? 'column' : printSettings.logoPosition === 'right' ? 'row-reverse' : 'row' }}>
              {showLogo && <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" className="print-logo" />}
              <div>
                <h1>{selectedBusiness?.name || t('yourCompany')}</h1>
                {printSettings.showBusinessAddress && <p>{selectedBusiness?.address || t('companyAddress')}</p>}
                {printSettings.showBusinessPhoneEmail && <p>{t('email')}: {selectedBusiness?.email || t('none')} | {t('phone')}: {selectedBusiness?.phone || t('none')}</p>}
              </div>
            </div>
            <div>
              <h2 style={{ background: printInvoiceData.isPurchase ? '#7c3aed' : '#0284c7', color: '#fff', padding: '6px 14px', borderRadius: '6px', display: 'inline-block', fontSize: '1rem', letterSpacing: '0.05em' }}>
                {printInvoiceData.isPurchase ? (t('purchaseInvoice') || 'PURCHASE INVOICE') : t('invoiceReceipt')}
              </h2>
              <p>{t('invoiceNumber')}: {printInvoiceData.invoice.invoice_number}</p>
              <p>{t('date')}: {printSettings.showInvoiceTime ? formatDateTime(printInvoiceData.invoice.issue_date, printInvoiceData.invoice.issue_time) : printInvoiceData.invoice.issue_date}</p>
            </div>
          </div>
        )}

        {itemsFirst ? (
          <>
            {itemsTableNode}
            {customerDetailsNode}
          </>
        ) : (
          <>
            {customerDetailsNode}
            {itemsTableNode}
          </>
        )}

        <div className="receipt-totals">
          {printSettings.showSubtotal && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: '6px' }}>
              <span>{t('subtotalLabel')}:</span>
              <span>Rs. {printInvoiceData.items.reduce((s, r) => s + parseFloat(r.subtotal || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>
          )}
          {printSettings.showDiscount && (parseFloat(printInvoiceData.invoice.discount_amount) > 0) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: '6px' }}>
              <span style={{ color: 'var(--text-muted)' }}>{t('discount') || 'Discount'}:</span>
              <span style={{ color: 'var(--text-muted)' }}>Rs. {parseFloat(printInvoiceData.invoice.discount_amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: '6px', borderTop: '1px solid var(--border-color)', paddingTop: '6px' }}>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{t('grandTotal')}:</span>
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Rs. {parseFloat(printInvoiceData.invoice.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
          </div>
          {printSettings.showPaidAmount && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', marginBottom: '6px' }}>
              <span>{t('paidAmount')}:</span>
              <span style={{ fontWeight: 600 }}>Rs. {parseFloat(printInvoiceData.invoice.paid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>
            </div>
          )}
          {printSettings.showRemainingBalance && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '280px', borderTop: '1px dashed var(--border-color)', paddingTop: '6px' }}>
              <span style={{ fontWeight: 600 }}>{t('remainingBalance')}:</span>
              <span style={{ fontWeight: 700, fontSize: '1.1rem', color: (parseFloat(printInvoiceData.invoice.total_amount || 0) - parseFloat(printInvoiceData.invoice.paid_amount || 0)) > 0 ? 'var(--brand-danger)' : 'inherit' }}>
                Rs. {Math.max(0, parseFloat(printInvoiceData.invoice.total_amount || 0) - parseFloat(printInvoiceData.invoice.paid_amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>

        {/* FBR invoice number + QR — only on FBR-accepted invoices, only for
            businesses that run FBR. Everything else prints as before. */}
        {fbrEnabled && <FbrPrintBlock invoice={printInvoiceData.invoice} />}

        {printSettings.showInvoiceFooterNote && (printSettings.invoiceNoteText?.trim() || printSettings.invoiceNoteTitle?.trim()) && (
          <div style={{ marginTop: '24px', padding: '12px 16px', border: '1px dashed #cbd5e1', borderRadius: '6px', background: '#f8fafc' }}>
            <span style={{ fontWeight: 700, display: 'block', marginBottom: '4px', fontSize: '0.88rem', color: '#1e293b' }}>
              {printSettings.invoiceNoteTitle || t('note') || 'Note'}:
            </span>
            {printSettings.invoiceNoteText && (
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {printSettings.invoiceNoteText}
              </p>
            )}
          </div>
        )}

        {printSettings.showSignatureBlock && (
          <div className="receipt-signatures" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '36px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '150px', borderBottom: '1px dashed #94a3b8', marginBottom: '6px' }}></div>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{t('customerSignature')}</span>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '150px', borderBottom: '1px dashed #94a3b8', marginBottom: '6px' }}></div>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{t('authorizedSignature')}</span>
            </div>
          </div>
        )}

        <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between', gap: '20px' }} className="no-print">
          <button onClick={() => { setPrintInvoiceData(null); if (printInvoiceData.isPurchase) setActiveTab('suppliers'); }} className="btn btn-secondary" style={{ padding: '12px 24px' }}>
            &larr; {printInvoiceData.isPurchase ? (t('backToSuppliers') || 'Back to Suppliers') : t('backToBillBook')}
          </button>
          <button onClick={handlePrint} className="btn btn-success" style={{ padding: '12px 24px', background: printInvoiceData.isPurchase ? '#7c3aed' : '#0284c7' }}>
            <Printer size={18} />
            <span>{t('printInvoice')}</span>
          </button>
        </div>
      </div>
    );
  }

  // Salary Slip Print View Check
  if (printSalarySlipData) {
    const { payroll, employee } = printSalarySlipData;
    return (
      <div className="receipt-container animate-fade-in">
        {/* Header */}
        <div className="receipt-header">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {selectedBusiness?.logo && <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" className="print-logo" />}
            <div>
              <h1>{selectedBusiness?.name || t('yourCompany')}</h1>
              <p>{selectedBusiness?.address || ''}</p>
              <p>{selectedBusiness?.email || ''} | {selectedBusiness?.phone || ''}</p>
            </div>
          </div>
          <div>
            <h2 style={{ background: '#0284c7', color: '#fff', padding: '6px 12px', borderRadius: '4px', display: 'inline-block' }}>{t('salarySlip')}</h2>
            <p style={{ marginTop: '6px' }}>{t('slipRef')}: SLIP-{payroll.id.slice(-6).toUpperCase()}</p>
            <p>{t('dateIssued')}: {payroll.date_paid || payroll.pay_period_end}</p>
          </div>
        </div>

        {/* Employee Info */}
        <div className="receipt-details">
          <div className="receipt-details-section">
            <h3>{t('employeeDetails')}</h3>
            <h4>{employee.name}</h4>
            {employee.phone && <p>{t('phone')}: {employee.phone}</p>}
            {employee.cnic && <p>{t('cnic')}: {employee.cnic}</p>}
            <p>{t('employmentType')}: {employee.salary_type}</p>
          </div>
          <div className="receipt-details-section text-right">
            <h3>{t('payPeriod')}</h3>
            <p>{t('from')}: {payroll.pay_period_start}</p>
            <p>{t('to')}: {payroll.pay_period_end}</p>
            <p style={{ fontWeight: 'bold' }}>
              Status: {payroll.status}
            </p>
          </div>
        </div>

        {/* Salary Breakdown Table */}
        <table className="receipt-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('description')}</th>
              <th style={{ textAlign: 'right' }}>{t('amount')} (PKR)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('baseSalary')}</td>
              <td style={{ textAlign: 'right' }}>Rs. {(payroll.basic_salary || 0).toLocaleString()}</td>
            </tr>
            {payroll.allowances > 0 && (
              <tr>
                <td>+ {t('allowances')}</td>
                <td style={{ textAlign: 'right' }}>Rs. {(payroll.allowances || 0).toLocaleString()}</td>
              </tr>
            )}
            {payroll.deductions > 0 && (
              <tr>
                <td>- {t('deductions')}</td>
                <td style={{ textAlign: 'right' }}>Rs. {(payroll.deductions || 0).toLocaleString()}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Net Total */}
        <div className="receipt-totals">
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ fontWeight: 'bold' }}>{t('netSalaryPaid')}</span>
            <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>Rs. {(payroll.net_paid || 0).toLocaleString()}</span>
          </div>
        </div>

        {/* Signature area */}
        <div className="receipt-signatures">
          <div>
            <div style={{ borderTop: '1px solid #333', width: '150px', paddingTop: '8px', margin: '0 auto' }}>{t('employeeSignature')}</div>
          </div>
          <div>
            <div style={{ borderTop: '1px solid #333', width: '150px', paddingTop: '8px', margin: '0 auto' }}>{t('authorizedSignatory')}</div>
          </div>
        </div>

        {/* Action Buttons -- hidden on print */}
        <div style={{ marginTop: '40px', display: 'flex', gap: '12px' }} className="no-print">
          <button onClick={() => setPrintSalarySlipData(null)} className="btn btn-secondary" style={{ padding: '12px 24px' }}>&larr; {t('backToStaff')}</button>
          <button onClick={triggerPrint} className="btn btn-primary" style={{ padding: '12px 24px', background: '#0284c7' }}>
            <Printer size={18} />
            <span>{t('printSalarySlip')}</span>
          </button>
        </div>
      </div>
    );
  }

  // Print Report Overlay — early return to avoid app UI bleeding into PDF on mobile
  if (activePrintReport) {
    return (
      <PrintReportOverlay
        report={activePrintReport}
        onClose={() => setActivePrintReport(null)}
      />
    );
  }

  // Business Card Print Overlay — early return so only the cards are printed
  // This fixes mobile where the full tab was printing instead of the cards
  if (showCardPrintOverlay) {
    const cName = designerData.name || selectedBusiness?.name || 'Your Name';
    const cTagline = designerData.tagline || 'CEO & Founder';
    const cPhone = designerData.phone || selectedBusiness?.phone || '+1 234 567 890';
    const cEmail = designerData.email || selectedBusiness?.email || 'email@example.com';
    const cAddress = designerData.address || selectedBusiness?.address || '123 Business Avenue, City';
    const cWebsite = designerData.website || 'www.example.com';
    const cssVars = { '--bc-primary': designerColor };
    const cCompName = designerData.companyName || selectedBusiness?.name || 'Your Company';
    const cCompTagline = designerData.companyTagline || 'Company Features / Tagline';
    const cLogo = selectedBusiness?.logo ? getLogoUrl(selectedBusiness.logo) : null;

    const renderCardContent = (key) => (
      <div key={key} className={`business-card bc-template-${designerTemplate}`} style={cssVars}>
        {designerTemplate === 'gradient' ? <div className="bc-glass-pane"></div> : null}
        <div className="bc-inner-layout">
          <div className="bc-col-left">
            {cLogo && <img src={cLogo} alt="Logo" className="bc-logo" />}
            <div className="bc-comp-name">{cCompName}</div>
            <div className="bc-comp-tagline">{cCompTagline}</div>
          </div>
          <div className="bc-divider"></div>
          <div className="bc-col-right">
            <div className="bc-person-info">
              <div className="bc-name">{cName}</div>
              <div className="bc-tagline">{cTagline}</div>
            </div>
            <div className="bc-details">
              <div className="bc-detail-item"><Phone size={10} /> {cPhone}</div>
              <div className="bc-detail-item"><Mail size={10} /> {cEmail}</div>
              <div className="bc-detail-item"><Globe size={10} /> {cWebsite}</div>
              <div className="bc-detail-item"><MapPin size={10} /> {cAddress.substring(0, 30)}{cAddress.length > 30 ? '...' : ''}</div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div style={{ fontFamily: 'var(--font-body)', minHeight: '100vh', background: '#f5f5f5', padding: '20px' }}>
        {/* Action bar — hidden when printing */}
        <div className="rpt-action-bar" style={{ marginBottom: '20px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={() => setShowCardPrintOverlay(false)}
            className="btn btn-secondary"
            style={{ padding: '10px 24px' }}
          >
            ✕ Close
          </button>
          <button
            onClick={() => window.print()}
            className="btn btn-primary"
            style={{ padding: '10px 24px', background: '#1e40af' }}
          >
            <Printer size={16} /> <span style={{ marginLeft: 6 }}>Print / Save PDF</span>
          </button>
        </div>

        {/* Cards — this is the only thing that prints */}
        <div
          className="print-cards-container"
          style={
            printLayout === 'single'
              ? { display: 'flex', justifyContent: 'center' }
              : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxWidth: '800px', margin: '0 auto' }
          }
        >
          {printLayout === 'single'
            ? renderCardContent('single')
            : Array(10).fill().map((_, i) => renderCardContent(i))
          }
        </div>
      </div>
    );
  }


  // ── Step 2: Member Selection Screen ─────────────────────────────────────
  if (orgAuth && showMemberSelect && !isAdmin) {
    const nonAdminMembers = memberList.filter(m => m.role !== 'ADMIN');
    const adminMember = memberList.find(m => m.role === 'ADMIN');
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0b0f19 0%, #1a2744 50%, #0b0f19 100%)', padding: '20px', fontFamily: 'var(--font-body)' }}>
        <div style={{ width: '100%', maxWidth: '520px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <svg width="44" height="44" viewBox="0 0 100 100" fill="none"><rect x="15" y="15" width="70" height="70" rx="20" stroke="#38bdf8" strokeWidth="6" strokeDasharray="10 5" /><path d="M35 50L45 60L65 40" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#f8fafc', margin: '10px 0 4px' }}>Who is logging in?</h2>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{orgName}</p>
          </div>

          {!selectedMemberId ? (
            // ── Member Card Grid ───────────────────────────────────────────
            <div>
              {/* Admin Card */}
              {adminMember && (
                <div style={{ marginBottom: '20px' }}>
                  <button onClick={handleContinueAsAdmin} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '16px', padding: '18px 20px', background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(56,189,248,0.1))', border: '2px solid rgba(16,185,129,0.4)', borderRadius: '14px', cursor: 'pointer', color: '#f8fafc', textAlign: 'left', transition: 'all 0.2s' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #38bdf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', fontWeight: 700, flexShrink: 0 }}>{adminMember.name.charAt(0).toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem' }}>{adminMember.name}</div>
                      <div style={{ fontSize: '0.78rem', color: '#10b981', marginTop: '2px' }}>Admin • Full Access</div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8' }}>Enter PIN →</div>
                  </button>
                </div>
              )}

              {/* Staff Members */}
              {nonAdminMembers.length > 0 && (
                <div>
                  <p style={{ color: '#64748b', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>Staff Members</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' }}>
                    {nonAdminMembers.map(m => (
                      <button key={m.id} onClick={() => { setSelectedMemberId(m.id); setMemberPinInput(''); setMemberPinError(false); }} style={{ padding: '18px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', cursor: 'pointer', color: '#f8fafc', textAlign: 'center', transition: 'all 0.2s' }}>
                        <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, margin: '0 auto 8px' }}>{m.name.charAt(0).toUpperCase()}</div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{m.name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px', textTransform: 'capitalize' }}>{m.role.toLowerCase()}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // ── PIN Entry for selected member ──────────────────────────────
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px', textAlign: 'center' }}>
              {(() => { const m = memberList.find(x => x.id === selectedMemberId); return (
                <div>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: m?.role === 'ADMIN' ? 'linear-gradient(135deg, #10b981, #38bdf8)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', fontWeight: 700, margin: '0 auto 12px', color: 'white' }}>{m?.name.charAt(0).toUpperCase()}</div>
                  <h3 style={{ color: '#f8fafc', fontWeight: 700, marginBottom: '4px' }}>{m?.name}</h3>
                  <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '24px', textTransform: 'capitalize' }}>{m?.role.toLowerCase()}</p>
                </div>
              ); })()}
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: '16px' }}>Enter your 4-digit PIN</p>
              <input
                autoFocus
                type="password"
                maxLength={4}
                placeholder="••••"
                value={memberPinInput}
                onChange={e => { setMemberPinInput(e.target.value.replace(/\D/g, '')); setMemberPinError(false); }}
                onKeyDown={e => e.key === 'Enter' && handleMemberLogin(selectedMemberId)}
                style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${memberPinError ? '#ef4444' : 'rgba(255,255,255,0.12)'}`, borderRadius: '10px', color: '#f8fafc', fontSize: '1.8rem', letterSpacing: '0.6em', textAlign: 'center', outline: 'none', boxSizing: 'border-box' }}
              />
              {memberPinError && <p style={{ color: '#ef4444', fontSize: '0.82rem', marginTop: '8px' }}>Incorrect PIN. Try again.</p>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button onClick={() => { setSelectedMemberId(null); setMemberPinInput(''); setMemberPinError(false); }} style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer', fontWeight: 500 }}>← Back</button>
                <button onClick={() => handleMemberLogin(selectedMemberId)} disabled={memberPinInput.length !== 4 || memberPinLoading} style={{ flex: 2, padding: '12px', background: memberPinInput.length === 4 ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '8px', color: memberPinInput.length === 4 ? 'white' : '#94a3b8', cursor: memberPinInput.length === 4 ? 'pointer' : 'default', fontWeight: 600, fontSize: '0.95rem', transition: 'all 0.2s' }}>
                  {memberPinLoading ? 'Verifying…' : 'Enter →'}
                </button>
              </div>
            </div>
          )}

          <p style={{ textAlign: 'center', marginTop: '24px', color: '#475569', fontSize: '0.78rem' }}>
            Not your business?{' '}
            <button onClick={() => {
              localStorage.removeItem('member_session');
              localStorage.removeItem('org_id');
              localStorage.removeItem('org_name');
              localStorage.removeItem('org_pin');
              localStorage.removeItem('selected_business_id');
              localStorage.removeItem('hisabflow_remember');
              setMemberSession(null);
              setSelectedMemberId(null);
              setMemberPinInput('');
              setMemberPinError(false);
              setOrgAuth('');
              setOrgName('');
              setShowMemberSelect(false);
            }} style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: '0.78rem', textDecoration: 'underline' }}>Switch Organisation</button>
          </p>
        </div>
      </div>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  if (!orgAuth && !isAdmin) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0b0f19 0%, #1a2744 50%, #0b0f19 100%)', padding: '20px', fontFamily: 'var(--font-body)' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          {/* Branding */}
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <svg width="48" height="48" viewBox="0 0 100 100" fill="none"><rect x="15" y="15" width="70" height="70" rx="20" stroke="#38bdf8" strokeWidth="6" strokeDasharray="10 5" /><path d="M35 50L45 60L65 40" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <h1 style={{ fontSize: '2.2rem', fontWeight: 800, color: '#f8fafc', letterSpacing: '-0.03em' }}>Hisab<span style={{ color: '#38bdf8' }}>Flow</span></h1>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.85rem', letterSpacing: '0.08em' }}>{t('brandTagline')}</p>
          </div>

          {/* Tab Switcher — hidden on admin route */}
          {!isAdminRoute && (
          <div style={{ display: 'flex', gap: '6px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '5px', marginBottom: '24px', border: '1px solid rgba(255,255,255,0.08)' }}>
            {[['login', t('shopLogin'), Lock], ['register', t('registerBusiness'), Building]].map(([tab, label, Icon]) => (
              <button key={tab} onClick={() => { setOnboardingTab(tab); setRegSuccess(false); setRegError(''); setOrgLoginError(''); setAdminLoginError(''); }}
                style={{ flex: 1, padding: '10px 6px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', transition: 'all 0.2s', background: onboardingTab === tab ? 'rgba(56,189,248,0.15)' : 'transparent', color: onboardingTab === tab ? '#38bdf8' : '#64748b' }}>
                <Icon size={13} />{label}
              </button>
            ))}
          </div>
          )}

          {/* Panel Card */}
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '16px', padding: '28px', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>

            {/* ---- Shop Login (hidden on admin route) ---- */}
            {!isAdminRoute && onboardingTab === 'login' && !showForgotPin && !showEmailLogin && (
              <form onSubmit={handleOrgLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '50%', background: 'rgba(56,189,248,0.1)', marginBottom: '12px' }}><Unlock size={28} color="#38bdf8" /></div>
                  <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', fontWeight: 700 }}>{t('signInToShop')}</h2>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '4px' }}>{t('loginHint')}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>{t('registeredPhone')}</label>
                  <input required type="tel" placeholder="e.g. 03001234567" value={orgLoginForm.phone} onChange={e => setOrgLoginForm({ ...orgLoginForm, phone: e.target.value })} style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>{t('fourDigitPIN')}</label>
                  <input required type="password" maxLength="4" placeholder="****" value={orgLoginForm.pin_code} onChange={e => setOrgLoginForm({ ...orgLoginForm, pin_code: e.target.value.replace(/\D/g, '') })} style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '1.4rem', letterSpacing: '0.6em', textAlign: 'center', outline: 'none' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.82rem', color: '#94a3b8' }}>
                    <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} style={{ accentColor: '#38bdf8', width: '16px', height: '16px', cursor: 'pointer' }} />
                    {t('rememberMe')}
                  </label>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPin(true); setForgotPinForm({ name: '', phone: '' }); setForgotPinResult(null); setForgotPinError(''); setForgotPinDone(false); setForgotPinResetError(''); setForgotPinResetForm({ code: '', new_pin: '', confirm_pin: '' }); }}
                    style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600, padding: 0, textDecoration: 'underline' }}
                  >
                    🔑 Forgot PIN?
                  </button>
                </div>
                {orgLoginError && <p style={{ color: '#ef4444', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>{orgLoginError}</p>}
                <button type="submit" style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>{t('loginBtn')}</button>
                <button
                  type="button"
                  onClick={() => { setShowEmailLogin(true); setOtpStage('email'); setOtpCode(''); setOtpError(''); setOtpNotice(''); }}
                  style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#38bdf8', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}
                >
                  <Mail size={14} /> {t('signInWithEmail')}
                </button>
                <p style={{ textAlign: 'center', fontSize: '0.78rem', color: '#475569' }}>{t('loginNoAccess')}</p>
              </form>
            )}

            {/* ---- Email OTP Sign-in ---- */}
            {!isAdminRoute && onboardingTab === 'login' && !showForgotPin && showEmailLogin && (
              <form onSubmit={otpStage === 'email' ? handleRequestOtp : handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '50%', background: 'rgba(56,189,248,0.1)', marginBottom: '12px' }}><Mail size={28} color="#38bdf8" /></div>
                  <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', fontWeight: 700 }}>{t('emailSignIn')}</h2>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '4px' }}>
                    {otpStage === 'email' ? t('emailSignInHint') : t('otpSentToLabel', { email: otpSentTo })}
                  </p>
                </div>

                {otpStage === 'email' ? (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>{t('emailAddress')}</label>
                    <input
                      required
                      type="email"
                      autoFocus
                      placeholder="owner@shop.com"
                      value={otpEmail}
                      onChange={e => setOtpEmail(e.target.value)}
                      style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }}
                    />
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>{t('sixDigitCode')}</label>
                    <input
                      required
                      autoFocus
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="000000"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '1.4rem', letterSpacing: '0.6em', textAlign: 'center', outline: 'none' }}
                    />
                    <button
                      type="button"
                      onClick={() => { setOtpStage('email'); setOtpCode(''); setOtpError(''); }}
                      style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600, padding: '6px 0 0', textDecoration: 'underline' }}
                    >
                      {t('changeEmail')}
                    </button>
                  </div>
                )}

                {otpNotice && otpStage === 'code' && (
                  <p style={{ color: '#22c55e', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(34,197,94,0.1)', borderRadius: '6px' }}>{otpNotice}</p>
                )}
                {otpError && (
                  <p style={{ color: '#ef4444', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>{otpError}</p>
                )}

                <button
                  type="submit"
                  disabled={otpLoading || (otpStage === 'email' && otpCooldownActive)}
                  style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: otpLoading ? 'wait' : 'pointer', opacity: otpLoading || (otpStage === 'email' && otpCooldownActive) ? 0.6 : 1 }}
                >
                  {otpStage === 'email'
                    ? (otpCooldownActive ? t('resendIn', { s: otpCooldown }) : (otpLoading ? t('sendingCode') : t('sendCode')))
                    : (otpLoading ? t('verifying') : t('verifyAndSignIn'))}
                </button>

                {otpStage === 'code' && (
                  <button
                    type="button"
                    disabled={otpLoading || otpCooldownActive}
                    onClick={handleRequestOtp}
                    style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: otpCooldownActive ? '#475569' : '#38bdf8', fontSize: '0.85rem', cursor: otpCooldownActive ? 'default' : 'pointer', fontWeight: 600 }}
                  >
                    {otpCooldownActive ? t('resendIn', { s: otpCooldown }) : t('resendCode')}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => { setShowEmailLogin(false); resetOtpFlow(); setOtpEmail(''); }}
                  style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: '#64748b', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}
                >
                  ← {t('usePhoneAndPin')}
                </button>
              </form>
            )}

            {/* ---- Forgot PIN Panel ---- */}
            {!isAdminRoute && onboardingTab === 'login' && showForgotPin && !showEmailLogin && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '50%', background: 'rgba(245,158,11,0.12)', marginBottom: '12px' }}>
                    <span style={{ fontSize: '28px' }}>🔑</span>
                  </div>
                  <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', fontWeight: 700 }}>Recover PIN</h2>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '4px' }}>
                    {forgotPinDone
                      ? 'Your PIN has been reset.'
                      : forgotPinResult
                        ? 'Enter the code we emailed you and choose a new PIN'
                        : 'We will email a reset code to the address saved for your organization'}
                  </p>
                </div>

                {forgotPinDone ? (
                  <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(34,197,94,0.08)', borderRadius: '12px', border: '1px solid rgba(34,197,94,0.25)' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>✅</div>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '12px' }}>
                      PIN reset successfully. You can now sign in with your new PIN.
                    </p>
                    <button
                      onClick={() => {
                        setShowForgotPin(false);
                        setForgotPinResult(null);
                        setForgotPinDone(false);
                        setForgotPinResetForm({ code: '', new_pin: '', confirm_pin: '' });
                        setOrgLoginForm({ ...orgLoginForm, phone: forgotPinForm.phone });
                      }}
                      style={{ width: '100%', padding: '11px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
                    >
                      ← Back to Login
                    </button>
                  </div>
                ) : !forgotPinResult ? (
                  <form onSubmit={handleForgotPin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>Registered Email Address</label>
                      <input
                        type="email"
                        placeholder="e.g. owner@example.com"
                        value={forgotPinForm.email}
                        onChange={e => setForgotPinForm({ ...forgotPinForm, email: e.target.value })}
                        style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>Registered Phone Number</label>
                      <input
                        type="tel"
                        placeholder="e.g. 03001234567"
                        value={forgotPinForm.phone}
                        onChange={e => setForgotPinForm({ ...forgotPinForm, phone: e.target.value })}
                        style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>Organization / Shop Name (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g. Ali General Store"
                        value={forgotPinForm.name}
                        onChange={e => setForgotPinForm({ ...forgotPinForm, name: e.target.value })}
                        style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }}
                      />
                    </div>

                    {forgotPinError && (
                      <p style={{ color: '#ef4444', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
                        ⚠️ {forgotPinError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={forgotPinLoading}
                      style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: forgotPinLoading ? 'wait' : 'pointer', opacity: forgotPinLoading ? 0.7 : 1 }}
                    >
                      {forgotPinLoading ? '⏳ Generating code...' : '📧 Send Reset Code'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowForgotPin(false)}
                      style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.82rem', cursor: 'pointer', padding: '4px' }}
                    >
                      ← Back to Login
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleResetPin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ textAlign: 'center', padding: '14px', background: 'rgba(56,189,248,0.08)', borderRadius: '10px', border: '1px solid rgba(56,189,248,0.2)' }}>
                      <p style={{ color: '#f8fafc', fontSize: '0.88rem', margin: 0, lineHeight: 1.5 }}>{forgotPinResult.message}</p>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>Reset Code (from email)</label>
                      <input
                        required
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="e.g. 482913"
                        value={forgotPinResetForm.code}
                        onChange={e => setForgotPinResetForm({ ...forgotPinResetForm, code: e.target.value.replace(/\D/g, '') })}
                        style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '1.05rem', letterSpacing: '0.3em', fontFamily: 'monospace', outline: 'none' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>New PIN (4 digits)</label>
                      <input
                        required
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="••••"
                        value={forgotPinResetForm.new_pin}
                        onChange={e => setForgotPinResetForm({ ...forgotPinResetForm, new_pin: e.target.value.replace(/\D/g, '') })}
                        style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '1rem', letterSpacing: '0.3em', fontFamily: 'monospace', outline: 'none' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>Confirm New PIN</label>
                      <input
                        required
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="••••"
                        value={forgotPinResetForm.confirm_pin}
                        onChange={e => setForgotPinResetForm({ ...forgotPinResetForm, confirm_pin: e.target.value.replace(/\D/g, '') })}
                        style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '1rem', letterSpacing: '0.3em', fontFamily: 'monospace', outline: 'none' }}
                      />
                    </div>

                    {forgotPinResetError && (
                      <p style={{ color: '#ef4444', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>
                        ⚠️ {forgotPinResetError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={forgotPinLoading}
                      style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: forgotPinLoading ? 'wait' : 'pointer', opacity: forgotPinLoading ? 0.7 : 1 }}
                    >
                      {forgotPinLoading ? '⏳ Resetting...' : '🔑 Reset PIN'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setForgotPinResult(null); setForgotPinResetError(''); setForgotPinResetForm({ code: '', new_pin: '', confirm_pin: '' }); }}
                      style={{ width: '100%', padding: '9px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#64748b', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 500 }}
                    >
                      ← Edit details or resend code
                    </button>
                  </form>
                )}

                <button
                  type="button"
                  onClick={() => { setShowForgotPin(false); setForgotPinResult(null); setForgotPinError(''); setForgotPinDone(false); setForgotPinResetError(''); setForgotPinResetForm({ code: '', new_pin: '', confirm_pin: '' }); }}
                  style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#64748b', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}
                >
                  ← Back to Login
                </button>
              </div>
            )}


            {/* ---- Register Business (hidden on admin route) ---- */}
            {!isAdminRoute && onboardingTab === 'register' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                  <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '50%', background: 'rgba(16,185,129,0.1)', marginBottom: '12px' }}><Building size={28} color="#10b981" /></div>
                  <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', fontWeight: 700 }}>{t('registerTitle')}</h2>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '4px' }}>{t('registerSubtitle')}</p>
                </div>
                {regSuccess ? (
                  <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(16,185,129,0.08)', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <CheckCircle2 size={40} color="#10b981" style={{ marginBottom: '12px' }} />
                    <h3 style={{ color: '#10b981', marginBottom: '8px' }}>{t('regPendingTitle')}</h3>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{t('regPendingMsg', { plan: regForm.subscription_plan === '2_days' ? t('plan2Days') : regForm.subscription_plan === '1_month' ? t('plan1Month') : regForm.subscription_plan === '1_year' ? t('plan1Year') : regForm.subscription_plan === '3_years' ? t('plan3Years') : t('planLifetime') })}</p>
                    <button onClick={() => setOnboardingTab('login')} style={{ marginTop: '16px', padding: '10px 20px', background: 'rgba(56,189,248,0.15)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: '8px', color: '#38bdf8', cursor: 'pointer', fontWeight: 600 }}>{t('goToLogin')}</button>
                  </div>
                ) : (
                  <form onSubmit={handleRegisterOrg} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[['name', t('shopName'), 'Al-Rehman General Store', 'text'], ['owner_name', t('ownerName'), 'Muhammad Ali', 'text'], ['email', t('contactEmail'), 'owner@shop.com', 'email'], ['phone', t('mobilePhone'), '03001234567', 'tel']].map(([field, label, ph, type]) => (
                      <div key={field}>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', marginBottom: '5px' }}>{label}</label>
                        <input required type={type} placeholder={ph} value={regForm[field]} onChange={e => setRegForm({ ...regForm, [field]: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.88rem', outline: 'none' }} />
                      </div>
                    ))}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', marginBottom: '5px' }}>{t('shopAddress')}</label>
                      <input type="text" placeholder="Block 4, Sector G-11, Islamabad" value={regForm.address} onChange={e => setRegForm({ ...regForm, address: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.88rem', outline: 'none' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', marginBottom: '5px' }}>{t('subscriptionPlan')}</label>
                      <select value={regForm.subscription_plan} onChange={e => setRegForm({ ...regForm, subscription_plan: e.target.value })} style={{ width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.88rem', outline: 'none' }}>
                        <option value="2_days" style={{ background: '#1e293b', color: '#f8fafc' }}>{t('plan2Days')}</option>
                        <option value="1_month" style={{ background: '#1e293b', color: '#f8fafc' }}>{t('plan1Month')}</option>
                        <option value="1_year" style={{ background: '#1e293b', color: '#f8fafc' }}>{t('plan1Year')}</option>
                        <option value="3_years" style={{ background: '#1e293b', color: '#f8fafc' }}>{t('plan3Years')}</option>
                        <option value="lifetime" style={{ background: '#1e293b', color: '#f8fafc' }}>{t('planLifetime')}</option>
                      </select>
                    </div>
                    {regError && <p style={{ color: '#ef4444', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>{regError}</p>}
                    <button type="submit" style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer' }}>{t('submitRegistration')}</button>
                  </form>
                )}
              </div>
            )}

            {/* ---- Admin Login (only accessible via /admin-panel URL) ---- */}
            {isAdminRoute && !isAdmin && (
              <form onSubmit={handleAdminLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'inline-flex', padding: '14px', borderRadius: '50%', background: 'rgba(99,102,241,0.1)', marginBottom: '12px' }}><Shield size={28} color="#6366f1" /></div>
                  <h2 style={{ color: '#f8fafc', fontSize: '1.3rem', fontWeight: 700 }}>{t('adminPortal')}</h2>
                  <p style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '4px' }}>{t('adminSubtitle')}</p>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>{t('adminUsername')}</label>
                  <input required type="text" placeholder="admin" value={adminForm.username} onChange={e => setAdminForm({ ...adminForm, username: e.target.value })} style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '6px' }}>{t('adminPassword')}</label>
                  <input required type="password" placeholder="********" value={adminForm.password} onChange={e => setAdminForm({ ...adminForm, password: e.target.value })} style={{ width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#f8fafc', fontSize: '0.9rem', outline: 'none' }} />
                </div>
                {adminLoginError && <p style={{ color: '#ef4444', fontSize: '0.82rem', padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: '6px' }}>{adminLoginError}</p>}
                <button type="submit" style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>{t('accessAdmin')}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 0b. Admin Dashboard -- shown when admin is authenticated
  if (isAdmin) {
    const fmtBytes = (b) => b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : b > 1024 ? `${(b/1024).toFixed(1)} KB` : `${b} B`;
    const pending = adminOrgs.filter(o => o.status === 'PENDING');
    const active = adminOrgs.filter(o => o.status === 'APPROVED');
    const others = adminOrgs.filter(o => !['PENDING','APPROVED'].includes(o.status));
    const statusColor = { PENDING: '#f59e0b', APPROVED: '#10b981', REJECTED: '#ef4444', DEACTIVATED: '#64748b' };
    return (
      <main id="app-main" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0b0f19 0%, #0f172a 100%)', color: '#f8fafc', fontFamily: 'var(--font-body)' }}>
        {/* Admin Header */}
        <header style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="32" height="32" viewBox="0 0 100 100" fill="none"><rect x="15" y="15" width="70" height="70" rx="20" stroke="#38bdf8" strokeWidth="6" strokeDasharray="10 5" /><path d="M35 50L45 60L65 40" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <div><div style={{ fontWeight: 800, fontSize: '1.1rem' }}>HisabFlow <span style={{ color: '#6366f1' }}>Admin</span></div><div style={{ fontSize: '0.72rem', color: '#475569' }}>{t('adminBusinessTitle')}</div></div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={fetchAdminData} style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem' }}><RefreshCw size={14} />{t('refresh')}</button>
            <button onClick={() => { setAdminToken(null); setIsAdmin(false); window.history.pushState({}, '', '/'); setOnboardingTab('login'); }} style={{ padding: '8px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem' }}><LogOut size={14} />{t('logOutAdmin')}</button>
          </div>
        </header>

        <div style={{ padding: '32px', maxWidth: '1300px', margin: '0 auto' }}>
          {/* Stats Row */}
          {adminStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              {[[t('totalRegistered'), adminStats.total_organizations, '#38bdf8', Database],
                [t('pendingApproval'), adminStats.pending_registrations, '#f59e0b', Clock],
                [t('activeShops'), adminStats.active_organizations, '#10b981', CheckCircle2],
                [t('deactivated'), adminStats.deactivated_organizations, '#64748b', XCircle],
                [t('backupsLogged'), adminStats.backups_logged, '#8b5cf6', HardDrive],
                [t('dbSize'), fmtBytes(adminStats.database_size_bytes || 0), '#06b6d4', Activity]
              ].map(([label, val, color, Icon]) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}><Icon size={16} color={color} /><span style={{ fontSize: '0.78rem', color: '#64748b' }}>{label}</span></div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tab Nav */}
          <div style={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '4px', marginBottom: '24px', width: 'fit-content', border: '1px solid rgba(255,255,255,0.06)' }}>
            {[[t('pendingRequests'), 'requests', pending.length], [t('renewalRequests'), 'renewals', adminOrgs.filter(o => o.renewal_requested).length], [t('allOrganizations'), 'tenants', adminOrgs.length], [t('backups'), 'backups', null]].map(([label, tab, cnt]) => (
              <button key={tab} onClick={() => setAdminTab(tab)} style={{ padding: '9px 18px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600, background: adminTab === tab ? 'rgba(99,102,241,0.2)' : 'transparent', color: adminTab === tab ? '#a5b4fc' : '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {label}{cnt != null && <span style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '10px', padding: '1px 7px', fontSize: '0.72rem' }}>{cnt}</span>}
              </button>
            ))}
          </div>

          {/* Pending Requests */}
          {adminTab === 'requests' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {pending.length === 0 && <div style={{ textAlign: 'center', padding: '48px', color: '#475569' }}><CheckCircle2 size={40} style={{ marginBottom: '12px', opacity: 0.4 }} /><p>{t('noPendingReqs')}</p></div>}
              {pending.map(org => (
                <div key={org.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{org.name}</span>
                      <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '10px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600 }}>{t('pendingBadge')}</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                      <span><strong>{t('owner')}:</strong> {org.owner_name}</span>
                      <span><strong>{t('phone')}:</strong> {org.phone}</span>
                      <span><strong>{t('email')}:</strong> {org.email}</span>
                    </div>
                    {org.address && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>{org.address}</div>}
                    <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: '6px' }}>{t('submitted')}: {new Date(org.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
                    <button onClick={() => setApproveModal({ id: org.id, name: org.name, pin: '' })} style={{ padding: '9px 16px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={14} />{t('approve')}</button>
                    <button onClick={() => handleAdminAction(org.id, 'reject')} style={{ padding: '9px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}><X size={14} />{t('reject')}</button>
                    <a
                      href={`https://wa.me/${org.phone || ''}?text=${encodeURIComponent(`Dear ${org.owner_name},\n\nYour registration for "${org.name}" has been ${new Date() > new Date() ? 'reviewed' : 'received'}.\n\nPlease wait for admin approval.\n\nThank you!\nHisabFlow Admin`)}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ padding: '9px 12px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '8px', color: '#25d366', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
                      title={t('sendWhatsApp')}
                    >
                      <Share2 size={14} /> WA
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Renewal Requests */}
          {adminTab === 'renewals' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {adminOrgs.filter(o => o.renewal_requested).length === 0 && <div style={{ textAlign: 'center', padding: '48px', color: '#475569' }}><RefreshCw size={40} style={{ marginBottom: '12px', opacity: 0.4 }} /><p>{t('noRenewalReqs')}</p></div>}
              {adminOrgs.filter(o => o.renewal_requested).map(org => (
                <div key={org.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '20px', border: '1px solid rgba(99,102,241,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{org.name}</span>
                      <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '10px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', fontWeight: 600 }}>{t('renewalBadge')}</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#94a3b8', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                      <span><strong>{t('owner')}:</strong> {org.owner_name}</span>
                      <span><strong>{t('phone')}:</strong> {org.phone}</span>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '4px' }}>
                      {t('currentPlan')}: <strong>{org.subscription_plan === '2_days' ? t('plan2Days') : org.subscription_plan === '1_month' ? t('plan1Month') : org.subscription_plan === '1_year' ? t('plan1Year') : org.subscription_plan === '3_years' ? t('plan3Years') : t('planLifetime')}</strong> &middot;
                      {t('ended')}: <strong>{org.subscription_end_date ? new Date(org.subscription_end_date).toLocaleDateString() : t('none')}</strong>
                      {org.renewal_requested_plan && <span> &middot; {t('requested')}: <strong style={{ color: '#a5b4fc' }}>{org.renewal_requested_plan === '2_days' ? t('plan2Days') : org.renewal_requested_plan === '1_month' ? t('plan1Month') : org.renewal_requested_plan === '1_year' ? t('plan1Year') : org.renewal_requested_plan === '3_years' ? t('plan3Years') : t('planLifetime')}</strong></span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
                    <button onClick={() => handleAdminAction(org.id, 'renew_approve')} style={{ padding: '9px 16px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={14} />{t('approveRenewal')}</button>
                    <button onClick={() => handleAdminAction(org.id, 'renew_reject')} style={{ padding: '9px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}><X size={14} />{t('reject')}</button>
                    <a
                      href={`https://wa.me/${org.phone || ''}?text=${encodeURIComponent(`Dear ${org.owner_name},\n\nYour subscription renewal for "${org.name}" has been processed.\n\nRequested Plan: ${org.renewal_requested_plan === '2_days' ? '2 Days Trial' : org.renewal_requested_plan === '1_month' ? '1 Month' : org.renewal_requested_plan === '1_year' ? '1 Year' : org.renewal_requested_plan === '3_years' ? '3 Years' : 'Lifetime'}\n\nPlease contact admin for details.\n\nThank you!\nHisabFlow Admin`)}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ padding: '9px 12px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '8px', color: '#25d366', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
                      title={t('sendWhatsApp')}
                    >
                      <Share2 size={14} /> WA
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* All Tenants */}
          {adminTab === 'tenants' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '12px', color: '#64748b' }} />
                <input
                  type="text"
                  placeholder={t('searchOrgs')}
                  value={adminOrgSearch}
                  onChange={(e) => setAdminOrgSearch(e.target.value)}
                  style={{ width: '100%', padding: '10px 10px 10px 38px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', background: 'rgba(255,255,255,0.04)', color: '#e2e8f0', boxSizing: 'border-box' }}
                />
              </div>
              {(() => {
                const filteredOrgs = adminOrgs.filter(org =>
                  !adminOrgSearch ||
                  org.name?.toLowerCase().includes(adminOrgSearch.toLowerCase()) ||
                  org.phone?.includes(adminOrgSearch) ||
                  org.owner_name?.toLowerCase().includes(adminOrgSearch.toLowerCase())
                );
                return filteredOrgs.length === 0
                  ? <div style={{ textAlign: 'center', padding: '48px', color: '#475569' }}>{adminOrgSearch ? t('noResults') : t('noOrgs')}</div>
                  : filteredOrgs.map(org => (
                <div key={org.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '12px', padding: '18px 22px', border: `1px solid rgba(255,255,255,0.06)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', align: 'center', gap: '14px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: statusColor[org.status] || '#475569', marginTop: '5px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700 }}>{org.name} <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 400 }}>#{org.phone}</span></div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{org.owner_name} * PIN: <strong style={{ color: org.pin_set ? '#10b981' : '#475569' }}>{org.pin_set ? 'set' : 'not set'}</strong> * <span style={{ color: statusColor[org.status] }}>{org.status}</span>{org.subscription_plan && <span> * Plan: <strong>{org.subscription_plan === '2_days' ? '2D' : org.subscription_plan === '1_month' ? '1M' : org.subscription_plan === '1_year' ? '1Y' : org.subscription_plan === '3_years' ? '3Y' : 'LT'}</strong></span>}{org.subscription_end_date && <span> * Ends: <strong>{new Date(org.subscription_end_date).toLocaleDateString()}</strong></span>}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {org.status === 'APPROVED' && <button onClick={() => handleAdminAction(org.id, 'deactivate')} style={{ padding: '7px 12px', background: 'rgba(100,116,139,0.15)', border: '1px solid rgba(100,116,139,0.3)', borderRadius: '7px', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem' }}>{t('deactivate')}</button>}
                    {org.status === 'DEACTIVATED' && <button onClick={() => handleAdminAction(org.id, 'activate')} style={{ padding: '7px 12px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '7px', color: '#10b981', cursor: 'pointer', fontSize: '0.8rem' }}>{t('activate')}</button>}
                    {org.status === 'PENDING' && <button onClick={() => setApproveModal({ id: org.id, name: org.name, pin: '' })} style={{ padding: '7px 12px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '7px', color: '#10b981', cursor: 'pointer', fontSize: '0.8rem' }}>{t('approve')}</button>}
                    {org.status === 'REJECTED' && <button onClick={() => setApproveModal({ id: org.id, name: org.name, pin: '' })} style={{ padding: '7px 12px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '7px', color: '#10b981', cursor: 'pointer', fontSize: '0.8rem' }}>{t('reApprove')}</button>}
                    {(org.status === 'DEACTIVATED' || org.status === 'REJECTED') && (
                      <button onClick={() => setDeleteOrgModal({ id: org.id, name: org.name })} style={{ padding: '7px 12px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '7px', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                    <button onClick={() => { setAdminTab('backups'); handleFetchBackups(org); }} style={{ padding: '7px 12px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '7px', color: '#a5b4fc', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}><HardDrive size={12} />Backups</button>
                  </div>
                </div>
              ));
              })()
              }
            </div>
          )}

          {/* Backups Tab */}
          {adminTab === 'backups' && (
            <div>
              {!selectedAdminOrg ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#475569' }}>
                  <HardDrive size={40} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p>Select an organization from <strong>All Organizations</strong> tab to manage its backups.</p>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <button onClick={() => setSelectedAdminOrg(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.82rem', marginBottom: '8px' }}><ArrowLeft size={14} />All orgs</button>
                      <h3 style={{ fontWeight: 700, fontSize: '1.1rem' }}>Backups -- {selectedAdminOrg.name}</h3>
                      <p style={{ color: '#64748b', fontSize: '0.8rem' }}>Individual database backups stored per organization</p>
                    </div>
                    <button onClick={handleRunBackup} style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '9px', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.88rem' }}><HardDrive size={15} />Run Manual Backup</button>
                  </div>
                  {orgBackups.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', color: '#475569' }}>No backups yet. Click "Run Manual Backup" to create the first one.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {orgBackups.map(b => (
                        <div key={b.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '16px 20px', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{b.backup_type} Backup</div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{new Date(b.created_at).toLocaleString()} * {fmtBytes(b.file_size)}</div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => handleDownloadBackup(b.id)} style={{ padding: '7px 14px', background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '7px', color: '#38bdf8', fontWeight: 600, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><Download size={13} />{t('download')}</button>
                            <button onClick={() => handleRestoreBackup(b.id)} style={{ padding: '7px 14px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '7px', color: '#f59e0b', fontWeight: 600, fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}><RefreshCw size={13} />{t('restore')}</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Approve Modal */}
        {approveModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
            <div style={{ background: '#1e293b', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '400px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontWeight: 700 }}>Approve: {approveModal.name}</h3>
                <button onClick={() => setApproveModal(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '20px' }}>Assign a 4-digit PIN code for this organization. The shop owner will use this PIN with their registered phone to access the app.</p>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#94a3b8', marginBottom: '8px' }}>{t('pinRequired')}</label>
              <input
                type="text" maxLength="4" placeholder="e.g. 7890"
                value={approveModal.pin}
                onChange={e => setApproveModal({ ...approveModal, pin: e.target.value.replace(/\D/g, '') })}
                style={{ width: '100%', padding: '14px', background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(56,189,248,0.3)', borderRadius: '10px', color: '#f8fafc', fontSize: '1.8rem', letterSpacing: '0.8em', textAlign: 'center', outline: 'none', marginBottom: '20px' }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setApproveModal(null)} style={{ flex: 1, padding: '11px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer', fontWeight: 600 }}>{t('cancel')}</button>
                <button onClick={handleApproveOrg} disabled={approveModal.pin.length !== 4} style={{ flex: 1, padding: '11px', background: approveModal.pin.length === 4 ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '8px', color: approveModal.pin.length === 4 ? '#fff' : '#475569', cursor: approveModal.pin.length === 4 ? 'pointer' : 'not-allowed', fontWeight: 700 }}>{t('approveCreateDB')}</button>
              </div>
            </div>
            </div>
          )}

        {/* Delete Org Confirmation Modal */}
        {deleteOrgModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
            <div style={{ background: '#1e293b', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '420px', border: '1px solid rgba(239,68,68,0.25)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Trash2 size={18} color="#ef4444" />
                  </div>
                  <h3 style={{ fontWeight: 700, color: '#f8fafc' }}>Delete Organization</h3>
                </div>
                <button onClick={() => setDeleteOrgModal(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={20} /></button>
              </div>
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
                <p style={{ color: '#fca5a5', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>⚠️ This action is permanent and cannot be undone.</p>
                <p style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: 1.5 }}>
                  Deleting <strong style={{ color: '#f8fafc' }}>{deleteOrgModal.name}</strong> will permanently remove:
                </p>
                <ul style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: '8px', paddingLeft: '18px', lineHeight: 1.8 }}>
                  <li>The organization registration record</li>
                  <li>All associated backup files</li>
                  <li>The organization's database file (all data)</li>
                </ul>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setDeleteOrgModal(null)} style={{ flex: 1, padding: '11px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#94a3b8', cursor: 'pointer', fontWeight: 600 }}>{t('cancel') || 'Cancel'}</button>
                <button onClick={handleDeleteOrg} style={{ flex: 1, padding: '11px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Trash2 size={14} /> Permanently Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    );
  }

  // 1. Splash Screen Render
  if (showSplash) {
    return (
      <main id="app-main" style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0b0f19 0%, #1e293b 100%)',
        color: '#f8fafc'
      }}>
        <div className="glow-glow" style={{ textAlign: 'center' }}>
          <svg width="120" height="120" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="15" width="70" height="70" rx="20" stroke="#38bdf8" strokeWidth="6" strokeDasharray="10 5" />
            <path d="M35 50L45 60L65 40" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', fontWeight: 800, marginTop: '20px', letterSpacing: '-0.05em' }}>
            Hisab<span style={{ color: '#38bdf8' }}>Flow</span>
          </h1>
          {/* Was #64748b — 4.0:1 against the #1e293b end of the gradient,
              under the 4.5:1 WCAG AA minimum. #94a3b8 is 6.1:1. */}
          <p style={{ color: '#94a3b8', marginTop: '10px', fontSize: '1rem', letterSpacing: '0.1em' }}>{t('brandTagline')}</p>
        </div>
        <div style={{ width: '200px', height: '4px', background: '#334155', borderRadius: '2px', marginTop: '40px', overflow: 'hidden' }}>
          <div className="skeleton-shimmer" style={{ width: '100%', height: '100%' }}></div>
        </div>
      </main>
    );
  }

  // 1.5 Subscription Expired Screen
  if (showSubscriptionExpired) {
    const endDate = localStorage.getItem('subscription_end_date');
    return (
      <main id="app-main" style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary)',
        padding: '20px'
      }}>
        <div className="glass-panel anim-scale-in" style={{ padding: '40px', width: '100%', maxWidth: '440px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', padding: '16px', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', color: 'var(--brand-danger)', marginBottom: '24px' }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <h2 style={{ fontWeight: 700, marginBottom: '8px' }}>{t('subExpired')}</h2>
          <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            {t('subExpiredMsg', { name: expiredOrgName, date: endDate })}
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '24px' }}>
            {t('subExpiredHint')}
          </p>

          {renewalMessage && (
            <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '16px', background: renewalMessage.includes('sent') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: renewalMessage.includes('sent') ? 'var(--brand-success)' : 'var(--brand-danger)', border: `1px solid ${renewalMessage.includes('sent') ? 'var(--brand-success)' : 'var(--brand-danger)'}` }}>
              {renewalMessage}
            </div>
          )}

          {renewalAlreadyRequested ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--brand-warning)', padding: '12px', background: 'rgba(245,158,11,0.1)', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.2)' }}>
              {t('subExpiredPending')}
            </p>
          ) : (
            <button onClick={() => setShowRenewalPlanModal(true)} className="btn btn-primary" style={{ padding: '12px 24px', fontSize: '0.95rem', fontWeight: 600 }}>
              {t('requestRenewal')}
            </button>
          )}

          <div style={{ marginTop: '24px' }}>
            <button onClick={() => { setShowSubscriptionExpired(false); setRenewalMessage(''); localStorage.removeItem('org_id'); localStorage.removeItem('org_name'); localStorage.removeItem('org_pin'); localStorage.removeItem('selected_business_id'); localStorage.removeItem('hisabflow_remember'); setOrgAuth(''); setOrgName(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
              {t('backToLogin')}
            </button>
          </div>
        </div>
      </main>
    );
  }

  // 2. Lock screen
  if (isLocked) {
    return (
      <main id="app-main" style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary)',
        padding: '20px'
      }}>
        <div className="glass-panel anim-scale-in" style={{ padding: '40px', width: '100%', maxWidth: '400px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', padding: '16px', borderRadius: '50%', background: 'rgba(56,189,248,0.1)', color: 'var(--accent-primary)', marginBottom: '24px' }}>
            <ShieldCheck size={40} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 800, marginBottom: '8px' }}>{t('securityLock')}</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem' }}>{t('lockHint')}</p>
          
          <form onSubmit={handlePinSubmit}>
            <input
              type="password"
              maxLength="4"
              placeholder="****"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '2rem',
                letterSpacing: '0.5em',
                textAlign: 'center',
                border: '2px solid var(--border-color)',
                borderRadius: '10px',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-primary)',
                outline: 'none',
                marginBottom: '10px'
              }}
            />
          {pinError && <p style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', marginBottom: '15px' }}>{t('incorrectPin')}</p>}
            <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px' }} disabled={pinVerifying}>{pinVerifying ? t('verifying') : t('unlockBtn')}</button>
          </form>

          {orgName && (
            <div style={{ marginTop: '20px', padding: '12px 16px', background: 'rgba(56,189,248,0.08)', borderRadius: '8px', border: '1px solid rgba(56,189,248,0.15)' }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t('loggedInAs')}</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--accent-primary)' }}>{orgName}</div>
            </div>
          )}
          <button onClick={handleLogoutBusiness} style={{ marginTop: '14px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.78rem', textDecoration: 'underline' }}>{t('switchOrg')}</button>
        </div>
      </main>
    );
  }

  return (
    <>
      {/* Main Layout */}
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-primary)', position: 'relative' }}>
      {/* Toast Alert Notification */}
      {statusMessage && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 24px',
          background: statusMessage.type === 'success' ? 'var(--brand-success)' : statusMessage.type === 'warning' ? 'var(--brand-warning)' : 'var(--accent-primary)',
          color: '#ffffff',
          borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--shadow-lg)',
          fontWeight: 500
        }} className="anim-slide-up">
          <Check size={20} />
          <span>{statusMessage.message}</span>
        </div>
      )}

      {/* LEFT SIDEBAR NAVIGATION */}
      <aside className={`app-sidebar ${showSidebar ? 'open' : ''}`} style={{
        width: '260px',
        minWidth: '260px',
        background: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border-color)',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
        overflowY: 'auto',
        padding: '24px 16px'
      }}>
        {/* Logo and Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px', paddingLeft: '8px' }}>
          <div style={{ width: '36px', height: '36px', background: 'var(--accent-primary)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <FileCheck size={20} />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 800 }}>
            Hisab<span style={{ color: 'var(--brand-info)' }}>Flow</span>
          </h2>
        </div>

        {/* Selected Business Profile Summary */}
        {selectedBusiness && (
          <div style={{
            background: 'var(--bg-tertiary)',
            padding: '12px',
            borderRadius: 'var(--radius-sm)',
            marginBottom: '24px',
            border: '1px solid var(--border-color)',
            position: 'relative'
          }}>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('activeShop')}</p>
            <h4 style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-primary)', marginTop: '2px' }}>{selectedBusiness.name}</h4>
            <button 
              onClick={() => {
                setBusinessForm({
                  name: selectedBusiness.name || '',
                  address: selectedBusiness.address || '',
                  phone: selectedBusiness.phone || '',
                  email: selectedBusiness.email || '',
                  currency: selectedBusiness.currency || 'PKR',
                  language: selectedBusiness.language || 'en',
                  logo: selectedBusiness.logo || ''
                });
                setShowEditBusinessModal(true);
              }} 
              disabled={!canEdit}
              style={{ position: 'absolute', right: '10px', top: '12px', border: 'none', background: 'none', cursor: canEdit ? 'pointer' : 'not-allowed', color: 'var(--brand-info)', opacity: canEdit ? 1 : 0.4 }}
              title={t('editCompanyInfo')}
            >
              <Settings size={14} />
            </button>
          </div>
        )}

        {/* Tabs Lists */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, overflowY: 'auto' }}>
          {[
            { id: 'dashboard',      label: t('dashboard'),      icon: Wallet,      perm: 'reports' },
            { id: 'stock',          label: t('stockInventory'), icon: Package,     perm: 'stock' },
            { id: 'customers',      label: t('customers'),      icon: Users,       perm: 'ledger' },
            { id: 'suppliers',      label: t('suppliers'),      icon: Building,    perm: 'ledger' },
            { id: 'cash',           label: t('cashBook'),       icon: Wallet,      perm: 'cash' },
            { id: 'bank',           label: t('bank'),           icon: Wallet,      perm: 'cash' },
            { id: 'income_expense', label: t('incomeExpense'),  icon: DollarSign,  perm: 'income_expense' },
            { id: 'billing',        label: t('billBook'),       icon: FileText,    perm: 'invoices' },
            { id: 'staff',          label: t('staffSalaries'),  icon: UserCheck,   perm: 'staff' },
            { id: 'settings',       label: t('settings'),       icon: Settings,    perm: 'settings' },
            { id: 'recyclebin',     label: t('recycleBin'),     icon: Trash2,       perm: 'can_delete' },
            // Manage Users — only visible to admins
            ...(isAdminMember ? [
              { id: 'manage_users', label: t('manageUsers') || 'Manage Users', icon: Shield, perm: 'manage_users' },
              { id: 'activity_log', label: t('activityLog') || 'Activity Log', icon: Activity, perm: 'manage_users' }
            ] : []),
          ].filter(tab => tab.perm === null || hasAccess(tab.perm)).map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedParty(null); setShowSidebar(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: active ? 'var(--accent-primary)' : 'transparent',
                  color: active ? '#ffffff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: 500,
                  textAlign: 'left',
                  transition: 'var(--transition-smooth)'
                }}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Member Session Info */}
        {orgAuth && (
          <div style={{ marginBottom: '8px', padding: '10px 12px', background: 'rgba(99,102,241,0.08)', borderRadius: '8px', border: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: (currentMemberSession?.role || 'ADMIN') === 'ADMIN' ? 'linear-gradient(135deg,#10b981,#38bdf8)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>{(currentMemberSession?.member_name || 'A')?.charAt(0).toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentMemberSession?.member_name || 'Admin'}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{(currentMemberSession?.role || 'ADMIN').toLowerCase()}</div>
            </div>
            <button onClick={handleMemberLogout} title="Switch User" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px' }}><RefreshCw size={13} /></button>
          </div>
        )}
        {orgName && (
          <div style={{ marginBottom: '10px', padding: '10px 12px', background: 'rgba(56,189,248,0.07)', borderRadius: '8px', border: '1px solid rgba(56,189,248,0.12)' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: '2px' }}>{t('activeOrg')}</div>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--accent-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orgName}</div>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '16px', marginTop: orgName ? '0' : 'auto', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={toggleTheme} className="btn btn-secondary" style={{ flex: 1, padding: '10px' }} title={t('toggleTheme')}>
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button onClick={() => { localStorage.removeItem('hisabflow_remember'); setIsLocked(true); }} className="btn btn-secondary" style={{ flex: 1, padding: '10px' }} title={t('lockSession')}>
              <Lock size={18} />
            </button>
          </div>
          <button onClick={() => setLang(lang === 'en' ? 'ur' : 'en')} className="btn btn-secondary" style={{ width: '100%', padding: '8px', fontSize: '0.78rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }} title={t('toggleLanguage')}>
            <Globe size={14} /> {lang === 'en' ? 'اردو' : 'English'}
          </button>
          <button onClick={handleLogoutBusiness} className="btn btn-secondary" style={{ width: '100%', padding: '8px', fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }} title={t('logOut')}>
            <LogOut size={14} /> {t('logOut')}
          </button>
        </div>

      </aside>

      {/* MAIN CONTAINER */}
      <main className="app-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', height: '100vh', position: 'relative' }}>
        
        {/* Mobile Sidebar Overlay */}
        {showSidebar && (
          <div 
            onClick={() => setShowSidebar(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999 }}
            className="mobile-overlay"
          />
        )}

        {/* Universal Print Header (Visible only on Print) */}
        <div className="print-only-header">
          {selectedBusiness?.logo && <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" className="print-logo" />}
          <h1 style={{ fontWeight: 800 }}>{selectedBusiness?.name || t('yourCompany')}</h1>
          <p style={{ color: '#555', marginTop: '4px' }}>{selectedBusiness?.address || t('companyAddress')}</p>
          <p style={{ color: '#555' }}>Email: {selectedBusiness?.email || 'N/A'} | Phone: {selectedBusiness?.phone || 'N/A'}</p>
        </div>

        {/* TOP HEADER */}
        <header style={{
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border-color)',
          padding: '16px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              className="btn btn-secondary mobile-menu-btn" 
              onClick={() => setShowSidebar(!showSidebar)}
              style={{ padding: '8px', border: 'none' }}
            >
              <Menu size={20} />
            </button>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 700, textTransform: 'capitalize' }}>
              {(activeTab === 'customers' || activeTab === 'suppliers') && selectedParty ? `${selectedParty.name} ${t('ledgerStatement')}` : { dashboard: t('dashboard'), customers: t('customers'), suppliers: t('suppliers'), cash: t('cashBook'), income_expense: t('incomeExpense'), billing: t('billBook'), stock: t('stockInventory'), staff: t('staffSalaries'), settings: t('settings'), recyclebin: t('recycleBin') || 'Recycle Bin', manage_users: t('manageUsers') || 'Manage Users', activity_log: t('activityLog') || 'Activity Log' }[activeTab] || activeTab}
            </h1>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{formatLiveTime(liveTime)}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{formatLiveDate(liveTime)}</div>
            </div>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={16} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>

        </header>
        <div style={{ padding: '32px', flex: 1, maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
          
          {/* TAB: DASHBOARD */}
          {activeTab === 'dashboard' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('dashTitle')}</h3>
                <button onClick={() => triggerPrintReport('dashboard', {})} className="btn btn-secondary" title={t('printBusinessOverview')}>
                  <Printer size={14} /><span>{t('printReport')}</span>
                </button>
              </div>
              {/* Top Row Cards */}
              <div className="grid-3">
                <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('customerReceivables')}</span>
                    <span className="chip chip-success">{t('youGet')}</span>
                  </div>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-success)' }}>
                    Rs. {totalGive.toLocaleString()}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('fromCustomerTabs')}</p>
                </div>

                <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('supplierPayables')}</span>
                    <span className="chip chip-danger">{t('youGive')}</span>
                  </div>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--brand-danger)' }}>
                    Rs. {totalGot.toLocaleString()}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('fromSupplierTabs')}</p>
                </div>

                <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{t('cashBalance')}</span>
                    <span className="chip chip-info">{t('inSafe')}</span>
                  </div>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700, color: cashBalance >= 0 ? 'var(--text-primary)' : 'var(--brand-danger)' }}>
                    Rs. {cashBalance.toLocaleString()}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('cashFlow')}</p>
                </div>

                <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', borderLeft: '3px solid #8b5cf6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: '#8b5cf6', fontWeight: 500 }}>{t('walkInSales')}</span>
                    <span className="chip" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}>Cash</span>
                  </div>
                  <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#8b5cf6' }}>
                    Rs. {globalWalkInSalesTotal.toLocaleString()}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Direct / Walk-in</p>
                </div>
              </div>

              {/* Alert widgets */}
              {lowStockCount > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  background: 'rgba(245,158,11,0.1)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  padding: '16px 24px',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--brand-warning)'
                }}>
                  <AlertTriangle size={24} />
                  <div>
                    <h4 style={{ fontWeight: 600, fontSize: '0.95rem' }}>{t('lowStock')}</h4>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {t('lowStockMsg', { count: lowStockCount })}
                    </p>
                  </div>
                  <button onClick={() => setActiveTab('stock')} className="btn btn-secondary" style={{ marginLeft: 'auto', background: 'var(--bg-secondary)', padding: '6px 12px', fontSize: '0.8rem' }}>
                    {t('viewStock')}
                  </button>
                </div>
              )}

              {/* Quick Action Hub */}
              <div className="glass-panel" style={{ padding: '24px' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '16px' }}>{t('quickOps')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <button onClick={() => { resetNewParty('CUSTOMER'); setShowAddPartyModal(true); }} className="btn btn-secondary" style={{ padding: '16px', flexDirection: 'column', gap: '8px' }}>
                    <Plus size={20} />
                    <span>{t('newCustomerSupplier')}</span>
                  </button>
                  <button onClick={() => setActiveTab('billing')} className="btn btn-secondary" style={{ padding: '16px', flexDirection: 'column', gap: '8px' }}>
                    <FileText size={20} />
                    <span>{t('createInvoice')}</span>
                  </button>
                  <button onClick={() => setActiveTab('cash')} className="btn btn-secondary" style={{ padding: '16px', flexDirection: 'column', gap: '8px' }}>
                    <Wallet size={20} />
<span>{t('logCashEntry')}</span>
                  </button>
                  <button disabled={!canEdit} onClick={() => { setIeForm({ type: 'EXPENSE', amount: '', category: 'Rent', description: '', date: new Date().toISOString().split('T')[0], time: currentTime(), attachment: null }); setShowAddIeModal(true); }} className="btn btn-secondary" style={{ padding: '16px', flexDirection: 'column', gap: '8px' }}>
                    <DollarSign size={20} />
                    <span>{t('addExpenseIncome')}</span>
                  </button>
                </div>
              </div>

              {/* Dashboard Charts Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Bar Chart: Income vs Expense */}
                <div className="glass-panel" style={{ padding: '24px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>{t('incomeVsExpense')}</h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '20px' }}>{t('allTimeComparison')}</p>
                  {(() => {
                    const totalInc = incomeExpenses.filter(i => !i.is_deleted && i.type === 'INCOME').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
                    const totalExp = incomeExpenses.filter(i => !i.is_deleted && i.type === 'EXPENSE').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
                    const max = Math.max(totalInc, totalExp, 1);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-success)' }}>{t('income')}</span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Rs. {totalInc.toLocaleString()}</span>
                          </div>
                          <div style={{ background: 'var(--bg-tertiary)', borderRadius: '6px', height: '12px', overflow: 'hidden' }}>
                            <div style={{ width: `${(totalInc / max) * 100}%`, background: 'var(--brand-success)', height: '100%', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                          </div>
                        </div>
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--brand-danger)' }}>{t('expense')}</span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 700 }}>Rs. {totalExp.toLocaleString()}</span>
                          </div>
                          <div style={{ background: 'var(--bg-tertiary)', borderRadius: '6px', height: '12px', overflow: 'hidden' }}>
                            <div style={{ width: `${(totalExp / max) * 100}%`, background: 'var(--brand-danger)', height: '100%', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                          </div>
                        </div>
                        <div style={{ marginTop: '8px', padding: '12px', background: (totalInc - totalExp) >= 0 ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', borderRadius: '8px', textAlign: 'center' }}>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('net')}</span>
                          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: (totalInc - totalExp) >= 0 ? 'var(--brand-success)' : 'var(--brand-danger)' }}>
                            Rs. {Math.abs(totalInc - totalExp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Donut-style: Cash + Receivables + Payables */}
                <div className="glass-panel" style={{ padding: '24px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '4px' }}>{t('financialPosition')}</h3>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '20px' }}>{t('cashRecPayables')}</p>
                  {(() => {
                    const items = [
                      { label: t('cashBalanceLabel'), value: Math.max(0, cashBalance), color: 'var(--brand-info)' },
                      { label: t('receivablesLabel'), value: totalGive, color: 'var(--brand-success)' },
                      { label: t('payablesLabel'), value: totalGot, color: 'var(--brand-danger)' },
                    ];
                    const maxVal = Math.max(...items.map(i => i.value), 1);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {items.map(item => (
                          <div key={item.label}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: item.color }}>{item.label}</span>
                              <span style={{ fontSize: '0.82rem', fontWeight: 700 }}>Rs. {item.value.toLocaleString()}</span>
                            </div>
                            <div style={{ background: 'var(--bg-tertiary)', borderRadius: '6px', height: '10px', overflow: 'hidden' }}>
                              <div style={{ width: `${(item.value / maxVal) * 100}%`, background: item.color, height: '100%', borderRadius: '6px', transition: 'width 0.6s ease' }} />
                            </div>
                          </div>
                        ))}
                        <div style={{ marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t('staffProductsInvoices', { staffCount: staff.filter(s => !s.is_deleted).length, productCount: products.filter(p => !p.is_deleted).length, invoiceCount: invoices.length })}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* TAB: CUSTOMERS LEDGER */}
          {activeTab === 'customers' && !selectedParty && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justify: 'space-between', align: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '260px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      placeholder={t('searchCustomers')}
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      style={{ width: '100%', padding: '10px 10px 10px 38px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <select value={customerAreaFilter} onChange={(e) => setCustomerAreaFilter(e.target.value)} style={{ padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} title={t('areaFilter') || 'Filter by area'}>
                    <option value="all">{t('allAreas') || 'All Areas'}</option>
                    {customerAreas.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <button onClick={() => triggerPrintReport('customers', { area: customerAreaFilter === 'all' ? null : customerAreaFilter, searchQuery: customerSearchQuery })} className="btn btn-secondary" title={t('printCustomerDirectory')}>
                    <Printer size={14} /><span>{t('printReport')}</span>
                  </button>
                  <button onClick={() => { resetNewParty('CUSTOMER'); setShowAddPartyModal(true); }} className="btn btn-primary">
                    <Plus size={16} />
                    <span>{t('addCustomer')}</span>
                  </button>
                </div>
              </div>

              {/* Customers list table */}
              <div className="glass-panel" style={{ overflow: 'hidden' }}>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '16px 24px' }}>{t('name')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('phone')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('address')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('area')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('cnic')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('balanceDue')}</th>
                      <th style={{ padding: '16px 24px', textAlign: 'right' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.filter(p => (customerAreaFilter === 'all' || p.area === customerAreaFilter) && (p.name.toLowerCase().includes(customerSearchQuery.toLowerCase()) || (p.phone && p.phone.includes(customerSearchQuery)))).map(p => {
                      const balance = p.cached_balance;
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td data-label={t('name')} style={{ padding: '16px 24px', fontWeight: 600 }}>{p.name}</td>
                          <td data-label={t('phone')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.phone || 'N/A'}</td>
                          <td data-label={t('address')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.address || '—'}</td>
                          <td data-label={t('area')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.area || '—'}</td>
                          <td data-label={t('cnic')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.cnic || 'N/A'}</td>
                          <td data-label={t('balanceDue')} style={{ padding: '16px 24px', fontWeight: 700, color: balance > 0 ? 'var(--brand-success)' : balance < 0 ? 'var(--brand-danger)' : 'var(--text-primary)' }}>
                            {balance > 0 ? `${t('get')}: Rs. ${balance.toLocaleString()}` : balance < 0 ? `${t('give')}: Rs. ${Math.abs(balance).toLocaleString()}` : t('settled')}
                          </td>
                          <td data-label={t('actions')} style={{ padding: '16px 24px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button onClick={() => setSelectedParty(p)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem' }}>
                                <Eye size={12} /><span>{t('viewLedger')}</span>
                              </button>
                              <button onClick={() => { setEditingParty({ ...p }); }} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-info)' }}>
                                <Pencil size={12} /><span>{t('edit')}</span>
                              </button>
                              <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteCustomer', { name: p.name }), () => deleteParty(p))} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-danger)' }}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {customers.length === 0 && (
                      <tr><td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noCustomers')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: SUPPLIERS LEDGER */}
          {activeTab === 'suppliers' && !selectedParty && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justify: 'space-between', align: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '260px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      placeholder={t('searchSuppliers')}
                      value={supplierSearchQuery}
                      onChange={(e) => setSupplierSearchQuery(e.target.value)}
                      style={{ width: '100%', padding: '10px 10px 10px 38px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <select value={supplierAreaFilter} onChange={(e) => setSupplierAreaFilter(e.target.value)} style={{ padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} title={t('areaFilter') || 'Filter by area'}>
                    <option value="all">{t('allAreas') || 'All Areas'}</option>
                    {supplierAreas.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <button onClick={() => triggerPrintReport('suppliers', { area: supplierAreaFilter === 'all' ? null : supplierAreaFilter, searchQuery: supplierSearchQuery })} className="btn btn-secondary" title={t('printSupplierDirectory')}>
                    <Printer size={14} /><span>{t('printReport')}</span>
                  </button>
                  <button onClick={() => { resetNewParty('SUPPLIER'); setShowAddPartyModal(true); }} className="btn btn-primary">
                    <Plus size={16} />
                    <span>{t('addSupplier')}</span>
                  </button>
                </div>
              </div>

              {/* Suppliers list table */}
              <div className="glass-panel" style={{ overflow: 'hidden' }}>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '16px 24px' }}>{t('name')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('phone')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('address')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('area')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('itemsSupplied')}</th>
                      <th style={{ padding: '16px 24px' }}>{t('balanceDue')}</th>
                      <th style={{ padding: '16px 24px', textAlign: 'right' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.filter(p => (supplierAreaFilter === 'all' || p.area === supplierAreaFilter) && (p.name.toLowerCase().includes(supplierSearchQuery.toLowerCase()) || (p.phone && p.phone.includes(supplierSearchQuery)))).map(p => {
                      const balance = p.cached_balance;
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td data-label={t('suppliers')} style={{ padding: '16px 24px', fontWeight: 600 }}>{p.name}</td>
                          <td data-label={t('phone')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.phone || 'N/A'}</td>
                          <td data-label={t('address')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.address || '—'}</td>
                          <td data-label={t('area')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.area || '—'}</td>
                          <td data-label={t('itemsSupplied')} style={{ padding: '16px 24px', color: 'var(--text-secondary)' }}>{p.items_supplied || 'Bakery Goods'}</td>
                          <td data-label={t('balanceDue')} style={{ padding: '16px 24px', fontWeight: 700, color: balance > 0 ? 'var(--brand-success)' : balance < 0 ? 'var(--brand-danger)' : 'var(--text-primary)' }}>
                            {balance > 0 ? `${t('get')}: Rs. ${balance.toLocaleString()}` : balance < 0 ? `${t('give')}: Rs. ${Math.abs(balance).toLocaleString()}` : t('settled')}
                          </td>
                          <td data-label={t('actions')} style={{ padding: '16px 24px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button onClick={() => triggerPurchaseInvoice(p)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-success)' }}>
                                <FileText size={12} /><span>{t('piLabel') || 'PI'}</span>
                              </button>
                              <button onClick={() => setSelectedParty(p)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem' }}>
                                <Eye size={12} /><span>{t('viewLedger')}</span>
                              </button>
                              <button onClick={() => { setEditingParty({ ...p }); }} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-info)' }}>
                                <Pencil size={12} /><span>{t('edit')}</span>
                              </button>
                              <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteSupplier', { name: p.name }), () => deleteParty(p))} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-danger)' }}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {suppliers.length === 0 && (
                      <tr><td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noSuppliers')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STATEMENT VIEW FOR ACTIVE SELECTED CUSTOMER / SUPPLIER */}
          {((activeTab === 'customers' || activeTab === 'suppliers') && selectedParty) && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={() => setSelectedParty(null)} className="btn btn-secondary" style={{ padding: '8px 14px' }}><ArrowLeft size={14} /> {t('backToList')}</button>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <a 
                    href={`https://wa.me/${selectedParty.phone || ''}?text=${encodeURIComponent(formatWAMessage('balance_reminder', { party_name: selectedParty.name, business_name: selectedBusiness?.name || 'our shop', balance: Math.abs(selectedParty.cached_balance).toLocaleString() }))}`}
                    onClick={(ev) => onWAClick(ev, waCardForParty(selectedParty))}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success"
                    style={{ textDecoration: 'none' }}
                  >
                    <Share2 size={16} />
                    <span>{t('whatsappReminder')}</span>
                  </a>
                  <button onClick={() => triggerPrintReport('ledger', { party: selectedParty, entries: ledgerEntries.filter(e => (e.party === selectedParty.id || e.party_id === selectedParty.id) && !e.is_deleted), dateFilter: ledgerDateFilter, searchQuery: ledgerSearchQuery })} className="btn btn-secondary">{t('printStatement')}</button>
                </div>
              </div>

              {/* Ledger Filters Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginTop: '12px' }}>
                <DateFilterBar filter={ledgerDateFilter} onChange={setLedgerDateFilter} />
                <div style={{ position: 'relative', minWidth: '220px' }}>
                  <Search size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    placeholder={t('searchDesc')}
                    value={ledgerSearchQuery}
                    onChange={e => setLedgerSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '9px 10px 9px 30px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              {/* Profile Card */}
              <div className="glass-panel" style={{ padding: '24px', display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700 }}>{selectedParty.name}</h2>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>{t('phone')}: {selectedParty.phone || t('none')} | {t('cnic')}: {selectedParty.cnic || t('none')}</p>
                  {selectedParty.address && <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('address')}: {selectedParty.address}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{t('runningBalance')}</span>
                  <h2 style={{ fontSize: '2rem', fontWeight: 800, marginTop: '4px', color: selectedParty.cached_balance > 0 ? 'var(--brand-success)' : selectedParty.cached_balance < 0 ? 'var(--brand-danger)' : 'var(--text-primary)' }}>
                    Rs. {Math.abs(selectedParty.cached_balance).toLocaleString()}
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, marginLeft: '8px' }}>
                      {selectedParty.cached_balance > 0 ? t('receivableGet') : selectedParty.cached_balance < 0 ? t('payableGive') : 'Clear'}
                    </span>
                  </h2>
                </div>
              </div>

              {/* Add Entry Button */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => { setLedgerForm({ amount: '', entry_type: 'GIVE', description: '', date: new Date().toISOString().split('T')[0], time: currentTime(), attachment: null, payment_method: 'CASH', bank_account_id: '' }); setShowAddLedgerModal(true); }} className="btn btn-primary">
                  <Plus size={16} />
                  <span>{t('logNewEntry')}</span>
                </button>
              </div>

              {/* Entry statement checklist */}
              <div className="glass-panel" style={{ overflow: 'auto' }}>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('entryNumber')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('date')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('description')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('paymentMethod') || 'Payment'}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('attachment') || 'Attachment'}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('discount') || 'Discount'}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--brand-success)' }}>{t('ledGave')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--brand-danger)' }}>{t('ledGot')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-primary)' }}>{t('runningBalance')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const allPartyEntries = ledgerEntries.filter(e => (e.party === selectedParty.id || e.party_id === selectedParty.id) && !e.is_deleted);
                      const withRunning = computeRunningBalances(allPartyEntries);
                      const filteredEntries = withRunning
                        .filter(e => {
                          if (!filterByDate(e.date, ledgerDateFilter)) return false;
                          if (ledgerSearchQuery) {
                            const q = ledgerSearchQuery.toLowerCase();
                            if (!(e.description || '').toLowerCase().includes(q)) return false;
                          }
                          return true;
                        })
                        .sort((a, b) => sortByDateTimeDesc(a, b) || new Date(b.created_at) - new Date(a.created_at));
                      const totalCount = allPartyEntries.length;
                      return filteredEntries.map((e, idx) => (
                        <tr key={e.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td data-label="#" style={{ padding: '12px 16px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>{totalCount - filteredEntries.length + idx + 1}</td>
                          <td data-label={t('date')} style={{ padding: '12px 16px', fontSize: '0.9rem' }}>{formatDateTime(e.date, e.time)}</td>
                          <td data-label={t('description')} style={{ padding: '12px 16px', fontSize: '0.9rem' }}>
                            <div>{e.description || t('genericEntry')}</div>
                            <span className={`chip ${e.sync_status === 'synced' ? 'chip-success' : 'chip-warning'}`} style={{ fontSize: '0.65rem', padding: '2px 6px' }}>{e.sync_status}</span>
                          </td>
                          <td data-label={t('paymentMethod') || 'Payment'} style={{ padding: '12px 16px', fontSize: '0.85rem' }}>
                            {e.payment_method === 'ONLINE' ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(56,189,248,0.1)', color: 'var(--brand-primary)', borderRadius: '4px', padding: '2px 8px', fontWeight: 600, fontSize: '0.78rem' }}>
                                🏦 {bankAccounts.find(acc => String(acc.id) === String(e.bank_account))?.name || t('bank') || 'Bank'}
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(16,185,129,0.1)', color: 'var(--brand-success)', borderRadius: '4px', padding: '2px 8px', fontWeight: 600, fontSize: '0.78rem' }}>
                                💵 {t('cash') || 'Cash'}
                              </span>
                            )}
                          </td>
                          <td data-label={t('attachment') || 'Attachment'} style={{ padding: '12px 16px', fontSize: '0.9rem' }}>
                            {e.attachment ? (
                              <a
                                href={getPhotoUrl(e.attachment)}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(ev) => { ev.preventDefault(); setPreviewAttachment(e.attachment); }}
                                style={{ color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', cursor: 'pointer', padding: '8px 0' }}
                              >
                                <Paperclip size={14} /> {t('view')}
                              </a>
                            ) : '-'}
                          </td>
                          <td data-label={t('discount')} style={{ padding: '12px 16px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                            {e.discount_amount && parseFloat(e.discount_amount) > 0 ? `Rs. ${parseFloat(e.discount_amount).toLocaleString()}` : '-'}
                          </td>
                          <td data-label={t('ledGave')} style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--brand-success)' }}>
                            {e.entry_type === 'GIVE' ? `Rs. ${e.amount.toLocaleString()}` : '-'}
                          </td>
                          <td data-label={t('ledGot')} style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--brand-danger)' }}>
                            {e.entry_type === 'GET' ? `Rs. ${e.amount.toLocaleString()}` : '-'}
                          </td>
                          <td data-label={t('runningBalance')} style={{ padding: '12px 16px', fontWeight: 700, color: e.runningBalance > 0 ? 'var(--brand-success)' : e.runningBalance < 0 ? 'var(--brand-danger)' : 'var(--text-primary)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 0 }}>
                              <span>Rs. {Math.abs(e.runningBalance).toLocaleString()}</span>
                              <span style={{ fontSize: '0.7rem', fontWeight: 600, opacity: 0.8 }}>
                                {e.runningBalance > 0 ? `(${t('receivable')})` : e.runningBalance < 0 ? `(${t('payable')})` : ''}
                              </span>
                            </div>
                          </td>
                          <td data-label={t('actions')} style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <a
                                href={`https://wa.me/${selectedParty.phone || ''}?text=${encodeURIComponent(formatWAMessage('transaction_receipt', { party_name: selectedParty.name, business_name: selectedBusiness?.name || 'HisabFlow', date: e.date, description: e.description || 'Generic Entry', amount: e.amount.toLocaleString(), type: e.entry_type === 'GIVE' ? 'GAVE (you gave)' : 'RECEIVED (you got)', balance: Math.abs(e.runningBalance ?? (selectedParty.cached_balance || 0)).toLocaleString() }))}`}
                                onClick={(ev) => onWAClick(ev, waCardForLedgerEntry(e, selectedParty))}
                                target="_blank"
                                rel="noreferrer"
                                style={{ padding: '4px 8px', fontSize: '0.75rem', borderRadius: '4px', border: '1px solid var(--border-color)', cursor: 'pointer', background: 'rgba(37,211,102,0.1)', color: '#25d366', display: 'inline-flex', alignItems: 'center', gap: '3px', textDecoration: 'none', fontWeight: 600 }}
                                title={t('whatsapp')}
                              >
                                <Share2 size={11} /> WA
                              </a>
                              <button onClick={() => setPrintEntryData({ entry: e, party: selectedParty })} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }} title={t('printReport')}>
                                <Printer size={11} />
                              </button>
                              <button disabled={!canEdit} onClick={() => setEditingLedgerEntry({ ...e, amount: String(e.amount) })} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }} title={t('edit')}>
                                <Pencil size={11} />
                              </button>
                              <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteTransaction', { amount: e.amount }), () => deleteLedgerEntry(e))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-danger)' }} title={t('delete')}>
                                <Trash2 size={11} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                    {ledgerEntries.filter(e => (e.party === selectedParty.id || e.party_id === selectedParty.id) && !e.is_deleted).length === 0 && (
                      <tr>
                        <td colSpan="10" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noTransactions')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: CASH BOOK */}
          {activeTab === 'cash' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Add Cash Entry Button */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => { setCashForm({ amount: '', entry_type: 'IN', description: '', category: 'Sales', date: new Date().toISOString().split('T')[0], time: currentTime(), attachment: null }); setShowAddCashModal(true); }} className="btn btn-primary">
                  <Plus size={16} />
                    <span>{t('logCashEntry')}</span>
                </button>
              </div>

              {/* Cash Book Filters */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <DateFilterBar filter={cashDateFilter} onChange={setCashDateFilter} />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    value={cashCategoryFilter}
                    onChange={e => setCashCategoryFilter(e.target.value)}
                    style={{ padding: '9px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                  >
                    <option value="all">{t('allCategories')}</option>
                    {[...new Set(cashEntries.filter(c => !c.is_deleted).map(c => c.category))].map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <div style={{ position: 'relative', minWidth: '200px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      placeholder={t('searchByDetails')}
                      value={cashSearchQuery}
                      onChange={e => setCashSearchQuery(e.target.value)}
                      style={{ width: '100%', padding: '9px 10px 9px 30px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
              </div>

              {/* Cash Transactions List */}
              <div className="glass-panel" style={{ overflow: 'auto' }}>
                <div style={{ padding: '16px 16px', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600 }}>{t('cashStatements')}</h3>
                    <span style={{ fontSize: '0.8rem', color: cashBalance >= 0 ? 'var(--brand-success)' : 'var(--brand-danger)', fontWeight: 700 }}>{t('balance')}: Rs. {cashBalance.toLocaleString()}</span>
                  </div>
                  <button onClick={() => triggerPrintReport('cash', { entries: filterCashEntries(cashEntries, cashDateFilter, cashCategoryFilter, cashSearchQuery).sort((a,b) => sortByDateTimeDesc(a,b)), period: dateFilterLabel(cashDateFilter, t) + (cashCategoryFilter !== 'all' && cashCategoryFilter ? ` · ${cashCategoryFilter}` : '') + (cashSearchQuery ? ` · "${cashSearchQuery}"` : '') })} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '0.8rem' }} title={t('printReport')}>
                    <Printer size={14} />
                    <span>{t('printReport')}</span>
                  </button>
                </div>

                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-primary)' }}>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('date')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('category')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('attachment') || 'Attachment'}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--brand-success)' }}>{t('cashInBtn')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--brand-danger)' }}>{t('cashOutBtn')}</th>
                      <th style={{ padding: '12px 16px', fontSize: '0.85rem' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterCashEntries(cashEntries, cashDateFilter, cashCategoryFilter, cashSearchQuery)
                      .sort((a, b) => sortByDateTimeDesc(a, b))
                      .map(c => (
                        <tr key={c.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td data-label={t('date')} style={{ padding: '12px 16px', fontSize: '0.9rem' }}>{formatDateTime(c.date, c.time)}</td>
                          <td data-label={t('category')} style={{ padding: '12px 16px', fontSize: '0.9rem' }}>
                            <div style={{ fontWeight: 600 }}>{c.category}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{c.description}</div>
                          </td>
                          <td data-label={t('attachment') || 'Attachment'} style={{ padding: '12px 16px', fontSize: '0.9rem' }}>
                            {c.attachment ? (
                              <a
                                href={getPhotoUrl(c.attachment)}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(ev) => { ev.preventDefault(); setPreviewAttachment(c.attachment); }}
                                style={{ color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', cursor: 'pointer', padding: '8px 0' }}
                              >
                                <Paperclip size={14} /> {t('view')}
                              </a>
                            ) : '-'}
                          </td>
                          <td data-label={t('cashIn')} style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--brand-success)' }}>
                            {c.entry_type === 'IN' ? `+ Rs. ${parseFloat(c.amount || 0).toLocaleString()}` : '-'}
                          </td>
                          <td data-label={t('cashOut')} style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--brand-danger)' }}>
                            {c.entry_type === 'OUT' ? `- Rs. ${parseFloat(c.amount || 0).toLocaleString()}` : '-'}
                          </td>
                          <td data-label={t('actions')} style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button onClick={() => setEditingCashEntry({ ...c, amount: String(c.amount) })} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }}><Pencil size={11} /></button>
                              <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteCashEntry'), () => deleteCashEntry(c))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-danger)' }}><Trash2 size={11} /></button>
                            </div>
                          </td>
                          </tr>
                        ))}
                      {cashEntries.filter(c => !c.is_deleted).length === 0 && (
                         <tr><td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noCashEntries')}</td></tr>
                       )}
                    </tbody>
</table>
              </div>

            </div>
          </div>
          )}

          {/* TAB: BANK */}
          {activeTab === 'bank' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('bankAccounts')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '4px' }}>{t('bankTransactions')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => { setEditingBankAccount(null); setBankAccountNameOther(false); setBankForm({ name: '', account_title: '', account_number: '', iban: '', branch: '', opening_balance: '0', is_active: true, notes: '' }); setShowBankModal(true); }}
                  disabled={!canEdit}
                  className="btn btn-primary"
                >
                  <Plus size={16} />
                  <span>{t('addBankAccount')}</span>
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
                {bankAccounts.filter(account => !account.is_deleted && (!selectedBusiness || String(account.business) === String(selectedBusiness.id))).map(account => {
                  const balance = Number(account.current_balance ?? account.opening_balance ?? 0);
                  return (
                    <div key={account.id} className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{account.name || t('bankAccount')}</div>
                          <h4 style={{ fontSize: '1.1rem', fontWeight: 700, marginTop: '6px' }}>{account.account_title || account.name}</h4>
                        </div>
                        <span className={`chip ${account.is_active ? 'chip-success' : 'chip-warning'}`} style={{ fontSize: '0.72rem' }}>
                          {account.is_active ? t('activeAccount') : t('inactive')}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        <div><strong>{t('accountNumber')}:</strong> {account.account_number || '—'}</div>
                        <div><strong>{t('iban')}:</strong> {account.iban || '—'}</div>
                        <div><strong>{t('branch')}:</strong> {account.branch || '—'}</div>
                        <div><strong>{t('openingBalance')}:</strong> Rs. {Number(account.opening_balance || 0).toLocaleString()}</div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('currentBalance')}</div>
                          <div style={{ fontSize: '1.3rem', fontWeight: 800, color: balance >= 0 ? 'var(--brand-success)' : 'var(--brand-danger)' }}>
                            Rs. {Math.abs(balance).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <button type="button" disabled={!canEdit} onClick={() => { setEditingBankAccount(account); setBankAccountNameOther(!!account.name && !bankNameOptions.includes(account.name)); setBankForm({ name: account.name || '', account_title: account.account_title || '', account_number: account.account_number || '', iban: account.iban || '', branch: account.branch || '', opening_balance: String(account.opening_balance || 0), is_active: !!account.is_active, notes: account.notes || '' }); setShowBankModal(true); }} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.75rem' }}>
                            <Pencil size={12} />
                          </button>
                          <button type="button" disabled={!canDelete} onClick={() => deleteBankAccount(account)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.75rem', color: 'var(--brand-danger)' }}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {bankAccounts.filter(account => !account.is_deleted && (!selectedBusiness || String(account.business) === String(selectedBusiness.id))).length === 0 && (
                <div className="glass-panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  {t('noCustomers') || 'No bank accounts created yet.'}
                </div>
              )}

              <div className="glass-panel" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>{t('bankTransactions')}</h3>
                  <button onClick={() => triggerPrintReport('bank', { entries: filteredBankTransactions, period: bankDateFilter.mode === 'all' ? t('allTime') : bankDateFilter.mode })} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '0.8rem' }} title={t('printReport')}>
                    <Printer size={14} /><span>{t('printReport')}</span>
                  </button>
                </div>
                <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <DateFilterBar filter={bankDateFilter} onChange={setBankDateFilter} />
                  <select value={bankAccountFilter} onChange={e => setBankAccountFilter(e.target.value)} style={{ padding: '5px 8px', fontSize: '0.78rem', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                    <option value="all">{t('allBankAccounts') || 'All Banks'}</option>
                    {bankAccounts.filter(account => !account.is_deleted && (!selectedBusiness || String(account.business) === String(selectedBusiness.id))).map(account => <option key={account.id} value={account.id}>{account.name || account.account_title}</option>)}
                  </select>
                  <select value={bankTransactionTypeFilter} onChange={e => setBankTransactionTypeFilter(e.target.value)} style={{ padding: '5px 8px', fontSize: '0.78rem', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                    <option value="all">{t('allTransactionTypes') || 'All Types'}</option>
                    <option value="DEPOSIT">{t('deposit') || 'Deposit'}</option>
                    <option value="WITHDRAWAL">{t('withdrawal') || 'Withdrawal'}</option>
                  </select>
                </div>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '12px 16px' }}>{t('date')}</th>
                      <th style={{ padding: '12px 16px' }}>{t('bankAccount')}</th>
                      <th style={{ padding: '12px 16px' }}>{t('bankTransactionType')}</th>
                      <th style={{ padding: '12px 16px' }}>{t('amount')}</th>
                      <th style={{ padding: '12px 16px' }}>{t('description')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBankTransactions.map(txn => (
                      <tr key={txn.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td data-label={t('date')} style={{ padding: '12px 16px' }}>{formatDateTime(txn.date, txn.time)}</td>
                        <td data-label={t('bankAccount')} style={{ padding: '12px 16px' }}>{bankAccounts.find(acc => String(acc.id) === String(txn.bank_account))?.name || txn.bank_account || '—'}</td>
                        <td data-label={t('bankTransactionType')} style={{ padding: '12px 16px', fontWeight: 600 }}>{txn.transaction_type || '-'}</td>
                        <td data-label={t('amount')} style={{ padding: '12px 16px', fontWeight: 700, color: txn.transaction_type === 'WITHDRAWAL' ? 'var(--brand-danger)' : 'var(--brand-success)' }}>
                          {txn.transaction_type === 'WITHDRAWAL' ? '- ' : '+ '}Rs. {Number(txn.amount || 0).toLocaleString()}
                        </td>
                        <td data-label={t('description')} style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{txn.description || '-'}</td>
                      </tr>
                    ))}
                    {filteredBankTransactions.length === 0 && (
                      <tr><td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noTransactions') || 'No bank transactions recorded yet.'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: INCOME & EXPENSE */}
          {activeTab === 'income_expense' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('incomeExpense')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '4px' }}>{t('ieSearch')}</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button onClick={() => {
                    const filtered = incomeExpenses.filter(item => {
                      if (item.is_deleted) return false;
                      if (!filterByDate(item.date, ieDateFilter)) return false;
                      if (ieSearchQuery) { const q = ieSearchQuery.toLowerCase(); return (item.category || '').toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q); }
                      return true;
                    });
                    triggerPrintReport('ie_category', { entries: filtered.sort((a,b) => sortByDateTimeDesc(a,b)), period: ieDateFilter.mode === 'all' ? 'All Time' : ieDateFilter.mode });
                  }} className="btn btn-secondary" title={t('printIEReport')} style={{ padding: '10px 14px' }}>
                    <Printer size={16} />
                    <span>{t('printReport')}</span>
                  </button>
                  <button disabled={!canEdit} onClick={() => { setIeForm({ type: 'EXPENSE', amount: '', category: 'Rent', description: '', date: new Date().toISOString().split('T')[0], time: currentTime(), attachment: null }); setShowAddIeModal(true); }} className="btn btn-primary">
                    <Plus size={16} />
                    <span>{t('addExpenseIncome')}</span>
                  </button>
                </div>
              </div>

              {/* Filters + Search Row for IE */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <DateFilterBar filter={ieDateFilter} onChange={setIeDateFilter} />
                <div style={{ position: 'relative', minWidth: '220px' }}>
                  <Search size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: 'var(--text-muted)' }} />
                  <input
                    type="text"
                    placeholder={t('ieSearch')}
                    value={ieSearchQuery}
                    onChange={e => setIeSearchQuery(e.target.value)}
                    style={{ width: '100%', padding: '9px 10px 9px 30px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                  />
                </div>
              </div>

              {/* IE Statements List */}
              <div className="glass-panel" style={{ overflow: 'hidden' }}>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '16px 20px' }}>{t('date')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('type')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('category')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('description')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('attachment') || 'Attachment'}</th>
                      <th style={{ padding: '16px 20px' }}>{t('amount')}</th>
                      <th style={{ padding: '16px 20px', textAlign: 'right' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomeExpenses.filter(item => {
                      if (item.is_deleted) return false;
                      if (!filterByDate(item.date, ieDateFilter)) return false;
                      if (ieSearchQuery) {
                        const q = ieSearchQuery.toLowerCase();
                        return (item.category || '').toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q);
                      }
                      return true;
                    }).map(item => (
                      <tr key={item.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td data-label={t('date')} style={{ padding: '16px 20px', fontSize: '0.9rem' }}>{formatDateTime(item.date, item.time)}</td>
                        <td data-label={t('type')} style={{ padding: '16px 20px' }}>
                          <span className={`chip ${item.type === 'INCOME' ? 'chip-success' : 'chip-danger'}`}>
                            {item.type}
                          </span>
                        </td>
                        <td data-label={t('category')} style={{ padding: '16px 20px', fontWeight: 600 }}>{item.category}</td>
                        <td data-label={t('description')} style={{ padding: '16px 20px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{item.description || 'N/A'}</td>
                        <td data-label={t('attachment') || 'Attachment'} style={{ padding: '16px 20px', fontSize: '0.9rem' }}>
                          {item.attachment ? (
                            <a
                              href={getPhotoUrl(item.attachment)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={() => setPreviewAttachment(item.attachment)}
                              style={{ color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', cursor: 'pointer', padding: '8px 0' }}
                            >
                              <Paperclip size={14} /> {t('view')}
                            </a>
                          ) : '-'}
                        </td>
                        <td data-label={t('amount')} style={{ padding: '16px 20px', fontWeight: 700, color: item.type === 'INCOME' ? 'var(--brand-success)' : 'var(--brand-danger)' }}>
                          Rs. {item.amount.toLocaleString()}
                        </td>
                        <td data-label={t('actions')} style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button disabled={!canEdit} onClick={() => setEditingIeEntry({ ...item, amount: String(item.amount) })} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-info)' }}>
                              <Pencil size={12} />
                            </button>
                            <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteRecord', { type: item.type.toLowerCase() }), () => deleteIeEntry(item))} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-danger)' }}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {incomeExpenses.filter(item => !item.is_deleted).length === 0 && (
                      <tr><td colSpan="6" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noLogs')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB: STOCK / INVENTORY */}
          {activeTab === 'stock' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                {/* Toggle: Stock / History */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('stockInventory')}</h3>
                  <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '3px' }}>
                    <button
                      onClick={() => setStockView('stock')}
                      style={{ padding: '6px 14px', fontSize: '0.8rem', background: stockView === 'stock' ? 'var(--accent-primary)' : 'transparent', color: stockView === 'stock' ? '#fff' : 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                    >{t('stockTab')}</button>
                    <button
                      onClick={() => setStockView('history')}
                      style={{ padding: '6px 14px', fontSize: '0.8rem', background: stockView === 'history' ? 'var(--accent-primary)' : 'transparent', color: stockView === 'history' ? '#fff' : 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' }}
                    >{t('stockHistoryTab')}</button>
                  </div>
                </div>

                {/* Action buttons — only on stock view */}
                {stockView === 'stock' && (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button onClick={() => triggerPrintReport('products', { searchQuery: stockSearchQuery, productType: stockProductTypeFilter })} className="btn btn-secondary" title={t('printStockReport')}>
                    <Printer size={14} /><span>{t('printReport')}</span>
                  </button>
                  <button onClick={() => { setNewProduct({ name: '', sku: '', purchase_price: '', sale_price: '', stock_quantity: '', min_stock_alert: '', unit: 'pcs', product_type: '', expiration_date: '', units_per_bulk: 1 }); setProductPhotoFile(null); setShowAddProductModal(true); }} className="btn btn-primary">
                    <Plus size={16} />
                    <span>{t('addProduct')}</span>
                  </button>
                </div>
                )}
              </div>

              {/* ── STOCK VIEW ── */}
              {stockView === 'stock' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Search bar */}
                <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={16} style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
                    <input
                      type="text"
                      placeholder={t('searchInventory')}
                      value={stockSearchQuery}
                      onChange={(e) => setStockSearchQuery(e.target.value)}
                      style={{ width: '100%', padding: '10px 10px 10px 38px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                    />
                  </div>
                  <select value={stockProductTypeFilter} onChange={e => setStockProductTypeFilter(e.target.value)} style={{ padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
                    <option value="all">{t('allTypes') || 'All Types'}</option>
                    {productTypes.map(pt => (
                      <option key={pt.id} value={pt.name}>{pt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Inventory Table */}
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                      {/* Name/SKU gets an explicit share of the row: the SKU and
                          expiration lines stay on one line instead of wrapping
                          into a stack of broken fragments. */}
                      <th style={{ padding: '16px 20px', width: '22%', minWidth: '240px' }}>{t('nameOrSku')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('costPrice')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('sellPrice')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('stockStatus')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('totalValue')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('retailValue')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('expectedProfit')}</th>
                      <th style={{ padding: '16px 20px' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.filter(p => !p.is_deleted && p.name.toLowerCase().includes(stockSearchQuery.toLowerCase()) && (stockProductTypeFilter === 'all' || p.product_type === stockProductTypeFilter)).map(p => {
                      const isLowStock = Number(p.stock_quantity) <= Number(p.min_stock_alert);
                      const hasPackaging = Boolean(p.bulk_unit && Number(p.units_per_bulk) > 1);
                      // Everything below is served by the API — this table
                      // renders inventory, it never calculates it. The Stock
                      // Status badge says how much is on the shelf with the
                      // words that go with it: packs when Units Quantity > 1,
                      // pieces otherwise ("40 packs", "39 packs + 4 pcs",
                      // "12 pcs").
                      const status = stockStatusText(p.stock_quantity, p.units_per_bulk, t);

                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td data-label={t('nameOrSku')} className="inventory-name-cell" style={{ padding: '16px 20px', minWidth: '240px' }}>
                            <div style={{ fontWeight: 600 }}>{p.name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t('skuBarcode')}: {p.sku || t('none')}</div>
                            {p.expiration_date && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                {t('expirationDate')}: <span style={{ color: new Date(p.expiration_date) < new Date() ? 'var(--brand-danger)' : 'inherit' }}>{new Date(p.expiration_date).toLocaleDateString()}</span>
                              </div>
                            )}
                          </td>
                          <td data-label={t('costPrice')} style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>
                            <div>Rs. {p.purchase_price} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ {p.unit || 'pcs'}</span></div>
                            {hasPackaging && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--brand-primary)', fontWeight: 500, marginTop: '2px' }}>
                                Rs. {priceText(p.packCost)} / {p.bulk_unit} ({p.units_per_bulk} {p.unit || 'pcs'})
                              </div>
                            )}
                          </td>
                          <td data-label={t('sellPrice')} style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>
                            <div>Rs. {p.sale_price} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ {p.unit || 'pcs'}</span></div>
                            {hasPackaging && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--brand-success)', fontWeight: 500, marginTop: '2px' }}>
                                Rs. {priceText(p.packRetail)} / {p.bulk_unit}
                              </div>
                            )}
                          </td>
                          <td data-label={t('stockStatus')} style={{ padding: '16px 20px' }}>
                            <span className={`chip ${isLowStock ? 'chip-danger' : 'chip-success'}`}>
                              {status}{isLowStock ? ` ${t('low')}` : ''}
                            </span>
                          </td>
                          <td data-label={t('totalValue')} style={{ padding: '16px 20px', fontWeight: 600 }}>
                            Rs. {moneyText(p.stockValueCost)}
                          </td>
                          <td data-label={t('retailValue')} style={{ padding: '16px 20px', fontWeight: 600 }}>
                            Rs. {moneyText(p.stockValueRetail)}
                          </td>
                          <td data-label={t('expectedProfit')} style={{ padding: '16px 20px', fontWeight: 600, color: p.expectedProfit < 0 ? 'var(--brand-danger)' : 'var(--brand-success)' }}>
                            Rs. {moneyText(p.expectedProfit)}
                          </td>
                          <td data-label={t('actions')} style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button onClick={() => setRestockModal({ show: true, product: p, quantity: '' })} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-success)' }}><PackagePlus size={12} /><span style={{ marginLeft: 4 }}>{t('restock')}</span></button>
                              {p.sku && <button onClick={() => setBarcodePrintModal({ show: true, product: p, quantity: 1 })} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--text-primary)' }} title={t('printBarcode')}><Barcode size={12} /></button>}
                              <button onClick={() => setEditingProduct(productToEditForm(p))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }}><Pencil size={12} /></button>
                              <button disabled={!canDelete} onClick={() => triggerDelete(t('removeProduct', { name: p.name }), () => deleteProduct(p))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-danger)' }}><Trash2 size={12} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {products.length === 0 && (
                      <tr>
                        <td colSpan="8" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('noProducts')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )} {/* end stock view */}

              {/* ── HISTORY VIEW ── */}
              {stockView === 'history' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
                    {t('stockHistoryDesc')}
                  </p>
                  
                  {/* Action Bar for History */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', width: '200px' }}>
                      <Search size={14} style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--text-muted)' }} />
                      <input 
                        type="text" 
                        placeholder={t('searchInventory')} 
                        value={stockHistorySearch} 
                        onChange={e => setStockHistorySearch(e.target.value)} 
                        style={{ width: '100%', padding: '6px 10px 6px 30px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '0.8rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      />
                    </div>
                    <select value={stockHistoryProductTypeFilter} onChange={e => setStockHistoryProductTypeFilter(e.target.value)} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '0.8rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                      <option value="all">{t('allTypes') || 'All Types'}</option>
                      {productTypes.map(pt => (
                        <option key={pt.id} value={pt.name}>{pt.name}</option>
                      ))}
                    </select>
                    <DateFilterBar filter={stockHistoryPeriod} onChange={setStockHistoryPeriod} />
                    <button onClick={() => triggerPrintReport('stock_history', { entries: filteredStockHistory, periodLabel: stockHistoryPeriod.mode === 'all' ? t('allTime') : stockHistoryPeriod.mode })} className="btn btn-secondary" title={t('printReport')}>
                      <Printer size={14} /><span>{t('printReport')}</span>
                    </button>
                  </div>
                </div>

                {/* Stock History / Movements Section */}
                <div>
                <div className="glass-panel" style={{ overflow: 'hidden' }}>
                  <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ padding: '16px 20px' }}>{t('product') || 'Product'}</th>
                        <th style={{ padding: '16px 20px' }}>{t('date') || 'Date'}</th>
                        <th style={{ padding: '16px 20px' }}>{t('type') || 'Type'}</th>
                        <th style={{ padding: '16px 20px' }}>{t('qty') || 'Quantity'}</th>
                        <th style={{ padding: '16px 20px' }}>{t('supplierOrDesc') || 'Supplier / Description'}</th>
                        <th style={{ padding: '16px 20px', textAlign: 'center' }}>{t('actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStockHistory.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                            {t('noStockMovements') || 'No stock movements logged yet.'}
                          </td>
                        </tr>
                      ) : (
                        filteredStockHistory
                          .slice()
                          .sort((a, b) => sortByDateTimeDesc(a, b) || new Date(b.created_at) - new Date(a.created_at))
                          .map((m) => {
                            const prod = products.find((p) => p.id === m.product);
                            return (
                              <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                <td data-label={t('product') || 'Product'} style={{ padding: '16px 20px', fontWeight: 600 }}>
                                  {prod ? prod.name : t('unknownProduct') || 'Unknown Product'}
                                </td>
                                <td data-label={t('date') || 'Date'} style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>
                                  {formatDateTime(m.date, m.time)}
                                </td>
                                <td data-label={t('type') || 'Type'} style={{ padding: '16px 20px' }}>
                                  <span className={`chip ${m.movement_type === 'IN' ? 'chip-success' : m.movement_type === 'OUT' ? 'chip-danger' : 'chip-warning'}`}>
                                    {m.movement_type === 'IN' ? (t('stockIn') || 'Stock In') : m.movement_type === 'OUT' ? (t('stockOut') || 'Stock Out') : (t('adjustment') || 'Adjustment')}
                                  </span>
                                </td>
                                <td data-label={t('qty') || 'Quantity'} style={{ padding: '16px 20px', fontWeight: 600 }}>
                                  {m.quantity} {prod ? prod.unit : ''}
                                </td>
                                <td data-label={t('supplierOrDesc') || 'Supplier / Description'} style={{ padding: '16px 20px', color: 'var(--text-primary)', fontWeight: m.description && m.description.includes('Supplied by') ? 600 : 400 }}>
                                  {m.description || '-'}
                                </td>
                                <td data-label={t('actions') || 'Actions'} style={{ padding: '16px 20px', textAlign: 'center' }}>
                                  <div style={{ display: 'inline-flex', gap: '6px' }}>
                                    <button onClick={() => setEditingStockMovement({ ...m, quantity: String(m.quantity) })} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }}><Pencil size={12} /></button>
                                    <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteMovement', { date: m.date }) || `Delete movement on ${m.date}?`, () => deleteStockMovement(m))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-danger)' }}><Trash2 size={12} /></button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                      )}
                    </tbody>
                  </table>
                </div>
                </div>
              </div>
              )}

            </div>
          )}

          {/* TAB: PRODUCT GALLERY — now rendered inside Settings > Gallery sub-tab */}

          {/* TAB: STAFF & SALARIES */}
          {activeTab === 'staff' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Header Row: Title + Search + Action Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('staffTitle')}</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '4px' }}>{t('staffSubtitle')}</p>
                </div>

                {/* Global search for this tab */}
                <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: '320px' }}>
                  <Search size={15} style={{ position: 'absolute', left: '11px', top: '11px', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                  <input
                    id="staff-tab-search"
                    type="text"
                    placeholder={t('staffSearch')}
                    value={staffSearch}
                    onChange={(e) => { setStaffSearch(e.target.value); setSalarySearch(e.target.value); }}
                    style={{ width: '100%', padding: '10px 10px 10px 36px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.88rem' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button onClick={() => triggerPrintReport('staff', { searchQuery: staffSearch })} className="btn btn-secondary" title={t('printStaffReport')}>
                    <Printer size={14} /><span>{t('printReport')}</span>
                  </button>
                  <button onClick={() => setShowAddPayrollModal(true)} className="btn btn-secondary">
                    <DollarSign size={16} />
                    <span>{t('paySalaries')}</span>
                  </button>
                  <button onClick={() => setShowAddStaffModal(true)} className="btn btn-primary">
                    <Plus size={16} />
                    <span>{t('addEmployee')}</span>
                  </button>
                </div>
              </div>

              {/* Attendance Log Widget */}
              <div className="glass-panel" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h4 style={{ fontWeight: 600 }}>{t('logAttendance')}</h4>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{t('attendanceDateHint')}</span>
                  </div>
                  <input
                    type="date"
                    value={attendanceDate}
                    onChange={(e) => setAttendanceDate(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontWeight: 500 }}
                  />
                </div>

                <div style={{ marginTop: '16px', overflowX: 'auto' }}>
                  <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ padding: '10px 14px' }}>{t('employee')}</th>
                        <th style={{ padding: '10px 14px' }}>{t('type')}</th>
                        <th style={{ padding: '10px 14px', textAlign: 'right' }}>{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.filter(s => !s.is_deleted).map(s => {
                        const currentAtt = attendances.find(a => a.staff_member === s.id && a.date === attendanceDate);
                        const status = currentAtt ? currentAtt.status : 'PENDING';
                        return (
                          <tr key={s.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td data-label={t('employee')} style={{ padding: '10px 14px', fontWeight: 600 }}>{s.name}</td>
                            <td data-label={t('type')} style={{ padding: '10px 14px', fontSize: '0.85rem' }}>{s.salary_type} (Rs. {s.base_salary.toLocaleString()})</td>
                            <td data-label={t('status')} style={{ padding: '10px 14px', textAlign: 'right' }}>
                              <div style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {['PRESENT', 'ABSENT', 'LEAVE'].map(opt => {
                                  const active = status === opt;
                                  let color = 'var(--text-secondary)';
                                  let bg = 'transparent';
                                  if (active) {
                                    if (opt === 'PRESENT') { bg = 'rgba(16,185,129,0.15)'; color = 'var(--brand-success)'; }
                                    if (opt === 'ABSENT') { bg = 'rgba(239,68,68,0.15)'; color = 'var(--brand-danger)'; }
                                    if (opt === 'LEAVE') { bg = 'rgba(245,158,11,0.15)'; color = 'var(--brand-warning)'; }
                                  }
                                  return (
                                    <button
                                      key={opt}
                                      onClick={() => handleAttendanceChange(s.id, opt)}
                                      style={{ padding: '4px 10px', fontSize: '0.75rem', borderRadius: '4px', border: '1px solid var(--border-color)', cursor: 'pointer', background: bg, color: color, fontWeight: active ? 600 : 500 }}
                                    >
                                      {opt === 'PRESENT' ? t('present') : opt === 'ABSENT' ? t('absent') : t('leave')}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Staff Directory */}
              <div className="glass-panel" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>{t('staffDirectory')}</h3>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {staff.filter(s => !s.is_deleted && (
                        s.name.toLowerCase().includes(staffSearch.toLowerCase()) ||
                        (s.phone || '').includes(staffSearch) ||
                        (s.cnic || '').includes(staffSearch)
                      )).length} {t('ofEmployees')} {staff.filter(s => !s.is_deleted).length}
                    </span>
                  </div>
                  {/* Per-section search (mirrors the global tab search) */}
                  <div style={{ position: 'relative', minWidth: '200px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                    <input
                      id="staff-directory-search"
                      type="text"
                      placeholder={t('filterByName')}
                      value={staffSearch}
                      onChange={(e) => setStaffSearch(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.82rem' }}
                    />
                  </div>
                </div>
                <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '12px 20px' }}>{t('name')}</th>
                      <th style={{ padding: '12px 20px' }}>{t('phone')}</th>
                      <th style={{ padding: '12px 20px' }}>{t('cnic')}</th>
                      <th style={{ padding: '12px 20px' }}>{t('salaryType')}</th>
                      <th style={{ padding: '12px 20px' }}>{t('baseSalary')}</th>
                      <th style={{ padding: '12px 20px', textAlign: 'right' }}>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff
                      .filter(s => !s.is_deleted)
                      .filter(s =>
                        s.name.toLowerCase().includes(staffSearch.toLowerCase()) ||
                        (s.phone || '').includes(staffSearch) ||
                        (s.cnic || '').includes(staffSearch)
                      )
                      .map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td data-label={t('name')} style={{ padding: '14px 20px', fontWeight: 600 }}>{s.name}</td>
                        <td data-label={t('phone')} style={{ padding: '14px 20px', color: 'var(--text-secondary)' }}>{s.phone || 'N/A'}</td>
                        <td data-label={t('cnic')} style={{ padding: '14px 20px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{s.cnic || 'N/A'}</td>
                        <td data-label={t('type')} style={{ padding: '14px 20px' }}>
                          <span className={`chip ${s.salary_type === 'MONTHLY' ? 'chip-info' : 'chip-warning'}`}>{s.salary_type}</span>
                        </td>
                        <td data-label={t('baseSalary')} style={{ padding: '14px 20px', fontWeight: 700 }}>Rs. {(s.base_salary || 0).toLocaleString()}</td>
                        <td data-label={t('actions')} style={{ padding: '14px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button
                              onClick={() => setEditingStaff({ ...s, base_salary: String(s.base_salary) })}
                              className="btn btn-secondary"
                              style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-info)' }}
                            >
                              <Pencil size={12} /><span>{t('edit')}</span>
                            </button>
                            <button
                              onClick={() => triggerDelete(t('removeStaff', { name: s.name }), () => deleteStaff(s))}
                              className="btn btn-secondary"
                              style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--brand-danger)' }}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {staff.filter(s => !s.is_deleted).filter(s =>
                      s.name.toLowerCase().includes(staffSearch.toLowerCase()) ||
                      (s.phone || '').includes(staffSearch) ||
                      (s.cnic || '').includes(staffSearch)
                    ).length === 0 && (
                      <tr><td colSpan="6" style={{ padding: '28px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        {staffSearch ? t('noStaffFound', { query: staffSearch }) : t('noStaffYet')}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Payroll Logs Checklist */}
              <div className="glass-panel" style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, flex: 1 }}>{t('salaryPayroll')}</h3>
                  {/* Salary search filter */}
                  <div style={{ position: 'relative', minWidth: '200px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                    <input
                      id="salary-history-search"
                      type="text"
                      placeholder={t('salarySearchPH')}
                      value={salarySearch}
                      onChange={(e) => setSalarySearch(e.target.value)}
                      style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.82rem' }}
                    />
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ padding: '12px 16px' }}>{t('employee')}</th>
                        <th style={{ padding: '12px 16px' }}>{t('period')}</th>
                        <th style={{ padding: '12px 16px' }}>{t('basic')}</th>
                        <th style={{ padding: '12px 16px' }}>{t('allowances')}</th>
                        <th style={{ padding: '12px 16px' }}>{t('deductions')}</th>
                        <th style={{ padding: '12px 16px' }}>{t('netPaid')}</th>
                        <th style={{ padding: '12px 16px' }}>{t('status')}</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('salarySlip')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrolls
                        .map(pay => ({ pay, emp: staff.find(s => s.id === (pay.staff_member || pay.staff_member_id)) }))
                        .filter(({ emp }) => emp && emp.name.toLowerCase().includes(salarySearch.toLowerCase()))
                        .map(({ pay, emp }) => (
                          <tr key={pay.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td data-label={t('employee')} style={{ padding: '12px 16px', fontWeight: 600 }}>{emp.name}</td>
                            <td data-label={t('period')} style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{pay.pay_period_start} - {pay.pay_period_end}</td>
                            <td data-label={t('basic')} style={{ padding: '12px 16px' }}>Rs. {(pay.basic_salary || 0).toLocaleString()}</td>
                            <td data-label={t('allowances')} style={{ padding: '12px 16px', color: 'var(--brand-success)' }}>Rs. {(pay.allowances || 0).toLocaleString()}</td>
                            <td data-label={t('deductions')} style={{ padding: '12px 16px', color: 'var(--brand-danger)' }}>Rs. {(pay.deductions || 0).toLocaleString()}</td>
                            <td data-label={t('netPaid')} style={{ padding: '12px 16px', fontWeight: 700 }}>Rs. {(pay.net_paid || 0).toLocaleString()}</td>
                            <td data-label={t('status')} style={{ padding: '12px 16px' }}>
                              <span className={`chip ${pay.status === 'PAID' ? 'chip-success' : 'chip-warning'}`}>
                                {pay.status}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                              <div style={{ display: 'inline-flex', gap: '6px' }}>
                                <a
                                  href={`https://wa.me/${emp.phone || ''}?text=${encodeURIComponent(formatWAMessage('salary_slip', { employee_name: emp.name, business_name: selectedBusiness?.name || 'HisabFlow', period_start: pay.pay_period_start, period_end: pay.pay_period_end, basic_salary: (pay.basic_salary || 0).toLocaleString(), allowances: (pay.allowances || 0).toLocaleString(), deductions: (pay.deductions || 0).toLocaleString(), net_paid: (pay.net_paid || 0).toLocaleString(), status: pay.status }))}`}
                                  onClick={(ev) => onWAClick(ev, waCardForPayroll(emp, pay))}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ padding: '6px 10px', fontSize: '0.78rem', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', background: 'rgba(37,211,102,0.1)', color: '#25d366', display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontWeight: 600 }}
                                   title={t('sendSalarySlipWA')}
                                >
                                   <Share2 size={12} /> {t('whatsapp')}
                                </a>
                                <button
                                  onClick={() => setPrintSalarySlipData({ payroll: pay, employee: emp })}
                                  className="btn btn-secondary"
                                  style={{ padding: '6px 12px', fontSize: '0.78rem', gap: '4px' }}
                                   title={t('generateSalarySlip')}
                                >
                                  <Printer size={12} />
                                   <span>{t('salarySlip')}</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      }
                      {payrolls
                        .map(pay => ({ pay, emp: staff.find(s => s.id === (pay.staff_member || pay.staff_member_id)) }))
                        .filter(({ emp }) => emp && emp.name.toLowerCase().includes(salarySearch.toLowerCase()))
                        .length === 0 && (
                        <tr><td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                          {salarySearch ? t('noPayrollMatch', { query: salarySearch }) : t('noPayrollYet')}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB: SETTINGS */}
          {activeTab === 'settings' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: (settingsSubTab === 'general' || settingsSubTab === 'fbr') ? '780px' : '1200px', width: '100%' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('settingsTitle')}</h3>

              {/* Settings Sub-Tab Navigation */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderBottom: '2px solid var(--border-color)', paddingBottom: '12px' }}>
                {[
                  { id: 'general', label: t('general') || 'General', icon: Settings },
                  { id: 'gallery', label: t('gallery') || 'Gallery', icon: Camera },
                  { id: 'designs', label: t('designs') || 'Designs', icon: Palette },
                  { id: 'fbr', label: 'FBR', icon: ShieldCheck },
                ].map(st => (
                  <button
                    key={st.id}
                    onClick={() => setSettingsSubTab(st.id)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: settingsSubTab === st.id ? 'var(--accent-primary)' : 'var(--border-color)',
                      background: settingsSubTab === st.id ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                      color: settingsSubTab === st.id ? '#ffffff' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <DynIcon icon={st.icon} size={16} />
                    <span>{st.label}</span>
                  </button>
                ))}
              </div>

              {/* ---- FBR SUB-TAB (Digital Invoicing) ---- */}
              {settingsSubTab === 'fbr' && fbrForm && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Master switch — the one flag the whole feature hangs off */}
                  <div style={{ padding: '20px', border: '1px solid ' + (fbrForm.enabled ? 'var(--accent-primary)' : 'var(--border-color)'), borderRadius: '10px', background: 'var(--bg-secondary)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '240px' }}>
                        <h4 style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>FBR Digital Invoicing</h4>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5, maxWidth: '52ch' }}>
                          Send each invoice to FBR (Pakistan's tax authority) the moment it is saved, and print FBR's
                          invoice number with a QR code on it. Only switch this on if your business is registered with
                          FBR (has an NTN / STRN). While this is off, the app works exactly as before.
                        </p>
                      </div>
                      <button
                        onClick={() => setFbrForm({ ...fbrForm, enabled: !fbrForm.enabled })}
                        aria-pressed={fbrForm.enabled}
                        style={{
                          padding: '8px 22px', borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                          border: '1px solid ' + (fbrForm.enabled ? '#16a34a' : 'var(--border-color)'),
                          background: fbrForm.enabled ? '#16a34a' : 'var(--bg-tertiary)',
                          color: fbrForm.enabled ? '#fff' : 'var(--text-secondary)',
                          transition: 'all 0.2s', flexShrink: 0
                        }}
                      >
                        {fbrForm.enabled ? 'ON' : 'OFF'}
                      </button>
                    </div>
                  </div>

                  {fbrForm.enabled && (<>
                    {/* Environment + invoice scope */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '4px', color: 'var(--text-primary)' }}>Where to send invoices</h4>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                        Start with Sandbox (FBR's free test ground  nothing real is submitted). Switch to Production
                        once a sandbox invoice comes back accepted.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                        {[
                          { val: 'SANDBOX', label: 'Sandbox (testing)', desc: 'Practice invoices no legal effect' },
                          { val: 'PRODUCTION', label: 'Production (live)', desc: 'Real invoices, reported to FBR' },
                        ].map(opt => (
                          <div
                            key={opt.val}
                            onClick={() => setFbrForm({ ...fbrForm, environment: opt.val })}
                            style={{
                              padding: '12px', borderRadius: '8px', cursor: 'pointer', userSelect: 'none',
                              border: '1px solid ' + (fbrForm.environment === opt.val ? '#0284c7' : 'var(--border-color)'),
                              background: fbrForm.environment === opt.val ? 'rgba(2,132,199,0.12)' : 'var(--bg-tertiary)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid ' + (fbrForm.environment === opt.val ? '#0284c7' : 'var(--text-muted)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {fbrForm.environment === opt.val && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0284c7' }} />}
                              </div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{opt.label}</div>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '24px', marginTop: '4px' }}>{opt.desc}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Which invoices go to FBR</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                        {[
                          { val: 'SALE', label: 'Sale invoices', desc: 'What you sell' },
                          { val: 'PURCHASE', label: 'Purchase invoices', desc: 'What you buy' },
                          { val: 'BOTH', label: 'Both', desc: 'Sales and purchases' },
                        ].map(opt => (
                          <div
                            key={opt.val}
                            onClick={() => setFbrForm({ ...fbrForm, invoice_scope: opt.val })}
                            style={{
                              padding: '12px', borderRadius: '8px', cursor: 'pointer', userSelect: 'none',
                              border: '1px solid ' + (fbrForm.invoice_scope === opt.val ? '#0284c7' : 'var(--border-color)'),
                              background: fbrForm.invoice_scope === opt.val ? 'rgba(2,132,199,0.12)' : 'var(--bg-tertiary)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid ' + (fbrForm.invoice_scope === opt.val ? '#0284c7' : 'var(--text-muted)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {fbrForm.invoice_scope === opt.val && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0284c7' }} />}
                              </div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{opt.label}</div>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '24px', marginTop: '4px' }}>{opt.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Your identity as FBR knows it */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text-primary)' }}>Your business as registered with FBR</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>NTN / CNIC *</label>
                          <input type="text" value={fbrForm.seller_ntn || ''} onChange={e => setFbrForm({ ...fbrForm, seller_ntn: e.target.value })} placeholder="7-digit NTN or 13-digit CNIC"
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Business name (with FBR) *</label>
                          <input type="text" value={fbrForm.seller_business_name || ''} onChange={e => setFbrForm({ ...fbrForm, seller_business_name: e.target.value })} placeholder="Exactly as FBR knows it"
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Province *</label>
                          <select value={fbrForm.seller_province || ''} onChange={e => setFbrForm({ ...fbrForm, seller_province: e.target.value })}
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                            <option value="">Choose province…</option>
                            {/* Keep the saved value selectable even when it is
                                not in the list (FBR spellings can differ). */}
                            {(() => {
                              const list = fbrForm.seller_province && !fbrProvinces.includes(fbrForm.seller_province)
                                ? [...fbrProvinces, fbrForm.seller_province] : fbrProvinces;
                              return list.map(p => <option key={p} value={p}>{p}</option>);
                            })()}
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Address</label>
                          <input type="text" value={fbrForm.seller_address || ''} onChange={e => setFbrForm({ ...fbrForm, seller_address: e.target.value })} placeholder="Shop address"
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>POS ID <span style={{ color: 'var(--text-muted)' }}>(if FBR gave you one)</span></label>
                          <input type="text" value={fbrForm.pos_id || ''} onChange={e => setFbrForm({ ...fbrForm, pos_id: e.target.value })} placeholder="e.g. 12345"
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Security Token {fbrForm.security_token_set ? <span style={{ color: '#16a34a' }}>(saved)</span> : <span style={{ color: 'var(--text-muted)' }}>(required)</span>}</label>
                          <input type="password" autoComplete="off" value={fbrForm.security_token || ''} onChange={e => setFbrForm({ ...fbrForm, security_token: e.target.value })}
                            placeholder={fbrForm.security_token_set ? '••••••••  (leave blank to keep)' : 'Paste token from e.fbr.gov.pk'}
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                          <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                            FBR portal → Registration → POS Client Registration. Stored safely on the server, never shown again.
                          </p>
                        </div>
                      </div>
                      {fbrForm.environment === 'SANDBOX' && (
                        <div style={{ marginTop: '12px', maxWidth: '240px' }}>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Sandbox scenario ID</label>
                          <input type="text" value={fbrForm.scenario_id || ''} onChange={e => setFbrForm({ ...fbrForm, scenario_id: e.target.value })} placeholder="e.g. SN001"
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        </div>
                      )}
                    </div>

                    {/* Tax defaults for products that don't override them */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '4px', color: 'var(--text-primary)' }}>Tax defaults</h4>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                        Used for products that don't have their own FBR tax rate. Most shops keep 0%.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Default sales-tax rate</label>
                          <select value={fbrForm.default_tax_rate || '0%'} onChange={e => setFbrForm({ ...fbrForm, default_tax_rate: e.target.value })}
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                            {['0%', '1%', '2%', '3%', '4%', '5%', '10%', '15%', '17%', '18%', '19%', '20%', '25%'].map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 600 }}>Sale type</label>
                          <input type="text" value={fbrForm.default_sale_type || ''} onChange={e => setFbrForm({ ...fbrForm, default_sale_type: e.target.value })}
                            style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        </div>
                      </div>
                    </div>

                    {/* Test result + actions */}
                    {fbrTestResult && (
                      <div style={{
                        padding: '12px 16px', borderRadius: '8px',
                        border: '1px solid ' + (fbrTestResult.ok ? '#16a34a' : '#dc2626'),
                        background: fbrTestResult.ok ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)',
                        fontSize: '0.84rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px'
                      }}>
                        {fbrTestResult.ok ? <CheckCircle2 size={16} color="#16a34a" /> : <XCircle size={16} color="#dc2626" />}
                        <span>{fbrTestResult.message}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button onClick={() => saveFbrSettings()} disabled={fbrBusy}
                        style={{ padding: '10px 26px', borderRadius: '8px', fontWeight: 700, fontSize: '0.88rem', cursor: fbrBusy ? 'wait' : 'pointer', border: '1px solid var(--accent-primary)', background: 'var(--accent-primary)', color: '#fff', opacity: fbrBusy ? 0.6 : 1 }}>
                        {fbrBusy ? 'Working…' : 'Save'}
                      </button>
                      <button onClick={() => saveFbrSettings({ withTest: true })} disabled={fbrBusy}
                        style={{ padding: '10px 26px', borderRadius: '8px', fontWeight: 700, fontSize: '0.88rem', cursor: fbrBusy ? 'wait' : 'pointer', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', opacity: fbrBusy ? 0.6 : 1 }}>
                        Save &amp; Test Connection
                      </button>
                    </div>
                    <p style={{ fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5, margin: 0 }}>
                      How it works after switching on: each new invoice is sent to FBR right after it is saved the
                      invoice itself is saved first and always, so billing never depends on FBR being reachable. Watch
                      for the FBR badge on saved invoices: green with an FBR number means accepted; red means FBR
                      rejected it (tap to see why and resend). Add an <b>HS Code</b> to each product and, optionally,
                      an NTN/STRN on customers .FBR needs them on every line.
                    </p>
                  </>)}
                </div>
              )}

              {/* ---- GALLERY SUB-TAB ---- */}
              {settingsSubTab === 'gallery' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {gallerySelectMode && (
                    <div className="glass-panel" style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--accent-primary)', color: '#fff', borderRadius: '10px' }}>
                      <ArrowLeft size={18} style={{ cursor: 'pointer' }} onClick={() => { setGallerySelectMode(false); setGallerySelectTarget(null); setActiveTab('billing'); }} />
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t('selectFromGallery') || 'Select a product from the gallery'}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700 }}>{t('productGallery') || 'Product Gallery'}</h2>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                      <select value={galleryCategoryFilter} onChange={(e) => setGalleryCategoryFilter(e.target.value)} style={{ padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}>
                        <option value="all">{t('allTypes') || 'All Types'}</option>
                        {productTypes.map(pt => (
                          <option key={pt.id} value={pt.name}>{pt.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {products.filter(p => !p.is_deleted && p.photo && (galleryCategoryFilter === 'all' || p.product_type === galleryCategoryFilter)).length === 0 ? (
                    <div className="glass-panel" style={{ padding: '60px', textAlign: 'center' }}>
                      <Camera size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>{t('noPhotosYet') || 'No product photos yet. Add photos when creating or editing products.'}</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
                      {products.filter(p => !p.is_deleted && p.photo && (galleryCategoryFilter === 'all' || p.product_type === galleryCategoryFilter)).map(p => (
                        <div key={p.id} className="glass-panel" style={{ overflow: 'hidden', cursor: gallerySelectMode ? 'pointer' : 'default', transition: 'transform 0.2s, box-shadow 0.2s', border: gallerySelectMode ? '2px solid var(--accent-primary)' : '1px solid var(--border-color)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                          onClick={() => {
                            if (gallerySelectMode && gallerySelectTarget && p) {
                              if (gallerySelectTarget.type === 'sale') selectSaleProduct(gallerySelectTarget.index, p);
                              else if (gallerySelectTarget.type === 'saleEntry') selectSaleEntryProduct(p);
                              else if (gallerySelectTarget.type === 'purchase') selectPurchaseProduct(gallerySelectTarget.index, p);
                              else if (gallerySelectTarget.type === 'purchaseEntry') selectPurchaseEntryProduct(p);
                              setGallerySelectMode(false);
                              setGallerySelectTarget(null);
                              setActiveTab('billing');
                            }
                          }}
                        >
                          <div style={{ width: '100%', height: '180px', overflow: 'hidden', background: 'var(--bg-tertiary)' }}>
                            <img src={getPhotoUrl(p.photo)} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          <div style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px' }}>{p.name}</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ color: 'var(--brand-success)', fontWeight: 600, fontSize: '0.85rem' }}>Rs. {parseFloat(p.sale_price).toLocaleString()}</span>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{pcsText(p.stock_quantity)} {p.unit}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ---- DESIGNS SUB-TAB ---- */}
              {settingsSubTab === 'designs' && (
                <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('businessCardDesigner')}</h3>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '4px' }}>Design and print premium business cards for your organization.</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => { setPrintLayout('single'); setShowCardPrintOverlay(true); }} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                        <Printer size={16} style={{ marginRight: '6px' }} /> {t('printSingle')}
                      </button>
                      <button onClick={() => { setPrintLayout('grid'); setShowCardPrintOverlay(true); }} className="btn btn-secondary" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                        <Printer size={16} style={{ marginRight: '6px' }} /> {t('printGrid')}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
                    <div style={{ flex: '1 1 300px', background: 'var(--bg-secondary)', padding: '20px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                      <h4 style={{ fontWeight: 600, marginBottom: '16px' }}>{t('cardDetails')}</h4>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('selectTemplate')}</label>
                        <select value={designerTemplate} onChange={e => setDesignerTemplate(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                          <option value="minimalist">{t('minimalist')}</option>
                          <option value="dark">{t('modernDark')}</option>
                          <option value="gradient">{t('creativeGradient')}</option>
                          <option value="organic">{t('organicGreen') || 'Organic Green'}</option>
                          <option value="navygold">{t('navyGoldLuxury') || 'Navy Gold Luxury'}</option>
                          <option value="mandala">{t('mandala') || 'Mandala Cultural'}</option>
                        </select>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('primaryColor')}</label>
                        <input type="color" value={designerColor} onChange={e => setDesignerColor(e.target.value)} style={{ width: '100%', height: '40px', padding: '2px', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <input type="text" placeholder={t('companyName') || 'Company Name'} value={designerData.companyName} onChange={e => setDesignerData({...designerData, companyName: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        <input type="text" placeholder={t('companyTagline') || 'Company Tagline (Features)'} value={designerData.companyTagline} onChange={e => setDesignerData({...designerData, companyTagline: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        <hr style={{ borderTop: '1px dashed var(--border-color)', margin: '4px 0' }} />
                        <input type="text" placeholder={selectedBusiness?.name || t('personName')} value={designerData.name} onChange={e => setDesignerData({...designerData, name: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        <input type="text" placeholder={t('tagline')} value={designerData.tagline} onChange={e => setDesignerData({...designerData, tagline: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        <input type="text" placeholder={selectedBusiness?.phone || t('phone')} value={designerData.phone} onChange={e => setDesignerData({...designerData, phone: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        <input type="email" placeholder={selectedBusiness?.email || t('email')} value={designerData.email} onChange={e => setDesignerData({...designerData, email: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                        <textarea placeholder={selectedBusiness?.address || t('address')} value={designerData.address} onChange={e => setDesignerData({...designerData, address: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', minHeight: '60px' }}></textarea>
                        <input type="text" placeholder={t('website') || 'Website'} value={designerData.website} onChange={e => setDesignerData({...designerData, website: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                      </div>
                    </div>
                    <div style={{ flex: '1 1 500px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'var(--bg-tertiary)', padding: '40px 20px', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border-color)', minHeight: '400px' }}>
                      {(() => {
                        const cName = designerData.name || selectedBusiness?.name || 'Your Name';
                        const cTagline = designerData.tagline || 'CEO & Founder';
                        const cPhone = designerData.phone || selectedBusiness?.phone || '+1 234 567 890';
                        const cEmail = designerData.email || selectedBusiness?.email || 'email@example.com';
                        const cAddress = designerData.address || selectedBusiness?.address || '123 Business Avenue, City';
                        const cWebsite = designerData.website || 'www.example.com';
                        const cssVars = { '--bc-primary': designerColor };
                        const cCompName = designerData.companyName || selectedBusiness?.name || 'Your Company';
                        const cCompTagline = designerData.companyTagline || 'Company Features / Tagline';
                        const cLogo = selectedBusiness?.logo ? getLogoUrl(selectedBusiness.logo) : null;
                        const renderCardContent = (key) => (
                          <div key={key} className={`business-card bc-template-${designerTemplate}`} style={cssVars}>
                            {designerTemplate === 'gradient' ? <div className="bc-glass-pane"></div> : null}
                            <div className="bc-inner-layout">
                              <div className="bc-col-left">
                                {cLogo && <img src={cLogo} alt="Logo" className="bc-logo" />}
                                <div className="bc-comp-name">{cCompName}</div>
                                <div className="bc-comp-tagline">{cCompTagline}</div>
                              </div>
                              <div className="bc-divider"></div>
                              <div className="bc-col-right">
                                <div className="bc-person-info">
                                  <div className="bc-name">{cName}</div>
                                  <div className="bc-tagline">{cTagline}</div>
                                </div>
                                <div className="bc-details">
                                  <div className="bc-detail-item"><Phone size={10} /> {cPhone}</div>
                                  <div className="bc-detail-item"><Mail size={10} /> {cEmail}</div>
                                  <div className="bc-detail-item"><Globe size={10} /> {cWebsite}</div>
                                  <div className="bc-detail-item"><MapPin size={10} /> {cAddress.substring(0, 30)}{cAddress.length > 30 ? '...' : ''}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                        if (printLayout === 'single') {
                          return (<div className="print-cards-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>{renderCardContent('single')}</div>);
                        }
                        return (<div className="print-cards-container">{Array(10).fill().map((_, i) => renderCardContent(i))}</div>);
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* ---- GENERAL SUB-TAB ---- */}
              {settingsSubTab === 'general' && (<>
              {(() => {
                const endDate = localStorage.getItem('subscription_end_date');
                const plan = localStorage.getItem('subscription_plan') || '1_month';
                const planLabel = plan === '2_days' ? t('plan2Days') : plan === '1_month' ? '1 Month' : plan === '1_year' ? '1 Year' : plan === '3_years' ? '3 Years' : 'Lifetime';
                const hasDate = !!endDate;
                const expiry = hasDate ? new Date(endDate) : null;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const daysLeft = hasDate ? Math.ceil((expiry - today) / (1000 * 60 * 60 * 24)) : null;
                const isExpired = daysLeft !== null && daysLeft < 0;
                const isUrgent = !isExpired && daysLeft !== null && daysLeft <= 7;
                const bgColor = isExpired ? 'rgba(239,68,68,0.08)' : isUrgent ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)';
                const borderColor = isExpired ? 'rgba(239,68,68,0.25)' : isUrgent ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.25)';
                const textColor = isExpired ? '#ef4444' : isUrgent ? '#f59e0b' : '#10b981';
                const iconColor = isExpired ? '#ef4444' : isUrgent ? '#f59e0b' : '#10b981';
                return (
                  <div className="glass-panel" style={{ padding: '20px', background: bgColor, border: `1px solid ${borderColor}` }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                      <div style={{ flexShrink: 0, marginTop: '2px' }}>
                        <Award size={22} color={iconColor} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{t('subPlan')}</h4>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                          {t('currentPlanLabel')} <strong>{planLabel}</strong>
                        </p>
                        {hasDate ? (
                          <p style={{ fontSize: '0.85rem', color: textColor, fontWeight: 600, marginBottom: '2px' }}>
                            {isExpired
                              ? `Expired on ${expiry.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' })}`
                              : `Expires on ${expiry.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' })} (${daysLeft} day${daysLeft !== 1 ? 's' : ''} left)`
                            }
                          </p>
                        ) : (
                          <p style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 600, marginBottom: '2px' }}>{t('subActive')}</p>
                        )}
                        {isExpired && (
                          <p style={{ fontSize: '0.8rem', color: '#ef4444', marginBottom: '12px' }}>{t('subExpiredRestrict')}</p>
                        )}
                        {isUrgent && !isExpired && (
                          <p style={{ fontSize: '0.8rem', color: '#f59e0b', marginBottom: '12px'}}>{t('subExpiringSoon')}</p>
                        )}
                        {!isExpired && !isUrgent && hasDate && (
                          <p style={{ fontSize: '0.8rem', color: '#10b981', marginBottom: '12px' }}>{t('subActiveFull')}</p>
                        )}
                        <button
                          onClick={() => setShowRenewalPlanModal(true)}
                          disabled={renewalAlreadyRequested}
                          style={{
                            padding: '8px 18px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            cursor: renewalAlreadyRequested ? 'default' : 'pointer',
                            background: renewalAlreadyRequested ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #38bdf8, #6366f1)',
                            color: renewalAlreadyRequested ? '#64748b' : '#fff',
                            opacity: renewalAlreadyRequested ? 0.7 : 1
                          }}
                        >
                          {renewalAlreadyRequested ? t('renewalRequested') : t('requestPlanRenewal')}
                        </button>
                        {renewalMessage && (
                          <p style={{ fontSize: '0.8rem', color: renewalMessage.includes('sent') || renewalMessage.includes('Sent') ? '#10b981' : '#ef4444', marginTop: '8px' }}>{renewalMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Backup Status */}
              <div className="glass-panel" style={{ padding: '20px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.18)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <div style={{ flexShrink: 0, marginTop: '2px' }}>
                    <HardDrive size={22} color="#6366f1" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                      <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{t('dataBackup')}</h4>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleTenantManualBackup}
                        disabled={isBackingUp}
                        style={{
                          fontSize: '0.78rem',
                          padding: '5px 12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          borderRadius: '8px',
                          cursor: isBackingUp ? 'not-allowed' : 'pointer',
                          opacity: isBackingUp ? 0.7 : 1,
                        }}
                      >
                        <RefreshCw size={13} className={isBackingUp ? "spin" : ""} />
                        {isBackingUp ? (t('backingUp') || 'Creating backup...') : (t('runManualBackup') || 'Run Manual Backup')}
                      </button>
                    </div>
                    {backupInfo.last_backup_at ? (
                      <>
                        <p style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 600, marginBottom: '2px' }}>
                          {t('lastBackup')}: {new Date(backupInfo.last_backup_at).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          {backupInfo.last_backup_size ? `${t('size')}: ${(backupInfo.last_backup_size / 1024).toFixed(1)} KB` : ''}
                        </p>
                      </>
                    ) : (
                      <p style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600, marginBottom: '2px' }}>{t('noBackupYet')}</p>
                    )}
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '4px', marginBottom: 0 }}>{t('backupDescription')}</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp Message Templates */}
              <div className="glass-panel" style={{ padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '4px' }}>{t('waTemplatesTitle')}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>{t('waTemplatesDesc')}</p>

                {/* Share format: plain text template vs rendered image card */}
                <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', background: 'var(--bg-secondary)' }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>{t('waShareModeTitle')}</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('waShareModeDesc')}</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { mode: 'text', icon: MessageSquare, title: t('waShareModeText'), desc: t('waShareModeTextDesc') },
                      { mode: 'image', icon: ImageIcon, title: t('waShareModeImage'), desc: t('waShareModeImageDesc') }
                    ].map(opt => {
                      const active = waShareMode === opt.mode;
                      return (
                        <button
                          key={opt.mode}
                          type="button"
                          onClick={() => updateWAShareMode(opt.mode)}
                          aria-pressed={active}
                          style={{
                            flex: '1 1 220px',
                            textAlign: 'left',
                            padding: '12px 14px',
                            borderRadius: '8px',
                            border: `1px solid ${active ? '#0284c7' : 'var(--border-color)'}`,
                            background: active ? 'rgba(2,132,199,0.10)' : 'var(--bg-tertiary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px'
                          }}
                        >
                          <DynIcon icon={opt.icon} size={18} />
                          <span style={{ minWidth: 0, flex: 1 }}>
                            <span style={{ display: 'block', fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>{opt.title}</span>
                            <span style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px' }}>{opt.desc}</span>
                          </span>
                          <span style={{ width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, alignSelf: 'center', border: `2px solid ${active ? '#0284c7' : 'var(--border-color)'}`, background: active ? '#0284c7' : 'transparent' }} />
                        </button>
                      );
                    })}
                  </div>

                  {waShareMode === 'image' && (
                    <div style={{ marginTop: '14px' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>{t('waShareModePreview')}</div>
                      <div style={{ display: 'flex', padding: '18px', borderRadius: '10px', background: 'var(--bg-tertiary)', border: '1px dashed var(--border-color)', maxWidth: '100%', overflowX: 'auto' }}>
                        <div style={{ margin: 'auto' }}><WACard card={waSampleCard()} /></div>
                      </div>
                    </div>
                  )}
                </div>
                
                {[
                  { key: 'balance_reminder', icon: Bell, titleKey: 'waBalanceReminder', descKey: 'waBalanceReminderDesc', vars: ['party_name', 'business_name', 'balance'] },
                  { key: 'transaction_receipt', icon: Receipt, titleKey: 'waTransactionReceipt', descKey: 'waTransactionReceiptDesc', vars: ['party_name', 'business_name', 'date', 'description', 'amount', 'type'] },
                  { key: 'invoice_message', icon: FileText, titleKey: 'waInvoiceMessage', descKey: 'waInvoiceMessageDesc', vars: ['starting_note', 'party_name', 'invoice_number', 'items', 'total', 'discount', 'subtotal', 'paid_amount', 'remaining', 'ending_note', 'date', 'business_name'] },
                  { key: 'salary_slip', icon: Wallet, titleKey: 'waSalarySlip', descKey: 'waSalarySlipDesc', vars: ['employee_name', 'business_name', 'period_start', 'period_end', 'basic_salary', 'allowances', 'deductions', 'net_paid', 'status'] }
                ].map(tmpl => (
                  <div key={tmpl.key} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                    <div 
                      onClick={() => setEditingTemplate(editingTemplate === tmpl.key ? null : tmpl.key)}
                      style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: editingTemplate === tmpl.key ? 'var(--bg-tertiary)' : 'transparent' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <DynIcon icon={tmpl.icon} size={18} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t(tmpl.titleKey)}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t(tmpl.descKey)}</div>
                        </div>
                      </div>
                      <span style={{ display: 'flex', alignItems: 'center' }}>{editingTemplate === tmpl.key ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                    </div>
                    
                    {editingTemplate === tmpl.key && (
                      <div style={{ padding: '16px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '6px', display: 'block', color: 'var(--text-secondary)' }}>{t('waAvailableVars')}</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {tmpl.vars.map(v => (
                              <button 
                                key={v} 
                                type="button"
                                onClick={() => {
                                  const textarea = document.getElementById(`tmpl-${tmpl.key}`);
                                  if (textarea) {
                                    const start = textarea.selectionStart;
                                    const end = textarea.selectionEnd;
                                    const text = textarea.value;
                                    const newText = text.substring(0, start) + `{${v}}` + text.substring(end);
                                    saveWATemplate(tmpl.key, newText);
                                    setTimeout(() => { textarea.selectionStart = textarea.selectionEnd = start + v.length + 2; textarea.focus(); }, 0);
                                  }
                                }}
                                style={{ padding: '3px 8px', fontSize: '0.72rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--brand-info)', cursor: 'pointer', fontWeight: 500 }}
                              >
                                {`{${v}}`}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <textarea
                          id={`tmpl-${tmpl.key}`}
                          value={waTemplates[tmpl.key]}
                          onChange={(e) => saveWATemplate(tmpl.key, e.target.value)}
                          rows={6}
                          style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem', fontFamily: 'monospace', resize: 'vertical', marginBottom: '10px' }}
                        />
                        
                        <div style={{ padding: '10px', background: 'var(--bg-tertiary)', borderRadius: '6px', border: '1px dashed var(--border-color)', marginBottom: '12px' }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', display: 'block' }}>{t('waPreview')}</label>
                          <pre style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', margin: 0, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                            {formatWAMessage(tmpl.key, {
                              party_name: 'Ahmed Ali',
                              business_name: selectedBusiness?.name || 'My Shop',
                              balance: '5,000',
                              date: new Date().toLocaleDateString(),
                              description: 'Grocery order',
                              amount: '2,500',
                              type: 'GAVE (you gave)',
                              invoice_number: 'INV-001',
                              items: 'Item 1 @ 500 x 2 = 1,000\nItem 2 @ 300 x 1 = 300',
                              total: '1,300',
                              discount: '50',
                              subtotal: '1,250',
                              paid_amount: '500',
                              remaining: '750',
                              starting_note: 'Assalamualikom',
                              ending_note: 'Thanks for your business!',
                              employee_name: 'Usman',
                              period_start: '2026-07-01',
                              period_end: '2026-07-31',
                              basic_salary: '30,000',
                              allowances: '5,000',
                              deductions: '2,000',
                              net_paid: '33,000',
                              status: 'PAID'
                            })}
                          </pre>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => resetWATemplate(tmpl.key)} className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('waResetDefault')}</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Invoices & Reports Print Customization */}
              <div className="glass-panel" style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(2,132,199,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Printer size={22} color="#0284c7" />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>{t('printCustomizationTitle')}</h3>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '2px 0 0' }}>{t('printCustomizationDesc')}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetPrintSettings}
                    style={{ padding: '6px 12px', fontSize: '0.78rem', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    {t('resetPrintSettings')}
                  </button>
                </div>

                {/* Sub-tab Switcher */}
                <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '18px' }}>
                  <button
                    type="button"
                    onClick={() => setPrintSettingsSubTab('invoices')}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 18px',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: 'none',
                      background: printSettingsSubTab === 'invoices' ? 'linear-gradient(135deg, #0284c7, #2563eb)' : 'var(--bg-tertiary)',
                      color: printSettingsSubTab === 'invoices' ? '#ffffff' : 'var(--text-secondary)'
                    }}
                  >
                    <Receipt size={14} /> {t('invoicesPrintTab')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPrintSettingsSubTab('reports')}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 18px',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: 'none',
                      background: printSettingsSubTab === 'reports' ? 'linear-gradient(135deg, #0284c7, #2563eb)' : 'var(--bg-tertiary)',
                      color: printSettingsSubTab === 'reports' ? '#ffffff' : 'var(--text-secondary)'
                    }}
                  >
                    <ChartColumn size={14} /> {t('reportsPrintTab')}
                  </button>
                </div>

                {/* INVOICES PRINT SETTINGS */}
                {printSettingsSubTab === 'invoices' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Header & Layout */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '14px', color: 'var(--text-primary)' }}>{t('headerAndLayout')}</h4>
                      
                      <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{t('logoPosition')}</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[
                            { val: 'left', label: t('logoLeft') },
                            { val: 'center', label: t('logoCenter') },
                            { val: 'right', label: t('logoRight') },
                            { val: 'none', label: t('logoNone') }
                          ].map(opt => (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => updatePrintSetting('logoPosition', opt.val)}
                              style={{
                                padding: '6px 14px',
                                borderRadius: '6px',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                border: '1px solid ' + (printSettings.logoPosition === opt.val ? '#0284c7' : 'var(--border-color)'),
                                background: printSettings.logoPosition === opt.val ? 'rgba(2,132,199,0.15)' : 'var(--bg-tertiary)',
                                color: printSettings.logoPosition === opt.val ? '#0284c7' : 'var(--text-primary)',
                                cursor: 'pointer'
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{t('headerLayout')}</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[
                            { val: 'standard', label: t('headerStandard') },
                            { val: 'center', label: t('headerCentered') }
                          ].map(opt => (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => updatePrintSetting('headerLayout', opt.val)}
                              style={{
                                padding: '6px 14px',
                                borderRadius: '6px',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                border: '1px solid ' + (printSettings.headerLayout === opt.val ? '#0284c7' : 'var(--border-color)'),
                                background: printSettings.headerLayout === opt.val ? 'rgba(2,132,199,0.15)' : 'var(--bg-tertiary)',
                                color: printSettings.headerLayout === opt.val ? '#0284c7' : 'var(--text-primary)',
                                cursor: 'pointer'
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{t('itemsPosition')}</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[
                            { val: 'standard', label: t('itemsStandard') },
                            { val: 'items_first', label: t('itemsFirst') }
                          ].map(opt => (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => updatePrintSetting('itemsPosition', opt.val)}
                              style={{
                                padding: '6px 14px',
                                borderRadius: '6px',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                border: '1px solid ' + (printSettings.itemsPosition === opt.val ? '#0284c7' : 'var(--border-color)'),
                                background: printSettings.itemsPosition === opt.val ? 'rgba(2,132,199,0.15)' : 'var(--bg-tertiary)',
                                color: printSettings.itemsPosition === opt.val ? '#0284c7' : 'var(--text-primary)',
                                cursor: 'pointer'
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* How the discount is entered — whole invoice or row by row */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '4px', color: 'var(--text-primary)' }}>{t('discountMode')}</h4>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>{t('discountModeDesc')}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                        {[
                          { val: 'whole', labelKey: 'discountWholeInvoice', descKey: 'discountWholeInvoiceDesc' },
                          { val: 'item', labelKey: 'discountPerItem', descKey: 'discountPerItemDesc' },
                          { val: 'both', labelKey: 'discountBoth', descKey: 'discountBothDesc' }
                        ].map(opt => (
                          <div
                            key={opt.val}
                            onClick={() => updatePrintSetting('discountMode', opt.val)}
                            style={{
                              padding: '12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              userSelect: 'none',
                              border: '1px solid ' + (printSettings.discountMode === opt.val ? '#0284c7' : 'var(--border-color)'),
                              background: printSettings.discountMode === opt.val ? 'rgba(2,132,199,0.12)' : 'var(--bg-tertiary)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid ' + (printSettings.discountMode === opt.val ? '#0284c7' : 'var(--text-muted)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {printSettings.discountMode === opt.val && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0284c7' }} />}
                              </div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t(opt.labelKey)}</div>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '24px', marginTop: '4px' }}>{t(opt.descKey)}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* How the typed number is read — flat amount or percentage */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '4px', color: 'var(--text-primary)' }}>{t('discountType')}</h4>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>{t('discountTypeDesc')}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                        {[
                          { val: 'amount', labelKey: 'discountTypeAmount', descKey: 'discountTypeAmountDesc' },
                          { val: 'percentage', labelKey: 'discountTypePercent', descKey: 'discountTypePercentDesc' }
                        ].map(opt => (
                          <div
                            key={opt.val}
                            onClick={() => updatePrintSetting('discountType', opt.val)}
                            style={{
                              padding: '12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              userSelect: 'none',
                              border: '1px solid ' + ((printSettings.discountType || 'amount') === opt.val ? '#0284c7' : 'var(--border-color)'),
                              background: (printSettings.discountType || 'amount') === opt.val ? 'rgba(2,132,199,0.12)' : 'var(--bg-tertiary)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid ' + ((printSettings.discountType || 'amount') === opt.val ? '#0284c7' : 'var(--text-muted)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {(printSettings.discountType || 'amount') === opt.val && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0284c7' }} />}
                              </div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t(opt.labelKey)}</div>
                            </div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: '24px', marginTop: '4px' }}>{t(opt.descKey)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Field Visibility Toggles Grid */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '14px', color: 'var(--text-primary)' }}>{t('fieldVisibility')}</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                        {[
                          { key: 'showSerialNumber', labelKey: 'showSerialNumber', descKey: 'showSerialNumberDesc' },
                          { key: 'showSubtotal', labelKey: 'showSubtotal', descKey: 'showSubtotalDesc' },
                          { key: 'showDiscount', labelKey: 'showDiscount', descKey: 'showDiscountDesc' },
                          { key: 'showPaidAmount', labelKey: 'showPaidAmount', descKey: 'showPaidAmountDesc' },
                          { key: 'showRemainingBalance', labelKey: 'showRemainingBalance', descKey: 'showRemainingBalanceDesc' },
                          { key: 'showPartyPhone', labelKey: 'showPartyPhone', descKey: 'showPartyPhoneDesc' },
                          { key: 'showPartyCnic', labelKey: 'showPartyCnic', descKey: 'showPartyCnicDesc' },
                          { key: 'showPartyAddress', labelKey: 'showPartyAddress', descKey: 'showPartyAddressDesc' },
                          { key: 'showBusinessAddress', labelKey: 'showBusinessAddress', descKey: 'showBusinessAddressDesc' },
                          { key: 'showBusinessPhoneEmail', labelKey: 'showBusinessPhoneEmail', descKey: 'showBusinessPhoneEmailDesc' },
                          { key: 'showInvoiceTime', labelKey: 'showInvoiceTime', descKey: 'showInvoiceTimeDesc' },
                          { key: 'showSignatureBlock', labelKey: 'showSignatureBlock', descKey: 'showSignatureBlockDesc' }
                        ].map(item => (
                          <div
                            key={item.key}
                            onClick={() => updatePrintSetting(item.key, !printSettings[item.key])}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '10px 12px',
                              borderRadius: '8px',
                              background: 'var(--bg-tertiary)',
                              border: '1px solid var(--border-color)',
                              cursor: 'pointer',
                              gap: '10px',
                              userSelect: 'none'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t(item.labelKey)}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{t(item.descKey)}</div>
                            </div>
                            <div
                              style={{
                                width: '38px',
                                height: '22px',
                                borderRadius: '11px',
                                background: printSettings[item.key] ? '#0284c7' : 'rgba(150,150,150,0.3)',
                                position: 'relative',
                                transition: 'background 0.2s ease',
                                flexShrink: 0
                              }}
                            >
                              <div
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  background: '#ffffff',
                                  position: 'absolute',
                                  top: '3px',
                                  left: printSettings[item.key] ? '19px' : '3px',
                                  transition: 'left 0.2s ease',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Custom Table Column Headers */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                        <div>
                          <h4 style={{ fontSize: '0.92rem', fontWeight: 700, margin: '0 0 4px', color: 'var(--text-primary)' }}>
                            {t('customColumnHeaders') || 'Custom Table Column Headers'}
                          </h4>
                          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>
                            {t('customColumnHeadersDesc') || 'Customize the column header titles printed on your invoices. Leave blank to use default names.'}
                          </p>
                        </div>
                        {Object.values(printSettings.columnNames || {}).some(Boolean) && (
                          <button
                            type="button"
                            onClick={() => updatePrintSetting('columnNames', {
                              serialNo: '',
                              itemDescription: '',
                              quantity: '',
                              unitPrice: '',
                              subtotal: '',
                              bulkQuantity: '',
                              smallQuantity: '',
                              bulkPrice: '',
                              smallPrice: ''
                            })}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#0284c7',
                              fontSize: '0.78rem',
                              cursor: 'pointer',
                              padding: '2px 6px',
                              textDecoration: 'underline',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {t('resetColumnNames') || 'Reset column names'}
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {t('colSerialNo') || 'Serial No. (S.N)'}
                          </label>
                          <input
                            type="text"
                            value={printSettings.columnNames?.serialNo || ''}
                            placeholder="S.N"
                            onChange={e => updateColumnName('serialNo', e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {t('colItemDescription') || 'Product / Item Name'}
                          </label>
                          <input
                            type="text"
                            value={printSettings.columnNames?.itemDescription || ''}
                            placeholder={t('itemDescription') || 'Product'}
                            onChange={e => updateColumnName('itemDescription', e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {t('colQuantity') || 'Quantity (Qty)'}
                          </label>
                          <input
                            type="text"
                            value={printSettings.columnNames?.quantity || ''}
                            placeholder={t('qty') || 'Qty'}
                            onChange={e => updateColumnName('quantity', e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {t('colUnitPrice') || 'Unit Price / Rate'}
                          </label>
                          <input
                            type="text"
                            value={printSettings.columnNames?.unitPrice || ''}
                            placeholder={t('unitPrice') || 'Price'}
                            onChange={e => updateColumnName('unitPrice', e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                          />
                        </div>

                        <div>
                          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {t('colSubtotal') || 'Total / Amount'}
                          </label>
                          <input
                            type="text"
                            value={printSettings.columnNames?.subtotal || ''}
                            placeholder={t('subtotal') || 'Amount'}
                            onChange={e => updateColumnName('subtotal', e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                          />
                        </div>
                      </div>

                      {/* Packaging / Bulk Columns (Optional) */}
                      <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px dashed var(--border-color)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px' }}>
                          <Package size={14} /> {t('packagingHeadersOptional') || 'Packaging / Bulk Column Headers (Optional)'}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '4px' }}>
                              {t('colBulkQuantity') || 'Packaging Unit (e.g. CTN)'}
                            </label>
                            <input
                              type="text"
                              value={printSettings.columnNames?.bulkQuantity || ''}
                              placeholder="CTN / CARTON"
                              onChange={e => updateColumnName('bulkQuantity', e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '4px' }}>
                              {t('colSmallQuantity') || 'Loose Pieces (PC)'}
                            </label>
                            <input
                              type="text"
                              value={printSettings.columnNames?.smallQuantity || ''}
                              placeholder="PC"
                              onChange={e => updateColumnName('smallQuantity', e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '4px' }}>
                              {t('colBulkPrice') || 'Packaging Cost / Price'}
                            </label>
                            <input
                              type="text"
                              value={printSettings.columnNames?.bulkPrice || ''}
                              placeholder="CTN Price / Cost"
                              onChange={e => updateColumnName('bulkPrice', e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginBottom: '4px' }}>
                              {t('colSmallPrice') || 'Piece Cost / Price'}
                            </label>
                            <input
                              type="text"
                              value={printSettings.columnNames?.smallPrice || ''}
                              placeholder="PC Price / Cost"
                              onChange={e => updateColumnName('smallPrice', e.target.value)}
                              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Invoice Footer Message / Note */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text-primary)' }}>{t('invoiceFooterNote')}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t('enableInvoiceNote')}</div>
                        </div>
                        <div
                          onClick={() => updatePrintSetting('showInvoiceFooterNote', !printSettings.showInvoiceFooterNote)}
                          style={{
                            width: '38px',
                            height: '22px',
                            borderRadius: '11px',
                            background: printSettings.showInvoiceFooterNote ? '#0284c7' : 'rgba(150,150,150,0.3)',
                            position: 'relative',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        >
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              background: '#ffffff',
                              position: 'absolute',
                              top: '3px',
                              left: printSettings.showInvoiceFooterNote ? '19px' : '3px',
                              transition: 'left 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                            }}
                          />
                        </div>
                      </div>

                      {printSettings.showInvoiceFooterNote && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                              {t('noteTitleLabel')}
                            </label>
                            <input
                              type="text"
                              value={printSettings.invoiceNoteTitle || ''}
                              placeholder={t('noteTitlePlaceholder')}
                              onChange={e => updatePrintSetting('invoiceNoteTitle', e.target.value)}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>
                              {t('noteTextLabel')}
                            </label>
                            <textarea
                              rows={3}
                              value={printSettings.invoiceNoteText || ''}
                              placeholder={t('noteTextPlaceholder')}
                              onChange={e => updatePrintSetting('invoiceNoteText', e.target.value)}
                              style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}
                            />
                          </div>

                          <div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>{t('quickPresets')}:</span>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {[
                                { title: 'Note', text: t('presetThankYou') },
                                { title: 'Important', text: t('presetNoReturn') },
                                { title: 'Terms & Conditions', text: t('presetTerms') }
                              ].map((preset, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => {
                                    updatePrintSetting('invoiceNoteTitle', preset.title);
                                    updatePrintSetting('invoiceNoteText', preset.text);
                                  }}
                                  style={{
                                    padding: '4px 10px',
                                    fontSize: '0.75rem',
                                    borderRadius: '4px',
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-tertiary)',
                                    color: '#0284c7',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                  }}
                                >
                                  + {preset.title}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Preview Toggle Button */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <button
                        type="button"
                        onClick={() => setShowInvoicePrintPreview(!showInvoicePrintPreview)}
                        className="btn btn-secondary"
                        style={{ padding: '8px 18px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {showInvoicePrintPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                        <span>{showInvoicePrintPreview ? t('closePreview') : t('previewInvoice')}</span>
                      </button>
                    </div>

                    {/* Live Preview Container */}
                    {showInvoicePrintPreview && (
                      <div style={{ border: '2px dashed var(--brand-info, #0284c7)', borderRadius: '10px', padding: '20px', background: '#ffffff', color: '#000000', marginTop: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#64748b', marginBottom: '14px', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                          <Info size={13} /> {t('previewNotice')}
                        </div>

                        {/* Preview Header */}
                        {printSettings.headerLayout === 'center' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', borderBottom: '2px solid #333', paddingBottom: '12px', marginBottom: '16px' }}>
                            {printSettings.logoPosition !== 'none' && selectedBusiness?.logo && (
                              <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" style={{ maxHeight: '55px', objectFit: 'contain', marginBottom: '6px' }} />
                            )}
                            <h2 style={{ margin: '0 0 2px', fontSize: '1.25rem', color: '#000' }}>{selectedBusiness?.name || 'Example Business Ltd'}</h2>
                            {printSettings.showBusinessAddress && <p style={{ margin: '2px 0', fontSize: '0.8rem', color: '#475569' }}>Main Commercial Market, Sector 4, Islamabad</p>}
                            {printSettings.showBusinessPhoneEmail && <p style={{ margin: '2px 0', fontSize: '0.8rem', color: '#475569' }}>info@business.com | +92 300 1234567</p>}
                            <div style={{ marginTop: '8px', borderTop: '1px dashed #ccc', paddingTop: '6px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ background: '#0284c7', color: '#fff', padding: '3px 10px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600 }}>INVOICE</span>
                              <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#334155' }}>
                                <div>INV-SAMPLE-001</div>
                                <div>{printSettings.showInvoiceTime ? '2026-09-19 14:30' : '2026-09-19'}</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #333', paddingBottom: '14px', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flexDirection: printSettings.logoPosition === 'center' ? 'column' : printSettings.logoPosition === 'right' ? 'row-reverse' : 'row' }}>
                              {printSettings.logoPosition !== 'none' && selectedBusiness?.logo && (
                                <img src={getLogoUrl(selectedBusiness.logo)} alt="Logo" style={{ maxHeight: '50px', objectFit: 'contain' }} />
                              )}
                              <div>
                                <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#000' }}>{selectedBusiness?.name || 'Example Business Ltd'}</h2>
                                {printSettings.showBusinessAddress && <p style={{ margin: '2px 0', fontSize: '0.8rem', color: '#475569' }}>Main Commercial Market, Sector 4, Islamabad</p>}
                                {printSettings.showBusinessPhoneEmail && <p style={{ margin: '2px 0', fontSize: '0.8rem', color: '#475569' }}>info@business.com | +92 300 1234567</p>}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ background: '#0284c7', color: '#fff', padding: '3px 10px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, display: 'inline-block', marginBottom: '4px' }}>INVOICE</span>
                              <div style={{ fontSize: '0.8rem', color: '#334155' }}>INV-SAMPLE-001</div>
                              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{printSettings.showInvoiceTime ? '2026-09-19 14:30' : '2026-09-19'}</div>
                            </div>
                          </div>
                        )}

                        {/* Customer block definition */}
                        {(() => {
                          const custBlock = (
                            <div style={{ marginBottom: '14px', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.82rem' }}>
                              <strong style={{ color: '#0f172a' }}>Muhammad Bilal</strong>
                              {printSettings.showPartyPhone && <div style={{ color: '#475569' }}>Phone: +92 321 9876543</div>}
                              {printSettings.showPartyCnic && <div style={{ color: '#475569' }}>CNIC: 37405-1234567-1</div>}
                              {printSettings.showPartyAddress && <div style={{ color: '#475569' }}>Address: House 12, Street 5, Rawalpindi</div>}
                            </div>
                          );
                          const colNames = printSettings.columnNames || {};
                          const snHeader = colNames.serialNo?.trim() || 'S.N';
                          const descHeader = colNames.itemDescription?.trim() || t('itemDescription') || 'Product';
                          const qtyHeader = colNames.quantity?.trim() || t('qty') || 'Qty';
                          const priceHeader = colNames.unitPrice?.trim() || t('unitPrice') || 'Price';
                          const amountHeader = colNames.subtotal?.trim() || t('subtotal') || 'Amount';
                          // The pack/loose names only mean something once they are set: the
                          // moment any of the four packaging fields holds a value the preview
                          // adds those columns, so what is typed in Settings is what is seen
                          // here — same fallbacks the printer itself uses (CTN / PC).
                          const hasPackCols = ['bulkQuantity', 'smallQuantity', 'bulkPrice', 'smallPrice']
                            .some(k => (colNames[k] || '').trim());
                          const bulkHeader = colNames.bulkQuantity?.trim() || 'CTN';
                          const looseHeader = colNames.smallQuantity?.trim() || 'PC';
                          const bulkPriceHeader = colNames.bulkPrice?.trim() || 'CTN Price';
                          const loosePriceHeader = colNames.smallPrice?.trim() || 'Pc Price';
                          const thStyle = { padding: '6px', color: '#0f172a' };
                          const tdStyle = { padding: '6px', color: '#334155' };

                          const tblBlock = (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '0.82rem' }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                                  {printSettings.showSerialNumber && <th style={{ ...thStyle, textAlign: 'center', width: '35px' }}>{snHeader}</th>}
                                  <th style={{ ...thStyle, textAlign: 'left' }}>{descHeader}</th>
                                  {hasPackCols ? (
                                    <>
                                      <th style={{ ...thStyle, textAlign: 'center' }}>{bulkHeader}</th>
                                      <th style={{ ...thStyle, textAlign: 'center' }}>{looseHeader}</th>
                                      <th style={{ ...thStyle, textAlign: 'right' }}>{bulkPriceHeader}</th>
                                      <th style={{ ...thStyle, textAlign: 'right' }}>{loosePriceHeader}</th>
                                    </>
                                  ) : (
                                    <>
                                      <th style={{ ...thStyle, textAlign: 'center' }}>{qtyHeader}</th>
                                      <th style={{ ...thStyle, textAlign: 'right' }}>{priceHeader}</th>
                                    </>
                                  )}
                                  <th style={{ ...thStyle, textAlign: 'right' }}>{amountHeader}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {hasPackCols ? (
                                  <>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      {printSettings.showSerialNumber && <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>1</td>}
                                      <td style={{ padding: '6px', color: '#0f172a' }}>Basmati Rice (5kg Bag)</td>
                                      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>2</td>
                                      <td style={{ ...tdStyle, textAlign: 'center' }}>-</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>Rs. 1,600</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>-</td>
                                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>Rs. 3,200</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      {printSettings.showSerialNumber && <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>2</td>}
                                      <td style={{ padding: '6px', color: '#0f172a' }}>Cooking Oil (1L)</td>
                                      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>1</td>
                                      <td style={{ ...tdStyle, textAlign: 'center' }}>3</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>Rs. 680</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>Rs. 170</td>
                                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>Rs. 1,190</td>
                                    </tr>
                                  </>
                                ) : (
                                  <>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      {printSettings.showSerialNumber && <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>1</td>}
                                      <td style={{ padding: '6px', color: '#0f172a' }}>Basmati Rice (5kg Bag)</td>
                                      <td style={{ ...tdStyle, textAlign: 'center' }}>2</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>Rs. 1,600</td>
                                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>Rs. 3,200</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      {printSettings.showSerialNumber && <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>2</td>}
                                      <td style={{ padding: '6px', color: '#0f172a' }}>Cooking Oil (1L)</td>
                                      <td style={{ ...tdStyle, textAlign: 'center' }}>7</td>
                                      <td style={{ ...tdStyle, textAlign: 'right' }}>Rs. 170</td>
                                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>Rs. 1,190</td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          );

                          return printSettings.itemsPosition === 'items_first' ? (
                            <>{tblBlock}{custBlock}</>
                          ) : (
                            <>{custBlock}{tblBlock}</>
                          );
                        })()}

                        {/* Totals */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', fontSize: '0.82rem', borderTop: '2px solid #cbd5e1', paddingTop: '8px' }}>
                          {printSettings.showSubtotal && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '220px', color: '#475569' }}>
                              <span>Subtotal:</span>
                              <span>Rs. 4,390</span>
                            </div>
                          )}
                          {printSettings.showDiscount && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '220px', color: '#dc2626' }}>
                              <span>Discount:</span>
                              <span>Rs. 150</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '220px', fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', borderTop: '1px solid #e2e8f0', paddingTop: '4px' }}>
                            <span>Grand Total:</span>
                            <span>Rs. 4,240</span>
                          </div>
                          {printSettings.showPaidAmount && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '220px', color: '#16a34a', fontWeight: 600 }}>
                              <span>Paid:</span>
                              <span>Rs. 3,000</span>
                            </div>
                          )}
                          {printSettings.showRemainingBalance && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '220px', color: '#dc2626', fontWeight: 700, borderTop: '1px dashed #cbd5e1', paddingTop: '4px' }}>
                              <span>Remaining:</span>
                              <span>Rs. 1,240</span>
                            </div>
                          )}
                        </div>

                        {/* Note */}
                        {printSettings.showInvoiceFooterNote && (printSettings.invoiceNoteText?.trim() || printSettings.invoiceNoteTitle?.trim()) && (
                          <div style={{ marginTop: '16px', padding: '10px 14px', border: '1px dashed #cbd5e1', borderRadius: '6px', background: '#f8fafc', fontSize: '0.8rem' }}>
                            <strong style={{ color: '#0f172a', display: 'block', marginBottom: '2px' }}>{printSettings.invoiceNoteTitle || 'Note'}:</strong>
                            <div style={{ color: '#475569', whiteSpace: 'pre-wrap' }}>{printSettings.invoiceNoteText || 'Goods once sold are non-refundable.'}</div>
                          </div>
                        )}

                        {/* Signatures */}
                        {printSettings.showSignatureBlock && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '0.75rem', color: '#64748b' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ width: '110px', borderBottom: '1px dashed #94a3b8', marginBottom: '4px' }}></div>
                              <span>{t('customerSignature')}</span>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ width: '110px', borderBottom: '1px dashed #94a3b8', marginBottom: '4px' }}></div>
                              <span>{t('authorizedSignature')}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* REPORTS PRINT NOTES & SETTINGS */}
                {printSettingsSubTab === 'reports' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div
                      onClick={() => updatePrintSetting('showReportFooterNote', !printSettings.showReportFooterNote)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        background: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-color)',
                        cursor: 'pointer'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t('enableReportNote')}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('reportsNotesDesc')}</div>
                      </div>
                      <div
                        style={{
                          width: '38px',
                          height: '22px',
                          borderRadius: '11px',
                          background: printSettings.showReportFooterNote ? '#0284c7' : 'rgba(150,150,150,0.3)',
                          position: 'relative',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#ffffff',
                            position: 'absolute',
                            top: '3px',
                            left: printSettings.showReportFooterNote ? '19px' : '3px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}
                        />
                      </div>
                    </div>

                    {/* Global Report Note */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '6px', color: 'var(--text-primary)' }}>{t('globalReportNote')}</h4>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{t('noteTitleLabel')}</label>
                        <input
                          type="text"
                          value={printSettings.reportNoteTitle || ''}
                          placeholder={t('tabNoteTitlePlaceholder')}
                          onChange={e => updatePrintSetting('reportNoteTitle', e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{t('noteTextLabel')}</label>
                        <textarea
                          rows={3}
                          value={printSettings.reportNoteText || ''}
                          placeholder={t('globalReportNotePlaceholder')}
                          onChange={e => updatePrintSetting('reportNoteText', e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}
                        />
                      </div>
                    </div>

                    {/* Section-Specific Overrides */}
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '16px', background: 'var(--bg-secondary)' }}>
                      <h4 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '4px', color: 'var(--text-primary)' }}>{t('tabSpecificNotes')}</h4>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '12px' }}>Customize notes for a specific tab report. If left blank, the global note above will be used automatically.</p>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{t('selectTabToCustomize')}</label>
                        <select
                          value={selectedReportTabToEdit}
                          onChange={e => setSelectedReportTabToEdit(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        >
                          <option value="ledger">{t('ledgerStatement') || 'Ledger Statement'}</option>
                          <option value="cash">{t('cashBook') || 'Cash Book'}</option>
                          <option value="ie">{t('incomeExpense') || 'Income & Expense'}</option>
                          <option value="bank">{t('bankTransactions') || 'Bank Transactions'}</option>
                          <option value="customers">{t('customers') || 'Customers Directory'}</option>
                          <option value="suppliers">{t('suppliers') || 'Suppliers Directory'}</option>
                          <option value="products">{t('stockInventory') || 'Stock & Inventory'}</option>
                          <option value="staff">{t('staffSalaryReport') || 'Staff & Salary'}</option>
                          <option value="invoices">{t('invoices') || 'Invoices & Daybook'}</option>
                          <option value="dashboard">{t('overview') || 'Business Overview Dashboard'}</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{t('noteTitleLabel')}</label>
                        <input
                          type="text"
                          value={printSettings.tabNoteTitles?.[selectedReportTabToEdit] || ''}
                          placeholder={printSettings.reportNoteTitle || t('tabNoteTitlePlaceholder')}
                          onChange={e => updateTabNoteTitle(selectedReportTabToEdit, e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>{t('noteTextLabel')}</label>
                        <textarea
                          rows={3}
                          value={printSettings.tabNotes?.[selectedReportTabToEdit] || ''}
                          placeholder={printSettings.reportNoteText || t('tabNoteTextPlaceholder')}
                          onChange={e => updateTabNote(selectedReportTabToEdit, e.target.value)}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem', resize: 'vertical' }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="glass-panel" style={{ padding: '28px' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '4px' }}>{t('changePin')}</h4>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                  {t('changePinDesc')}
                </p>

                {/* Current PIN display from localStorage */}
                {localStorage.getItem('org_pin') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 18px', borderRadius: '10px', background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.2)', marginBottom: '20px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(56,189,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <KeyRound size={18} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '2px' }}>{t('currentPIN')}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, letterSpacing: '0.5em', color: 'var(--accent-primary)', fontFamily: 'monospace' }}>
                          {pinVisible ? localStorage.getItem('org_pin') : '••••'}
                        </div>
                        <button type="button" onClick={() => setPinVisible(!pinVisible)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex' }}>
                          {pinVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <span style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '20px', background: 'rgba(56,189,248,0.15)', color: 'var(--accent-primary)', fontWeight: 600 }}>ACTIVE</span>
                  </div>
                )}

                <form onSubmit={handleChangePin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>{t('currentPIN')}</label>
                    <input
                      type="password"
                      maxLength="4"
                      placeholder={localStorage.getItem('org_pin') ? '••••' : t('enterCurrentPIN')}
                      value={currentPin}
                      onChange={e => setCurrentPin(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', letterSpacing: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>{t('newPIN')}</label>
                    <input
                      type="password"
                      maxLength="4"
                      placeholder={t('enterNewPIN')}
                      value={newPin}
                      onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', letterSpacing: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px', color: 'var(--text-primary)' }}>{t('confirmNewPIN')}</label>
                    <input
                      type="password"
                      maxLength="4"
                      placeholder={t('confirmNewPINPH')}
                      value={confirmPin}
                      onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', letterSpacing: '4px' }}
                    />
                  </div>
                  {pinMessage && (
                    <div style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 500, background: pinMessageType === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: pinMessageType === 'success' ? 'var(--brand-success)' : 'var(--brand-danger)', border: `1px solid ${pinMessageType === 'success' ? 'var(--brand-success)' : 'var(--brand-danger)'}` }}>
                      {pinMessage}
                    </div>
                  )}
                  <button type="submit" className="btn btn-primary" style={{ padding: '12px', fontSize: '0.95rem', fontWeight: 600 }}>
                    {t('changePinBtn')}
                  </button>
                </form>
              </div>
            </>
            )}

            </div>
          )}

          {/* TAB: RECYCLE BIN */}
          {activeTab === 'recyclebin' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '700px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Trash2 size={24} color="#ef4444" />
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('recycleBin')}</h3>
                </div>
                <button onClick={fetchRecycleBin} style={{ padding: '7px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px', color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '6px' }}><RefreshCw size={13} />{t('refresh')}</button>
              </div>

              {recycleBinItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', color: '#475569' }}>
                  <Trash2 size={48} style={{ marginBottom: '12px', opacity: 0.2 }} />
                  <p style={{ fontSize: '0.95rem' }}>{t('recycleBinEmpty')}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {recycleBinItems.map(item => (
                    <div key={item.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '14px 18px', border: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                          <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontWeight: 600, textTransform: 'uppercase' }}>{item.type_label}</span>
                          <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)' }}>{item.name}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {item.deleted_at ? `Deleted ${new Date(item.deleted_at).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'Deleted'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => handleRestoreItem(item.id)} style={{ padding: '6px 14px', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '6px', color: '#10b981', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}><RefreshCw size={12} />{t('restore')}</button>
                        <button disabled={!canDelete} onClick={() => handlePermanentDelete(item.id)} style={{ padding: '6px 14px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '6px', color: '#ef4444', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}><Trash2 size={12} />{t('delete')}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB: MANAGE USERS (Admin only) */}
          {activeTab === 'manage_users' && isAdminMember && (
            <ManageUsersTab
              orgId={orgAuth}
              backendUrl={BACKEND_URL}
              currentMember={currentMemberSession}
              onMembersChange={(members) => setMemberList(members)}
            />
          )}

          {/* TAB: ACTIVITY LOG (Admin only) */}
          {activeTab === 'activity_log' && isAdminMember && (
            <ActivityLogTab orgId={orgAuth} />
          )}

          {/* TAB: BILL BOOK (INVOICE BUILDER) */}
          {activeTab === 'billing' && (
            <div className="anim-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('invoiceBuilderTitle')}</h3>
                  <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-tertiary)', borderRadius: '8px', padding: '3px' }}>
                    <button disabled={!canEdit} onClick={() => setBillBookView('builder')} style={{ padding: '6px 14px', fontSize: '0.8rem', background: billBookView === 'builder' ? 'var(--accent-primary)' : 'transparent', color: billBookView === 'builder' ? '#fff' : 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>{t('createInvoice') || 'Create Invoice'}</button>
                    <button onClick={() => setBillBookView('history')} style={{ padding: '6px 14px', fontSize: '0.8rem', background: billBookView === 'history' ? 'var(--accent-primary)' : 'transparent', color: billBookView === 'history' ? '#fff' : 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>{t('invoiceHistory')} ({filteredInvoices.filter(i => !i.invoice_number?.startsWith('PI-')).length})</button>
                    <button onClick={() => setBillBookView('purchases')} style={{ padding: '6px 14px', fontSize: '0.8rem', background: billBookView === 'purchases' ? 'var(--accent-primary)' : 'transparent', color: billBookView === 'purchases' ? '#fff' : 'var(--text-secondary)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>{t('purchaseHistory') || 'Purchase History'} ({filteredInvoices.filter(i => i.invoice_number?.startsWith('PI-')).length})</button>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <DateFilterBar filter={invoicePeriod} onChange={setInvoicePeriod} />
                  <button onClick={() => triggerPrintReport('invoices', { period: invoicePeriod, filteredInvoices, periodLabel: invoicePeriod.mode === 'all' ? t('allTime') : invoicePeriod.mode })} className="btn btn-secondary" title={t('printInvoiceReport')}>
                    <Printer size={14} /><span>{t('printReport')}</span>
                  </button>
                </div>
              </div>
              {billBookView === 'builder' ? (
              <div className="billing-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '32px' }}>
                
                {/* Form Builder */}
                <div className="glass-panel" style={{ padding: '28px', height: 'fit-content' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '24px' }}>{t('invoiceBuilderTitle')} {t('detailsLabel')}</h3>
                  <form onSubmit={(e) => submitInvoice(e, true)} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('selectCustomer')}</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <input
                            type="text"
                            placeholder={t('searchCustomerPH') || 'Search by name or phone...'}
                            value={custSearchQuery || (invoiceBuilder.party_id ? customers.find(c => c.id === invoiceBuilder.party_id)?.name || '' : '')}
                            onFocus={() => { setCustDropdownOpen(true); setCustSearchQuery(''); }}
                            onBlur={() => setTimeout(() => setCustDropdownOpen(false), 200)}
                            onChange={(e) => { setCustSearchQuery(e.target.value); setCustDropdownOpen(true); if (!e.target.value) setInvoiceBuilder({ ...invoiceBuilder, party_id: '' }); }}
                            style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                          />
                          {custDropdownOpen && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                              {customers.filter(c => {
                                if (!custSearchQuery) return true;
                                const q = custSearchQuery.toLowerCase();
                                return (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(custSearchQuery);
                              }).length === 0 && (
                                <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>{t('noResults') || 'No customers found'}</div>
                              )}
                              {customers.filter(c => {
                                if (!custSearchQuery) return true;
                                const q = custSearchQuery.toLowerCase();
                                return (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(custSearchQuery);
                              }).map(c => (
                                <div key={c.id} onMouseDown={() => { setInvoiceBuilder({ ...invoiceBuilder, party_id: c.id }); setCustSearchQuery(''); setCustDropdownOpen(false); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.9rem', color: 'var(--text-primary)' }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                  <strong>{c.name}</strong> <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{c.phone || ''}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button type="button" onClick={() => { resetNewParty('CUSTOMER'); setShowAddPartyModal(true); }} className="btn btn-primary" style={{ padding: '12px 14px', fontSize: '1.1rem', lineHeight: 1, flexShrink: 0 }} title={t('addCustomer')}>+</button>
                      </div>
                    </div>

                    <div>
                      <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '12px' }}>{t('lineItems')}</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {isMobileView && invoiceBuilder.items.map((row, index) => {
                          const filteredProducts = products.filter(p => !p.is_deleted).filter(p => {
                            if (!prodSearchQuery) return true;
                            return (p.name || '').toLowerCase().includes(prodSearchQuery.toLowerCase()) || (p.sku || '').toLowerCase().includes(prodSearchQuery.toLowerCase());
                          });
                          return (
                          <div key={index} style={{ display: 'grid', gridTemplateColumns: itemDiscMode ? 'minmax(0, 5fr) minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr)' : 'minmax(0, 5fr) minmax(0, 2fr) minmax(0, 2fr)', gap: '8px', alignItems: 'center' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ position: 'relative' }}>
                                <input
                                  type="text"
                                  data-srow={index}
                                  placeholder={t('searchProductPH') || 'Search product...'}
                                  value={prodDropdownRow === index ? (prodSearchQuery || '') : (row.product_id ? products.find(p => p.id === row.product_id)?.name || '' : row.custom_item_name || '')}
                                  onFocus={() => { setProdDropdownRow(index); setProdSearchQuery(''); setProdHighlightedIdx(0); }}
                                  onBlur={() => setTimeout(() => { if (prodDropdownRow === index) setProdDropdownRow(null); }, 200)}
                                  onChange={(e) => { setProdSearchQuery(e.target.value); setProdDropdownRow(index); setProdHighlightedIdx(0); if (!e.target.value) handleInvoiceItemChange(index, 'product_id', ''); }}
                                  onKeyDown={(e) => {
                                    if (prodDropdownRow !== index) return;
                                    if (e.key === 'ArrowDown') { e.preventDefault(); setProdHighlightedIdx(prev => Math.min(prev + 1, filteredProducts.length - 1)); }
                                    else if (e.key === 'ArrowUp') { e.preventDefault(); setProdHighlightedIdx(prev => Math.max(prev - 1, 0)); }
                                    else if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (filteredProducts.length > 0 && filteredProducts[prodHighlightedIdx]) {
                                        selectSaleProduct(index, filteredProducts[prodHighlightedIdx]);
                                      } else if (prodSearchQuery.trim()) {
                                        selectSaleCustomItem(index, prodSearchQuery.trim());
                                      }
                                    }
                                  }}
                                  style={{ width: '100%', padding: '10px 36px 10px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                                />
                                 <button type="button" title={t('selectFromGallery') || 'Select from Gallery'}
                                  onClick={() => {
                                    setGallerySelectMode(true);
                                    setGallerySelectTarget({ type: 'sale', index });
                                    setActiveTab('settings');
                                    setSettingsSubTab('gallery');
                                  }}
                                  style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                                >
                                  <Camera size={16} />
                                </button>
                                {prodDropdownRow === index && (
                                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                                    {filteredProducts.length === 0 && prodSearchQuery && (
                                      <div
                                        onMouseDown={() => selectSaleCustomItem(index, prodSearchQuery)}
                                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--accent-primary)', fontWeight: 600 }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                      >
                                        <Plus size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                        {t('addNewProductLabel', { name: prodSearchQuery }) || `Add "${prodSearchQuery}" as new item`}
                                      </div>
                                    )}
                                    {filteredProducts.length === 0 && !prodSearchQuery && (
                                      <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>{t('noResults') || 'No products found'}</div>
                                    )}
                                    {filteredProducts.map((p, pi) => {
                                        const upb = numOr(p.units_per_bulk, 1);
                                        const rowQtyType = row.qty_type || 'piece';
                                        const displayPrice = rowQtyType === 'pack' && upb > 1
                                          ? round2(numOr(p.sale_price, 0) * upb)
                                          : numOr(p.sale_price, 0);
                                        return (
                                          <div key={p.id} onMouseDown={() => selectSaleProduct(index, p)}
                                            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', background: pi === prodHighlightedIdx ? 'var(--bg-tertiary)' : 'transparent' }}
                                            onMouseEnter={() => setProdHighlightedIdx(pi)}>
                                            <span><strong>{p.name}</strong> <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{p.sku || ''}</span></span>
                                            <span style={{ fontWeight: 600, color: 'var(--brand-success)' }}>Rs. {displayPrice.toLocaleString()}</span>
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            </div>

                              <div style={{ display: 'flex', gap: '6px', flex: 1, minWidth: 0 }}>
                                <input type="text" data-calc="" placeholder={t('qty')} value={row.qty_type === 'pack' ? (row.quantity / (row.units_per_bulk || 1)) : row.quantity} onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  const upb = row.units_per_bulk || 1;
                                  const pieces = row.qty_type === 'pack' ? val * upb : val;
                                  handleInvoiceItemChange(index, 'quantity', pieces);
                                }} style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                                {(row.units_per_bulk && row.units_per_bulk > 1) && (
                                  <select value={row.qty_type || 'piece'} onChange={(e) => handleInvoiceItemChange(index, 'qty_type', e.target.value)} style={{ padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.75rem', minWidth: '80px' }}>
                                    <option value="piece">{t('qtyTypePiece')}</option>
                                    <option value="pack">{t('qtyTypePack')}</option>
                                  </select>
                                )}
                              </div>

                              <input type="text" data-calc="" placeholder={t('rate')} value={row.qty_type === 'pack' && row.units_per_bulk ? (row.unit_price * row.units_per_bulk).toFixed(2) : row.unit_price} onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const upb = row.units_per_bulk || 1;
                                const piecePrice = row.qty_type === 'pack' ? val / upb : val;
                                handleInvoiceItemChange(index, 'unit_price', piecePrice);
                              }} style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                              {itemDiscMode && (
                                <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : t('discount')} value={row.discount ?? ''} onChange={(e) => handleInvoiceItemChange(index, 'discount', e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem', textAlign: 'right' }} />
                              )}
                              {qtyModeHint(row, row.product_id ? products.find(p => p.id === row.product_id) : null)}
                          </div>
                              );
                            })}
                            {!isMobileView && (
                              <>
                                    {invoiceBuilder.items.filter(it => it.product_id || it.custom_item_name).length > 0 && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {invoiceBuilder.items.filter(it => it.product_id || it.custom_item_name).map((row, i) => {
                                           const product = row.product_id ? products.find(p => p.id === row.product_id) : null;
                                           const name = product ? product.name : (row.custom_item_name || '');
                                           const upb = row.units_per_bulk || 1;
                                           const isPack = row.qty_type === 'pack' && upb > 1;
                                           const displayQty = isPack ? (row.quantity / upb) : row.quantity;
                                           const displayUnit = isPack ? (displayQty === 1 ? t('pack') : t('packs')) : (displayQty === 1 ? t('piece') : t('pieces'));
                                           const displayRate = isPack ? (row.unit_price * upb).toFixed(2) : row.unit_price;
                                           return (
                                             <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px 12px' }}>
                                               <Check size={14} style={{ color: 'var(--brand-success)', flexShrink: 0 }} />
                                               <div style={{ flex: 1, minWidth: 0 }}>
                                                 <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                                                 <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{displayQty} {displayUnit}{isPack ? ` (${row.quantity} ${t('qtyTypePiece')})` : ''} x Rs.{displayRate} = Rs.{round2(numOr(row.quantity, 0) * numOr(row.unit_price, 0)).toLocaleString()}</div>
                                               </div>
                                               {itemDiscMode && (
                                                 <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : t('discount')} value={row.discount ?? ''} onChange={e => setCommittedSaleDiscount(row, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} style={{ width: '92px', padding: '4px 8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right', flexShrink: 0 }} />
                                               )}
                                               <button type="button" onClick={() => removeCommittedSaleItem(i)} title={t('remove') || 'Remove'} style={{ background: 'none', border: 'none', color: 'var(--brand-danger)', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
                                                 <X size={15} />
                                               </button>
                                             </div>
                                           );
                                         })}
                                      </div>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: itemDiscMode ? 'minmax(0, 4fr) minmax(136px, 3fr) minmax(88px, 2fr) minmax(88px, 2fr)' : 'minmax(0, 4fr) minmax(136px, 3fr) minmax(88px, 2fr)', gap: '8px', alignItems: 'center', border: '1px dashed var(--border-color)', borderRadius: '10px', padding: '10px', background: 'var(--bg-tertiary)' }}>
                                      <div style={{ minWidth: 0, position: 'relative' }}>
                                        <input
                                          ref={saleProductInputRef}
                                          type="text"
                                          placeholder={t('searchProductPH') || 'Search product...'}
                                          value={saleEntryDropdownOpen ? (prodSearchQuery || '') : (saleEntryItem.product_id ? products.find(p => p.id === saleEntryItem.product_id)?.name || '' : saleEntryItem.custom_item_name || '')}
                                          onFocus={() => { setSaleEntryDropdownOpen(true); setProdSearchQuery(''); setProdHighlightedIdx(0); }}
                                          onBlur={() => setTimeout(() => setSaleEntryDropdownOpen(false), 200)}
                                          onChange={(e) => { setProdSearchQuery(e.target.value); setSaleEntryDropdownOpen(true); setProdHighlightedIdx(0); if (!e.target.value) setSaleEntryItem({ ...saleEntryItem, product_id: '', custom_item_name: '' }); }}
                                          onKeyDown={(e) => {
                                            if (!saleEntryDropdownOpen) return;
                                            if (e.key === 'ArrowDown') { e.preventDefault(); setProdHighlightedIdx(prev => Math.min(prev + 1, filteredSaleProducts.length - 1)); }
                                            else if (e.key === 'ArrowUp') { e.preventDefault(); setProdHighlightedIdx(prev => Math.max(prev - 1, 0)); }
                                            else if (e.key === 'Enter') {
                                              e.preventDefault();
                                              if (filteredSaleProducts.length > 0 && filteredSaleProducts[prodHighlightedIdx]) {
                                                selectSaleEntryProduct(filteredSaleProducts[prodHighlightedIdx]);
                                              } else if (prodSearchQuery.trim()) {
                                                selectSaleEntryCustomItem(prodSearchQuery.trim());
                                              }
                                            }
                                          }}
                                          style={{ width: '100%', padding: '10px 36px 10px 10px', border: '1px solid var(--brand-success)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                                        />
                                        <button type="button" title={t('selectFromGallery') || 'Select from Gallery'}
                                          onClick={() => {
                                            setGallerySelectMode(true);
                                            setGallerySelectTarget({ type: 'saleEntry' });
                                            setActiveTab('settings');
                                     setSettingsSubTab('gallery');
                                          }}
                                          style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                                          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
                                          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                                        >
                                          <Camera size={16} />
                                        </button>
                                        {saleEntryDropdownOpen && (
                                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                                            {filteredSaleProducts.length === 0 && prodSearchQuery && (
                                              <div
                                                onMouseDown={() => selectSaleEntryCustomItem(prodSearchQuery)}
                                                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--accent-primary)', fontWeight: 600 }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                              >
                                                <Plus size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                                {t('addNewProductLabel', { name: prodSearchQuery }) || `Add "${prodSearchQuery}" as new item`}
                                              </div>
                                            )}
                                            {filteredSaleProducts.length === 0 && !prodSearchQuery && (
                                              <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>{t('noResults') || 'No products found'}</div>
                                            )}
                                            {filteredSaleProducts.map((p, pi) => {
                                              const upb = numOr(p.units_per_bulk, 1);
                                              const displayPrice = saleEntryItem.qty_type === 'pack' && upb > 1
                                                ? round2(numOr(p.sale_price, 0) * upb)
                                                : numOr(p.sale_price, 0);
                                              return (
                                                <div key={p.id} onMouseDown={() => selectSaleEntryProduct(p)}
                                                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', background: pi === prodHighlightedIdx ? 'var(--bg-tertiary)' : 'transparent' }}
                                                  onMouseEnter={() => setProdHighlightedIdx(pi)}>
                                                  <span><strong>{p.name}</strong> <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{p.sku || ''}</span></span>
                                                  <span style={{ fontWeight: 600, color: 'var(--brand-success)' }}>Rs. {displayPrice.toLocaleString()}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>

                                      <div style={{ display: 'flex', gap: '6px', flex: 1, minWidth: 0 }}>
                                <input type="text" data-calc="" placeholder={t('qty')} value={saleEntryItem.qty_type === 'pack' && saleEntryItem.units_per_bulk ? (saleEntryItem.quantity / saleEntryItem.units_per_bulk) : saleEntryItem.quantity} onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  const upb = saleEntryItem.units_per_bulk || 1;
                                  const pieces = saleEntryItem.qty_type === 'pack' ? val * upb : val;
                                  setSaleEntryItem({ ...saleEntryItem, quantity: pieces });
                                }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitSaleEntryItem(); } }} style={{ flex: 1, minWidth: '50px', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                                {(saleEntryItem.units_per_bulk && saleEntryItem.units_per_bulk > 1) && (
                                  <select value={saleEntryItem.qty_type || 'piece'} onChange={(e) => {
                                    const mode = e.target.value;
                                    lastQtyTypeRef.current = mode;
                                    const upb = packSizeFor(saleEntryItem, saleEntryItem.product_id ? products.find(p => p.id === saleEntryItem.product_id) : null);
                                    setSaleEntryItem({ ...saleEntryItem, qty_type: mode, quantity: initialPiecesFor(mode, upb, saleEntryItem.quantity) });
                                  }} style={{ padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.75rem', minWidth: '80px' }}>
                                    <option value="piece">{t('qtyTypePiece')}</option>
                                    <option value="pack">{t('qtyTypePack')}</option>
                                  </select>
                                )}
                              </div>

                              <input type="text" data-calc="" placeholder={t('rate')} value={saleEntryItem.qty_type === 'pack' && saleEntryItem.units_per_bulk ? (saleEntryItem.unit_price * saleEntryItem.units_per_bulk).toFixed(2) : saleEntryItem.unit_price} onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const upb = saleEntryItem.units_per_bulk || 1;
                                const piecePrice = saleEntryItem.qty_type === 'pack' ? val / upb : val;
                                setSaleEntryItem({ ...saleEntryItem, unit_price: piecePrice });
                              }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitSaleEntryItem(); } }} style={{ flex: 1, minWidth: 0, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                              {itemDiscMode && (
                                <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : t('discount')} value={saleEntryItem.discount ?? ''} onChange={(e) => setSaleEntryItem({ ...saleEntryItem, discount: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitSaleEntryItem(); } }} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', textAlign: 'right' }} />
                              )}
                              {qtyModeHint(saleEntryItem, saleEntryItem.product_id ? products.find(p => p.id === saleEntryItem.product_id) : null)}
                                    </div>
                                  </>
                            )}
                      </div>
                    </div>
                    {isMobileView && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                      <button type="button" onClick={addInvoiceItemRow} className="btn btn-secondary" style={{ padding: '8px 20px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Plus size={14} /> {t('addItemRow') || 'Add Item Row'}
                      </button>
                    </div>
                    )}

                    <div className="glass-panel" style={{ padding: '16px', background: 'var(--bg-tertiary)' }}>
                      {(() => {
                        const sub = calcSaleTotal();
                        const disc = saleDiscountTotal();
                        const tot = Math.max(0, sub - disc);
                        const paid = parseFloat(invoiceBuilder.paid_amount) || 0;
                        const rem = Math.max(0, tot - paid);
                        return (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-muted)' }}>{t('subtotalLabel') || 'Subtotal'}:</span>
                              <span style={{ fontSize: '1rem', color: 'var(--text-primary)' }}>Rs. {sub.toLocaleString()}</span>
                            </div>
                            {itemDiscMode && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: wholeDiscMode ? '8px' : '12px', borderBottom: wholeDiscMode ? 'none' : '1px dashed var(--border-color)', paddingBottom: wholeDiscMode ? 0 : '12px' }}>
                                <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-muted)' }}>{wholeDiscMode ? (t('discountItemsLabel') || 'Item Discount') : (t('discount') || 'Discount')}:</span>
                                <span style={{ width: '120px', textAlign: 'right', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>Rs. {saleItemDiscountSum().toLocaleString()}</span>
                              </div>
                            )}
                            {wholeDiscMode && (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '12px', borderBottom: '1px dashed var(--border-color)', paddingBottom: '12px' }}>
                                <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-muted)' }}>{itemDiscMode ? (t('discountInvoiceLabel') || 'Invoice Discount') : (t('discount') || 'Discount')}:</span>
                                <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : '0'} value={invoiceBuilder.discount_amount} onChange={e => setInvoiceBuilder({ ...invoiceBuilder, discount_amount: e.target.value })} style={{ width: '120px', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', textAlign: 'right' }} />
                                {isPercentDiscount && <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-muted)' }}>%</span>}
                              </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{t('grandTotal')}:</span>
                              <strong style={{ fontSize: '1.25rem', color: 'var(--accent-primary)' }}>
                                Rs. {tot.toLocaleString()}
                              </strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                              <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{t('paidAmount')}:</span>
                              <input type="text" data-calc="" value={invoiceBuilder.paid_amount} onChange={e => setInvoiceBuilder({ ...invoiceBuilder, paid_amount: e.target.value })} style={{ width: '120px', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', textAlign: 'right' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '40px', fontSize: '1rem', fontWeight: 600, color: 'var(--accent-primary)' }}>
                              <span>{t('remainingBalance')}:</span>
                              <span>Rs. {rem.toLocaleString()}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('status')}</label>
                      <select
                        value={invoiceBuilder.status}
                        onChange={(e) => setInvoiceBuilder({ ...invoiceBuilder, status: e.target.value })}
                        style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      >
                        <option value="UNPAID">{t('unpaid')}</option>
                        <option value="PAID">{t('paid')}</option>
                      </select>
                    </div>

                    {/* Payment Method — shown only when PAID */}
                    {invoiceBuilder.status === 'PAID' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px' }}>{t('paymentMethod')}</label>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            {[['CASH', t('cash') || 'Cash', Wallet], ['ONLINE', t('online') || 'Online', Smartphone]].map(([val, label, icon]) => (
                              <button
                                key={val}
                                type="button"
                                onClick={() => setInvoiceBuilder({ ...invoiceBuilder, payment_method: val })}
                                style={{
                                  flex: 1,
                                  padding: '10px 14px',
                                  borderRadius: '8px',
                                  border: invoiceBuilder.payment_method === val ? '2px solid var(--brand-success)' : '1px solid var(--border-color)',
                                  background: invoiceBuilder.payment_method === val ? 'rgba(34,197,94,0.1)' : 'var(--bg-tertiary)',
                                  color: 'var(--text-primary)',
                                  fontWeight: invoiceBuilder.payment_method === val ? 600 : 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                }}
                              >
                                <DynIcon icon={icon} size={15} /> {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {invoiceBuilder.payment_method === 'ONLINE' && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('bankAccount') || 'Bank Account'}</label>
                              <select
                                value={invoiceBuilder.bank_account_id || ''}
                                onChange={(e) => setInvoiceBuilder({ ...invoiceBuilder, bank_account_id: e.target.value })}
                                style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                              >
                                <option value="">{t('selectBankAccount') || '-- Select Bank Account --'}</option>
                                {bankAccounts.filter(acc => !acc.is_deleted && (!selectedBusiness || String(acc.business) === String(selectedBusiness.id))).map(acc => (
                                  <option key={acc.id} value={acc.id}>{acc.name || acc.account_title || 'Bank Account'}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('paymentReference') || 'Payment Reference'}</label>
                              <input
                                type="text"
                                value={invoiceBuilder.payment_reference || ''}
                                onChange={(e) => setInvoiceBuilder({ ...invoiceBuilder, payment_reference: e.target.value })}
                                placeholder="Txn ref / transfer ID"
                                style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('invoiceNumber')}</label>
                        <input
                          type="text"
                          required
                          value={invoiceBuilder.invoice_number}
                          onChange={(e) => setInvoiceBuilder({ ...invoiceBuilder, invoice_number: e.target.value })}
                          style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('dateOfIssue')}</label>
                        <input
                          type="date"
                          required
                          value={invoiceBuilder.issue_date}
                          onChange={(e) => setInvoiceBuilder({ ...invoiceBuilder, issue_date: e.target.value })}
                          style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                        <input
                          type="time"
                          value={invoiceBuilder.issue_time}
                          onChange={(e) => { setInvoiceBuilder({ ...invoiceBuilder, issue_time: e.target.value }); setInvoiceTimeManual(true); }}
                          style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                        />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-success"
                        style={{ flex: 1, padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        onClick={() => saveAndWhatsApp({
                          phone: parties.find(p => p.id === invoiceBuilder.party_id)?.phone || '',
                          text: formatWAMessage('invoice_message', waSaleInvoiceVars()),
                          card: waSaleInvoiceCard(),
                          save: () => submitInvoice(null, false),
                        })}
                        title={t('sendInvoiceWA')}
                      >
                        <Share2 size={16} /> {t('placeOrder')} & {t('whatsapp')}
                      </button>
                      <button type="submit" className="btn btn-primary" style={{ flex: 1, padding: '12px' }}>
                        {t('saveAndPrint') || 'Save & Print'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Professional A4 Live Invoice Preview */}
                <div style={{ background: '#ffffff', color: '#000000', padding: '32px', borderRadius: '8px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: 'fit-content' }}>
                  <div style={{ borderBottom: '2px solid #000000', paddingBottom: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.4rem' }}>
                          {selectedBusiness?.name || t('yourCompany')}
                        </h2>
                        <p style={{ fontSize: '0.8rem', color: '#666' }}>{selectedBusiness?.address || t('companyAddress')}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <h3 style={{ fontWeight: 700, color: 'var(--brand-info)' }}>{t('invoice')}</h3>
                        <p style={{ fontSize: '0.8rem', fontWeight: 600 }}>{invoiceBuilder.invoice_number}</p>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', fontSize: '0.85rem' }}>
                    <div>
                      <p style={{ fontWeight: 600, color: '#555' }}>{t('billTo')}</p>
                      <h4 style={{ fontSize: '0.95rem', marginTop: '4px' }}>
                        {parties.find(p => p.id === invoiceBuilder.party_id)?.name || t('valuedCustomer')}
                      </h4>
                      <p>{parties.find(p => p.id === invoiceBuilder.party_id)?.phone || ''}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontWeight: 600, color: '#555' }}>{t('dateOfIssueLabel')}</p>
                      <p>{formatDateTime(invoiceBuilder.issue_date, invoiceBuilder.issue_time)}</p>
                    </div>
                  </div>

                  {/* Billed Items Table */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '24px' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                        <th style={{ padding: '8px', textAlign: 'left' }}>{t('name')}</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>{t('qty')}</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>{t('unitPrice')}</th>
                        <th style={{ padding: '8px', textAlign: 'right' }}>{t('subtotal')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resolveSaleItemsForSubmit().filter(r => r.product_id || r.custom_item_name).map((row, index) => {
                        const product = products.find(p => p.id === row.product_id);
                        const rowDisc = itemDiscMode ? discountValueOf(row.discount, row.subtotal) : 0;
                        const rowQtyText = describeLineQty(row);
                        const rowUnitPrice = (row.qty_type === 'pack' && row.bulk_unit_price > 0)
                          ? row.bulk_unit_price
                          : row.unit_price;
                        return (
                          <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '8px' }}>
                              <div>{product ? product.name : (row.custom_item_name || t('chooseItem'))}</div>
                              {rowDisc > 0 && (
                                <div style={{ fontSize: '0.75rem', color: '#0f766e', marginTop: '2px' }}>
                                  {t('discount')}: - Rs. {rowDisc.toLocaleString()}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{rowQtyText}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>Rs. {Number(rowUnitPrice || 0).toLocaleString()}</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Rs. {Number(row.subtotal || 0).toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Totals */}
                  {(() => {
                    const sub = calcSaleTotal();
                    const itemDisc = itemDiscMode ? saleItemDiscountSum() : 0;
                    const wholeDisc = wholeDiscMode ? discountValueOf(invoiceBuilder.discount_amount, sub) : 0;
                    const disc = saleDiscountTotal();
                    const tot = Math.max(0, sub - disc);
                    const paid = parseFloat(invoiceBuilder.paid_amount) || 0;
                    const rem = Math.max(0, tot - paid);

                    return (
                      <div style={{ marginTop: 'auto', borderTop: '2px solid #eee', paddingTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <div style={{ display: 'flex', gap: '40px', fontSize: '0.9rem', marginBottom: '6px' }}>
                          <span style={{ color: '#555' }}>{t('subtotalLabel')}:</span>
                          <span style={{ fontWeight: 600 }}>Rs. {sub.toLocaleString()}</span>
                        </div>
                        {itemDiscMode && wholeDiscMode && itemDisc > 0 && (
                          <div style={{ display: 'flex', gap: '40px', fontSize: '0.85rem', marginBottom: '4px', color: '#0f766e' }}>
                            <span>{t('discountItemsLabel') || 'Item Discount'}:</span>
                            <span style={{ fontWeight: 600 }}>- Rs. {itemDisc.toLocaleString()}</span>
                          </div>
                        )}
                        {itemDiscMode && wholeDiscMode && wholeDisc > 0 && (
                          <div style={{ display: 'flex', gap: '40px', fontSize: '0.85rem', marginBottom: '6px', color: '#0f766e' }}>
                            <span>{t('discountInvoiceLabel') || 'Invoice Discount'}:</span>
                            <span style={{ fontWeight: 600 }}>- Rs. {wholeDisc.toLocaleString()}</span>
                          </div>
                        )}
                        {!(itemDiscMode && wholeDiscMode) && disc > 0 && (
                          <div style={{ display: 'flex', gap: '40px', fontSize: '0.9rem', marginBottom: '6px', color: '#0f766e' }}>
                            <span>{t('discount')}:</span>
                            <span style={{ fontWeight: 600 }}>- Rs. {disc.toLocaleString()}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '40px', fontSize: '1.15rem', fontWeight: 800, marginTop: '4px', marginBottom: '6px' }}>
                          <span>{t('grandTotal')}:</span>
                          <span>Rs. {tot.toLocaleString()}</span>
                        </div>
                        {paid > 0 && (
                          <div style={{ display: 'flex', gap: '40px', fontSize: '0.9rem', marginBottom: '4px' }}>
                            <span style={{ color: '#555' }}>{t('paidAmount')}:</span>
                            <span style={{ fontWeight: 600 }}>Rs. {paid.toLocaleString()}</span>
                          </div>
                        )}
                        {(paid > 0 || rem > 0) && (
                          <div style={{ display: 'flex', gap: '40px', fontSize: '0.95rem', fontWeight: 700, marginTop: '2px', color: rem > 0 ? '#dc2626' : '#16a34a' }}>
                            <span>{t('remainingBalance')}:</span>
                            <span>Rs. {rem.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

              </div>
              ) : billBookView === 'purchases' ? (
                <div className="glass-panel" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>{t('purchaseHistory') || 'Purchase History'} ({filteredInvoices.filter(i => i.invoice_number?.startsWith('PI-')).length})</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: 'var(--text-muted)' }} />
                        <input type="text" placeholder={t('searchByNameOrPhone')} value={invoiceSearchQuery} onChange={e => setInvoiceSearchQuery(e.target.value)} style={{ padding: '9px 10px 9px 30px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '200px' }} />
                      </div>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{t('totalRevenue')}: <strong style={{ color: 'var(--brand-success)' }}>Rs. {filteredInvoices.filter(i => i.invoice_number?.startsWith('PI-')).reduce((s, i) => s + parseFloat(i.total_amount || 0), 0).toLocaleString()}</strong></div>
                    </div>
                  </div>
                  {filteredInvoices.filter(i => i.invoice_number?.startsWith('PI-')).length > 0 ? (
                    <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                          <th style={{ padding: '12px 16px' }}>#</th>
                          <th style={{ padding: '12px 16px' }}>{t('invoiceNumber')}</th>
                          <th style={{ padding: '12px 16px' }}>{t('supplierLabel') || 'Supplier'}</th>
                          <th style={{ padding: '12px 16px' }}>{t('date')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('amount')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('paidAmount')} / {t('remainingBalance')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center' }}>{t('status')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.filter(i => i.invoice_number?.startsWith('PI-')).map((inv, idx) => {
                          const party = parties.find(p => p.id === (inv.party || inv.party_id));
                          const paid = parseFloat(inv.paid_amount || 0);
                          const total = parseFloat(inv.total_amount || 0);
                          const remaining = Math.max(0, total - paid);
                          return (
                            <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td data-label="#" style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>{idx + 1}</td>
                              <td data-label={t('invoiceNumber')} style={{ padding: '12px 16px', fontWeight: 600 }}>
                                {inv.invoice_number}
                                {fbrEnabled && (inv.fbr_status || fbrScopeCovers(inv)) && (
                                  <div style={{ marginTop: '4px' }}><FbrBadge invoice={inv} onResend={resendToFbr} /></div>
                                )}
                              </td>
                              <td data-label={t('supplierLabel') || 'Supplier'} style={{ padding: '12px 16px' }}>{party?.name || '—'}</td>
                              <td data-label={t('date')} style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{formatDateTime(inv.issue_date, inv.issue_time)}</td>
                              <td data-label={t('amount')} style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Rs. {total.toLocaleString()}</td>
                              <td data-label={t('paidAmount') + ' / ' + t('remainingBalance')} style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.82rem' }}>
                                <span style={{ color: 'var(--brand-success)' }}>Rs. {paid.toLocaleString()}</span> / <span style={{ color: remaining > 0 ? 'var(--brand-danger)' : 'var(--text-muted)' }}>Rs. {remaining.toLocaleString()}</span>
                              </td>
                              <td data-label={t('status')} style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <span className={`chip ${inv.status === 'PAID' ? 'chip-success' : 'chip-warning'}`} style={{ fontSize: '0.72rem' }}>{inv.status === 'PAID' ? t('paid') : t('unpaid')}</span>
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                <div style={{ display: 'inline-flex', gap: '6px' }}>
                                  <button onClick={() => handlePrintSingleInvoice(inv)} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }} title={t('printReport')}><Printer size={11} /></button>
                                  <button onClick={async () => { const items = await api(`/invoice-items/?invoice=${inv.id}`); setEditingInvoice({ ...inv, total_amount: String(inv.total_amount), _items: (items || []).map(it => ({ ...it, quantity: String(it.quantity), unit_price: String(it.unit_price) })) }); }} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }} title={t('edit')}><Pencil size={11} /></button>
                                  <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteInvoice', { number: inv.invoice_number }), () => deleteInvoice(inv))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-danger)' }} title={t('delete')}><Trash2 size={11} /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('noInvoices')}</div>
                  )}
                </div>
              ) : (
                <div className="glass-panel" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>{t('invoiceHistory')} ({filteredInvoices.filter(i => !i.invoice_number?.startsWith('PI-')).length})</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: 'var(--text-muted)' }} />
                        <input type="text" placeholder={t('searchByNameOrPhone')} value={invoiceSearchQuery} onChange={e => setInvoiceSearchQuery(e.target.value)} style={{ padding: '9px 10px 9px 30px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '200px' }} />
                      </div>
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>{t('totalRevenue')}: <strong style={{ color: 'var(--brand-success)' }}>Rs. {filteredInvoices.filter(i => !i.invoice_number?.startsWith('PI-')).reduce((s, i) => s + parseFloat(i.total_amount || 0), 0).toLocaleString()}</strong></div>
                    </div>
                  </div>
                  {filteredInvoices.filter(i => !i.invoice_number?.startsWith('PI-')).length > 0 ? (
                    <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                          <th style={{ padding: '12px 16px' }}>#</th>
                          <th style={{ padding: '12px 16px' }}>{t('invoiceNumber')}</th>
                          <th style={{ padding: '12px 16px' }}>{t('name')}</th>
                          <th style={{ padding: '12px 16px' }}>{t('date')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('amount')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('paidAmount')} / {t('remainingBalance')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center' }}>{t('status')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center' }}>{t('paymentMethod')}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.filter(i => !i.invoice_number?.startsWith('PI-')).map((inv, idx) => {
                          const cust = parties.find(p => p.id === (inv.party || inv.party_id));
                          const paid = parseFloat(inv.paid_amount || 0);
                          const total = parseFloat(inv.total_amount || 0);
                          const remaining = Math.max(0, total - paid);
                          return (
                            <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td data-label="#" style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>{idx + 1}</td>
                              <td data-label={t('invoiceNumber')} style={{ padding: '12px 16px', fontWeight: 600 }}>
                                {inv.invoice_number}
                                {fbrEnabled && (inv.fbr_status || fbrScopeCovers(inv)) && (
                                  <div style={{ marginTop: '4px' }}><FbrBadge invoice={inv} onResend={resendToFbr} /></div>
                                )}
                              </td>
                              <td data-label={t('name')} style={{ padding: '12px 16px' }}>{cust?.name || '—'}</td>
                              <td data-label={t('date')} style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{formatDateTime(inv.issue_date, inv.issue_time)}</td>
                              <td data-label={t('amount')} style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>Rs. {total.toLocaleString()}</td>
                              <td data-label={t('paidAmount') + ' / ' + t('remainingBalance')} style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.82rem' }}>
                                <span style={{ color: 'var(--brand-success)' }}>Rs. {paid.toLocaleString()}</span> / <span style={{ color: remaining > 0 ? 'var(--brand-danger)' : 'var(--text-muted)' }}>Rs. {remaining.toLocaleString()}</span>
                              </td>
                              <td data-label={t('status')} style={{ padding: '12px 16px', textAlign: 'center' }}>
                                <span className={`chip ${inv.status === 'PAID' ? 'chip-success' : 'chip-warning'}`} style={{ fontSize: '0.72rem' }}>{inv.status === 'PAID' ? t('paid') : t('unpaid')}</span>
                              </td>
                              <td data-label={t('paymentMethod')} style={{ padding: '12px 16px', textAlign: 'center' }}>
                                {inv.payment_method ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', padding: '3px 8px', borderRadius: '12px', background: inv.payment_method === 'CASH' ? 'rgba(34,197,94,0.1)' : 'rgba(99,102,241,0.1)', color: inv.payment_method === 'CASH' ? 'var(--brand-success)' : 'var(--accent-primary)', fontWeight: 600 }}>
                                    {inv.payment_method === 'CASH' ? <Wallet size={11} /> : <Smartphone size={11} />}
                                    {inv.payment_method === 'CASH' ? 'Cash' : 'Online'}
                                  </span>
                                ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>—</span>}
                              </td>
                              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                <div style={{ display: 'inline-flex', gap: '6px' }}>
                                  <button onClick={() => handlePrintSingleInvoice(inv)} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }} title={t('printReport')}><Printer size={11} /></button>
                                  <button onClick={async () => { const items = await api(`/invoice-items/?invoice=${inv.id}`); setEditingInvoice({ ...inv, total_amount: String(inv.total_amount), _items: (items || []).map(it => ({ ...it, quantity: String(it.quantity), unit_price: String(it.unit_price) })) }); }} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-info)' }} title={t('edit')}><Pencil size={11} /></button>
                                  <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteInvoice', { number: inv.invoice_number }), () => deleteInvoice(inv))} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem', color: 'var(--brand-danger)' }} title={t('delete')}><Trash2 size={11} /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('noInvoices')}</div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </main>
    </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* GLOBAL DELETE CONFIRMATION MODAL                   */}
      {/* ═══════════════════════════════════════════════════ */}
      {duplicateReview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 490 }}>
          <div className="glass-panel anim-scale-in" style={{ padding: '28px', width: '100%', maxWidth: '520px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <AlertTriangle size={26} color="var(--brand-warning)" />
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{t('duplicateEntryFound') || 'Duplicate entry found'}</h3>
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '18px' }}>
              {t('duplicateEntryMessage') || 'These records have exactly the same details. The first record will be kept.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '22px' }}>
              {duplicateReview.records.map((record, index) => (
                <div key={record.id || index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.82rem' }}>
                    <strong>{duplicateReview.label}</strong>
                    <div style={{ color: 'var(--text-secondary)' }}>{formatDateTime(record.date || record.issue_date, record.time || record.issue_time)}{record.amount !== undefined ? ` · Rs. ${Number(record.amount).toLocaleString()}` : record.total_amount !== undefined ? ` · Rs. ${Number(record.total_amount).toLocaleString()}` : ''}</div>
                  </div>
                  {index === 0 ? <span className="chip chip-success" style={{ fontSize: '0.72rem' }}>{t('keep') || 'Keep'}</span> : <button disabled={!canDelete} onClick={() => triggerDelete(t('deleteDuplicateEntry') || 'Delete this duplicate entry?', async () => { await deleteDuplicateRecord(duplicateReview, record); reviewedDuplicateGroups.current.add(duplicateReview.key); setDuplicateReview(null); })} className="btn btn-secondary" style={{ padding: '6px 10px', color: 'var(--brand-danger)', fontSize: '0.75rem' }}><Trash2 size={12} /> {t('delete')}</button>}
                </div>
              ))}
            </div>
            <button onClick={() => { reviewedDuplicateGroups.current.add(duplicateReview.key); setDuplicateReview(null); }} className="btn btn-secondary" style={{ width: '100%' }}>{t('closeBtn') || 'Review later'}</button>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <div className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '400px', background: 'var(--bg-secondary)', textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', padding: '14px', background: 'rgba(239,68,68,0.1)', borderRadius: '50%', marginBottom: '16px' }}>
              <Trash2 size={28} color="var(--brand-danger)" />
            </div>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>{t('confirmDelete')}</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem' }}>{deleteConfirm.label}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} className="btn btn-secondary" style={{ minWidth: '100px' }}>{t('cancel')}</button>
              <button onClick={() => { deleteConfirm.onConfirm(); setDeleteConfirm(null); }} className="btn btn-danger" style={{ minWidth: '100px' }}>{t('delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================== */}
      {/* ADD / EDIT COMPANY PROFILE MODAL                   */}
      {/* =================================================== */}
      {showEditBusinessModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateBusiness} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '500px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editCompany')}</h3>
              <button type="button" onClick={() => setShowEditBusinessModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              {/* Logo upload field with preview */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                  {businessLogoPreview ? (
                    <img src={businessLogoPreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="logo preview" />
                  ) : businessForm.logo ? (
                    <img src={getLogoUrl(businessForm.logo)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="current logo" />
                  ) : (
                    <span style={{ fontSize: '1.5rem', color: 'var(--text-muted)' }}>🏢</span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '4px' }}>{t('companyLogo') || 'Company Logo'}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setBusinessLogoFile(file);
                        setBusinessLogoPreview(URL.createObjectURL(file));
                      }
                    }}
                    style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('companyName')}</label>
                <input required type="text" value={businessForm.name} onChange={e => setBusinessForm({ ...businessForm, name: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('shopAddressLabel')}</label>
                <input type="text" value={businessForm.address} onChange={e => setBusinessForm({ ...businessForm, address: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('officialEmail')}</label>
                <input type="email" value={businessForm.email} onChange={e => setBusinessForm({ ...businessForm, email: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('contactPhone')}</label>
                <input type="text" value={businessForm.phone} onChange={e => setBusinessForm({ ...businessForm, phone: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowEditBusinessModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges')}</button>
            </div>
          </form>
        </div>
      )}

      {/* =================================================== */}
      {/* ADD PARTY MODAL                                    */}
      {/* =================================================== */}
      {showAddPartyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddParty} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '485px', maxHeight: '85vh', overflowY: 'auto', overflowX: 'hidden', background: 'var(--bg-secondary)', scrollbarWidth: 'thin', msOverflowStyle: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{newParty.party_type === 'CUSTOMER' ? t('addCustomer') : newParty.party_type === 'SUPPLIER' ? t('addSupplier') : t('addBoth') || 'Add Customer/Supplier'}</h3>
              <button disabled={!canEdit} type="button" onClick={() => { setShowAddPartyModal(false); setShowAddAreaInline(false); setNewAreaName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
              <button type="button" onClick={handleImportContact} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', border: '1px dashed var(--border-color)', borderRadius: '6px', background: 'transparent', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                <Download size={16} /> {t('pickFromContacts') || 'Import from Contacts (.vcf)'}
              </button>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('name')}</label>
                <input required type="text" value={newParty.name} onChange={e => setNewParty({ ...newParty, name: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('type') || 'Type'}</label>
                <select value={newParty.party_type} onChange={e => setNewParty({ ...newParty, party_type: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="CUSTOMER" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t('customers')}</option>
                  <option value="SUPPLIER" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t('suppliers')}</option>
                  <option value="BOTH" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t('both') || 'Both'}</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('phone')}</label>
                <input type="text" value={newParty.phone} onChange={e => setNewParty({ ...newParty, phone: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('cnicOptional')}</label>
                <input type="text" placeholder={t('cnicPH')} value={newParty.cnic} onChange={e => setNewParty({ ...newParty, cnic: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              {/* FBR: a registered customer's tax number goes on the invoice.
                  Walk-ins can leave it blank FBR accepts them as Unregistered. */}
              {fbrEnabled && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>NTN / STRN <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — FBR)</span></label>
                  <input type="text" placeholder="Leave empty if they have none" value={newParty.ntn_strn || ''} onChange={e => setNewParty({ ...newParty, ntn_strn: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('address')}</label>
                <input type="text" value={newParty.address} onChange={e => setNewParty({ ...newParty, address: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('area') || 'Area'}</label>
                {showAddAreaInline ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input type="text" autoFocus placeholder={t('areaPH') || 'e.g. DHA Phase 5'} value={newAreaName} onChange={e => setNewAreaName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNewArea(); } if (e.key === 'Escape') { setShowAddAreaInline(false); setNewAreaName(''); } }} style={{ flex: 1, padding: '10px', border: '1px solid var(--brand-success)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                    <button type="button" onClick={addNewArea} className="btn btn-primary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('save')}</button>
                    <button type="button" onClick={() => { setShowAddAreaInline(false); setNewAreaName(''); }} className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('cancel')}</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select value={newParty.area} onChange={e => { if (e.target.value === '__add_new__') { setShowAddAreaInline(true); } else { setNewParty({ ...newParty, area: e.target.value }); } }} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                      <option value="">{t('selectArea') || '-- Select Area --'}</option>
                      {areas.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                      <option value="__add_new__">+ {t('addNewArea') || 'Add New Area'}</option>
                    </select>
                  </div>
                )}
              </div>

              {(newParty.party_type === 'SUPPLIER' || newParty.party_type === 'BOTH') && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('itemsSuppliedLabel')}</label>
                  <input type="text" placeholder={t('itemsPH')} value={newParty.items_supplied} onChange={e => setNewParty({ ...newParty, items_supplied: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button disabled={!canEdit} type="button" onClick={() => { setShowAddPartyModal(false); setShowAddAreaInline(false); setNewAreaName(''); }} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveParty')}</button>
            </div>
          </form>
        </div>
      )}

      {/* CONTACT PICKER MODAL */}
      {showContactPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600 }}>
          <div className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '485px', background: 'var(--bg-secondary)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700 }}>{t('selectContact') || 'Select a Contact'}</h3>
              <button type="button" onClick={() => { setShowContactPicker(false); setImportedContacts([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '12px' }}>{importedContacts.length} {t('contactsFound') || 'contacts found'}</p>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {importedContacts.map((c, i) => (
                <button key={i} type="button" onClick={() => { setNewParty({ ...newParty, name: c.name, phone: c.phone }); setShowContactPicker(false); setImportedContacts([]); }} style={{ padding: '12px 14px', textAlign: 'left', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', cursor: 'pointer', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--accent-primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.name || t('unknown') || 'Unknown'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.phone || ''}</div>
                  </div>
                  <Plus size={16} style={{ color: 'var(--accent-primary)' }} />
                </button>
              ))}
            </div>
            <div style={{ marginTop: '16px' }}>
              <button type="button" onClick={() => { setShowContactPicker(false); setImportedContacts([]); }} className="btn btn-secondary" style={{ width: '100%' }}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================== */}
      {/* ADD INCOME EXPENSE RECORD MODAL                    */}
      {/* =================================================== */}
      {showAddIeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddIeEntry} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('logOperationsFlow')}</h3>
              <button type="button" onClick={() => setShowAddIeModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('type')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={() => setIeForm({ ...ieForm, type: 'INCOME' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: ieForm.type === 'INCOME' ? 'rgba(16,185,129,0.15)' : 'transparent', color: ieForm.type === 'INCOME' ? 'var(--brand-success)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('income')}</button>
                  <button type="button" onClick={() => setIeForm({ ...ieForm, type: 'EXPENSE' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: ieForm.type === 'EXPENSE' ? 'rgba(239,68,68,0.15)' : 'transparent', color: ieForm.type === 'EXPENSE' ? 'var(--brand-danger)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('expense')}</button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('amountRs')}</label>
                <input required type="text" data-calc="" value={ieForm.amount} onChange={e => setIeForm({ ...ieForm, amount: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('category')}</label>
                {!showCustomIeCategory ? (
                  <select value={ieForm.category} onChange={e => { if (e.target.value === '__custom__') { setShowCustomIeCategory(true); } else { setIeForm({ ...ieForm, category: e.target.value }); } }} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                    <option value="Rent">{t('shopRent')}</option>
                    <option value="Electricity">{t('electricityBill')}</option>
                    <option value="Packaging">{t('packagingMaterial')}</option>
                    <option value="Raw Material">{t('rawIngredients')}</option>
                    <option value="Salary">{t('staffSalaryOption')}</option>
                    <option value="Sales">{t('salesReturn')}</option>
                    <option value="__custom__">{t('customCatOption')}</option>
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" required placeholder={t('catPH')} value={customIeCategory} onChange={e => setCustomIeCategory(e.target.value)} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                    <button type="button" onClick={() => setShowCustomIeCategory(false)} className="btn btn-secondary">{t('cancel')}</button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('description')}</label>
                <input type="text" value={ieForm.description} onChange={e => setIeForm({ ...ieForm, description: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('attachment') || 'Attachment'}</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setIeForm({ ...ieForm, attachment: e.target.files[0] })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date')}</label>
                  <input required type="date" value={ieForm.date} onChange={e => setIeForm({ ...ieForm, date: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input type="time" value={ieForm.time} onChange={e => setIeForm({ ...ieForm, time: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowAddIeModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('logRecordBtn')}</button>
            </div>
          </form>
        </div>
      )}

      {/* =================================================== */}
      {/* ADD STAFF MEMBER MODAL                             */}
      {/* =================================================== */}
      {showAddStaffModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddStaff} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('addEmployee')}</h3>
              <button type="button" onClick={() => setShowAddStaffModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('staffName')}</label>
                <input required type="text" value={staffForm.name} onChange={e => setStaffForm({ ...staffForm, name: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('phone')}</label>
                <input type="text" value={staffForm.phone} onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('cnic')}</label>
                <input type="text" value={staffForm.cnic} onChange={e => setStaffForm({ ...staffForm, cnic: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('salaryType')}</label>
                <select value={staffForm.salary_type} onChange={e => setStaffForm({ ...staffForm, salary_type: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="MONTHLY">{t('monthly')}</option>
                  <option value="DAILY">{t('daily')}</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('baseSalaryLabel')}</label>
                <input required type="text" data-calc="" value={staffForm.base_salary} onChange={e => setStaffForm({ ...staffForm, base_salary: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowAddStaffModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('addEmployee')}</button>
            </div>
          </form>
        </div>
      )}

      {/* LEDGER ENTRY MODAL */}
      {showAddLedgerModal && selectedParty && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddLedgerEntry} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('logEntry', { name: selectedParty?.name })}</h3>
              <button type="button" onClick={() => setShowAddLedgerModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('transactionType')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setLedgerForm({ ...ledgerForm, entry_type: 'GIVE' })}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)',
                      background: ledgerForm.entry_type === 'GIVE' ? 'rgba(16,185,129,0.1)' : 'transparent',
                      color: ledgerForm.entry_type === 'GIVE' ? 'var(--brand-success)' : 'var(--text-secondary)',
                      fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {selectedParty?.party_type === 'CUSTOMER' || selectedParty?.party_type === 'BOTH' ? t('youGaveNeg') : t('youPaidNeg')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLedgerForm({ ...ledgerForm, entry_type: 'GET' })}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)',
                      background: ledgerForm.entry_type === 'GET' ? 'rgba(239,68,68,0.1)' : 'transparent',
                      color: ledgerForm.entry_type === 'GET' ? 'var(--brand-danger)' : 'var(--text-secondary)',
                      fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {selectedParty?.party_type === 'CUSTOMER' || selectedParty?.party_type === 'BOTH' ? t('youGotPos') : t('theySuppliedPos')}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('amountRs')}</label>
                <input
                  type="text"
                  data-calc=""
                  required
                  value={ledgerForm.amount}
                  onChange={(e) => setLedgerForm({ ...ledgerForm, amount: e.target.value })}
                  style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', width: '100%' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('paymentMethod') || 'Payment Method'}</label>
                  <select
                    value={ledgerForm.payment_method}
                    onChange={e => setLedgerForm({ ...ledgerForm, payment_method: e.target.value, bank_account_id: e.target.value === 'CASH' ? '' : ledgerForm.bank_account_id })}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  >
                    <option value="CASH">{t('cash') || 'Cash'}</option>
                    <option value="ONLINE">{t('bankOnline') || 'Bank / Online'}</option>
                  </select>
                </div>
                {ledgerForm.payment_method === 'ONLINE' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('bankAccount') || 'Bank Account'}</label>
                    <select
                      value={ledgerForm.bank_account_id}
                      onChange={e => setLedgerForm({ ...ledgerForm, bank_account_id: e.target.value })}
                      style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      required
                    >
                      <option value="">{t('selectBankAccount') || '-- Select Bank Account --'}</option>
                      {bankAccounts.filter(acc => !acc.is_deleted && (!selectedBusiness || String(acc.business) === String(selectedBusiness.id))).map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name || acc.account_title || 'Bank Account'}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('description')}</label>
                <input
                  type="text"
                  value={ledgerForm.description}
                  onChange={(e) => setLedgerForm({ ...ledgerForm, description: e.target.value })}
                  placeholder={t('descPH')}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('attachment') || 'Attachment'}</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setLedgerForm({ ...ledgerForm, attachment: e.target.files[0] })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date')}</label>
                  <input
                    type="date"
                    required
                    value={ledgerForm.date}
                    onChange={(e) => setLedgerForm({ ...ledgerForm, date: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input
                    type="time"
                    value={ledgerForm.time}
                    onChange={(e) => setLedgerForm({ ...ledgerForm, time: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowAddLedgerModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('logEntry', { name: '' })}</button>
            </div>
          </form>
        </div>
      )}

      {/* CASH ENTRY MODAL */}
      {showAddCashModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddCashEntry} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('logCashEntryTitle')}</h3>
              <button type="button" onClick={() => setShowAddCashModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('cashFlow')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setCashForm({ ...cashForm, entry_type: 'IN' })}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)',
                      background: cashForm.entry_type === 'IN' ? 'rgba(16,185,129,0.1)' : 'transparent',
                      color: cashForm.entry_type === 'IN' ? 'var(--brand-success)' : 'var(--text-secondary)',
                      fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {t('cashInFlow')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCashForm({ ...cashForm, entry_type: 'OUT' })}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)',
                      background: cashForm.entry_type === 'OUT' ? 'rgba(239,68,68,0.1)' : 'transparent',
                      color: cashForm.entry_type === 'OUT' ? 'var(--brand-danger)' : 'var(--text-secondary)',
                      fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {t('cashOutFlow')}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('amountRs')}</label>
                <input
                  type="text"
                  data-calc=""
                  required
                  value={cashForm.amount}
                  onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })}
                  style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('category')}</label>
                {!showCustomCategoryInput ? (
                  <select
                    value={cashForm.category}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setShowCustomCategoryInput(true);
                      } else {
                        setCashForm({ ...cashForm, category: e.target.value });
                      }
                    }}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  >
                    <option value="Sales">{t('salesIncome')}</option>
                    <option value="Salary">{t('staffSalaryOption')}</option>
                    <option value="Utilities">{t('shopUtilities')}</option>
                    <option value="General">{t('generalExpenses')}</option>
                    <option value="__custom__">{t('customCategory')}</option>
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      required
                      placeholder={t('catPH')}
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                    />
                    <button type="button" onClick={() => setShowCustomCategoryInput(false)} className="btn btn-secondary" style={{ padding: '10px' }}>{t('cancel')}</button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('detailsLabel')}</label>
                <input
                  type="text"
                  value={cashForm.description}
                  onChange={(e) => setCashForm({ ...cashForm, description: e.target.value })}
                  placeholder={t('rentPH')}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('attachment') || 'Attachment'}</label>
                <input type="file" accept="image/*,.pdf" onChange={e => setCashForm({ ...cashForm, attachment: e.target.files[0] })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date')}</label>
                  <input
                    type="date"
                    required
                    value={cashForm.date}
                    onChange={(e) => setCashForm({ ...cashForm, date: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input
                    type="time"
                    value={cashForm.time}
                    onChange={(e) => setCashForm({ ...cashForm, time: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowAddCashModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('logCashEntry')}</button>
            </div>
          </form>
        </div>
      )}

      {/* BANK ACCOUNT MODAL */}
      {showBankModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={saveBankAccount} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '480px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{editingBankAccount ? t('editBankAccount') : t('addBankAccount')}</h3>
              <button type="button" onClick={() => { setShowBankModal(false); setEditingBankAccount(null); setBankForm({ name: '', account_title: '', account_number: '', iban: '', branch: '', opening_balance: '0', is_active: true, notes: '' }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('bankName') || 'Bank Name'}</label>
                <select required value={bankAccountNameOther ? 'Other' : bankForm.name} onChange={e => { if (e.target.value === 'Other') { setBankAccountNameOther(true); setBankForm({ ...bankForm, name: '' }); } else { setBankAccountNameOther(false); setBankForm({ ...bankForm, name: e.target.value }); } }} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="">{t('selectBankName') || '-- Select Bank --'}</option>
                  {bankNameOptions.map(bank => <option key={bank} value={bank}>{bank}</option>)}
                  <option value="Other">{t('other') || 'Other'}</option>
                </select>
                {bankAccountNameOther && (
                  <input required type="text" value={bankForm.name} onChange={e => setBankForm({ ...bankForm, name: e.target.value })} placeholder={t('enterBankName') || 'Enter bank name'} style={{ width: '100%', padding: '10px', marginTop: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('accountTitle')}</label>
                <input type="text" value={bankForm.account_title} onChange={e => setBankForm({ ...bankForm, account_title: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('accountNumber')}</label>
                  <input type="text" value={bankForm.account_number} onChange={e => setBankForm({ ...bankForm, account_number: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('iban')}</label>
                  <input type="text" value={bankForm.iban} onChange={e => setBankForm({ ...bankForm, iban: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('branch')}</label>
                  <input type="text" value={bankForm.branch} onChange={e => setBankForm({ ...bankForm, branch: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('openingBalance')}</label>
                  <input type="number" step="0.01" value={bankForm.opening_balance} onChange={e => setBankForm({ ...bankForm, opening_balance: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('status')}</label>
                <select value={bankForm.is_active ? 'active' : 'inactive'} onChange={e => setBankForm({ ...bankForm, is_active: e.target.value === 'active' })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="active">{t('activeAccount')}</option>
                  <option value="inactive">{t('inactive')}</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('description')}</label>
                <textarea value={bankForm.notes} onChange={e => setBankForm({ ...bankForm, notes: e.target.value })} rows="3" style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => { setShowBankModal(false); setEditingBankAccount(null); setBankForm({ name: '', account_title: '', account_number: '', iban: '', branch: '', opening_balance: '0', is_active: true, notes: '' }); }} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editingBankAccount ? t('saveChanges') : t('save')}</button>
            </div>
          </form>
        </div>
      )}

      {/* =================================================== */}
      {/* PAY SALARIES (PAYROLL LOG) MODAL                    */}
      {/* =================================================== */}
      {/* PAY SALARIES (PAYROLL LOG) MODAL                    */}
      {/* =================================================== */}
      {showAddPayrollModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddPayroll} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '480px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('logSalaryTitle')}</h3>
              <button type="button" onClick={() => setShowAddPayrollModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('chooseEmployeeTitle')}</label>
                <select required value={payrollForm.staff_member_id} onChange={e => setPayrollForm({ ...payrollForm, staff_member_id: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="">{t('chooseEmployee')}</option>
                  {staff.filter(s => !s.is_deleted).map(s => (
                    <option key={s.id} value={s.id}>{s.name} (Base: Rs. {s.base_salary})</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('periodStart')}</label>
                  <input type="date" required value={payrollForm.pay_period_start} onChange={e => setPayrollForm({ ...payrollForm, pay_period_start: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('periodEnd')}</label>
                  <input type="date" required value={payrollForm.pay_period_end} onChange={e => setPayrollForm({ ...payrollForm, pay_period_end: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('allowancesLabel')}</label>
                  <input type="text" data-calc="" value={payrollForm.allowances} onChange={e => setPayrollForm({ ...payrollForm, allowances: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('deductionsLabel')}</label>
                  <input type="text" data-calc="" value={payrollForm.deductions} onChange={e => setPayrollForm({ ...payrollForm, deductions: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('status')}</label>
                <select value={payrollForm.status} onChange={e => setPayrollForm({ ...payrollForm, status: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="UNPAID">{t('unpaidAccrued')}</option>
                  <option value="PAID">{t('paidOutDirectly')}</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowAddPayrollModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('logSalaryTitle')}</button>
            </div>
          </form>
        </div>
      )}

      {/* EDIT MODALS FOR CRUD */}
      {editingParty && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateParty} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '450px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editParty')}</h3>
              <button type="button" onClick={() => { setEditingParty(null); setShowAddAreaInline(false); setNewAreaName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('contactName')}</label>
                <input required type="text" value={editingParty.name} onChange={e => setEditingParty({ ...editingParty, name: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('phone')}</label>
                <input type="text" value={editingParty.phone || ''} onChange={e => setEditingParty({ ...editingParty, phone: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('type') || 'Type'}</label>
                <select value={editingParty.party_type} onChange={e => setEditingParty({ ...editingParty, party_type: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="CUSTOMER" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t('customers')}</option>
                  <option value="SUPPLIER" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t('suppliers')}</option>
                  <option value="BOTH" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>{t('both') || 'Both'}</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('cnic')}</label>
                <input type="text" value={editingParty.cnic || ''} onChange={e => setEditingParty({ ...editingParty, cnic: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              {/* FBR: a registered customer's tax number goes on the invoice.
                  Walk-ins can leave it blank — FBR accepts them as Unregistered. */}
              {fbrEnabled && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>NTN / STRN <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional — FBR)</span></label>
                  <input type="text" placeholder="Leave empty if they have none" value={editingParty.ntn_strn || ''} onChange={e => setEditingParty({ ...editingParty, ntn_strn: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('shopAddressLabel')}</label>
                <input type="text" value={editingParty.address || ''} onChange={e => setEditingParty({ ...editingParty, address: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('area') || 'Area'}</label>
                {showAddAreaInline ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input type="text" autoFocus placeholder={t('areaPH') || 'e.g. DHA Phase 5'} value={newAreaName} onChange={e => setNewAreaName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNewArea(); } if (e.key === 'Escape') { setShowAddAreaInline(false); setNewAreaName(''); } }} style={{ flex: 1, padding: '10px', border: '1px solid var(--brand-success)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                    <button type="button" onClick={addNewArea} className="btn btn-primary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('save')}</button>
                    <button type="button" onClick={() => { setShowAddAreaInline(false); setNewAreaName(''); }} className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('cancel')}</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <select value={editingParty.area || ''} onChange={e => { if (e.target.value === '__add_new__') { setShowAddAreaInline(true); } else { setEditingParty({ ...editingParty, area: e.target.value }); } }} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                      <option value="">{t('selectArea') || '-- Select Area --'}</option>
                      {areas.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                      <option value="__add_new__">+ {t('addNewArea') || 'Add New Area'}</option>
                    </select>
                  </div>
                )}
              </div>
              {(editingParty.party_type === 'SUPPLIER' || editingParty.party_type === 'BOTH') && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('itemsSuppliedLabel')}</label>
                  <input type="text" value={editingParty.items_supplied || ''} onChange={e => setEditingParty({ ...editingParty, items_supplied: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => { setEditingParty(null); setShowAddAreaInline(false); setNewAreaName(''); }} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges')}</button>
            </div>
          </form>
        </div>
      )}
      {editingStockMovement && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateStockMovement} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editStockMovement') || 'Edit Stock Movement'}</h3>
              <button type="button" onClick={() => setEditingStockMovement(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('type') || 'Type'}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={() => setEditingStockMovement({ ...editingStockMovement, movement_type: 'IN' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingStockMovement.movement_type === 'IN' ? 'rgba(16,185,129,0.15)' : 'transparent', color: editingStockMovement.movement_type === 'IN' ? 'var(--brand-success)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('stockIn') || 'Stock In'}</button>
                  <button type="button" onClick={() => setEditingStockMovement({ ...editingStockMovement, movement_type: 'OUT' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingStockMovement.movement_type === 'OUT' ? 'rgba(239,68,68,0.15)' : 'transparent', color: editingStockMovement.movement_type === 'OUT' ? 'var(--brand-danger)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('stockOut') || 'Stock Out'}</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('qty') || 'Quantity'} *</label>
                  <input required type="text" data-calc="" placeholder={t('qty')} value={editingStockMovement.quantity} onChange={(e) => setEditingStockMovement({ ...editingStockMovement, quantity: e.target.value.replace(/[^0-9.]/g, '') })} style={{ flex: 1, padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date') || 'Date'} *</label>
                  <input required type="date" value={editingStockMovement.date} onChange={e => setEditingStockMovement({ ...editingStockMovement, date: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input type="time" value={editingStockMovement.time || ''} onChange={e => setEditingStockMovement({ ...editingStockMovement, time: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('supplierOrDesc') || 'Supplier / Description'}</label>
                <input type="text" value={editingStockMovement.description || ''} onChange={e => setEditingStockMovement({ ...editingStockMovement, description: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} placeholder={t('supplierOrDesc')} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setEditingStockMovement(null)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel') || 'Cancel'}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges') || 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {editingLedgerEntry && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateLedgerEntry} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editTransaction')}</h3>
              <button disabled={!canEdit} type="button" onClick={() => setEditingLedgerEntry(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('transactionType')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button disabled={!canEdit} type="button" onClick={() => setEditingLedgerEntry({ ...editingLedgerEntry, entry_type: 'GIVE' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingLedgerEntry.entry_type === 'GIVE' ? 'rgba(16,185,129,0.15)' : 'transparent', color: editingLedgerEntry.entry_type === 'GIVE' ? 'var(--brand-success)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('gavePayNeg')}</button>
                  <button disabled={!canEdit} type="button" onClick={() => setEditingLedgerEntry({ ...editingLedgerEntry, entry_type: 'GET' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingLedgerEntry.entry_type === 'GET' ? 'rgba(239,68,68,0.15)' : 'transparent', color: editingLedgerEntry.entry_type === 'GET' ? 'var(--brand-danger)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('gotSuppliedPos')}</button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('amountRs')}</label>
                <input required type="text" data-calc="" value={editingLedgerEntry.amount} onChange={e => setEditingLedgerEntry({ ...editingLedgerEntry, amount: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('description')}</label>
                <input type="text" value={editingLedgerEntry.description || ''} onChange={e => setEditingLedgerEntry({ ...editingLedgerEntry, description: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('paymentMethod') || 'Payment Method'}</label>
                  <select
                    value={editingLedgerEntry.payment_method || 'CASH'}
                    onChange={e => setEditingLedgerEntry({ ...editingLedgerEntry, payment_method: e.target.value, bank_account: e.target.value === 'CASH' ? null : editingLedgerEntry.bank_account })}
                    style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                  >
                    <option value="CASH">{t('cash') || 'Cash'}</option>
                    <option value="ONLINE">{t('bankOnline') || 'Bank / Online'}</option>
                  </select>
                </div>
                {(editingLedgerEntry.payment_method === 'ONLINE') && (
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('bankAccount') || 'Bank Account'}</label>
                    <select
                      value={editingLedgerEntry.bank_account || ''}
                      onChange={e => setEditingLedgerEntry({ ...editingLedgerEntry, bank_account: e.target.value })}
                      style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      required
                    >
                      <option value="">{t('selectBankAccount') || '-- Select Bank Account --'}</option>
                      {bankAccounts.filter(acc => !acc.is_deleted && (!selectedBusiness || String(acc.business) === String(selectedBusiness.id))).map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name || acc.account_title || 'Bank Account'}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date')}</label>
                  <input required type="date" value={editingLedgerEntry.date} onChange={e => setEditingLedgerEntry({ ...editingLedgerEntry, date: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input type="time" value={editingLedgerEntry.time || ''} onChange={e => setEditingLedgerEntry({ ...editingLedgerEntry, time: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button disabled={!canEdit} type="button" onClick={() => setEditingLedgerEntry(null)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('entryUpdated')}</button>
            </div>
          </form>
        </div>
      )}

      {editingCashEntry && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateCashEntry} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '450px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editCashEntry')}</h3>
              <button type="button" onClick={() => setEditingCashEntry(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('flowDirection')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={() => setEditingCashEntry({ ...editingCashEntry, entry_type: 'IN' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingCashEntry.entry_type === 'IN' ? 'rgba(16,185,129,0.15)' : 'transparent', color: editingCashEntry.entry_type === 'IN' ? 'var(--brand-success)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('cashInFlow')}</button>
                  <button type="button" onClick={() => setEditingCashEntry({ ...editingCashEntry, entry_type: 'OUT' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingCashEntry.entry_type === 'OUT' ? 'rgba(239,68,68,0.15)' : 'transparent', color: editingCashEntry.entry_type === 'OUT' ? 'var(--brand-danger)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('cashOutFlow')}</button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('amountRs')}</label>
                <input required type="text" data-calc="" value={editingCashEntry.amount} onChange={e => setEditingCashEntry({ ...editingCashEntry, amount: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('category')}</label>
                <input type="text" value={editingCashEntry.category} onChange={e => setEditingCashEntry({ ...editingCashEntry, category: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('description')}</label>
                <input type="text" value={editingCashEntry.description || ''} onChange={e => setEditingCashEntry({ ...editingCashEntry, description: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date')}</label>
                  <input required type="date" value={editingCashEntry.date} onChange={e => setEditingCashEntry({ ...editingCashEntry, date: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input type="time" value={editingCashEntry.time || ''} onChange={e => setEditingCashEntry({ ...editingCashEntry, time: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setEditingCashEntry(null)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('cashUpdated')}</button>
            </div>
          </form>
        </div>
      )}

      {editingIeEntry && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateIeEntry} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '450px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editOperation')}</h3>
              <button type="button" onClick={() => setEditingIeEntry(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('type')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={() => setEditingIeEntry({ ...editingIeEntry, type: 'INCOME' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingIeEntry.type === 'INCOME' ? 'rgba(16,185,129,0.15)' : 'transparent', color: editingIeEntry.type === 'INCOME' ? 'var(--brand-success)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('income')}</button>
                  <button type="button" onClick={() => setEditingIeEntry({ ...editingIeEntry, type: 'EXPENSE' })} style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: editingIeEntry.type === 'EXPENSE' ? 'rgba(239,68,68,0.15)' : 'transparent', color: editingIeEntry.type === 'EXPENSE' ? 'var(--brand-danger)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer' }}>{t('expense')}</button>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('amountRs')}</label>
                <input required type="text" data-calc="" value={editingIeEntry.amount} onChange={e => setEditingIeEntry({ ...editingIeEntry, amount: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('category')}</label>
                <input type="text" value={editingIeEntry.category} onChange={e => setEditingIeEntry({ ...editingIeEntry, category: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('description')}</label>
                <input type="text" value={editingIeEntry.description || ''} onChange={e => setEditingIeEntry({ ...editingIeEntry, description: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('date')}</label>
                  <input required type="date" value={editingIeEntry.date} onChange={e => setEditingIeEntry({ ...editingIeEntry, date: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                  <input type="time" value={editingIeEntry.time || ''} onChange={e => setEditingIeEntry({ ...editingIeEntry, time: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setEditingIeEntry(null)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges')}</button>
            </div>
          </form>
        </div>
      )}

      {editingInvoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateInvoice} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '550px', background: 'var(--bg-secondary)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editInvoice')}: {editingInvoice.invoice_number}</h3>
              <button disabled={!canEdit} type="button" onClick={() => setEditingInvoice(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('status')}</label>
                <select value={editingInvoice.status} onChange={e => setEditingInvoice({ ...editingInvoice, status: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                  <option value="UNPAID">{t('unpaid')}</option>
                  <option value="PAID">{t('paid')}</option>
                </select>
              </div>
            </div>
            {editingInvoice._items && editingInvoice._items.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '10px' }}>{t('lineItems')}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {editingInvoice._items.map((item, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: '8px', alignItems: 'center', padding: '10px', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>{item.description}</div>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{t('qty')}</label>
                        <input type="text" data-calc="" value={item.quantity} onChange={e => {
                          const items = [...editingInvoice._items];
                          items[idx] = { ...items[idx], quantity: e.target.value };
                          setEditingInvoice({ ...editingInvoice, _items: items });
                        }} style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: '4px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{t('unitPrice')}</label>
                        <input type="text" data-calc="" value={item.unit_price} onChange={e => {
                          const items = [...editingInvoice._items];
                          items[idx] = { ...items[idx], unit_price: e.target.value };
                          setEditingInvoice({ ...editingInvoice, _items: items });
                        }} style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: '4px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '10px', textAlign: 'right', fontSize: '0.9rem', fontWeight: 600 }}>
                  {t('total')}: Rs. {editingInvoice._items.reduce((s, r) => s + parseFloat(r.quantity || 0) * parseFloat(r.unit_price || 0), 0).toLocaleString()}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button disabled={!canEdit} type="button" onClick={() => setEditingInvoice(null)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges')}</button>
            </div>
          </form>
        </div>
      )}

      {editingProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '16px' }}>
          <form onSubmit={handleUpdateProduct} className="glass-panel anim-scale-in custom-scrollbar" style={{ padding: '32px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', background: 'var(--bg-secondary)', scrollbarWidth: 'thin' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editProduct')}</h3>
              <button type="button" onClick={() => { setEditingProduct(null); setShowAddProductTypeInline(false); setNewProductTypeName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('productName')}</label>
                <input required type="text" value={editingProduct.name} onChange={e => setEditingProduct({ ...editingProduct, name: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              {/* Purchase Price Section */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('purchasePrice')} *</label>
                <input
                  required
                  type="text"
                  data-calc=""
                  value={editingProduct.purchase_price}
                  onChange={e => setEditingProduct({ ...editingProduct, purchase_price: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <SaveHint value={editingProduct.purchase_price} unitsPerPack={editingProduct.units_per_bulk} kind="price" t={t} />
              </div>

              {/* Sale Price Section */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('salePrice')} *</label>
                <input
                  required
                  type="text"
                  data-calc=""
                  value={editingProduct.sale_price}
                  onChange={e => setEditingProduct({ ...editingProduct, sale_price: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <SaveHint value={editingProduct.sale_price} unitsPerPack={editingProduct.units_per_bulk} kind="price" t={t} />
              </div>

              {/* Stock Quantity. Counted in packs it is split into whole packs
                  plus loose pieces, so a part-pack is typed as itself rather
                  than the decimal one box would have to show. */}
              <div style={packMode(editingProduct.units_per_bulk) ? { gridColumn: 'span 2' } : undefined}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('stockQty')} *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: '1 1 0' }}>
                    {packMode(editingProduct.units_per_bulk) && (
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>{t('packs')}</div>
                    )}
                    <input
                      required
                      type="text"
                      data-calc=""
                      value={editingProduct.stock_quantity}
                      onChange={e => setEditingProduct({ ...editingProduct, stock_quantity: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                    />
                  </div>
                  {packMode(editingProduct.units_per_bulk) && (
                    <div style={{ flex: '1 1 0' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>{t('loosePieces')}</div>
                      <input
                        type="text"
                        data-calc=""
                        value={editingProduct.stock_loose ?? ''}
                        onChange={e => setEditingProduct({ ...editingProduct, stock_loose: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      />
                    </div>
                  )}
                </div>
                {packMode(editingProduct.units_per_bulk) && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                    {totalPiecesText(editingProduct.stock_quantity, editingProduct.stock_loose, editingProduct.units_per_bulk, t)}
                  </div>
                )}
              </div>

              <div style={packMode(editingProduct.units_per_bulk) ? { gridColumn: 'span 2' } : undefined}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>
                  {t('lowStockAlert')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({t('optional')})</span>
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: '1 1 0' }}>
                    {packMode(editingProduct.units_per_bulk) && (
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>{t('packs')}</div>
                    )}
                    <input type="text" data-calc="" value={editingProduct.min_stock_alert} onChange={e => setEditingProduct({ ...editingProduct, min_stock_alert: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }} />
                  </div>
                  {packMode(editingProduct.units_per_bulk) && (
                    <div style={{ flex: '1 1 0' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '4px' }}>{t('loosePieces')}</div>
                      <input type="text" data-calc="" value={editingProduct.alert_loose ?? ''} onChange={e => setEditingProduct({ ...editingProduct, alert_loose: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }} />
                    </div>
                  )}
                </div>
                {packMode(editingProduct.units_per_bulk) && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                    {totalPiecesText(editingProduct.min_stock_alert, editingProduct.alert_loose, editingProduct.units_per_bulk, t)}
                  </div>
                )}
              </div>

              {/* Units Quantity (Pieces per Pack) Section */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('unitsQty')} *</label>
                <input
                  required
                  type="number"
                  step="1"
                  min="1"
                  data-calc=""
                  placeholder="e.g. 12"
                  value={editingProduct.units_per_bulk === '' || editingProduct.units_per_bulk === null || editingProduct.units_per_bulk === undefined ? '' : editingProduct.units_per_bulk}
                  onChange={e => setEditingProduct(changeUnits(editingProduct, e.target.value))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>{t('unitsQtyHelper')}</div>
              </div>
              {/* Expiration Date */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('expiryDateOptional')}</label>
                <input type="date" value={editingProduct.expiration_date || ''} onChange={e => setEditingProduct({ ...editingProduct, expiration_date: e.target.value || null })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('skuBarcode')}</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input type="text" value={editingProduct.sku || ''} onChange={e => setEditingProduct({ ...editingProduct, sku: e.target.value })} style={{ flex: 1, padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                  <button type="button" onClick={() => setEditingProduct({ ...editingProduct, sku: generateSku() })} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }} title={t('generateBarcode')}>
                    <Barcode size={14} />
                  </button>
                </div>
                {editingProduct.sku && (
                  <div style={{ marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <BarcodePreview value={editingProduct.sku} height={35} fontSize={10} />
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('productType') || 'Product Type'}</label>
                {showAddProductTypeInline ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input type="text" autoFocus placeholder={t('productTypePH') || 'e.g. Bakery, Electronics'} value={newProductTypeName} onChange={e => setNewProductTypeName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNewProductType(); } if (e.key === 'Escape') { setShowAddProductTypeInline(false); setNewProductTypeName(''); } }} style={{ flex: 1, padding: '10px', border: '1px solid var(--brand-success)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                    <button type="button" onClick={addNewProductType} className="btn btn-primary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('save')}</button>
                    <button type="button" onClick={() => { setShowAddProductTypeInline(false); setNewProductTypeName(''); }} className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('cancel')}</button>
                  </div>
                ) : (
                  <select value={editingProduct.product_type || ''} onChange={e => { if (e.target.value === '__add_new__') { setShowAddProductTypeInline(true); } else { setEditingProduct({ ...editingProduct, product_type: e.target.value }); } }} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                    <option value="">{t('selectProductType') || '-- Select Type --'}</option>
                    {productTypes.map(pt => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                    <option value="__add_new__">+ {t('addNewProductType') || 'Add New Type'}</option>
                  </select>
                )}
              </div>
              {/* FBR Digital Invoicing — only when this business turned FBR on.
                  These three travel with every line of every FBR invoice. */}
              {fbrEnabled && (
                <div style={{ gridColumn: 'span 2', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', background: 'var(--bg-tertiary)' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>FBR (used on every invoice line)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>HS Code</label>
                      <input
                        type="text"
                        placeholder="8 digits — e.g. 01012100"
                        value={editingProduct.hsn_code || ''}
                        onChange={e => setEditingProduct({ ...editingProduct, hsn_code: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      />
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>FBR rejects any line without one.</div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>FBR tax rate</label>
                      <select
                        value={editingProduct.fbr_tax_rate || ''}
                        onChange={e => setEditingProduct({ ...editingProduct, fbr_tax_rate: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      >
                        <option value="">Business default ({fbrConfig?.default_tax_rate || '0%'})</option>
                        {['0%', '1%', '2%', '3%', '4%', '5%', '10%', '15%', '17%', '18%', '19%', '20%', '25%'].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>FBR unit of measure</label>
                      <input
                        type="text"
                        placeholder="Leave empty to use the product's unit (pcs / kg / ltr …)"
                        value={editingProduct.fbr_uom || ''}
                        onChange={e => setEditingProduct({ ...editingProduct, fbr_uom: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('productPhoto') || 'Product Photo'}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {editingProduct.photo && !editProductPhotoFile && (
                  <img src={getPhotoUrl(editingProduct.photo)} alt="Current" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-color)' }} />
                )}
                {editProductPhotoFile && (
                  <img src={URL.createObjectURL(editProductPhotoFile)} alt="Preview" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-color)' }} />
                )}
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px 14px', border: '2px dashed var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem', transition: 'border-color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
                >
                  <Camera size={18} />
                  <span>{editProductPhotoFile ? editProductPhotoFile.name : (editingProduct.photo ? (t('replacePhoto') || 'Replace Photo') : (t('browsePhoto') || 'Browse Photo'))}</span>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setEditProductPhotoFile(e.target.files[0] || null)} />
                </label>
                {editProductPhotoFile && (
                  <button type="button" onClick={() => setEditProductPhotoFile(null)} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => { setEditingProduct(null); setEditProductPhotoFile(null); setShowAddProductTypeInline(false); setNewProductTypeName(''); }} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges')}</button>
            </div>
          </form>
        </div>
      )}

      {restockModal.show && restockModal.product && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '16px' }}>
          <form onSubmit={handleRestock} className="glass-panel anim-scale-in custom-scrollbar" style={{ padding: '32px', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-secondary)', scrollbarWidth: 'thin' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('restock')}: {restockModal.product.name}</h3>
              <button type="button" onClick={() => setRestockModal({ show: false, product: null, quantity: '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                <span>{t('currentStock')}: <strong style={{ color: 'var(--text-primary)' }}>
                  {stockStatusText(restockModal.product.stock_quantity, restockModal.product.units_per_bulk, t)}
                </strong></span>
                {packMode(restockModal.product.units_per_bulk) && (
                  <span style={{ color: 'var(--brand-primary)', fontWeight: 500 }}>
                    ({pcsText(restockModal.product.stock_quantity)} {restockModal.product.unit || 'pcs'})
                  </span>
                )}
              </div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('addQuantity')} ({packMode(restockModal.product.units_per_bulk) ? t('qtyTypePack') : (restockModal.product.unit || 'pcs')})</label>
              <input required type="text" data-calc="" placeholder="e.g. 10" value={restockModal.quantity} onChange={e => setRestockModal({ ...restockModal, quantity: e.target.value })} style={{ width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              {/* The box holds packs, so the hint is the piece count it adds. */}
              {packMode(restockModal.product.units_per_bulk) && parseFloat(restockModal.quantity) > 0 && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                  {pcsText(Math.round(parseFloat(restockModal.quantity) * numOr(restockModal.product.units_per_bulk, 1)))} {t('qtyTypePiece')}
                </div>
              )}
              {!packMode(restockModal.product.units_per_bulk) && parseFloat(restockModal.quantity) > 0 && packBreakdown(restockModal.quantity, restockModal.product.units_per_bulk, t) && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                  {packBreakdown(restockModal.quantity, restockModal.product.units_per_bulk, t)}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setRestockModal({ show: false, product: null, quantity: '' })} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('restock')}</button>
            </div>
          </form>
        </div>
      )}

      {/* =================================================== */}
      {/* BARCODE PRINT MODAL                                */}
      {/* =================================================== */}
      {barcodePrintModal.show && barcodePrintModal.product && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <div className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '420px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('printBarcode')}</h3>
              <button type="button" onClick={() => setBarcodePrintModal({ show: false, product: null, quantity: 1 })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ padding: '14px 18px', background: '#fff', borderRadius: '10px', border: '1px solid var(--border-color)', display: 'block', maxWidth: '340px', width: '100%', margin: '0 auto', boxSizing: 'border-box', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px', color: '#09090b', textTransform: 'uppercase', letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{barcodePrintModal.product.name}</div>
                <BarcodePreview value={barcodePrintModal.product.sku} width={1.6} height={45} fontSize={13} sideText={`Rs. ${parseFloat(barcodePrintModal.product.sale_price || 0).toLocaleString()}`} />
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '6px' }}>{t('labelQuantity') || 'Number of Labels'}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button type="button" onClick={() => setBarcodePrintModal(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))} className="btn btn-secondary" style={{ padding: '6px 12px' }}>-</button>
                <input type="number" min="1" max="50" value={barcodePrintModal.quantity} onChange={e => setBarcodePrintModal(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))} style={{ width: '60px', textAlign: 'center', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                <button type="button" onClick={() => setBarcodePrintModal(prev => ({ ...prev, quantity: Math.min(50, prev.quantity + 1) }))} className="btn btn-secondary" style={{ padding: '6px 12px' }}>+</button>
              </div>
            </div>
            <div id="barcode-print-area" style={{ display: 'none' }}>
              {Array.from({ length: barcodePrintModal.quantity }).map((_, i) => (
                <div key={i} className="barcode-label" style={{ padding: '10px 14px', textAlign: 'center', pageBreakInside: 'avoid', marginBottom: '12px', border: '1px solid #000', borderRadius: '6px', background: '#fff', display: 'inline-block', maxWidth: '100%', boxSizing: 'border-box' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '6px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{barcodePrintModal.product.name}</div>
                  <BarcodePreview value={barcodePrintModal.product.sku} width={1.5} height={40} fontSize={12} sideText={`Rs. ${parseFloat(barcodePrintModal.product.sale_price || 0).toLocaleString()}`} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setBarcodePrintModal({ show: false, product: null, quantity: 1 })} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="button" onClick={() => {
                const printArea = document.getElementById('barcode-print-area');
                if (!printArea) return;
                printArea.style.display = 'block';
                const printWindow = window.open('', '_blank', 'width=400,height=600');
                // A popup blocker may deny this; without the guard we would
                // throw on a null window and leave the area stuck visible.
                if (!printWindow) {
                  showNotification(t('popupBlocked') || 'Pop-up blocked. Please allow pop-ups for this site.', 'warning');
                  printArea.style.display = 'none';
                  return;
                }
                // Build the document with DOM APIs instead of document.write().
                // write() after load implicitly calls document.open(), which
                // discards whatever the window contained, and it is the classic
                // sink for injecting markup assembled from data.
                const doc = printWindow.document;
                doc.title = 'Print Barcode';
                const style = doc.createElement('style');
                style.textContent =
                  'body{font-family:Arial,sans-serif;margin:10px;}' +
                  '.barcode-label{padding:10px 0;text-align:center;page-break-inside:avoid;}' +
                  'svg{max-width:100%;}';
                // A freshly opened about:blank may have no <head>/<body> yet,
                // so build them rather than assuming they exist.
                const head = doc.head || doc.createElement('head');
                if (!head.parentNode) doc.documentElement.appendChild(head);
                head.appendChild(style);
                const body = doc.body || doc.createElement('body');
                if (!body.parentNode) doc.documentElement.appendChild(body);
                // insertAdjacentHTML parses markup rather than executing it, and
                // this markup is React-rendered from already-escaped state.
                body.insertAdjacentHTML('beforeend', printArea.innerHTML);
                doc.close();
                printWindow.focus();
                setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
                printArea.style.display = 'none';
              }} className="btn btn-primary" style={{ flex: 1 }}><Printer size={14} /> {t('print')}</button>
            </div>
          </div>
        </div>
      )}

      {/* =================================================== */}
      {/* EDIT STAFF MEMBER MODAL                            */}
      {/* =================================================== */}
      {editingStaff && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleUpdateStaff} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('editStaff')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>{t('updateEmployeeInfo')}</p>
              </div>
              <button type="button" onClick={() => setEditingStaff(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('fullName')}</label>
                <input required type="text" value={editingStaff.name} onChange={e => setEditingStaff({ ...editingStaff, name: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('phone')}</label>
                <input type="text" value={editingStaff.phone || ''} onChange={e => setEditingStaff({ ...editingStaff, phone: e.target.value })} placeholder="03001234567" style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('cnic')}</label>
                <input type="text" value={editingStaff.cnic || ''} onChange={e => setEditingStaff({ ...editingStaff, cnic: e.target.value })} placeholder="35202-XXXXXXX-X" style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('salaryType')}</label>
                  <select value={editingStaff.salary_type} onChange={e => setEditingStaff({ ...editingStaff, salary_type: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                    <option value="MONTHLY">{t('monthly')}</option>
                    <option value="DAILY">{t('daily')}</option>
                    <option value="HOURLY">{t('hourly')}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('baseSalaryLabel')}</label>
                  <input required type="text" data-calc="" value={editingStaff.base_salary} onChange={e => setEditingStaff({ ...editingStaff, base_salary: e.target.value })} style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setEditingStaff(null)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveChanges')}</button>
            </div>
          </form>
        </div>
      )}

      {/* =================================================== */}
      {/* PURCHASE INVOICE MODAL (PI)                         */}
      {/* =================================================== */}
      {showPurchaseInvoiceModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={(e) => submitPurchaseInvoice(e, true)} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '650px', background: 'var(--bg-secondary)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                  {t('purchaseInvoice') || 'Purchase Invoice'} (PI)
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                  {t('supplierLabel') || 'Supplier'}: <strong style={{ color: 'var(--text-primary)' }}>{parties.find(p => p.id === purchaseInvoiceBuilder.party_id)?.name}</strong>
                </p>
              </div>
              <button type="button" onClick={() => setShowPurchaseInvoiceModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '12px' }}>{t('lineItems')}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {isMobileView && purchaseInvoiceBuilder.items.map((row, index) => {
                  const piFilteredProducts = products.filter(p => !p.is_deleted).filter(p => {
                    if (!piProdSearchQuery) return true;
                    return (p.name || '').toLowerCase().includes(piProdSearchQuery.toLowerCase()) || (p.sku || '').toLowerCase().includes(piProdSearchQuery.toLowerCase());
                  });
                  return (
                  <div key={index} style={{ border: '1px solid var(--border-color)', borderRadius: '10px', padding: '14px', background: 'var(--bg-tertiary)', display: 'flex', flexDirection: 'column', gap: '10px', position: 'relative' }}>
                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => {
                        const items = purchaseInvoiceBuilder.items.filter((_, i) => i !== index);
                        setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, items: items.length ? items : [{ product_id: '', quantity: 1, unit_price: 0, custom_item_name: '', sku: '', unit: 'pcs', sale_price: 0, min_stock_alert: 5 }] });
                      }}
                      style={{ position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', color: 'var(--brand-danger)', cursor: 'pointer', padding: '2px' }}
                    >
                      <Trash2 size={15} />
                    </button>

                    {/* Row 1: Product Search */}
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('product') || 'Product'}</label>
                      <input
                        type="text"
                        data-prow={index}
                        placeholder={t('searchProductPH') || 'Search or type product name...'}
                        value={piProdDropdownRow === index ? (piProdSearchQuery || '') : (row.product_id ? products.find(p => p.id === row.product_id)?.name || '' : row.custom_item_name || '')}
                        onFocus={() => { setPiProdDropdownRow(index); setPiProdSearchQuery(''); setPiProdHighlightedIdx(0); }}
                        onBlur={() => setTimeout(() => { if (piProdDropdownRow === index) setPiProdDropdownRow(null); }, 200)}
                        onChange={(e) => { setPiProdSearchQuery(e.target.value); setPiProdDropdownRow(index); setPiProdHighlightedIdx(0); handlePurchaseInvoiceItemChange(index, 'custom_item_name', e.target.value); if (!e.target.value) handlePurchaseInvoiceItemChange(index, 'product_id', ''); }}
                        onKeyDown={(e) => {
                          if (piProdDropdownRow !== index) return;
                          if (e.key === 'ArrowDown') { e.preventDefault(); setPiProdHighlightedIdx(prev => Math.min(prev + 1, piFilteredProducts.length - 1)); }
                          else if (e.key === 'ArrowUp') { e.preventDefault(); setPiProdHighlightedIdx(prev => Math.max(prev - 1, 0)); }
                          else if (e.key === 'Enter') {
                            e.preventDefault();
                            if (piFilteredProducts.length > 0 && piFilteredProducts[piProdHighlightedIdx]) {
                              selectPurchaseProduct(index, piFilteredProducts[piProdHighlightedIdx]);
                            } else if (piProdSearchQuery.trim()) {
                              selectPurchaseCustomItem(index, piProdSearchQuery.trim());
                            }
                          }
                        }}
                        style={{ width: '100%', padding: '9px 36px 9px 12px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                      />
                      <button type="button" title={t('selectFromGallery') || 'Select from Gallery'}
                        onClick={() => {
                          setGallerySelectMode(true);
                          setGallerySelectTarget({ type: 'purchase', index });
                          setActiveTab('settings');
                          setSettingsSubTab('gallery');
                        }}
                        style={{ position: 'absolute', right: '6px', top: '32px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                      >
                        <Camera size={16} />
                      </button>
                      {piProdDropdownRow === index && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                          {piFilteredProducts.length === 0 && piProdSearchQuery && (
                            <div
                              onMouseDown={() => selectPurchaseCustomItem(index, piProdSearchQuery)}
                              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--brand-primary)', fontWeight: 600 }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                              <Plus size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                              {t('addNewProductLabel', { name: piProdSearchQuery }) || `Add "${piProdSearchQuery}" as new product`}
                            </div>
                          )}
                          {piFilteredProducts.length === 0 && !piProdSearchQuery && (
                            <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>{t('typeNewProduct') || 'Type above to add as new product'}</div>
                          )}
                          {piFilteredProducts.map((p, pi) => (
                            <div key={p.id} onMouseDown={() => selectPurchaseProduct(index, p)}
                              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', background: pi === piProdHighlightedIdx ? 'var(--bg-tertiary)' : 'transparent' }}
                              onMouseEnter={() => setPiProdHighlightedIdx(pi)}>
                              <span><strong>{p.name}</strong> <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{p.sku || ''}</span></span>
                              <span style={{ fontWeight: 600, color: 'var(--brand-success)' }}>Rs. {p.purchase_price}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Row 2: Qty, Cost Price, Sale Price */}
                    <div className="pi-item-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('qty') || 'Qty'} *</label>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            type="text"
                            data-calc=""
                            value={row.qty_type === 'pack' && row.units_per_bulk ? (row.quantity / row.units_per_bulk) : row.quantity}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const upb = row.units_per_bulk || 1;
                              const pieces = row.qty_type === 'pack' ? val * upb : val;
                              handlePurchaseInvoiceItemChange(index, 'quantity', pieces);
                            }}
                            style={{ flex: 1, minWidth: 0, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                          />
                          {(row.units_per_bulk && row.units_per_bulk > 1) && (
                            <select value={row.qty_type || 'piece'} onChange={(e) => handlePurchaseInvoiceItemChange(index, 'qty_type', e.target.value)} style={{ padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.7rem', minWidth: '70px' }}>
                              <option value="piece">{t('qtyTypePiece')}</option>
                              <option value="pack">{t('qtyTypePack')}</option>
                            </select>
                          )}
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('purchasePrice') || 'Cost Price'} *</label>
                        <input
                          type="text"
                          data-calc=""
                          value={row.qty_type === 'pack' && row.units_per_bulk ? (row.unit_price * row.units_per_bulk).toFixed(2) : row.unit_price}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const upb = row.units_per_bulk || 1;
                            const piecePrice = row.qty_type === 'pack' ? val / upb : val;
                            handlePurchaseInvoiceItemChange(index, 'unit_price', piecePrice);
                          }}
                          style={{ flex: 1, minWidth: 0, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('salePrice') || 'Sale Price'}</label>
                        <input
                          type="text"
                          data-calc=""
                          value={row.qty_type === 'pack' && row.units_per_bulk ? (row.sale_price * row.units_per_bulk).toFixed(2) : row.sale_price}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const upb = row.units_per_bulk || 1;
                            const piecePrice = row.qty_type === 'pack' ? val / upb : val;
                            handlePurchaseInvoiceItemChange(index, 'sale_price', piecePrice);
                          }}
                          style={{ flex: 1, minWidth: 0, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>
                      {qtyModeHint(row, row.product_id ? products.find(p => p.id === row.product_id) : null)}
                    </div>

                    {/* Row 3: SKU, Unit, Min Stock Alert */}
                    <div className="pi-item-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('skuBarcode') || 'SKU'}</label>
                        <input
                          type="text"
                          placeholder="e.g. ABC-001"
                          value={row.sku || ''}
                          onChange={(e) => handlePurchaseInvoiceItemChange(index, 'sku', e.target.value)}
                          style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('unit') || 'Unit'}</label>
                        <input
                          type="text"
                          placeholder="pcs / kg / ltr"
                          value={row.unit || 'pcs'}
                          onChange={(e) => handlePurchaseInvoiceItemChange(index, 'unit', e.target.value)}
                          style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('lowStockAlert') || 'Low Stock Alert'}</label>
                        <input
                          type="text"
                          data-calc=""
                          value={row.min_stock_alert ?? 5}
                          onChange={(e) => handlePurchaseInvoiceItemChange(index, 'min_stock_alert', parseFloat(e.target.value) || 5)}
                          style={{ flex: 1, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>

                    {/* Row 4: Expiration Date */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('expiryDateOptional') || 'Expiration Date (Optional)'}</label>
                      <input
                        type="date"
                        value={row.expiration_date || ''}
                        onChange={(e) => handlePurchaseInvoiceItemChange(index, 'expiration_date', e.target.value || '')}
                        style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                      />
                    </div>

                    {itemDiscMode && (
                      <div>
                        <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('discount')}</label>
                        <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : t('discount')} value={row.discount ?? ''} onChange={(e) => handlePurchaseInvoiceItemChange(index, 'discount', e.target.value)} style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', textAlign: 'right' }} />
                      </div>
                    )}
                    {/* Subtotal */}
                    <div style={{ textAlign: 'right', fontSize: '0.82rem', color: 'var(--text-muted)', paddingTop: '4px', borderTop: '1px solid var(--border-color)' }}>
                      {t('subtotal') || 'Subtotal'}: <strong style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>Rs. {((parseFloat(row.quantity) || 0) * (parseFloat(row.unit_price) || 0)).toLocaleString()}</strong>
                    </div>
                  </div>
                  );
                })}
                {!isMobileView && (
                  <>
                        {purchaseInvoiceBuilder.items.filter(it => it.product_id || it.custom_item_name).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {purchaseInvoiceBuilder.items.filter(it => it.product_id || it.custom_item_name).map((row, i) => {
                              const product = row.product_id ? products.find(p => p.id === row.product_id) : null;
                              const name = product ? product.name : (row.custom_item_name || '');
                              const upb = row.units_per_bulk || 1;
                              const isPack = row.qty_type === 'pack' && upb > 1;
                              const displayQty = isPack ? (row.quantity / upb) : row.quantity;
                              const displayUnit = isPack ? (displayQty === 1 ? t('pack') : t('packs')) : (displayQty === 1 ? t('piece') : t('pieces'));
                              const displayRate = isPack ? (row.unit_price * upb).toFixed(2) : row.unit_price;
                              // quantity is stored in pieces — show them alongside
                              // so "2 packs" is never mistaken for 2 pieces.
                              const pieceNote = isPack ? ` (${row.quantity} ${t('qtyTypePiece')})` : '';
                              return (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '8px 12px' }}>
                                  <Check size={14} style={{ color: 'var(--brand-success)', flexShrink: 0 }} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{displayQty} {displayUnit}{pieceNote} x Rs.{displayRate}{row.sale_price ? ` | Sale Rs.${isPack ? (row.sale_price * upb).toFixed(2) : row.sale_price}` : ''}</div>
                                  </div>
                                  {itemDiscMode && (
                                    <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : t('discount')} value={row.discount ?? ''} onChange={e => setCommittedPurchaseDiscount(row, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} style={{ width: '92px', padding: '4px 8px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.8rem', textAlign: 'right', flexShrink: 0 }} />
                                  )}
                                  <button type="button" onClick={() => removeCommittedPurchaseItem(i)} title={t('remove') || 'Remove'} style={{ background: 'none', border: 'none', color: 'var(--brand-danger)', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
                                    <X size={15} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div style={{ border: '1px dashed var(--border-color)', borderRadius: '10px', padding: '14px', background: 'var(--bg-tertiary)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ position: 'relative' }}>
                            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('product') || 'Product'}</label>
                            <input
                              ref={purchaseProductInputRef}
                              type="text"
                              placeholder={t('searchProductPH') || 'Search or type product name...'}
                              value={piEntryDropdownOpen ? (piProdSearchQuery || '') : (purchaseEntryItem.product_id ? products.find(p => p.id === purchaseEntryItem.product_id)?.name || '' : purchaseEntryItem.custom_item_name || '')}
                              onFocus={() => { setPiEntryDropdownOpen(true); setPiProdSearchQuery(''); setPiProdHighlightedIdx(0); }}
                              onBlur={() => setTimeout(() => setPiEntryDropdownOpen(false), 200)}
                              onChange={(e) => { setPiProdSearchQuery(e.target.value); setPiEntryDropdownOpen(true); setPiProdHighlightedIdx(0); if (!e.target.value) setPurchaseEntryItem({ ...purchaseEntryItem, product_id: '', custom_item_name: '' }); }}
                              onKeyDown={(e) => {
                                if (!piEntryDropdownOpen) return;
                                if (e.key === 'ArrowDown') { e.preventDefault(); setPiProdHighlightedIdx(prev => Math.min(prev + 1, filteredPiProducts.length - 1)); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); setPiProdHighlightedIdx(prev => Math.max(prev - 1, 0)); }
                                else if (e.key === 'Enter') {
                                  e.preventDefault();
                                  if (filteredPiProducts.length > 0 && filteredPiProducts[piProdHighlightedIdx]) {
                                    selectPurchaseEntryProduct(filteredPiProducts[piProdHighlightedIdx]);
                                  } else if (piProdSearchQuery.trim()) {
                                    selectPurchaseEntryCustomItem(piProdSearchQuery.trim());
                                  }
                                }
                              }}
                              style={{ width: '100%', padding: '9px 36px 9px 12px', border: '1px solid var(--brand-success)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                            <button type="button" title={t('selectFromGallery') || 'Select from Gallery'}
                              onClick={() => {
                                setGallerySelectMode(true);
                                setGallerySelectTarget({ type: 'purchaseEntry' });
                                setActiveTab('settings');
                                setSettingsSubTab('gallery');
                              }}
                              style={{ position: 'absolute', right: '6px', top: '32px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
                              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                            >
                              <Camera size={16} />
                            </button>
                            {piEntryDropdownOpen && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto', zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
                                {filteredPiProducts.length === 0 && piProdSearchQuery && (
                                  <div
                                    onMouseDown={() => selectPurchaseEntryCustomItem(piProdSearchQuery)}
                                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--brand-primary)', fontWeight: 600 }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                  >
                                    <Plus size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                    {t('addNewProductLabel', { name: piProdSearchQuery }) || `Add "${piProdSearchQuery}" as new product`}
                                  </div>
                                )}
                                {filteredPiProducts.length === 0 && !piProdSearchQuery && (
                                  <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>{t('typeNewProduct') || 'Type above to add as new product'}</div>
                                )}
                                {filteredPiProducts.map((p, pi) => {
                                  const upb = numOr(p.units_per_bulk, 1);
                                  const displayPrice = purchaseEntryItem.qty_type === 'pack' && upb > 1
                                    ? round2(numOr(p.purchase_price, 0) * upb)
                                    : numOr(p.purchase_price, 0);
                                  return (
                                    <div key={p.id} onMouseDown={() => selectPurchaseEntryProduct(p)}
                                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-primary)', display: 'flex', justifyContent: 'space-between', background: pi === piProdHighlightedIdx ? 'var(--bg-tertiary)' : 'transparent' }}
                                      onMouseEnter={() => setPiProdHighlightedIdx(pi)}>
                                      <span><strong>{p.name}</strong> <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{p.sku || ''}</span></span>
                                      <span style={{ fontWeight: 600, color: 'var(--brand-success)' }}>Rs. {displayPrice.toLocaleString()}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <div className="pi-item-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('qty') || 'Qty'} *</label>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <input type="text" data-calc="" value={purchaseEntryItem.qty_type === 'pack' && purchaseEntryItem.units_per_bulk ? (purchaseEntryItem.quantity / purchaseEntryItem.units_per_bulk) : purchaseEntryItem.quantity} onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  const upb = purchaseEntryItem.units_per_bulk || 1;
                                  const pieces = purchaseEntryItem.qty_type === 'pack' ? val * upb : val;
                                  setPurchaseEntryItem({ ...purchaseEntryItem, quantity: pieces });
                                }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPurchaseEntryItem(); } }} style={{ flex: 1, minWidth: 0, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                                {(purchaseEntryItem.units_per_bulk && purchaseEntryItem.units_per_bulk > 1) && (
                                  <select value={purchaseEntryItem.qty_type || 'piece'} onChange={(e) => {
                                    const mode = e.target.value;
                                    lastQtyTypeRef.current = mode;
                                    const upb = packSizeFor(purchaseEntryItem, purchaseEntryItem.product_id ? products.find(p => p.id === purchaseEntryItem.product_id) : null);
                                    setPurchaseEntryItem({ ...purchaseEntryItem, qty_type: mode, quantity: initialPiecesFor(mode, upb, purchaseEntryItem.quantity) });
                                  }} style={{ padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.7rem', minWidth: '70px' }}>
                                    <option value="piece">{t('qtyTypePiece')}</option>
                                    <option value="pack">{t('qtyTypePack')}</option>
                                  </select>
                                )}
                              </div>
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('purchasePrice') || 'Cost Price'} *</label>
                              <input type="text" data-calc="" value={purchaseEntryItem.qty_type === 'pack' && purchaseEntryItem.units_per_bulk ? (purchaseEntryItem.unit_price * purchaseEntryItem.units_per_bulk).toFixed(2) : purchaseEntryItem.unit_price} onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const upb = purchaseEntryItem.units_per_bulk || 1;
                                const piecePrice = purchaseEntryItem.qty_type === 'pack' ? val / upb : val;
                                setPurchaseEntryItem({ ...purchaseEntryItem, unit_price: piecePrice });
                              }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPurchaseEntryItem(); } }} style={{ flex: 1, minWidth: 0, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('salePrice') || 'Sale Price'}</label>
                              <input
                                type="text"
                                data-calc=""
                                value={purchaseEntryItem.qty_type === 'pack' && purchaseEntryItem.units_per_bulk ? (purchaseEntryItem.sale_price * purchaseEntryItem.units_per_bulk).toFixed(2) : purchaseEntryItem.sale_price}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  const upb = purchaseEntryItem.units_per_bulk || 1;
                                  const piecePrice = purchaseEntryItem.qty_type === 'pack' ? val / upb : val;
                                  setPurchaseEntryItem({ ...purchaseEntryItem, sale_price: piecePrice });
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPurchaseEntryItem(); } }}
                                style={{ flex: 1, minWidth: 0, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                              />
                            </div>
                            {itemDiscMode && (
                              <div>
                                <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('discount')}</label>
                                <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : '0'} value={purchaseEntryItem.discount ?? ''} onChange={(e) => setPurchaseEntryItem({ ...purchaseEntryItem, discount: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPurchaseEntryItem(); } }} style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', textAlign: 'right' }} />
                              </div>
                            )}
                            {qtyModeHint(purchaseEntryItem, purchaseEntryItem.product_id ? products.find(p => p.id === purchaseEntryItem.product_id) : null)}
                          </div>

                          <div className="pi-item-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('skuBarcode') || 'SKU'}</label>
                              <input
                                type="text"
                                placeholder="e.g. ABC-001"
                                value={purchaseEntryItem.sku || ''}
                                onChange={(e) => setPurchaseEntryItem({ ...purchaseEntryItem, sku: e.target.value })}
                                style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('unit') || 'Unit'}</label>
                              <input
                                type="text"
                                placeholder="pcs / kg / ltr"
                                value={purchaseEntryItem.unit || 'pcs'}
                                onChange={(e) => setPurchaseEntryItem({ ...purchaseEntryItem, unit: e.target.value })}
                                style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('lowStockAlert') || 'Low Stock Alert'}</label>
                              <input
                                type="text"
                                data-calc=""
                                value={purchaseEntryItem.min_stock_alert ?? 5}
                                onChange={(e) => setPurchaseEntryItem({ ...purchaseEntryItem, min_stock_alert: parseFloat(e.target.value) || 5 })}
                                style={{ flex: 1, padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                              />
                            </div>
                          </div>

                          {/* Expiration Date */}
                          <div>
                            <label style={{ display: 'block', fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('expiryDateOptional') || 'Expiration Date (Optional)'}</label>
                            <input
                              type="date"
                              value={purchaseEntryItem.expiration_date || ''}
                              onChange={(e) => setPurchaseEntryItem({ ...purchaseEntryItem, expiration_date: e.target.value || '' })}
                              style={{ width: '100%', padding: '9px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem' }}
                            />
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={() => commitPurchaseEntryItem()} className="btn btn-primary" style={{ padding: '8px 20px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Check size={14} /> {t('addItem') || 'Add Item'}
                            </button>
                          </div>
                        </div>
                      </>
                )}
              </div>
            </div>
            {isMobileView && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <button type="button" onClick={addPurchaseInvoiceItemRow} className="btn btn-secondary" style={{ padding: '8px 20px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={14} /> {t('addItemRow') || 'Add Item Row'}
              </button>
            </div>
            )}

            <div className="glass-panel" style={{ padding: '16px', marginBottom: '24px', background: 'var(--bg-tertiary)' }}>
              {(() => {
                const sub = calcPurchaseTotal();
                const disc = purchaseDiscountTotal();
                const tot = Math.max(0, sub - disc);
                const paid = parseFloat(purchaseInvoiceBuilder.paid_amount) || 0;
                const rem = Math.max(0, tot - paid);
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-muted)' }}>{t('subtotalLabel') || 'Subtotal'}:</span>
                      <span style={{ fontSize: '1rem', color: 'var(--text-primary)' }}>Rs. {sub.toLocaleString()}</span>
                    </div>
                    {itemDiscMode && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: wholeDiscMode ? '8px' : '12px', borderBottom: wholeDiscMode ? 'none' : '1px dashed var(--border-color)', paddingBottom: wholeDiscMode ? 0 : '12px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-muted)' }}>{wholeDiscMode ? (t('discountItemsLabel') || 'Item Discount') : (t('discount') || 'Discount')}:</span>
                        <span style={{ width: '120px', textAlign: 'right', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>Rs. {purchaseItemDiscountSum().toLocaleString()}</span>
                      </div>
                    )}
                    {wholeDiscMode && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '12px', borderBottom: '1px dashed var(--border-color)', paddingBottom: '12px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-muted)' }}>{itemDiscMode ? (t('discountInvoiceLabel') || 'Invoice Discount') : (t('discount') || 'Discount')}:</span>
                        <input type="text" data-calc="" placeholder={isPercentDiscount ? '0%' : '0'} value={purchaseInvoiceBuilder.discount_amount} onChange={e => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, discount_amount: e.target.value })} style={{ width: '120px', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', textAlign: 'right' }} />
                        {isPercentDiscount && <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-muted)' }}>%</span>}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{t('grandTotal')}:</span>
                      <strong style={{ fontSize: '1.25rem', color: 'var(--accent-primary)' }}>
                        Rs. {tot.toLocaleString()}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{t('paidAmount')}:</span>
                      <input type="text" data-calc="" value={purchaseInvoiceBuilder.paid_amount} onChange={e => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, paid_amount: e.target.value })} style={{ width: '120px', padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem', textAlign: 'right' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '40px', fontSize: '1rem', fontWeight: 600, color: 'var(--accent-primary)' }}>
                      <span>{t('remainingBalance')}:</span>
                      <span>Rs. {rem.toLocaleString()}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: purchaseInvoiceBuilder.status === 'PAID' ? '1fr 1fr' : '1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('status')}</label>
                <select
                  value={purchaseInvoiceBuilder.status}
                  onChange={(e) => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, status: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                >
                  <option value="UNPAID">{t('unpaid')}</option>
                  <option value="PAID">{t('paid')}</option>
                </select>
              </div>

              {purchaseInvoiceBuilder.status === 'PAID' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px' }}>{t('paymentMethod')}</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[['CASH', t('cash') || 'Cash', Wallet], ['ONLINE', t('online') || 'Online', Smartphone]].map(([val, label, icon]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, payment_method: val })}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '8px',
                            border: purchaseInvoiceBuilder.payment_method === val ? '2px solid var(--brand-success)' : '1px solid var(--border-color)',
                            background: purchaseInvoiceBuilder.payment_method === val ? 'rgba(34,197,94,0.1)' : 'var(--bg-tertiary)',
                            color: 'var(--text-primary)',
                            fontWeight: purchaseInvoiceBuilder.payment_method === val ? 600 : 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            fontSize: '0.85rem'
                          }}
                        >
                          <DynIcon icon={icon} size={15} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {purchaseInvoiceBuilder.payment_method === 'ONLINE' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('bankAccount') || 'Bank Account'}</label>
                        <select
                          value={purchaseInvoiceBuilder.bank_account_id || ''}
                          onChange={(e) => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, bank_account_id: e.target.value })}
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                        >
                          <option value="">{t('selectBankAccount') || '-- Select Bank Account --'}</option>
                          {bankAccounts.filter(acc => !acc.is_deleted && (!selectedBusiness || String(acc.business) === String(selectedBusiness.id))).map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.name || acc.account_title || 'Bank Account'}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('paymentReference') || 'Payment Reference'}</label>
                        <input
                          type="text"
                          value={purchaseInvoiceBuilder.payment_reference || ''}
                          onChange={(e) => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, payment_reference: e.target.value })}
                          placeholder="Txn ref / transfer ID"
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('invoiceNumber')}</label>
                <input
                  type="text"
                  required
                  value={purchaseInvoiceBuilder.invoice_number}
                  onChange={(e) => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, invoice_number: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('dateOfIssue')}</label>
                <input
                  type="date"
                  required
                  value={purchaseInvoiceBuilder.issue_date}
                  onChange={(e) => setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, issue_date: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('time') || 'Time'}</label>
                <input
                  type="time"
                  value={purchaseInvoiceBuilder.issue_time}
                  onChange={(e) => { setPurchaseInvoiceBuilder({ ...purchaseInvoiceBuilder, issue_time: e.target.value }); setPiTimeManual(true); }}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
              </div>
              <div></div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => setShowPurchaseInvoiceModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button
                type="button"
                className="btn btn-success"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                onClick={() => saveAndWhatsApp({
                  phone: parties.find(p => p.id === purchaseInvoiceBuilder.party_id)?.phone || '',
                  text: formatWAMessage('invoice_message', waPurchaseInvoiceVars()),
                  card: waPurchaseInvoiceCard(),
                  save: () => submitPurchaseInvoice(null, false),
                })}
                title={t('sendInvoiceWA')}
              >
                <Share2 size={16} /> {t('placeOrder')} & {t('whatsapp')}
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('saveAndPrint') || 'Save & Print'}</button>
            </div>
          </form>
        </div>
      )}

      {/* =================================================== */}
      {/* ADD PRODUCT MODAL                                  */}
      {/* =================================================== */}
      {showAddProductModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <form onSubmit={handleAddProduct} className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '520px', background: 'var(--bg-secondary)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700 }}>{t('addNewProduct')}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>{t('addItemToInventory')}</p>
              </div>
              <button type="button" onClick={() => { setShowAddProductModal(false); setShowAddProductTypeInline(false); setNewProductTypeName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              {/* Product Name - full width */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('productName')} *</label>
                <input
                  required
                  type="text"
                  placeholder={t('prodNamePH')}
                  value={newProduct.name}
                  onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
              </div>
              {/* Purchase Price Section */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('purchasePrice')} *</label>
                <input
                  required
                  type="text"
                  data-calc=""
                  value={newProduct.purchase_price}
                  onChange={e => setNewProduct({ ...newProduct, purchase_price: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <SaveHint value={newProduct.purchase_price} unitsPerPack={newProduct.units_per_bulk} kind="price" t={t} />
              </div>

              {/* Sale Price Section */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('salePrice')} *</label>
                <input
                  required
                  type="text"
                  data-calc=""
                  value={newProduct.sale_price}
                  onChange={e => setNewProduct({ ...newProduct, sale_price: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <SaveHint value={newProduct.sale_price} unitsPerPack={newProduct.units_per_bulk} kind="price" t={t} />
              </div>

              {/* Stock Quantity Section */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('stockQty')} *</label>
                <input
                  required
                  type="text"
                  data-calc=""
                  value={newProduct.stock_quantity}
                  onChange={e => setNewProduct({ ...newProduct, stock_quantity: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <SaveHint value={newProduct.stock_quantity} unitsPerPack={newProduct.units_per_bulk} kind="qty" t={t} />
              </div>

              {/* Units Quantity (Pieces per Pack) Section */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('unitsQty')} *</label>
                <input
                  required
                  type="number"
                  step="1"
                  min="1"
                  data-calc=""
                  placeholder="e.g. 12"
                  value={newProduct.units_per_bulk === '' || newProduct.units_per_bulk === null || newProduct.units_per_bulk === undefined ? '' : newProduct.units_per_bulk}
                  onChange={e => setNewProduct(changeUnits(newProduct, e.target.value))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>{t('unitsQtyHelper')}</div>
              </div>

              {/* Low Stock Limit */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>
                  {t('lowStockAlert')} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({t('optional')})</span>
                </label>
                <input
                  type="text"
                  data-calc=""
                  value={newProduct.min_stock_alert}
                  onChange={e => setNewProduct({ ...newProduct, min_stock_alert: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
                <SaveHint value={newProduct.min_stock_alert} unitsPerPack={newProduct.units_per_bulk} kind="qty" t={t} />
              </div>

              {/* Expiration Date */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('expiryDateOptional')}</label>
                <input
                  type="date"
                  value={newProduct.expiration_date}
                  onChange={e => setNewProduct({ ...newProduct, expiration_date: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                />
              </div>
              {/* SKU */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('skuBarcode')}</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    placeholder={t('skuPH')}
                    value={newProduct.sku}
                    onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })}
                    style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                  />
                  <button type="button" onClick={() => setNewProduct({ ...newProduct, sku: generateSku() })} className="btn btn-secondary" style={{ padding: '8px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }} title={t('generateBarcode')}>
                    <Barcode size={14} />
                  </button>
                </div>
                {newProduct.sku && (
                  <div style={{ marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                    <BarcodePreview value={newProduct.sku} height={35} fontSize={10} />
                  </div>
                )}
              </div>
              {/* Product Type */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('productType') || 'Product Type'}</label>
                {showAddProductTypeInline ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input type="text" autoFocus placeholder={t('productTypePH') || 'e.g. Bakery, Electronics'} value={newProductTypeName} onChange={e => setNewProductTypeName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNewProductType(); } if (e.key === 'Escape') { setShowAddProductTypeInline(false); setNewProductTypeName(''); } }} style={{ flex: 1, padding: '10px 12px', border: '1px solid var(--brand-success)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }} />
                    <button type="button" onClick={addNewProductType} className="btn btn-primary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('save')}</button>
                    <button type="button" onClick={() => { setShowAddProductTypeInline(false); setNewProductTypeName(''); }} className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: '0.8rem' }}>{t('cancel')}</button>
                  </div>
                ) : (
                  <select value={newProduct.product_type} onChange={e => { if (e.target.value === '__add_new__') { setShowAddProductTypeInline(true); } else { setNewProduct({ ...newProduct, product_type: e.target.value }); } }} style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                    <option value="">{t('selectProductType') || '-- Select Type --'}</option>
                    {productTypes.map(pt => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                    <option value="__add_new__">+ {t('addNewProductType') || 'Add New Type'}</option>
                  </select>
                )}
              </div>
              {/* FBR Digital Invoicing  only when this business turned FBR on.
                  These three travel with every line of every FBR invoice. */}
              {fbrEnabled && (
                <div style={{ gridColumn: 'span 2', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', background: 'var(--bg-tertiary)' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '10px' }}>FBR (used on every invoice line)</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>HS Code</label>
                      <input
                        type="text"
                        placeholder="8 digits  e.g. 01012100"
                        value={newProduct.hsn_code}
                        onChange={e => setNewProduct({ ...newProduct, hsn_code: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      />
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>FBR rejects any line without one.</div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>FBR tax rate</label>
                      <select
                        value={newProduct.fbr_tax_rate}
                        onChange={e => setNewProduct({ ...newProduct, fbr_tax_rate: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      >
                        <option value="">Business default ({fbrConfig?.default_tax_rate || '0%'})</option>
                        {['0%', '1%', '2%', '3%', '4%', '5%', '10%', '15%', '17%', '18%', '19%', '20%', '25%'].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>FBR unit of measure</label>
                      <input
                        type="text"
                        placeholder="Leave empty to use the product's unit (pcs / kg / ltr …)"
                        value={newProduct.fbr_uom}
                        onChange={e => setNewProduct({ ...newProduct, fbr_uom: e.target.value })}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>{t('productPhoto') || 'Product Photo'}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', border: '2px dashed var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem', transition: 'border-color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
                >
                  <Camera size={18} />
                  <span>{productPhotoFile ? productPhotoFile.name : (t('browsePhoto') || 'Browse Photo')}</span>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setProductPhotoFile(e.target.files[0] || null)} />
                </label>
                {productPhotoFile && (
                  <button type="button" onClick={() => setProductPhotoFile(null)} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--bg-tertiary)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem' }}>
                    <X size={14} />
                  </button>
                )}
              </div>
              {productPhotoFile && (
                <img src={URL.createObjectURL(productPhotoFile)} alt="Preview" style={{ marginTop: '8px', width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-color)' }} />
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => { setShowAddProductModal(false); setShowAddProductTypeInline(false); setNewProductTypeName(''); }} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('addToCatalog')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Renewal Plan Selection Modal */}
      {showRenewalPlanModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 }}>
          <div className="glass-panel anim-scale-in" style={{ padding: '32px', width: '100%', maxWidth: '420px', background: 'var(--bg-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700 }}>{t('selectRenewalPlan')}</h3>
              <button onClick={() => setShowRenewalPlanModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '20px' }}>{t('selectRenewalDesc')}</p>
            {[
              { value: '2_days', label: t('plan2Days'), desc: t('trialDesc') || '2-day free trial' },
              { value: '1_month', label: '1 Month', desc: t('bestForTrying') },
              { value: '1_year', label: '1 Year', desc: t('mostPopular') },
              { value: '3_years', label: '3 Years', desc: t('bestValue') },
              { value: 'lifetime', label: t('planLifetime'), desc: 'Never expire' },
            ].map(plan => (
              <div
                key={plan.value}
                onClick={() => setSelectedRenewalPlan(plan.value)}
                style={{
                  padding: '14px 16px', borderRadius: '10px', marginBottom: '10px', cursor: 'pointer',
                  border: selectedRenewalPlan === plan.value ? '2px solid #6366f1' : '1px solid var(--border-color)',
                  background: selectedRenewalPlan === plan.value ? 'rgba(99,102,241,0.08)' : 'var(--bg-tertiary)',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{plan.label}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>{plan.desc}</div>
                  </div>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '50%', border: selectedRenewalPlan === plan.value ? '2px solid #6366f1' : '2px solid var(--border-color)',
                    background: selectedRenewalPlan === plan.value ? '#6366f1' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    {selectedRenewalPlan === plan.value && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />}
                  </div>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={() => setShowRenewalPlanModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>{t('cancel')}</button>
              <button onClick={() => handleRenewalRequest(selectedRenewalPlan)} className="btn btn-primary" style={{ flex: 1 }}>{t('sendRequest')}</button>
            </div>
          </div>
        </div>
      )}



      {/* Floating Calculator Button */}
      {!calculatorState.isOpen && calculatorTarget && (
        <button
          onClick={() => setCalculatorState({ isOpen: true, value: '', label: 'Calculator', onUpdate: calculatorTarget })}
          style={{
            position: 'fixed', bottom: '24px', right: '24px', zIndex: 999,
            width: '52px', height: '52px', borderRadius: '50%',
            background: 'var(--accent-primary)', color: '#fff',
            border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Calculator"
        >
          <Calculator size={22} />
        </button>
      )}

      <CalculatorOverlay calculatorState={calculatorState} setCalculatorState={setCalculatorState} />

      {/* ATTACHMENT PREVIEW MODAL - rendered via Portal to document.body */}
      <AttachmentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
    </>
  );
}


export default function AppWithI18n() {
  return (
    <I18nProvider>
      <App />
    </I18nProvider>
  );
}
